"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnCfbLogicalSchemaConverter = void 0;
exports.convertOrCadDsnCfbToLogicalSchema = convertOrCadDsnCfbToLogicalSchema;
exports.parseOrCadDsnCfbLogicalSchema = parseOrCadDsnCfbLogicalSchema;
const LogicalSchema_1 = require("../dsn/LogicalSchema");
const OrcadDsnCfbParser_1 = require("./OrcadDsnCfbParser");
const OrcadDsnCfbMaterializer_1 = require("./OrcadDsnCfbMaterializer");
const OrcadDsnCfbDeclarativeSpecs_1 = require("./OrcadDsnCfbDeclarativeSpecs");
const OrcadCaptureFontMetrics_1 = require("./OrcadCaptureFontMetrics");
const OrcadDsnCfbTypes_1 = require("./OrcadDsnCfbTypes");
const OrcadDsnCfbOccurrenceOwnership_1 = require("./OrcadDsnCfbOccurrenceOwnership");
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
const base64_1 = require("../../../../Utils/base64");
const portOccurrenceNormalization_1 = require("../../portOccurrenceNormalization");
const orcadLibraryProvenance_1 = require("../../orcadLibraryProvenance");
var OrcadDsnCfbLogicalSchemaConverter;
(function (OrcadDsnCfbLogicalSchemaConverter) {
    const isCapsymPowerSymbolName = OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.isCapsymPowerSymbolName;
    let DiagnosticSeverity;
    (function (DiagnosticSeverity) {
        DiagnosticSeverity["Info"] = "info";
        DiagnosticSeverity["Warning"] = "warning";
        DiagnosticSeverity["Error"] = "error";
    })(DiagnosticSeverity = OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity || (OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity = {}));
    let PhysicalPadRowSource;
    (function (PhysicalPadRowSource) {
        PhysicalPadRowSource["CatalogScan"] = "catalog_scan";
        PhysicalPadRowSource["FramedRecord"] = "framed_record";
    })(PhysicalPadRowSource || (PhysicalPadRowSource = {}));
    let PhysicalPadOutcome;
    (function (PhysicalPadOutcome) {
        PhysicalPadOutcome["Decoded"] = "decoded";
        PhysicalPadOutcome["PadsUndecoded"] = "pads_undecoded";
        PhysicalPadOutcome["NoPins"] = "no_pins";
    })(PhysicalPadOutcome || (PhysicalPadOutcome = {}));
    const PHYSICAL_PIN_DIAGNOSTIC_LIMIT_PER_REASON = 25;
    let MultipartSectionState;
    (function (MultipartSectionState) {
        MultipartSectionState["Resolved"] = "resolved";
        MultipartSectionState["ReferenceWithheld"] = "reference_withheld";
        MultipartSectionState["NoOccurrenceAnnotation"] = "no_occurrence_annotation";
        MultipartSectionState["Withheld"] = "withheld";
    })(MultipartSectionState || (MultipartSectionState = {}));
    const LAST_FOUR_DIGIT_YEAR_MS = Date.UTC(9999, 11, 31, 23, 59, 59, 999);
    const DEFAULT_COLOR_VALUE = 48;
    const DEFAULT_LINE_STYLE = 0;
    const DEFAULT_LINE_WIDTH = 0;
    const DEFAULT_NET_LINE_STYLE = 5;
    const DEFAULT_NET_LINE_WIDTH = 3;
    const DEFAULT_DISPLAY_TYPE_VALUE = 1;
    const DEFAULT_PIN_TYPE = 4;
    const UNNAMED_SYMBOL_PIN = '$PIN0';
    const PAGE_ALIAS_STRING_MARKER = 0x0003_0000;
    const DEFAULT_PIN_NAMES_VISIBLE = false;
    const DEFAULT_PIN_NUMBERS_VISIBLE = false;
    const DEFAULT_PIN_NAMES_ROTATED = true;
    const POWER_PIN_TYPE = 7;
    const PinFlag = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinDisplayFlag;
    const UNNAMED_DISPLAY_PROPERTY_PREFIX = '<unnamed property ';
    const UNNAMED_DISPLAY_PROPERTY_NAME = (propertyCode) => `${UNNAMED_DISPLAY_PROPERTY_PREFIX}${propertyCode}>`;
    function emitCatalogFallbackDiagnostics(document, schematics, options) {
        if (!options.diagnostics) {
            return;
        }
        const hasStringCatalog = document.streams.some((stream) => stream.parsed?.kind === 'library' && (stream.parsed.stringCatalog?.length ?? 0) > 0);
        if (!hasStringCatalog) {
            options.diagnostics.push({
                severity: 'warning',
                message: 'CFB DSN Library string catalog could not be decoded; no part carries catalog metadata and no display record carries a name',
            });
            return;
        }
        let partInstsWithoutProperties = 0;
        for (const schematic of schematics) {
            for (const page of schematic.Page) {
                for (const partInst of page.PartInst ?? []) {
                    if ((partInst.UserProperty ?? []).length === 0) {
                        partInstsWithoutProperties++;
                    }
                }
            }
        }
        let unnamedDisplayProperties = 0;
        for (const schematic of schematics) {
            for (const page of schematic.Page) {
                const converted = [
                    ...(page.PartInst ?? []).flatMap((part) => part.DisplayProperty ?? []),
                    ...(page.DrawnInst ?? []).flatMap((drawnInst) => drawnInst.DisplayProperty ?? []),
                    ...(page.Global ?? []).flatMap((global) => (global.DisplayProperty ? [global.DisplayProperty] : [])),
                    ...(page.OffPageConnector ?? []).flatMap((connector) => connector.OffPageConnectorDisplayProp ?? []),
                    ...(page.Port ?? []).flatMap((port) => port.PortDisplayProp ?? []),
                    ...(page.TitleBlock ?? []).flatMap((titleBlock) => titleBlock.DisplayProperty ?? []),
                ];
                unnamedDisplayProperties += converted.filter((property) => property.Name.startsWith(UNNAMED_DISPLAY_PROPERTY_PREFIX)).length;
            }
        }
        if (partInstsWithoutProperties > 0) {
            options.diagnostics.push({
                severity: 'warning',
                message: `CFB DSN materialised no user properties for ${partInstsWithoutProperties} part instance(s); the design's property rows name none for them`,
            });
        }
        if (unnamedDisplayProperties > 0) {
            options.diagnostics.push({
                severity: 'warning',
                message: `CFB DSN Library string catalog names no property for ${unnamedDisplayProperties} display record(s); they are reported under their numeric property code`,
            });
        }
    }
    function convertDocument(document, options = {}) {
        const collectedDiagnostics = (options.diagnostics ??= []);
        for (const diagnostic of document.diagnostics ?? []) {
            collectedDiagnostics.push({
                severity: diagnostic.severity,
                message: `CFB decode [${diagnostic.code}] ${diagnostic.message}`,
                streamPath: diagnostic.streamPath,
                offset: diagnostic.offset,
            });
        }
        const pages = getPageStreams(document);
        const physicalPinDiagnostics = createPhysicalPinDiagnosticState(collectedDiagnostics);
        const cacheCatalog = buildCacheCatalog(document, physicalPinDiagnostics);
        const libraryInstanceCatalog = buildLibraryInstanceCatalog(document);
        const occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document);
        const recordedRootName = occurrenceTrust.streamVetoes.size === 0 && occurrenceTrust.viewVetoes.size === 0 ?
            uniqueRecordedRootName(document, document.schematicViews.length > 0 ?
                document.schematicViews.map((stream) => stream.schematicName ?? stream.viewName)
                : Array.from(new Set(pages.map((page) => page.parsed.viewName))))
            : undefined;
        const libraryStringCatalog = buildLibraryStringCatalog(document);
        const cisSchematicInfoCatalog = buildCisSchematicInfoCatalog(document);
        reportUndecodedSymbolPinFlagBits(document, physicalPinDiagnostics);
        reportUndecodedSymbolPinFields(document, physicalPinDiagnostics);
        const partValueConnectorGeometryGroups = collectPartValueConnectorGeometryGroups(document);
        const liveConnectorPins = buildLiveConnectorSymbolPinCatalog(document, physicalPinDiagnostics);
        const packageViewPinCatalog = buildPackageViewPinCatalog(document, cacheCatalog, physicalPinDiagnostics);
        const instanceSourceLibraryCatalog = buildInstanceSourceLibraryCatalog(document, physicalPinDiagnostics);
        const markerSourceLibraryCatalog = buildMarkerSourceLibraryCatalog(document);
        calibrateRecordedPlacedPorts(document);
        const physicalPinNumberCatalogs = buildPhysicalPinNumberCatalogs(document, cacheCatalog, physicalPinDiagnostics);
        const placedPartCacheIdentityCatalog = buildPlacedPartCacheIdentityCatalog(document, cacheCatalog, packageViewPinCatalog, physicalPinNumberCatalogs.pinNumbers, instanceSourceLibraryCatalog, markerSourceLibraryCatalog, physicalPinDiagnostics);
        recoverMissingPlacedPartReferences(document, cacheCatalog, placedPartCacheIdentityCatalog, physicalPinDiagnostics);
        const sheetSymbolPackageCatalog = buildSheetSymbolPackageCatalog(document, options.sourceFileName, options.diagnostics);
        const hierarchySheetReferenceCatalog = buildHierarchySheetReferenceCatalog(document, physicalPinDiagnostics, occurrenceTrust);
        const hierarchyOccurrenceCatalog = buildHierarchyOccurrenceCatalog(document, physicalPinDiagnostics, occurrenceTrust);
        const partInstanceOccurrenceReferences = new WeakMap();
        const variantPropertyCatalog = buildVariantPropertyCatalog(document);
        const pageTextOptions = pageCommentTextOptionsForDocument(document);
        const defaultFont = font();
        const convertedCache = buildCache(document, options, cacheCatalog, physicalPinNumberCatalogs.pinNumbers, physicalPinNumberCatalogs.sectionCounts, physicalPinNumberCatalogs.numberingStyles, physicalPinNumberCatalogs.padPresence, physicalPinDiagnostics, placedPartCacheIdentityCatalog, markerSourceLibraryCatalog, liveConnectorPins, partValueConnectorGeometryGroups);
        const pageCommentTextCount = document.streams.reduce((total, stream) => stream.parsed?.kind === 'page' ?
            total + (stream.decodedPrimitives ?? []).filter((primitive) => primitive.kind === 'comment-text').length
            : total, 0);
        if (pageCommentTextCount > 0 && !pageTextOptions.defaultFontName) {
            emitBudgetedDiagnostic(physicalPinDiagnostics, '', 'comment-text-font-unnamed', `no design default names a text face, so ${pageCommentTextCount} page comment text record(s) carry no font name`, 'CFB text metric', 'info');
        }
        reportUndecodedPinLabelVisibility((convertedCache.Package ?? []).reduce((total, pkg) => total + (pkg.LibPart ?? []).reduce((count, libPart) => count + (libPart.NormalView?.length ?? 0), 0), 0), physicalPinDiagnostics);
        const connectorSymbolGeometryCatalog = buildConnectorSymbolGeometryCatalog(document, physicalPinDiagnostics, partValueConnectorGeometryGroups, convertedCache, liveConnectorPins);
        populatePlacedSymbolSourceLibraries(document, placedPartCacheIdentityCatalog, markerSourceLibraryCatalog, physicalPinDiagnostics);
        const multipartSectionSuffixes = buildMultipartSectionSuffixLookup(document, placedPartCacheIdentityCatalog, physicalPinNumberCatalogs.sectionCounts, physicalPinDiagnostics, occurrenceTrust);
        const schematicViewNames = new WeakMap();
        const schematics = buildSchematics(document, pages, options, pageTextOptions, defaultFont, cacheCatalog, markerSourceLibraryCatalog, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog, connectorSymbolGeometryCatalog, placedPartCacheIdentityCatalog, physicalPinNumberCatalogs.padPresence, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, variantPropertyCatalog, multipartSectionSuffixes, physicalPinDiagnostics, occurrenceTrust, recordedRootName, schematicViewNames);
        const storedIntersheetReferences = intersheetReferenceValues(document, options.diagnostics);
        applyIntersheetReferenceValues(schematics, storedIntersheetReferences, pageStreamPathsByPageName(pages), options.diagnostics);
        const rootName = selectRootSchematicName(document, schematics, physicalPinDiagnostics, occurrenceTrust, recordedRootName);
        const maxEpoch = maxPageModifyEpoch(pages);
        emitCatalogFallbackDiagnostics(document, schematics, options);
        finalizePhysicalPinDiagnostics(physicalPinDiagnostics);
        return {
            DesignExportVersion: 17,
            Name: options.sourceFileName ?? findDesignName(document) ?? rootName,
            ObjectType: 'SCHEMATIC',
            RootName: rootName,
            UseExplicitDbId: true,
            DateTime: dateTimeFromEpoch(maxEpoch),
            DefaultValues: defaultValues(defaultFont),
            Cache: convertedCache,
            Netgroup: { Bundle: [] },
            Schematic: schematics,
            Occurrences: buildOccurrences(schematics, rootName, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, buildOccurrenceNetSystemIdCatalog(document, physicalPinDiagnostics, occurrenceTrust), schematicViewNames, physicalPinDiagnostics),
            Cis: {
                Groups: {},
                BomVariants: {},
            },
            IntersheetReferenceValues: Array.from(new Set(storedIntersheetReferences.values())),
        };
    }
    OrcadDsnCfbLogicalSchemaConverter.convertDocument = convertDocument;
    const IntersheetReferenceValuePattern = /^(\[Grid.+\]|\d+[!^]?(,\d+[!^]?)*)$/;
    const IntersheetReferenceUserPropertyName = 'IREF';
    const OrCadStoredIntersheetReferenceConflictDiagnosticType = 'iref-stored-conflict';
    const StoredIntersheetReferenceDbIdByteLength = 4;
    const StoredIntersheetReferenceIndexBackOffset = 20;
    const StoredIntersheetReferenceIndexLead = 12;
    const IntersheetReferenceRoles = new Set([
        'port',
        'off-page-connector',
    ]);
    function storedIntersheetReferenceKey(pageStreamPath, role, dbId) {
        return JSON.stringify([pageStreamPath, role, dbId]);
    }
    function pageStreamLookupKey(viewName, pageName) {
        return JSON.stringify([viewName, pageName]);
    }
    function pageStreamPathsByPageName(pages) {
        const paths = new Map();
        const ambiguous = new Set();
        for (const page of pages) {
            const key = pageStreamLookupKey(page.parsed.viewName, page.parsed.pageName);
            const existing = paths.get(key);
            if (existing !== undefined && existing !== page.stream.path) {
                ambiguous.add(key);
                continue;
            }
            paths.set(key, page.stream.path);
        }
        for (const key of ambiguous) {
            paths.delete(key);
        }
        return paths;
    }
    function libraryStringCatalog(document) {
        const catalog = [];
        for (const stream of document.streams) {
            if (stream.parsed?.kind !== 'library') {
                continue;
            }
            for (const value of stream.parsed.stringCatalog ?? []) {
                catalog.push(value);
            }
        }
        return catalog;
    }
    function storedIntersheetReferenceIndexes(record, strings) {
        const recordEnd = record.offset + record.byteLength;
        const earliestIndexOffset = Math.max(0, record.offset - StoredIntersheetReferenceIndexLead);
        const indexes = [];
        for (const string of strings) {
            if (string.offset >= recordEnd) {
                break;
            }
            const valueIndex = string.precedingInt32Le20;
            if (valueIndex === undefined || string.offset < record.offset || string.value !== record.symbolName) {
                continue;
            }
            if (string.followingUInt32Le !== record.dbId) {
                continue;
            }
            if (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(string) + StoredIntersheetReferenceDbIdByteLength >
                recordEnd) {
                continue;
            }
            if (string.offset - StoredIntersheetReferenceIndexBackOffset < earliestIndexOffset) {
                continue;
            }
            indexes.push(valueIndex);
        }
        return indexes;
    }
    function intersheetReferenceValues(document, diagnostics) {
        const catalog = libraryStringCatalog(document);
        const values = new Map();
        const conflicting = new Set();
        const conflictingConnectors = new Set();
        let outOfRangeIndexes = 0;
        let unshapedValues = 0;
        for (const stream of document.streams) {
            const records = stream.decodedPageObjects;
            if (!records) {
                continue;
            }
            const strings = [...(stream.lengthPrefixedStrings ?? [])].sort((left, right) => left.offset - right.offset);
            for (const record of records) {
                if (record.kind !== 'placed-symbol' || !IntersheetReferenceRoles.has(record.role)) {
                    continue;
                }
                const key = storedIntersheetReferenceKey(stream.path, record.role, record.dbId);
                for (const valueIndex of storedIntersheetReferenceIndexes(record, strings)) {
                    if (valueIndex < 0 || valueIndex >= catalog.length) {
                        outOfRangeIndexes++;
                        conflicting.add(key);
                        continue;
                    }
                    const value = catalog[valueIndex];
                    if (value.trim().length === 0) {
                        continue;
                    }
                    if (!IntersheetReferenceValuePattern.test(value)) {
                        unshapedValues++;
                        conflicting.add(key);
                        continue;
                    }
                    const existing = values.get(key);
                    if (existing !== undefined && existing !== value) {
                        conflictingConnectors.add(key);
                        conflicting.add(key);
                        continue;
                    }
                    values.set(key, value);
                }
            }
        }
        for (const key of conflicting) {
            values.delete(key);
        }
        if (diagnostics) {
            if (outOfRangeIndexes > 0) {
                diagnostics.push({
                    type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
                    severity: 'warning',
                    message: `${outOfRangeIndexes} connector placement record reading(s) point outside the Library string catalog, so those connectors carry no stored intersheet reference`,
                });
            }
            if (unshapedValues > 0) {
                diagnostics.push({
                    type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
                    severity: 'warning',
                    message: `${unshapedValues} connector placement record reading(s) point at a Library string that is not an intersheet-reference value, so those connectors carry no stored value`,
                });
            }
            if (conflictingConnectors.size > 0) {
                diagnostics.push({
                    type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
                    severity: 'warning',
                    message: `${conflictingConnectors.size} connector(s) are given two different intersheet-reference values by their placement records, so they carry none`,
                });
            }
        }
        return values;
    }
    function canonicalIntersheetReferenceValue(value) {
        const collapsed = value.trim().replace(/\s+/g, ' ');
        if (!/^\d/.test(collapsed)) {
            return collapsed;
        }
        const pages = collapsed
            .split(',')
            .map((token) => token.trim())
            .filter((token) => token.length > 0);
        return Array.from(new Set(pages)).sort().join(',');
    }
    function applyIntersheetReferenceValues(schematics, values, pageStreamPaths, diagnostics) {
        let conflicts = 0;
        let unmappedPages = 0;
        const reconcile = (key, userProperties) => {
            const value = key === undefined ? undefined : values.get(key);
            if (value === undefined) {
                return userProperties;
            }
            const present = userProperties ?? [];
            const existing = present.filter((property) => property.Name === IntersheetReferenceUserPropertyName);
            if (existing.length === 0) {
                return [...present, { Name: IntersheetReferenceUserPropertyName, Value: value }];
            }
            const canonical = canonicalIntersheetReferenceValue(value);
            if (existing.some((property) => canonicalIntersheetReferenceValue(property.Value) !== canonical)) {
                conflicts++;
                return present.filter((property) => property.Name !== IntersheetReferenceUserPropertyName);
            }
            let kept = false;
            return present.filter((property) => {
                if (property.Name !== IntersheetReferenceUserPropertyName) {
                    return true;
                }
                const first = !kept;
                kept = true;
                return first;
            });
        };
        for (const schematic of schematics) {
            for (const page of schematic.Page) {
                const streamPath = pageStreamPaths.get(pageStreamLookupKey(schematic.Name, page.Name));
                if (streamPath === undefined) {
                    unmappedPages++;
                    continue;
                }
                for (const port of page.Port ?? []) {
                    port.PortUserProp = reconcile(storedIntersheetReferenceKey(streamPath, 'port', port.DbId), port.PortUserProp);
                }
                for (const connector of page.OffPageConnector ?? []) {
                    connector.OffPageConnectorUserProp = reconcile(storedIntersheetReferenceKey(streamPath, 'off-page-connector', connector.DbId), connector.OffPageConnectorUserProp);
                }
            }
        }
        if (diagnostics && conflicts > 0) {
            diagnostics.push({
                type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
                severity: 'warning',
                message: `${conflicts} connector(s) spell one intersheet reference in their own page record and another in the Library string catalog, so they carry none`,
            });
        }
        if (diagnostics && unmappedPages > 0) {
            diagnostics.push({
                type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
                severity: 'warning',
                message: `${unmappedPages} page(s) share a view and page name with another page stream, so their connectors carry no stored intersheet reference`,
            });
        }
    }
    function nearMissSchematicViewKey(name) {
        return name.replace(/[\s_-]+/g, '').toUpperCase();
    }
    function hierarchyClaimedViewKeys(document, diagnostics, occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
        const keys = new Set();
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind !== 'hierarchy') {
                continue;
            }
            for (const entry of parsed.entries) {
                if (!entry.contentsViewName) {
                    continue;
                }
                if (withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream.path, entry.contentsViewName, entry.refInstId ?? entry.dbId, 'root-selection')) {
                    continue;
                }
                if (entry.contentsViewName === parsed.viewName) {
                    if (entry.dbId !== undefined) {
                        emitBudgetedDiagnostic(diagnostics, parsed.viewName, 'hierarchy-view-draws-itself', `view "${parsed.viewName}" carries a sheet-symbol instance whose child is that same view, which the parse does not expand`, 'CFB design', 'warning');
                    }
                    continue;
                }
                keys.add(entry.contentsViewName);
            }
        }
        return keys;
    }
    function reportSchematicViewNearMisses(unclaimed, viewFolderByExportedName, childViewNames, diagnostics) {
        const claimedNearKeys = new Map();
        for (const claimed of childViewNames) {
            claimedNearKeys.set(nearMissSchematicViewKey(claimed), claimed);
        }
        for (const schematic of unclaimed) {
            const viewFolder = viewFolderByExportedName.get(schematic.Name) ?? schematic.Name;
            const near = claimedNearKeys.get(nearMissSchematicViewKey(schematic.Name)) ??
                claimedNearKeys.get(nearMissSchematicViewKey(viewFolder));
            if (near === undefined) {
                continue;
            }
            emitBudgetedDiagnostic(diagnostics, schematic.Name, 'schematic-view-name-near-miss', `no hierarchy row names schematic "${schematic.Name}", and one names "${near}", which differs only in case or separators`, 'CFB design', 'warning');
        }
    }
    function selectRootSchematicName(document, schematics, diagnostics, occurrenceTrust, recordedRootName) {
        const childViewNames = hierarchyClaimedViewKeys(document, diagnostics, occurrenceTrust);
        if (occurrenceTrust.streamVetoes.size > 0 || occurrenceTrust.viewVetoes.size > 0) {
            emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', 'root-selection: incomplete hierarchy claims cannot establish an unclaimed root', 'CFB hierarchy', 'warning');
            return '';
        }
        const viewFolderByExportedName = new Map();
        for (const stream of document.schematicViews) {
            viewFolderByExportedName.set(stream.schematicName ?? stream.viewName, stream.viewName);
        }
        const unclaimed = schematics.filter((schematic) => {
            const viewFolder = viewFolderByExportedName.get(schematic.Name) ?? schematic.Name;
            return !childViewNames.has(schematic.Name) && !childViewNames.has(viewFolder);
        });
        reportSchematicViewNearMisses(unclaimed, viewFolderByExportedName, childViewNames, diagnostics);
        if (recordedRootName !== undefined) {
            return recordedRootName;
        }
        if (unclaimed.length === 1) {
            return unclaimed[0].Name;
        }
        if (schematics.length > 0) {
            const candidates = unclaimed.map((schematic) => `"${schematic.Name}"`).join(', ');
            emitBudgetedDiagnostic(diagnostics, 'design', 'root-schematic-ambiguous', `schematics=${schematics.length} unclaimedByHierarchy=${unclaimed.length}${candidates === '' ? '' : ` (${candidates})`}; the design names no single root`, 'CFB design', 'warning');
        }
        return '';
    }
    function parseLogicalSchema(fileContents, options = {}) {
        return convertDocument((0, OrcadDsnCfbParser_1.parseOrCadDsnCfb)(fileContents, options), options);
    }
    OrcadDsnCfbLogicalSchemaConverter.parseLogicalSchema = parseLogicalSchema;
    OrcadDsnCfbLogicalSchemaConverter.__testing = {
        hierarchyClaimedViewKeys,
        buildOccurrenceNetSystemIdCatalog,
        buildMultipartSectionSuffixLookup,
        buildHierarchyReferenceLookup,
        buildHierarchyOccurrenceCatalog,
        sectionIndexMatchesDesignator,
        arcExtentPoints,
        decodedViewSuffix,
        portSymbolBoundingBox,
        preferredProjectModuleDsnPath,
        createPhysicalPinDiagnosticState,
        referencePrefixOfDesignator,
    };
    function getPageStreams(document) {
        const pages = [];
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind === 'page') {
                pages.push({ stream, parsed, index: pages.length });
            }
        }
        return pages;
    }
    function buildVariantPropertyCatalog(document) {
        const catalog = new Map();
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind !== 'variant-text') {
                continue;
            }
            for (const row of parsed.rows) {
                const properties = variantUserPropertiesFromRow(row.properties ?? []);
                const byName = new Map(properties.map((property) => [property.Name, property.Value]));
                const partNumber = byName.get('Part_Number');
                const value = byName.get('Value');
                if (!partNumber || !value) {
                    continue;
                }
                catalog.set(variantPropertyKey(partNumber, value), properties);
            }
        }
        return catalog;
    }
    function variantUserPropertiesFromRow(properties) {
        const result = [];
        for (const property of properties) {
            const name = variantPropertyName(property.name);
            if (!name || property.value.length === 0 || property.value === 'UNDEFINED') {
                continue;
            }
            result.push({ Name: name, Value: property.value });
        }
        return result;
    }
    function variantPropertyName(name) {
        switch (name) {
            case 'Part Number':
                return 'Part_Number';
            case 'Part type':
                return 'Part_Type';
            case 'Value':
            case 'Description':
            case 'Tolerance':
            case 'Implementation':
            case 'PCB Footprint':
                return name;
            default:
                return undefined;
        }
    }
    function variantPropertyKey(partNumber, value) {
        return `${partNumber.toUpperCase()}|${normalizeCatalogValue(value).toUpperCase()}`;
    }
    function buildSchematics(document, pages, options, pageTextOptions, defaultFont, cacheCatalog, markerSourceLibraries, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog, connectorSymbolGeometryCatalog, placedPartCacheIdentityCatalog, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, variantPropertyCatalog, multipartSectionSuffixes, physicalPinDiagnostics, occurrenceTrust, recordedRootName, schematicViewNames) {
        const pageGroups = new Map();
        for (const page of pages) {
            const group = pageGroups.get(page.parsed.viewName) ?? [];
            group.push(page);
            pageGroups.set(page.parsed.viewName, group);
        }
        const schematicStreams = sortSchematicStreams(document.schematicViews.length > 0 ? document.schematicViews : synthesizeSchematicStreams(pageGroups), hierarchyClaimedViewKeys(document, physicalPinDiagnostics, occurrenceTrust), recordedRootName);
        return schematicStreams.map((schematicStream) => {
            const schematicPages = (pageGroups.get(schematicStream.viewName) ?? [])
                .map((page) => buildPage(page, pages.length, options, pageTextOptions, defaultFont, cacheCatalog, markerSourceLibraries, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog, connectorSymbolGeometryCatalog, placedPartCacheIdentityCatalog, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, variantPropertyCatalog, multipartSectionSuffixes, physicalPinDiagnostics))
                .sort(compareSchematicPagesByXmlOrder);
            const schematic = {
                Name: schematicStream.schematicName ?? schematicStream.viewName,
                StoredPhysicalNetNames: rootHierarchyPhysicalNetNames(document, schematicStream.viewName, physicalPinDiagnostics),
                MaxDbId: maxDbId(schematicPages),
                DateTime: dateTimeFromEpoch(maxPageModifyEpoch(pageGroups.get(schematicStream.viewName) ?? [])),
                Page: schematicPages,
            };
            schematicViewNames.set(schematic, schematicStream.viewName);
            return schematic;
        });
    }
    function compareSchematicPagesByXmlOrder(left, right) {
        const leftOrdinal = pageOrdinalFromName(left.Name);
        const rightOrdinal = pageOrdinalFromName(right.Name);
        if (leftOrdinal !== undefined && rightOrdinal !== undefined && leftOrdinal !== rightOrdinal) {
            return leftOrdinal - rightOrdinal;
        }
        if (leftOrdinal !== undefined && rightOrdinal === undefined) {
            return -1;
        }
        if (leftOrdinal === undefined && rightOrdinal !== undefined) {
            return 1;
        }
        return left.Name.localeCompare(right.Name);
    }
    function pageOrdinalFromName(name) {
        const match = /^(?:PAGE[_\s-]*)?0*(\d+)(?:\\b|[_\s-])/i.exec(name);
        if (!match) {
            return undefined;
        }
        const ordinal = Number(match[1]);
        return Number.isFinite(ordinal) ? ordinal : undefined;
    }
    function sortSchematicStreams(streams, claimedViewKeys, recordedRootName) {
        const isRoot = (stream) => !claimedViewKeys.has(stream.viewName) && !claimedViewKeys.has(stream.schematicName ?? stream.viewName);
        return [...streams].sort((left, right) => {
            const leftIsRecordedRoot = (left.schematicName ?? left.viewName) === recordedRootName;
            const rightIsRecordedRoot = (right.schematicName ?? right.viewName) === recordedRootName;
            if (leftIsRecordedRoot !== rightIsRecordedRoot) {
                return leftIsRecordedRoot ? -1 : 1;
            }
            const leftIsRoot = isRoot(left);
            const rightIsRoot = isRoot(right);
            if (leftIsRoot !== rightIsRoot) {
                return leftIsRoot ? -1 : 1;
            }
            return 0;
        });
    }
    function uniqueRecordedRootName(document, candidateNames) {
        const recordedRootNames = document.streams.flatMap((stream) => stream.parsed?.kind === 'library' && stream.parsed.recordedRootName !== undefined ?
            [stream.parsed.recordedRootName]
            : []);
        if (recordedRootNames.length !== 1) {
            return undefined;
        }
        const recordedRootName = recordedRootNames[0];
        return candidateNames.filter((name) => name === recordedRootName).length === 1 ? recordedRootName : undefined;
    }
    function synthesizeSchematicStreams(pageGroups) {
        return Array.from(pageGroups, ([viewName, group]) => ({
            kind: 'schematic',
            viewName,
            schematicName: viewName,
            pageCountHint: group.length,
            pageNames: group.map((page) => page.parsed.pageName),
        }));
    }
    function buildPage(page, pageCount, options, pageTextOptions, defaultFont, cacheCatalog, markerSourceLibraries, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog, connectorSymbolGeometryCatalog, placedPartCacheIdentityCatalog, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, variantPropertyCatalog, multipartSectionSuffixes, physicalPinDiagnostics) {
        const pageSizeInfo = pageSizeInfoFromCfb(page.parsed.pageSizeInfo);
        const pageObjects = page.stream.decodedPageObjects ?? [];
        const primitives = page.stream.decodedPrimitives ?? [];
        const pageObjectNetNames = buildPageObjectNetNameLookup(pageObjects, page.stream.lengthPrefixedStrings, connectorSymbolGeometryCatalog, physicalPinDiagnostics);
        fillMissingPlacedConnectorNetNames(pageObjects, pageObjectNetNames, page.stream.lengthPrefixedStrings, connectorSymbolGeometryCatalog, physicalPinDiagnostics);
        const titleBlocks = pageObjects
            .filter(isPlacedSymbolRole('titleblock'))
            .map((object) => buildTitleBlock(object, pageCount));
        if (titleBlocks.length > 0 && storedPageNumber(titleBlocks) === undefined) {
            emitBudgetedDiagnostic(physicalPinDiagnostics, page.parsed.pageName, 'title-block-page-number-unresolved', `page ${page.parsed.pageName} stores no single title-block page number`, 'CFB page numbering', 'warning');
        }
        const globals = pageObjects
            .filter((object) => isPlacedSymbolRole('global')(object))
            .map((object) => buildGlobal(object, pageObjectNetNames, physicalPinDiagnostics))
            .sort(compareNamedPageObjects);
        const offPageConnectors = pageObjects
            .filter(isPlacedSymbolRole('off-page-connector'))
            .map((object) => buildOffPageConnector(object, pageObjectNetNames, pageObjects, physicalPinDiagnostics));
        const ports = pageObjects
            .filter(isLogicalPagePort)
            .map((object) => buildPort(object, pageObjectNetNames, physicalPinDiagnostics));
        const pageReferenceLookup = buildPageReferenceLookup(page.stream.lengthPrefixedStrings, pageObjects, cacheCatalog, placedPartCacheIdentityCatalog, physicalPinDiagnostics, page.parsed.pageName);
        const placedParts = pageObjects.filter(isPlacedSymbolRole('part-inst')).sort(comparePageObjectDbId);
        const placedLibraryRecordNames = new Map();
        const placedLibraryRecordNameCounts = new Map();
        for (const object of placedParts) {
            const packageName = object.resolvedMetadata?.pkgName ?? stripNormalViewSuffix(object.graphicName);
            const recordName = libraryEntryForPlacedPackage(libraryInstanceCatalog, object.dbId, packageName)?.record.name;
            if (recordName) {
                placedLibraryRecordNames.set(object.dbId, recordName);
                placedLibraryRecordNameCounts.set(recordName, (placedLibraryRecordNameCounts.get(recordName) ?? 0) + 1);
            }
        }
        const partInsts = placedParts.map((object) => buildPartInst(object, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog.get(cisSchematicInfoKey(page.parsed.viewName, page.parsed.pageName)), placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, multipartSectionSuffixes, pageReferenceLookup, variantPropertyCatalog, physicalPinDiagnostics, (placedLibraryRecordNameCounts.get(placedLibraryRecordNames.get(object.dbId) ?? '') ?? 0) > 1, page.parsed.viewName));
        applySheetReferenceDisplayOverrides(partInsts, page.stream.lengthPrefixedStrings, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences);
        enrichDrawnInstPortsFromPageConnectivity(pageObjects, physicalPinDiagnostics, page.stream.lengthPrefixedStrings);
        enrichDrawnInstReferences(pageObjects, physicalPinDiagnostics, page.parsed.pageName);
        const drawnInstPortDbIds = buildDrawnInstPortDbIdLookup(pageObjects, physicalPinDiagnostics);
        const drawnInsts = pageObjects
            .filter(isDrawnInst)
            .sort(comparePageObjectDbId)
            .map((object) => buildDrawnInst(object, libraryStringCatalog, physicalPinDiagnostics, drawnInstPortDbIds.get(object)));
        const busEntries = buildBusEntries(pageObjects);
        const primitiveGraphics = buildPagePrimitiveGraphics(primitives, pageTextOptions);
        const nets = buildPageNets(page.stream, options, defaultFont, physicalPinDiagnostics);
        return {
            Name: page.parsed.pageName,
            TitleBlockDisplayed: true,
            BorderDisplayed: true,
            GridRefDisplayed: true,
            PageSizeInfo: pageSizeInfo,
            PageSize: page.parsed.pageSize ?? `${pageSizeInfo.Width}x${pageSizeInfo.Height}`,
            OuterBorderMargin: { X: 0, Y: 0 },
            OuterLineStyle: DEFAULT_LINE_STYLE,
            OuterLineWidth: DEFAULT_LINE_WIDTH,
            OuterBorderIsVisible: true,
            InnerBorderMargin: { X: 0, Y: 0 },
            InnerLineStyle: DEFAULT_LINE_STYLE,
            InnerLineWidth: DEFAULT_LINE_WIDTH,
            InnerLineVisible: true,
            Color: color(),
            Font: font(),
            LabelColor: color(),
            HorizontalCount: 2,
            HorizontalWidth: 10,
            HorizontalChar: 0,
            HorizontalVisible: true,
            HorizontalAscending: false,
            HorizontalStyle: DEFAULT_LINE_STYLE,
            HorizontalSeparatorWidth: DEFAULT_LINE_WIDTH,
            VerticalCount: 2,
            VerticalWidth: 6,
            VerticalChar: 1,
            VerticalVisible: true,
            VerticalAscending: false,
            VerticalStyle: DEFAULT_LINE_STYLE,
            VerticalSeparatorWidth: DEFAULT_LINE_WIDTH,
            PageDateTime: pageDateTime(page.parsed, physicalPinDiagnostics),
            ...(titleBlocks.length > 0 ? { TitleBlock: titleBlocks } : {}),
            ...(nets.NetScalar.length > 0 ? { NetScalar: nets.NetScalar } : {}),
            ...(nets.NetBus.length > 0 ? { NetBus: nets.NetBus } : {}),
            ...(busEntries.length > 0 ? { BusEntry: busEntries } : {}),
            ...(globals.length > 0 ? { Global: globals } : {}),
            ...(partInsts.length > 0 ? { PartInst: partInsts } : {}),
            ...(drawnInsts.length > 0 ? { DrawnInst: drawnInsts } : {}),
            ...(ports.length > 0 ? { Port: ports } : {}),
            ...(offPageConnectors.length > 0 ? { OffPageConnector: offPageConnectors } : {}),
            ...primitiveGraphics,
        };
    }
    function buildCache(document, options, cacheCatalog, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPadPresenceCatalog, physicalPinDiagnostics, placedPartCacheIdentityCatalog, markerSourceLibraries, liveConnectorPins, partValueConnectorGeometryGroups) {
        const packageStreams = document.streams.filter((stream) => stream.parsed?.kind === 'package');
        const symbolStreams = document.streams.filter((stream) => stream.parsed?.kind === 'symbol');
        const pageSymbolRoles = collectPageSymbolRoles(document);
        const titleBlockSymbol = buildTitleBlockSymbol(document, pageSymbolRoles);
        const cache = {};
        const cacheObjectGroups = groupRootCacheObjects(document, physicalPinDiagnostics);
        const placedViewEvidence = buildPlacedViewEvidenceCatalog(document, placedPartCacheIdentityCatalog);
        const livePackages = packageStreams.map((stream) => ({
            pkg: buildPackage(stream, physicalPinDiagnostics, placedViewEvidence),
            hasDecodedPhysicalPart: (stream.decodedSymbolObjects?.filter(isPinNumberMap).length ?? 0) > 0,
        }));
        const rootCachePackages = buildRootCachePackages(cacheObjectGroups, cacheCatalog, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPinDiagnostics, placedViewEvidence);
        const cachePackageNames = new Set(rootCachePackages.map(({ pkg }) => crossSourcePackageKey(pkg.Name)));
        const cacheLibrariesByPackageName = new Map();
        for (const { pkg } of rootCachePackages) {
            const key = crossSourcePackageKey(pkg.Name);
            const libraries = cacheLibrariesByPackageName.get(key) ?? new Set();
            libraries.add(canonicalSourceLibraryName(pkg.SourceLibName));
            cacheLibrariesByPackageName.set(key, libraries);
        }
        const decodedLiveSectionsByPackage = new Map();
        for (const { pkg, hasDecodedPhysicalPart } of livePackages) {
            if (!hasDecodedPhysicalPart) {
                continue;
            }
            const sections = decodedLiveSectionsByPackage.get(crossSourcePackageKey(pkg.Name)) ?? new Set();
            for (const libPart of pkg.LibPart) {
                sections.add(stripNormalViewSuffix(libPart.CellName ?? pkg.Name));
            }
            decodedLiveSectionsByPackage.set(crossSourcePackageKey(pkg.Name), sections);
        }
        const packages = [
            ...livePackages.flatMap(({ pkg, hasDecodedPhysicalPart }) => {
                if (hasDecodedPhysicalPart || !cachePackageNames.has(crossSourcePackageKey(pkg.Name))) {
                    return [pkg];
                }
                emitPhysicalPinDiagnostic(physicalPinDiagnostics, pkg.Name, 'duplicate-live-package', `name="${pkg.Name}" src="${pkg.SourceLibName}"`, pkg.SourceLibName);
                return [];
            }),
            ...rootCachePackages.flatMap(({ pkg, fallbackCellNames, markedSectionNumberingStyle }) => {
                const decodedLiveSections = decodedLiveSectionsByPackage.get(crossSourcePackageKey(pkg.Name));
                if (!decodedLiveSections) {
                    return [pkg];
                }
                const definingLibraries = cacheLibrariesByPackageName.get(crossSourcePackageKey(pkg.Name));
                if (definingLibraries && definingLibraries.size > 1) {
                    emitPhysicalPinDiagnostic(physicalPinDiagnostics, pkg.Name, 'live-package-library-ambiguous', `name="${pkg.Name}" src="${pkg.SourceLibName}" libraries=${definingLibraries.size}`, pkg.SourceLibName);
                    return [pkg];
                }
                const keptLibParts = pkg.LibPart.filter((libPart) => {
                    const cellName = stripNormalViewSuffix(libPart.CellName ?? pkg.Name);
                    return !(fallbackCellNames.has(cellName) && decodedLiveSections.has(cellName));
                });
                if (keptLibParts.length === pkg.LibPart.length) {
                    return [pkg];
                }
                emitPhysicalPinDiagnostic(physicalPinDiagnostics, pkg.Name, 'duplicate-fallback-cache-package', `name="${pkg.Name}" src="${pkg.SourceLibName}" dropped=${pkg.LibPart.length - keptLibParts.length} kept=${keptLibParts.length}`, pkg.SourceLibName);
                if (keptLibParts.length === 0) {
                    return [];
                }
                return [
                    {
                        ...pkg,
                        AlphabeticNumbering: markedSectionNumberingStyle ?? (keptLibParts.length > 1 ? 1 : 2),
                        IsHomogeneous: keptLibParts.length <= 1,
                        LibPart: keptLibParts,
                    },
                ];
            }),
        ];
        for (const pkg of packages) {
            if (!/\.DSN$/i.test(pkg.SourceLibName)) {
                continue;
            }
            for (const libPart of pkg.LibPart) {
                const pins = libPart.NormalView?.[0]?.PinScalar ?? [];
                if ((!libPart.PhysicalPart || libPart.PhysicalPart.length === 0) &&
                    pins.length === 3 &&
                    pins.every((pin) => isNumericString(pin.Name))) {
                    libPart.PhysicalPart = [{ PinNumber: pins.map((pin) => ({ Position: pin.Position, Number: pin.Name })) }];
                    emitPhysicalPinDiagnostic(physicalPinDiagnostics, libPart.CellName ?? pkg.Name, 'dsn-three-pin-physical-part-synthesized', `cell="${libPart.CellName}" source="${pkg.SourceLibName}"`, pkg.SourceLibName, 'info');
                }
            }
        }
        const placedSourcePackages = buildPlacedSourcePackages(document, rootCachePackages.map(({ pkg }) => pkg), placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, physicalPinDiagnostics);
        const placedSourcePackageIdentities = new Set(placedSourcePackages.map((pkg) => packageSourceKey(pkg.Name, pkg.SourceLibName)));
        packages.splice(0, packages.length, ...packages.filter((pkg) => !placedSourcePackageIdentities.has(packageSourceKey(pkg.Name, pkg.SourceLibName))), ...placedSourcePackages);
        const packageIdentities = new Set(packages.map((pkg) => packageSourceKey(pkg.Name, pkg.SourceLibName)));
        const placedPackages = buildLivePartPackages(document, packageIdentities, placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, physicalPinDiagnostics);
        const placedPackageIdentities = new Set(placedPackages.map((pkg) => packageSourceKey(pkg.Name, pkg.SourceLibName)));
        packages.splice(0, packages.length, ...packages.filter((pkg) => !placedPackageIdentities.has(packageSourceKey(pkg.Name, pkg.SourceLibName))), ...placedPackages);
        diagnoseMissingSourcePackageBodies(document, packages, physicalPinDiagnostics);
        if (packages.length > 0) {
            cache.Package = packages;
        }
        const portSymbols = [];
        const offPageSymbols = [];
        const globalSymbols = [];
        const ercSymbols = [];
        for (const stream of symbolStreams) {
            const name = stream.parsed.symbolName;
            if (isPortSymbolName(name, pageSymbolRoles)) {
                portSymbols.push(buildPortSymbol(stream));
            }
            else if (isOffPageSymbolName(name)) {
                offPageSymbols.push(buildOffPageSymbol(stream));
            }
            else if (isErcSymbolName(name)) {
                ercSymbols.push(buildErcSymbol(stream));
            }
            else if (pageSymbolRoles.get(name) === 'global' || isCapsymPowerSymbolName(name)) {
                globalSymbols.push(buildGlobalSymbol(stream, liveConnectorPins, physicalPinDiagnostics));
            }
            else if (pageSymbolRoles.has(name)) {
                globalSymbols.push(buildGlobalSymbol(stream, liveConnectorPins, physicalPinDiagnostics));
            }
        }
        addRootCacheConnectorSymbols([...cacheObjectGroups, ...partValueConnectorGeometryGroups], document, pageSymbolRoles, portSymbols, offPageSymbols, globalSymbols, ercSymbols, liveConnectorPins, physicalPinDiagnostics);
        addMissingLiveConnectorSymbols(document, pageSymbolRoles, portSymbols, offPageSymbols, globalSymbols, liveConnectorPins, physicalPinDiagnostics);
        if (portSymbols.length > 0) {
            cache.PortSymbol = portSymbols;
        }
        if (offPageSymbols.length > 0) {
            cache.OffPageSymbol = offPageSymbols;
        }
        if (globalSymbols.length > 0) {
            cache.GlobalSymbol = globalSymbols;
        }
        if (ercSymbols.length > 0) {
            cache.ERCSymbol = ercSymbols;
        }
        if (titleBlockSymbol) {
            cache.TitleBlockSymbol = [titleBlockSymbol];
        }
        emitDerivedSymbolBoundingBoxDiagnostics(cache, options);
        if (!cache.Package && !cache.PortSymbol && !cache.OffPageSymbol && !cache.GlobalSymbol && !cache.TitleBlockSymbol) {
            options.diagnostics?.push({ severity: 'warning', message: 'CFB DSN cache contained no materialized symbols' });
        }
        return cache;
    }
    function addMissingLiveConnectorSymbols(document, pageSymbolRoles, portSymbols, offPageSymbols, globalSymbols, liveConnectorPins, diagnostics) {
        const existingPortSymbols = new Set(portSymbols.map((symbol) => symbol.Name));
        const existingOffPageSymbols = new Set(offPageSymbols.map((symbol) => symbol.Name));
        const existingGlobalSymbols = new Set(globalSymbols.map((symbol) => symbol.Name));
        const sourceLibNames = buildSymbolSourceCatalog(document);
        for (const [symbolName, role] of pageSymbolRoles) {
            if (role === 'part-inst' || role === 'titleblock') {
                continue;
            }
            const pin = liveConnectorSymbolPin(liveConnectorPins, symbolName, sourceLibNames.get(symbolName) ?? '');
            const boundingBox = pin ? boundingBoxFromSymbolPins([pin]) : { X1: 0, X2: 0, Y1: 0, Y2: 0 };
            if (role === 'global' && !existingGlobalSymbols.has(symbolName)) {
                globalSymbols.push({
                    Name: symbolName,
                    SourceLibName: '',
                    BoundingBox: boundingBox,
                    Line: [],
                    PinScalar: symbolPinsFromCorroboratedLivePin(symbolName, pin, diagnostics),
                });
                existingGlobalSymbols.add(symbolName);
                continue;
            }
            if (role === 'off-page-connector' && !existingOffPageSymbols.has(symbolName)) {
                offPageSymbols.push({
                    Name: symbolName,
                    SourceLibName: '',
                    BoundingBox: boundingBox,
                    PinScalar: pin ? [offPageSymbolPinScalarFromCfb(pin, 0)] : [],
                });
                existingOffPageSymbols.add(symbolName);
                continue;
            }
            if (role === 'port' && !existingPortSymbols.has(symbolName)) {
                portSymbols.push({
                    Name: symbolName,
                    SourceLibName: '',
                    BoundingBox: boundingBox,
                    ...(pin ? { PinScalar: symbolPinScalarFromCfb(pin) } : {}),
                });
                existingPortSymbols.add(symbolName);
            }
        }
    }
    function groupRootCacheObjects(document, physicalPinDiagnostics) {
        const cacheStream = document.streams.find((stream) => stream.parsed?.kind === 'cache');
        if (!cacheStream) {
            return [];
        }
        const sourceLibNamesByOffset = buildCacheSourceLibNameOffsetCatalog(cacheStream);
        const entries = [
            ...(cacheStream.decodedSymbolObjects ?? []).map((record) => ({ offset: record.offset, record })),
            ...(cacheStream.decodedPrimitives ?? []).map((record) => ({ offset: record.offset, record })),
        ].sort((left, right) => left.offset - right.offset);
        const groups = [];
        let current;
        for (const entry of entries) {
            const record = entry.record;
            if (isCacheObjectGroupHeader(record)) {
                if (current && (current.records.length > 1 || current.primitives.length > 0)) {
                    groups.push(current);
                }
                current = {
                    name: record.name,
                    headerKind: record.kind,
                    sourceLibName: sourceLibNamesByOffset.get(record.offset),
                    records: [record],
                    primitives: [],
                };
                continue;
            }
            if (!current) {
                continue;
            }
            if (isSymbolObjectRecord(record)) {
                current.records.push(record);
            }
            else {
                current.primitives.push(record);
            }
        }
        if (current && (current.records.length > 1 || current.primitives.length > 0)) {
            groups.push(current);
        }
        if (physicalPinDiagnostics) {
            reportViewPrimitiveCountMismatches(groups, physicalPinDiagnostics);
        }
        return resolveBlankSourceCacheGroups(groups, physicalPinDiagnostics);
    }
    function reportViewPrimitiveCountMismatches(groups, diagnostics) {
        let undecodedHeaders = 0;
        for (const group of groups) {
            const header = group.records[0];
            if (header?.kind !== 'view-header') {
                continue;
            }
            if (header.primitiveCount === undefined) {
                undecodedHeaders++;
                continue;
            }
            if (header.primitiveCount === group.primitives.length) {
                continue;
            }
            emitPhysicalPinDiagnostic(diagnostics, group.name, 'view-primitive-count-mismatch', `declared=${header.primitiveCount} decoded=${group.primitives.length}`, group.sourceLibName);
        }
        if (undecodedHeaders > 0) {
            emitBudgetedDiagnostic(diagnostics, '', 'view-primitive-count-undecoded', `views=${undecodedHeaders}`, 'CFB symbol', 'info');
        }
    }
    function resolveBlankSourceCacheGroups(groups, diagnostics) {
        const sourcesByCell = new Map();
        for (const group of groups) {
            const canonicalSource = canonicalSourceLibraryName(group.sourceLibName);
            if (!canonicalSource) {
                continue;
            }
            const cellName = stripNormalViewSuffix(group.name);
            const sources = sourcesByCell.get(cellName) ?? new Map();
            sources.set(canonicalSource, group.sourceLibName ?? '');
            sourcesByCell.set(cellName, sources);
        }
        return groups.map((group) => {
            if (canonicalSourceLibraryName(group.sourceLibName)) {
                return group;
            }
            const cellName = stripNormalViewSuffix(group.name);
            const sources = sourcesByCell.get(cellName);
            if ((sources?.size ?? 0) >= 2 && diagnostics) {
                emitPhysicalPinDiagnostic(diagnostics, cellName, 'blank-source-group', `cell="${cellName}" candidates=${sources?.size ?? 0}`);
            }
            return group;
        });
    }
    function buildCacheSourceLibNameOffsetCatalog(cacheStream) {
        const catalog = new Map();
        for (const source of cacheStream.cacheSourceLibraries ?? []) {
            catalog.set(source.headerOffset, source.library.value);
        }
        return catalog;
    }
    function buildPackageViewPinCatalog(document, cacheCatalog, physicalPinDiagnostics) {
        const candidatesByIdentity = new Map();
        const boundsByPins = new Map();
        for (const group of groupRootCacheObjects(document, physicalPinDiagnostics)) {
            if (group.headerKind !== 'view-header') {
                continue;
            }
            const cellName = stripNormalViewSuffix(group.name);
            const entry = catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName, physicalPinDiagnostics);
            const packageName = entry?.packageName ?? cellName;
            const sourceLibName = entry?.sourceLibName ?? group.sourceLibName ?? '';
            const pins = completeCacheSymbolPins(group);
            const bounds = group.records.find((record) => record.kind === 'view-header')?.declaredBoundingBox;
            if (bounds) {
                boundsByPins.set(pins, JSON.stringify(bounds));
            }
            if (pins.length > 0) {
                for (const viewName of [group.name, cellName]) {
                    const key = cacheViewPinIdentityKey(viewName, packageName, sourceLibName);
                    const candidates = candidatesByIdentity.get(key) ?? [];
                    candidates.push(pins);
                    candidatesByIdentity.set(key, candidates);
                }
            }
        }
        const pinsByIdentity = new Map();
        const ambiguousIdentities = new Set();
        const pinsWithDeclaredBounds = new Set();
        for (const [key, candidates] of candidatesByIdentity) {
            const distinct = new Map(candidates.map((pins) => [cachePinVectorSignature(pins), pins]));
            if (distinct.size === 1) {
                for (const pins of distinct.values()) {
                    pinsByIdentity.set(key, pins);
                    if (candidates.every((candidate) => boundsByPins.has(candidate)) &&
                        new Set(candidates.map((candidate) => boundsByPins.get(candidate))).size === 1) {
                        pinsWithDeclaredBounds.add(pins);
                    }
                }
            }
            else {
                ambiguousIdentities.add(key);
            }
        }
        return { pinsByIdentity, ambiguousIdentities, pinsWithDeclaredBounds };
    }
    function cacheViewPinIdentityKey(viewName, packageName, sourceLibName) {
        return `${cacheIdentityKey(stripNormalViewSuffix(viewName), packageName, sourceLibName)}\0${viewName.toUpperCase()}`;
    }
    function cachePinVectorSignature(pins) {
        return JSON.stringify(pins.map((pin) => [pin.name, pin.startX, pin.startY, pin.hotptX, pin.hotptY]));
    }
    function cachePinsForPlacedGraphic(catalog, object, cellName, entry, diagnostics) {
        const exactKey = cacheViewPinIdentityKey(object.graphicName, entry.packageName, entry.canonicalSourceLibName);
        const exactPins = catalog.pinsByIdentity.get(exactKey);
        if (exactPins) {
            return exactPins;
        }
        const cellKey = cacheViewPinIdentityKey(cellName, entry.packageName, entry.canonicalSourceLibName);
        if (catalog.ambiguousIdentities.has(cellKey)) {
            emitPhysicalPinDiagnostic(diagnostics, cellName, 'cache-view-pin-ambiguous', `cell="${cellName}" package="${entry.packageName}" library="${entry.sourceLibName}" graphic="${object.graphicName}"`, entry.sourceLibName);
            return undefined;
        }
        return catalog.pinsByIdentity.get(cellKey);
    }
    function buildConnectorSymbolGeometryCatalog(document, diagnostics, partValueConnectorGeometryGroups, cache, liveConnectorPins) {
        const candidatesByIdentity = new Map();
        const candidatesBySymbol = new Map();
        const cacheCandidateCountByIdentity = new Map();
        const cacheCandidateCountBySymbol = new Map();
        const evidenceIdentityKeys = new Set();
        const evidenceSymbolKeys = new Set();
        const sourceLibrariesBySymbol = buildSymbolSourceLibraryCatalog(document);
        const connectorSymbolKeys = new Set(Array.from(collectPageSymbolRoles(document))
            .filter(([, role]) => role === 'off-page-connector' || role === 'global')
            .map(([name]) => liveConnectorPinSymbolKey(name)));
        const addCandidate = (symbolName, sourceLibName, geometry, preferCacheOrder = false) => {
            const identityKey = liveConnectorPinIdentityKey(symbolName, sourceLibName);
            evidenceIdentityKeys.add(identityKey);
            const identityCandidates = candidatesByIdentity.get(identityKey) ?? [];
            if (preferCacheOrder) {
                const cacheCandidateCount = cacheCandidateCountByIdentity.get(identityKey) ?? 0;
                identityCandidates.splice(cacheCandidateCount, 0, geometry);
                cacheCandidateCountByIdentity.set(identityKey, cacheCandidateCount + 1);
            }
            else {
                identityCandidates.push(geometry);
            }
            candidatesByIdentity.set(identityKey, identityCandidates);
            const symbolKey = liveConnectorPinSymbolKey(symbolName);
            evidenceSymbolKeys.add(symbolKey);
            const symbolCandidates = candidatesBySymbol.get(symbolKey) ?? [];
            if (preferCacheOrder) {
                const cacheCandidateCount = cacheCandidateCountBySymbol.get(symbolKey) ?? 0;
                symbolCandidates.splice(cacheCandidateCount, 0, geometry);
                cacheCandidateCountBySymbol.set(symbolKey, cacheCandidateCount + 1);
            }
            else {
                symbolCandidates.push(geometry);
            }
            candidatesBySymbol.set(symbolKey, symbolCandidates);
        };
        for (const stream of document.streams) {
            if (stream.parsed?.kind !== 'symbol') {
                continue;
            }
            const pins = (stream.decodedSymbolObjects ?? []).filter(isSymbolPin);
            if (pins.length === 0) {
                continue;
            }
            const symbolName = stream.parsed.symbolName;
            if (!connectorSymbolKeys.has(liveConnectorPinSymbolKey(symbolName))) {
                continue;
            }
            const geometry = {
                pins,
                boundingBox: connectorSymbolBoundingBox(stream.decodedPrimitives ?? [], pins),
                hasDecodedBounds: (stream.decodedPrimitives?.length ?? 0) > 0,
            };
            for (const sourceLibName of sourceLibrariesBySymbol.get(symbolName) ?? ['']) {
                addCandidate(symbolName, sourceLibName, geometry);
            }
        }
        for (const group of groupRootCacheObjects(document, diagnostics)) {
            const pins = group.records.filter(isSymbolPin);
            if (pins.length === 0 || !connectorSymbolKeys.has(liveConnectorPinSymbolKey(group.name))) {
                continue;
            }
            addCandidate(group.name, group.sourceLibName ?? '', {
                pins,
                boundingBox: connectorSymbolBoundingBox(group.primitives, pins),
                hasDecodedBounds: group.primitives.length > 0,
            }, true);
        }
        for (const group of partValueConnectorGeometryGroups) {
            const pins = group.records.filter(isSymbolPin);
            if (pins.length === 0) {
                continue;
            }
            addCandidate(group.name, group.sourceLibName ?? '', {
                pins,
                boundingBox: connectorSymbolBoundingBox(group.primitives, pins),
                hasDecodedBounds: group.primitives.length > 0,
            }, true);
        }
        for (const key of liveConnectorPins.byIdentity.keys()) {
            evidenceIdentityKeys.add(key);
        }
        for (const key of liveConnectorPins.bySymbol.keys()) {
            evidenceSymbolKeys.add(key);
        }
        const emittedByIdentity = new Map();
        const emittedBySymbol = new Map();
        for (const symbol of [...(cache.OffPageSymbol ?? []), ...(cache.GlobalSymbol ?? [])]) {
            const sourceLibName = symbol.SourceLibName ?? '';
            const identityKey = liveConnectorPinIdentityKey(symbol.Name, sourceLibName);
            const symbolKey = liveConnectorPinSymbolKey(symbol.Name);
            if (!evidenceIdentityKeys.has(identityKey) && !evidenceSymbolKeys.has(symbolKey)) {
                continue;
            }
            const pins = (Array.isArray(symbol.PinScalar) ? symbol.PinScalar : [symbol.PinScalar]).filter((pin) => pin !== undefined);
            if (pins.length === 0) {
                emitPhysicalPinDiagnostic(diagnostics, symbol.Name, 'connector-pin-absent', `symbol="${symbol.Name}"`, sourceLibName, 'info');
            }
            const geometry = {
                pins: pins.map(connectorPinGeometryFromLogicalPin),
                boundingBox: symbol.BoundingBox,
                hasDecodedBounds: true,
            };
            const identityCandidates = emittedByIdentity.get(identityKey) ?? [];
            identityCandidates.push(geometry);
            emittedByIdentity.set(identityKey, identityCandidates);
            const symbolCandidates = emittedBySymbol.get(symbolKey) ?? [];
            symbolCandidates.push(geometry);
            emittedBySymbol.set(symbolKey, symbolCandidates);
        }
        for (const [key, values] of emittedByIdentity) {
            candidatesByIdentity.set(key, connectorCandidatesWithPinGeometry(candidatesByIdentity.get(key) ?? [], values));
        }
        for (const [key, values] of emittedBySymbol) {
            candidatesBySymbol.set(key, connectorCandidatesWithPinGeometry(candidatesBySymbol.get(key) ?? [], values));
        }
        return {
            byIdentity: corroboratedConnectorSymbolGeometries(candidatesByIdentity, diagnostics, true),
            bySymbol: corroboratedConnectorSymbolGeometries(candidatesBySymbol, diagnostics, false),
            candidatesBySymbol: new Map(Array.from(candidatesBySymbol, ([key, values]) => [
                key,
                Array.from(new Map(values.map((value) => [connectorSymbolGeometryKey(value), value])).values()),
            ])),
        };
    }
    function connectorCandidatesWithPinGeometry(observed, published) {
        if (published.some((geometry) => geometry.pins.length > 0)) {
            return [...published];
        }
        const pinned = observed.filter((geometry) => geometry.pins.length > 0);
        return pinned.length > 0 ? pinned : [...published];
    }
    function collectPartValueConnectorGeometryGroups(document) {
        const cacheStream = document.streams.find((stream) => stream.parsed?.kind === 'cache');
        if (!cacheStream) {
            return [];
        }
        const connectorNames = new Set(Array.from(collectPageSymbolRoles(document))
            .filter(([, role]) => role === 'off-page-connector' || role === 'global')
            .map(([name]) => liveConnectorPinSymbolKey(name)));
        const entries = [
            ...(cacheStream.decodedSymbolObjects ?? []).map((record) => ({ offset: record.offset, record })),
            ...(cacheStream.decodedPrimitives ?? []).map((record) => ({ offset: record.offset, record })),
        ].sort((left, right) => left.offset - right.offset);
        const groups = [];
        for (let index = 0; index < entries.length; index++) {
            const header = entries[index]?.record;
            if (!header || !isSymbolObjectRecord(header)) {
                continue;
            }
            const headerIdentity = partValueConnectorHeaderIdentity(header, connectorNames);
            if (!headerIdentity) {
                continue;
            }
            const records = [header];
            const primitives = [];
            for (let bodyIndex = index + 1; bodyIndex < entries.length; bodyIndex++) {
                const record = entries[bodyIndex]?.record;
                if (!record) {
                    break;
                }
                if (isSymbolObjectRecord(record) &&
                    (isCacheObjectGroupHeader(record) || partValueConnectorHeaderIdentity(record, connectorNames))) {
                    break;
                }
                if (isSymbolObjectRecord(record)) {
                    records.push(record);
                }
                else {
                    primitives.push(record);
                }
            }
            if (records.some(isSymbolPin)) {
                groups.push({
                    ...headerIdentity,
                    headerKind: 'named-record',
                    records,
                    primitives,
                });
            }
        }
        return groups;
    }
    function connectorPinGeometryFromLogicalPin(pin) {
        return {
            name: pin.Name,
            startX: pin.StartX,
            startY: pin.StartY,
            hotptX: pin.HotptX,
            hotptY: pin.HotptY,
        };
    }
    function connectorSymbolBoundingBox(primitives, pins) {
        return primitives.length > 0 ?
            boundingBoxFromGeometry(primitiveGeometry([...primitives]))
            : boundingBoxFromSymbolPins([...pins]);
    }
    function partValueConnectorHeaderIdentity(record, connectorNames) {
        if (record.kind !== 'part-value') {
            return undefined;
        }
        const name = record.strings[0]?.value;
        const sourceLibName = record.strings.find((entry) => isSourceLibraryPath(entry.value))?.value;
        if (!name || !sourceLibName || !connectorNames.has(liveConnectorPinSymbolKey(name))) {
            return undefined;
        }
        return { name, sourceLibName };
    }
    function corroboratedConnectorSymbolGeometries(candidates, diagnostics, identityScoped) {
        const geometries = new Map();
        for (const [key, values] of candidates) {
            const geometryKeys = new Set(values.map(connectorSymbolGeometryKey));
            if (geometryKeys.size === 1 && values[0]) {
                geometries.set(key, values[0]);
                continue;
            }
            const [symbolName, sourceLibName = ''] = identityScoped ? key.split('\0') : [key, ''];
            emitBudgetedDiagnostic(diagnostics, key, 'connector-pin-geometry-conflict', `symbol="${symbolName}" src="${sourceLibName}" geometries=${geometryKeys.size}`, 'CFB connector', 'warning');
        }
        return geometries;
    }
    function connectorSymbolGeometryKey(geometry) {
        const box = geometry.boundingBox;
        return [
            box.X1,
            box.X2,
            box.Y1,
            box.Y2,
            ...geometry.pins.flatMap((pin) => [pin.name, pin.hotptX, pin.hotptY, pin.startX, pin.startY]),
        ].join(',');
    }
    function populatePlacedSymbolSourceLibraries(document, placedPartCacheIdentityCatalog, markerSourceLibraries, diagnostics) {
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                if (object.kind !== 'placed-symbol') {
                    continue;
                }
                const partIdentity = placedPartCacheIdentityCatalog.get(object);
                if (object.role === 'part-inst') {
                    if (partIdentity?.sourceLibName && object.resolvedMetadata) {
                        object.resolvedMetadata.libName = partIdentity.sourceLibName;
                    }
                    continue;
                }
                const markerSource = markerSourceLibraries.get(object);
                const explicitSource = object.resolvedMetadata?.libName;
                const sources = [markerSource, explicitSource].filter((source) => Boolean(source));
                const identities = new Set(sources.map(canonicalSourceLibraryName));
                object.resolvedMetadata ??= { kind: object.role };
                if (identities.size === 1) {
                    object.resolvedMetadata.libName = markerSource ?? explicitSource;
                    continue;
                }
                object.resolvedMetadata.libName = '';
                emitDecisionDiagnostic(diagnostics, identities.size > 1 ? 'connector-library-source-conflict' : 'connector-library-unresolved', `symbol="${object.symbolName}" page="${stream.path}" offset=${object.offset} dbId=${object.dbId} marker=${JSON.stringify(markerSource ?? '')} explicit=${JSON.stringify(explicitSource ?? '')}: source library withheld`, 'CFB connector', 'warning');
            }
        }
    }
    function buildRootCachePackages(cacheObjectGroups, cacheCatalog, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPinDiagnostics, placedViewEvidence) {
        const packageGroups = new Map();
        for (const group of cacheObjectGroups) {
            if (group.headerKind !== 'view-header') {
                continue;
            }
            const catalogEntry = catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName, physicalPinDiagnostics);
            const pkgName = catalogEntry?.packageName ?? stripNormalViewSuffix(group.name);
            const sourceLibName = catalogEntry?.sourceLibName ?? group.sourceLibName ?? '';
            if (pkgName.length === 0) {
                continue;
            }
            const key = packageSourceKey(pkgName, sourceLibName);
            const existing = packageGroups.get(key) ?? { pkgName, sourceLibName, catalogEntry, groups: [] };
            existing.catalogEntry ??= catalogEntry;
            existing.groups.push(group);
            packageGroups.set(key, existing);
        }
        return Array.from(packageGroups.values()).map(({ pkgName, sourceLibName, catalogEntry, groups }) => buildPackageFromCacheGroups(pkgName, sourceLibName, groups, cacheCatalog, catalogEntry, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPinDiagnostics, placedViewEvidence));
    }
    function buildPackageFromCacheGroups(pkgName, sourceLibName, groups, cacheCatalog, catalogEntry, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPinDiagnostics, placedViewEvidence) {
        const markedSectionNumberingStyle = resolveSectionNumberingStyle(sectionNumberingStyles, pkgName, sourceLibName);
        const materializedGroups = groups
            .filter(isXmlMaterializedPackageViewGroup)
            .sort((left, right) => comparePackageSectionNames(stripNormalViewSuffix(left.name), stripNormalViewSuffix(right.name)));
        const orderedGroups = selectCacheGroupsByCellName(materializedGroups, (group) => placedViewEvidence.get(cacheIdentityKey(stripNormalViewSuffix(group.name), pkgName, catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName ?? sourceLibName, physicalPinDiagnostics)?.sourceLibName ??
            group.sourceLibName ??
            sourceLibName)) ?? [], physicalPinDiagnostics);
        const firstPartValue = groups.flatMap((group) => group.records).find(isPartValue);
        const attestsPhysicalPads = (firstPartValue?.value ?? '') !== '';
        const libPartPadOutcomes = [];
        const libParts = orderedGroups.flatMap((group) => {
            const groupCatalogEntry = catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName ?? sourceLibName, physicalPinDiagnostics) ?? catalogEntry;
            const cellName = stripNormalViewSuffix(group.name);
            const groupSourceLibName = groupCatalogEntry?.sourceLibName ?? group.sourceLibName ?? sourceLibName;
            const directPins = completeCacheSymbolPins(group);
            const pinMaps = physicalPinNumberCatalog.get(cacheIdentityKey(cellName, pkgName, groupSourceLibName));
            const expectedArities = new Set((pinMaps ?? []).map((map) => map.pinNumbers.length));
            const pins = directPins.length > 0 ?
                directPins
                : corroboratedSiblingViewPins(group, materializedGroups.map((candidate) => {
                    const candidateEntry = catalogEntryForGraphicName(cacheCatalog, candidate.name, candidate.sourceLibName ?? sourceLibName, physicalPinDiagnostics) ?? catalogEntry;
                    return {
                        group: candidate,
                        cellName: stripNormalViewSuffix(candidate.name),
                        packageName: candidateEntry?.packageName ?? pkgName,
                        sourceLibName: candidateEntry?.sourceLibName ?? candidate.sourceLibName ?? sourceLibName,
                        pins: completeCacheSymbolPins(candidate),
                    };
                }), {
                    cellName,
                    packageName: pkgName,
                    sourceLibName: groupSourceLibName,
                    expectedArities,
                    hasInformativePinMap: (pinMaps ?? []).some((map) => !isOrdinalPinNumberMap(map.pinNumbers)),
                    placedObjects: placedViewEvidence.get(cacheIdentityKey(cellName, pkgName, groupSourceLibName)) ?? [],
                }, physicalPinDiagnostics);
            const normalView = buildNormalView(group, pins);
            if (isEmptyNormalView(normalView)) {
                return [];
            }
            const padResolution = pinNumberMapsForPackage(physicalPinNumberCatalog, physicalPadSectionCounts, stripNormalViewSuffix(group.name), pkgName, groupCatalogEntry?.canonicalSourceLibName ?? sourceLibName, pins, physicalPinDiagnostics);
            libPartPadOutcomes.push(padResolution.outcome);
            return [
                {
                    CellName: stripNormalViewSuffix(group.name),
                    NormalView: [normalView],
                    PhysicalPart: buildPhysicalPartsFromPins(pins, padResolution, attestsPhysicalPads, physicalPinDiagnostics, stripNormalViewSuffix(group.name), groupCatalogEntry?.canonicalSourceLibName ?? sourceLibName),
                },
            ];
        });
        const referencePrefix = catalogEntry?.referencePrefix ?? firstPartValue?.referencePrefix;
        if (referencePrefix === undefined) {
            emitBudgetedDiagnostic(physicalPinDiagnostics, `${pkgName}\0${canonicalSourceLibraryName(sourceLibName)}`, 'package-reference-prefix-unrecorded', `package "${pkgName}" records no reference prefix, so it declares none`, 'CFB DSN package', 'info');
        }
        return {
            pkg: {
                AlphabeticNumbering: markedSectionNumberingStyle ?? (libParts.length > 1 ? 1 : 2),
                IsHomogeneous: libParts.length <= 1,
                Name: pkgName,
                PcbFootprint: firstPartValue?.value ?? '',
                PcbLib: '',
                RefdesPrefix: referencePrefix ?? '',
                SourceLibName: catalogEntry?.sourceLibName ?? sourceLibName,
                LibPart: libParts,
            },
            markedSectionNumberingStyle,
            fallbackCellNames: new Set(libParts
                .filter((_libPart, index) => libPartPadOutcomes[index] !== PhysicalPadOutcome.Decoded)
                .map((libPart) => stripNormalViewSuffix(libPart.CellName))),
        };
    }
    function comparePackageSectionNames(left, right) {
        return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
    }
    function selectCacheGroupsByCellName(groups, placedObjectsForGroup, diagnostics) {
        const groupsByCellName = new Map();
        for (const group of groups) {
            const cellName = stripNormalViewSuffix(group.name);
            groupsByCellName.set(cellName, [...(groupsByCellName.get(cellName) ?? []), group]);
        }
        const selected = [];
        const seenCellNames = new Set();
        for (const group of groups) {
            const cellName = stripNormalViewSuffix(group.name);
            if (seenCellNames.has(cellName)) {
                continue;
            }
            seenCellNames.add(cellName);
            selected.push(placementCorroboratedCacheGroup(groupsByCellName.get(cellName) ?? [group], placedObjectsForGroup, cellName, diagnostics) ?? group);
        }
        return selected;
    }
    function recordedPlacedPortArity(object) {
        const positions = new Set((object.portInstances ?? []).map((portInstance) => portInstance.position));
        if (positions.size === 0) {
            return undefined;
        }
        return [...positions].every((position) => position >= 0 && position < positions.size) ? positions.size : undefined;
    }
    function placementCorroboratedCacheGroup(groups, placedObjectsForGroup, cellName, diagnostics) {
        const definitions = groups.filter((group) => group.records.some(isSymbolPin));
        const arities = new Set(definitions.map((group) => group.records.filter(isSymbolPin).length));
        if (definitions.length < 2 || arities.size < 2) {
            return undefined;
        }
        const recordedArities = new Set();
        for (const group of definitions) {
            for (const object of placedObjectsForGroup(group)) {
                const recordedArity = recordedPlacedPortArity(object);
                if (recordedArity !== undefined) {
                    recordedArities.add(recordedArity);
                }
            }
        }
        const corroborated = recordedArities.size === 1 ?
            definitions.filter((group) => recordedArities.has(group.records.filter(isSymbolPin).length))
            : [];
        if (corroborated.length !== 1) {
            emitPhysicalPinDiagnostic(diagnostics, cellName, 'same-name-view-arity-unresolved', `cell="${cellName}" arities=${[...arities].join(',')} recorded=${[...recordedArities].join(',')}`, corroborated[0]?.sourceLibName ?? definitions[0]?.sourceLibName);
            return undefined;
        }
        emitPhysicalPinDiagnostic(diagnostics, cellName, 'same-name-view-arity-selected', `cell="${cellName}" arities=${[...arities].join(',')} chose=${[...recordedArities][0]}`, corroborated[0].sourceLibName, 'info');
        return corroborated[0];
    }
    function isXmlMaterializedPackageViewGroup(group) {
        return !/\.(?:Convert|IEEE)$/i.test(group.name ?? '');
    }
    function compareXmlTextOrder(left, right) {
        if (left === right) {
            return 0;
        }
        return left < right ? -1 : 1;
    }
    function isEmptyNormalView(normalView) {
        return !(normalView.PinScalar?.length ||
            normalView.PinBus?.length ||
            normalView.Line?.length ||
            normalView.Rectangle?.length ||
            normalView.Ellipse?.length ||
            normalView.Polygon?.length ||
            normalView.Polyline?.length ||
            normalView.Arc?.length ||
            normalView.CommentText?.length ||
            normalView.Bitmap?.length);
    }
    function completeCacheSymbolPins(group) {
        return group.records.filter(isSymbolPin);
    }
    function corroboratedSiblingViewPins(targetGroup, candidates, target, diagnostics) {
        const localTargetGeometry = siblingViewLocalGeometry(targetGroup, candidates);
        const targetGeometry = Object.keys(localTargetGeometry).length > 0 ? localTargetGeometry
            : target.hasInformativePinMap ? primitiveGeometry(targetGroup.primitives)
                : {};
        const geometryKey = siblingViewGeometryKey(targetGeometry);
        const expectedArities = [...target.expectedArities];
        const matches = geometryKey === undefined || expectedArities.length !== 1 ?
            []
            : candidates.filter((candidate) => candidate.group !== targetGroup &&
                candidate.cellName === target.cellName &&
                candidate.packageName === target.packageName &&
                canonicalSourceLibraryName(candidate.sourceLibName) === canonicalSourceLibraryName(target.sourceLibName) &&
                candidate.pins.length === expectedArities[0] &&
                target.placedObjects.some((object) => (object.materializedPortInstances?.length ?? 0) === candidate.pins.length &&
                    placedPartGeometryMatchesCachePins(object, candidate.pins)) &&
                (siblingViewGeometryKey(siblingViewLocalGeometry(candidate.group, candidates)) === geometryKey ||
                    (target.hasInformativePinMap &&
                        siblingViewGeometryKey(primitiveGeometry(candidate.group.primitives)) === geometryKey) ||
                    symbolPinsAttachToGeometry(candidate.pins, targetGeometry)));
        const signatures = new Map();
        for (const match of matches) {
            signatures.set(symbolPinGeometrySignature(match.pins), match.pins);
        }
        if (signatures.size === 1) {
            return [...(signatures.values().next().value ?? [])];
        }
        if (target.placedObjects.length > 0) {
            emitPhysicalPinDiagnostic(diagnostics, target.cellName, 'view-pinless', `cell="${target.cellName}" package="${target.packageName}" src="${target.sourceLibName}" siblingCandidates=${matches.length}`, target.sourceLibName);
        }
        return [];
    }
    function siblingViewLocalGeometry(group, candidates) {
        const startOffset = group.records[0]?.offset;
        if (startOffset === undefined) {
            return {};
        }
        const endOffset = candidates
            .map((candidate) => candidate.group.records[0]?.offset)
            .filter((offset) => offset !== undefined && offset > startOffset)
            .sort((left, right) => left - right)[0];
        return primitiveGeometry(group.primitives.filter((primitive) => primitive.offset > startOffset && (endOffset === undefined || primitive.offset < endOffset)));
    }
    function siblingViewGeometryKey(geometry) {
        return Object.keys(geometry).length > 0 ? JSON.stringify(geometry) : undefined;
    }
    function symbolPinsAttachToGeometry(pins, geometry) {
        if (Object.keys(geometry).length === 0) {
            return false;
        }
        const bounds = boundingBoxFromGeometry(geometry, { excludeTextWhenPossible: true });
        return pins.every((pin) => pin.startX >= bounds.X1 && pin.startX <= bounds.X2 && pin.startY >= bounds.Y1 && pin.startY <= bounds.Y2);
    }
    function symbolPinGeometrySignature(pins) {
        return pins
            .map((pin) => [pin.name, pin.startX, pin.startY, pin.hotptX, pin.hotptY, pin.pinType ?? ''].join(','))
            .join(';');
    }
    function buildPhysicalPartFromPins(pins, attestsPhysicalPads, pinNumbers, physicalPinDiagnostics, cellName, sourceLibName) {
        if (!pinNumbers && physicalPinDiagnostics && pins.length > 0) {
            emitPhysicalPinDiagnostic(physicalPinDiagnostics, cellName ?? 'unknown', attestsPhysicalPads ? 'pad-numbers-undecoded' : 'fallback-pin-names', attestsPhysicalPads ?
                `cell "${cellName ?? 'unknown'}" attests pads no row numbered, so its pin names stand in for them`
                : `cell "${cellName ?? 'unknown'}" attests no pads, so its positions are keyed by pin name`, sourceLibName);
        }
        return {
            PinNumber: pins.map((pin, position) => ({ Position: position, Number: pinNumbers?.[position] ?? pin.name })),
        };
    }
    function buildPhysicalPartsFromPins(pins, resolution, attestsPhysicalPads, physicalPinDiagnostics, cellName, sourceLibName) {
        const decodedMaps = resolution.pinNumberMaps ?? [];
        const sectionCount = Math.max(resolution.sectionCount, decodedMaps.length);
        if (physicalPinDiagnostics && decodedMaps.length > 0 && decodedMaps.length < sectionCount) {
            const undecodedSections = Array.from({ length: sectionCount }, (_unused, index) => index + 1).slice(decodedMaps.length);
            emitPhysicalPinDiagnostic(physicalPinDiagnostics, cellName ?? 'unknown', 'section-pads-partial', `cell="${cellName ?? 'unknown'}" sections=${sectionCount} decoded=${decodedMaps.length} fallback=${undecodedSections.join(',')}`, sourceLibName);
        }
        return Array.from({ length: sectionCount }, (_unused, index) => buildPhysicalPartFromPins(pins, attestsPhysicalPads, decodedMaps[index], physicalPinDiagnostics, cellName, sourceLibName));
    }
    function arePinNumberMapsPairwiseDisjoint(maps) {
        for (let i = 0; i < maps.length; i++) {
            const seen = new Set(maps[i]);
            for (let j = i + 1; j < maps.length; j++) {
                if (maps[j].some((value) => seen.has(value))) {
                    return false;
                }
            }
        }
        return true;
    }
    function reportUndecodedPinLabelVisibility(viewCount, diagnostics) {
        if (viewCount === 0) {
            return;
        }
        emitBudgetedDiagnostic(diagnostics, '', 'view-pin-visibility-undecoded', `views=${viewCount}`, 'CFB symbol', 'info');
    }
    function addRootCacheConnectorSymbols(cacheObjectGroups, document, pageSymbolRoles, portSymbols, offPageSymbols, globalSymbols, ercSymbols, liveConnectorPins, diagnostics) {
        const sourceLibNames = buildSymbolSourceCatalog(document);
        const sourceLibNamesBySymbol = buildSymbolSourceLibraryCatalog(document);
        const existingPortSymbols = new Set(portSymbols.map((symbol) => packageSourceKey(symbol.Name, symbol.SourceLibName ?? '')));
        const existingOffPageSymbols = new Set(offPageSymbols.map((symbol) => packageSourceKey(symbol.Name, symbol.SourceLibName ?? '')));
        const existingGlobalSymbols = new Set(globalSymbols.map((symbol) => packageSourceKey(symbol.Name, symbol.SourceLibName ?? '')));
        const existingErcSymbols = new Set(ercSymbols.map((symbol) => symbol.Name));
        for (const group of cacheObjectGroups) {
            if (group.headerKind !== 'named-record') {
                continue;
            }
            const name = group.name;
            const role = pageSymbolRoles.get(name);
            if (isPortSymbolName(name, pageSymbolRoles)) {
                const sources = sourceLibNamesBySymbol.get(name) ?? [sourceLibNames.get(name) ?? ''];
                for (const sourceLibName of sources) {
                    const key = packageSourceKey(name, sourceLibName);
                    if (existingPortSymbols.has(key)) {
                        continue;
                    }
                    portSymbols.push(buildPortSymbolFromCacheGroup(group, sourceLibName, diagnostics));
                    existingPortSymbols.add(key);
                }
                continue;
            }
            if (role === 'off-page-connector' || isOffPageSymbolName(name)) {
                const sourceLibName = group.sourceLibName ?? sourceLibNames.get(name) ?? '';
                const key = packageSourceKey(name, sourceLibName);
                if (existingOffPageSymbols.has(key)) {
                    continue;
                }
                offPageSymbols.push(buildOffPageSymbolFromCacheGroup(group, sourceLibName));
                existingOffPageSymbols.add(key);
                continue;
            }
            if (role === 'global' || isCapsymPowerSymbolName(name)) {
                const sources = sourceLibNamesBySymbol.get(name) ?? [sourceLibNames.get(name) ?? ''];
                for (const sourceLibName of sources) {
                    const key = packageSourceKey(name, sourceLibName);
                    if (existingGlobalSymbols.has(key)) {
                        continue;
                    }
                    globalSymbols.push(buildGlobalSymbolFromCacheGroup(group, sourceLibName, liveConnectorPins, diagnostics));
                    existingGlobalSymbols.add(key);
                }
                continue;
            }
            if (isErcSymbolName(name) && !existingErcSymbols.has(name)) {
                ercSymbols.push(buildErcSymbolFromCacheGroup(group, sourceLibNames.get(name)));
                existingErcSymbols.add(name);
            }
        }
    }
    function buildLiveConnectorSymbolPinCatalog(document, diagnostics) {
        const candidatesByIdentity = new Map();
        const candidatesBySymbol = new Map();
        const recordedKeys = new Set();
        for (const stream of document.streams) {
            const wires = (stream.decodedPageObjects ?? []).filter(isWire);
            for (const object of stream.decodedPageObjects ?? []) {
                if (object.kind !== 'placed-symbol' || object.role === 'part-inst' || object.role === 'titleblock') {
                    continue;
                }
                const sourceLibName = object.resolvedMetadata?.libName ?? '';
                const materializedPins = object.materializedPortInstances ?? [];
                const isRecorded = materializedPins.length > 0;
                const pins = isRecorded ?
                    materializedPins.map((materializedPin) => ({
                        kind: 'symbol-pin',
                        offset: object.offset,
                        byteLength: object.byteLength,
                        payloadByteLength: object.payloadByteLength,
                        name: materializedPin.name,
                        startX: materializedPin.startX ?? 0,
                        startY: materializedPin.startY ?? 0,
                        hotptX: materializedPin.hotptX ?? materializedPin.x ?? 0,
                        hotptY: materializedPin.hotptY ?? materializedPin.y ?? 0,
                    }))
                    : inferredUnrotatedLiveConnectorPins(object, wires);
                for (const pin of pins) {
                    const identityKey = liveConnectorPinIdentityKey(object.symbolName, sourceLibName);
                    const symbolKey = liveConnectorPinSymbolKey(object.symbolName);
                    if (isRecorded) {
                        recordedKeys.add(identityKey);
                        recordedKeys.add(symbolKey);
                    }
                    const identityCandidates = candidatesByIdentity.get(identityKey) ?? [];
                    identityCandidates.push(pin);
                    candidatesByIdentity.set(identityKey, identityCandidates);
                    const symbolCandidates = candidatesBySymbol.get(symbolKey) ?? [];
                    symbolCandidates.push(pin);
                    candidatesBySymbol.set(symbolKey, symbolCandidates);
                }
            }
        }
        const byIdentity = corroboratedLiveConnectorPins(candidatesByIdentity, recordedKeys, diagnostics, true);
        const bySymbol = corroboratedLiveConnectorPins(candidatesBySymbol, recordedKeys, diagnostics, false);
        return { byIdentity, bySymbol };
    }
    function inferredUnrotatedLiveConnectorPins(object, wires) {
        if ((object.resolvedMetadata?.rotation ?? 0) !== 0 || (object.resolvedMetadata?.mirror ?? 0) !== 0) {
            return [];
        }
        const point = placedSymbolConnectionPoints(object).points.find((candidate) => wires.some((wire) => isWireEndpoint(wire, candidate.x, candidate.y)));
        if (!point) {
            return [];
        }
        const hotptX = point.x - object.locX;
        const hotptY = point.y - object.locY;
        return [
            {
                kind: 'symbol-pin',
                offset: object.offset,
                byteLength: object.byteLength,
                payloadByteLength: object.payloadByteLength,
                name: object.symbolName,
                startX: hotptX,
                startY: hotptY,
                hotptX,
                hotptY,
            },
        ];
    }
    function corroboratedLiveConnectorPins(candidates, recordedKeys, diagnostics, identityScoped) {
        const pins = new Map();
        for (const [key, values] of candidates) {
            const geometries = new Set(values.map(liveConnectorPinGeometryKey));
            const [symbolName, sourceLibName = ''] = identityScoped ? key.split('\0') : [key, ''];
            if (geometries.size === 1 && values[0]) {
                if (!recordedKeys.has(key) && values.length < 2) {
                    emitPhysicalPinDiagnostic(diagnostics, symbolName, 'live-pin-uncorroborated', `symbol="${symbolName}" src="${sourceLibName}" placements=${values.length}`, sourceLibName);
                    continue;
                }
                pins.set(key, values[0]);
                continue;
            }
            emitPhysicalPinDiagnostic(diagnostics, symbolName, 'live-pin-geometry-conflict', `symbol="${symbolName}" src="${sourceLibName}" geometries=${geometries.size}`, sourceLibName);
        }
        return pins;
    }
    function liveConnectorSymbolPin(catalog, symbolName, sourceLibName) {
        return (catalog.byIdentity.get(liveConnectorPinIdentityKey(symbolName, sourceLibName)) ??
            catalog.bySymbol.get(liveConnectorPinSymbolKey(symbolName)));
    }
    function liveConnectorPinIdentityKey(symbolName, sourceLibName) {
        return `${liveConnectorPinSymbolKey(symbolName)}\0${canonicalSourceLibraryName(sourceLibName)}`;
    }
    function liveConnectorPinSymbolKey(symbolName) {
        return stripNormalViewSuffix(symbolName).toUpperCase();
    }
    function liveConnectorPinGeometryKey(pin) {
        return `${pin.hotptX},${pin.hotptY},${pin.startX},${pin.startY}`;
    }
    function diagnoseMissingSourcePackageBodies(document, packages, diagnostics) {
        const existingKeys = new Set(packages.flatMap((pkg) => [
            packageSourceKey(pkg.Name, pkg.SourceLibName),
            ...pkg.LibPart.map((part) => packageSourceKey(part.CellName ?? pkg.Name, pkg.SourceLibName)),
        ]));
        for (const entry of collectPackageLibraryEntries(document)) {
            const key = packageSourceKey(entry.packageName, entry.sourceLibName);
            if (existingKeys.has(key)) {
                continue;
            }
            emitDecisionDiagnostic(diagnostics, 'package-library-body-missing', `name="${entry.packageName}" src="${entry.sourceLibName}": catalogue identity has no body in its source library`, 'CFB physical pin map', 'warning');
        }
    }
    function collectPackageLibraryEntries(document, diagnostics) {
        const cache = document.streams.find((stream) => stream.path === 'Root Entry/Cache');
        const stringsByOffset = new Map(cache?.lengthPrefixedStrings.map((entry) => [entry.offset, entry.value]));
        const headerNamesByOffset = new Map((cache?.decodedSymbolObjects ?? []).flatMap((record) => record.kind === 'view-header' || record.kind === 'named-record' ? [[record.offset, record.name]] : []));
        const entries = [];
        const seen = new Set();
        const framedOffsets = new Set();
        for (const source of cache?.cacheSourceLibraries ?? []) {
            const name = stringsByOffset.get(source.headerOffset) ?? headerNamesByOffset.get(source.headerOffset);
            if (!name || !source.library.value) {
                continue;
            }
            framedOffsets.add(source.headerOffset);
            const normalizedPackageName = stripNormalViewSuffix(name);
            const sourceLibName = source.library.value;
            const key = packageSourceKey(normalizedPackageName, sourceLibName);
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            entries.push({
                cellName: normalizedPackageName,
                packageName: normalizedPackageName,
                sourceLibName,
                canonicalSourceLibName: canonicalSourceLibraryName(sourceLibName),
                contentsViewName: normalizedPackageName,
                partValue: normalizedPackageName,
            });
        }
        if (diagnostics) {
            for (const header of cache?.decodedSymbolObjects ?? []) {
                if (header.kind === 'view-header' && !framedOffsets.has(header.offset)) {
                    emitDecisionDiagnostic(diagnostics, 'package-library-unframed', `cell="${header.name}" offset=${header.offset}: no framed library; sourced physical mapping withheld`, 'CFB physical pin map', 'warning');
                }
            }
        }
        return entries;
    }
    function buildMarkerSourceLibraryCatalog(document) {
        const stringCatalog = document.streams.find((stream) => stream.parsed?.kind === 'library')?.parsed;
        const resolved = new Map();
        if (stringCatalog?.kind !== 'library' || !stringCatalog.stringCatalog) {
            return resolved;
        }
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                if (object.kind !== 'placed-symbol') {
                    continue;
                }
                const source = stringCatalog.stringCatalog[object.marker];
                if (isSourceLibraryPath(source)) {
                    resolved.set(object, source);
                }
            }
        }
        return resolved;
    }
    function createPhysicalPinDiagnosticState(diagnostics) {
        return {
            diagnostics,
            emitted: new Set(),
            emittedCountByReason: new Map(),
            suppressedCountByReason: new Map(),
            messagePrefixByReason: new Map(),
        };
    }
    function emitDecisionDiagnostic(state, reason, detail, messagePrefix, severity) {
        state.diagnostics.push({ severity, message: `${messagePrefix} [${reason}] ${detail}` });
    }
    function emitBudgetedDiagnostic(state, dedupeScope, reason, detail, messagePrefix, severity) {
        const key = `${dedupeScope}\0${reason}`;
        if (state.emitted.has(key)) {
            return;
        }
        state.emitted.add(key);
        state.messagePrefixByReason.set(reason, messagePrefix);
        const emittedCount = state.emittedCountByReason.get(reason) ?? 0;
        state.emittedCountByReason.set(reason, emittedCount + 1);
        if (emittedCount < PHYSICAL_PIN_DIAGNOSTIC_LIMIT_PER_REASON) {
            state.diagnostics.push({ severity, message: `${messagePrefix} [${reason}] ${detail}` });
            return;
        }
        state.suppressedCountByReason.set(reason, (state.suppressedCountByReason.get(reason) ?? 0) + 1);
    }
    function reportUndecodedSymbolPinFlagBits(document, diagnostics) {
        for (const stream of document.streams) {
            for (const record of stream.decodedSymbolObjects ?? []) {
                if (record.kind !== 'symbol-pin' || record.displayFlags === undefined) {
                    continue;
                }
                const unknownBits = record.displayFlags & ~OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.ORCAD_DSN_CFB_KNOWN_SYMBOL_PIN_DISPLAY_FLAGS;
                if (unknownBits === 0) {
                    continue;
                }
                emitPhysicalPinDiagnostic(diagnostics, record.name, 'pin-flags-unknown-bit', `pin="${record.name}" stream="${stream.path}" bits=0x${unknownBits.toString(16).padStart(4, '0')}`);
            }
        }
    }
    function reportUndecodedSymbolPinFields(document, diagnostics) {
        for (const stream of document.streams) {
            for (const record of stream.decodedSymbolObjects ?? []) {
                if (record.kind !== 'symbol-pin') {
                    continue;
                }
                if (record.displayFlags === undefined) {
                    emitBudgetedDiagnostic(diagnostics, `${stream.path}\0${record.name}`, 'pin-display-flags-undecoded', `pin="${record.name}" stream="${stream.path}"`, 'CFB symbol pin', 'warning');
                }
                if (record.pinType === undefined) {
                    emitBudgetedDiagnostic(diagnostics, `${stream.path}\0${record.name}`, 'pin-type-undecoded', `pin="${record.name}" stream="${stream.path}"`, 'CFB symbol pin', 'warning');
                }
            }
        }
    }
    function emitPhysicalPinDiagnostic(state, cellName, reason, detail, sourceLibName, severity = 'warning') {
        const strippedCell = stripNormalViewSuffix(cellName);
        emitBudgetedDiagnostic(state, `${strippedCell}\0${canonicalSourceLibraryName(sourceLibName)}`, reason, detail, 'CFB physical pin map', severity);
    }
    function recoverMissingPlacedPartReferences(document, cacheCatalog, placedPartCacheIdentityCatalog, diagnostics) {
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
                    continue;
                }
                const displayedValue = object.displayProperties?.find((property) => property.propertyName === 'Value' && property.displayedValue !== undefined)?.displayedValue ?? object.partValue;
                const hasUndecodedPartReference = object.displayProperties?.some((property) => property.propertyName === 'Part Reference' && property.displayedValue === undefined);
                const recorded = recordedPlacementReference(object, [object.reference]);
                if (recorded.recorded) {
                    if (hasUndecodedPartReference === true && displayedValue !== undefined) {
                        emitDecisionDiagnostic(diagnostics, 'reference-recovery-withheld', `stream=${JSON.stringify(stream.path)} dbId=${object.dbId} source=value candidate=${JSON.stringify(displayedValue)} recorded=${JSON.stringify(recorded.reference)}: recorded page evidence takes precedence`, 'CFB reference', 'info');
                    }
                    continue;
                }
                const catalogValue = object.resolvedMetadata?.userProperties?.find((property) => property.name === 'Value')?.value;
                const cacheIdentity = placedPartCacheIdentityCatalog.get(object);
                if (cacheIdentity === null) {
                    continue;
                }
                const packageName = cacheIdentity?.packageName ?? object.resolvedMetadata?.pkgName ?? stripNormalViewSuffix(object.graphicName);
                const referencePrefix = referencePrefixForPlacedPackage(document, cacheCatalog, object, packageName, cacheIdentity);
                const matchesReferencePrefix = displayedValue !== undefined && referencePrefix !== undefined && displayedValue.startsWith(referencePrefix);
                const packageNamePrefix = nonEmpty(leadingLettersOfPackageName(packageName));
                const matchesPackageNamePrefix = displayedValue !== undefined &&
                    packageNamePrefix !== undefined &&
                    displayedValue.startsWith(packageNamePrefix);
                if (hasUndecodedPartReference !== true ||
                    displayedValue === undefined ||
                    displayedValue.endsWith('?') ||
                    catalogValue === undefined ||
                    !(matchesReferencePrefix || (displayedValue === catalogValue && matchesPackageNamePrefix))) {
                    continue;
                }
                object.reference = displayedValue;
                emitPhysicalPinDiagnostic(diagnostics, packageName, 'reference-recovered', `dbId=${object.dbId} source=value`, cacheIdentity?.sourceLibName, 'info');
            }
        }
    }
    function referencePrefixForPlacedPackage(document, cacheCatalog, object, packageName, cacheIdentity) {
        if (cacheIdentity) {
            return nonEmpty(cacheIdentity.catalogEntry.referencePrefix ?? '');
        }
        const prefixes = new Set(catalogEntriesForGraphicName(cacheCatalog, object.graphicName)
            .filter((entry) => entry.packageName === packageName)
            .map((entry) => entry.referencePrefix ?? '')
            .filter((prefix) => prefix.length > 0));
        for (const stream of document.streams) {
            if (stream.parsed?.kind !== 'package' || stripNormalViewSuffix(stream.parsed.packageName) !== packageName) {
                continue;
            }
            const prefix = stream.decodedSymbolObjects?.find(isPartValue)?.referencePrefix;
            if (prefix) {
                prefixes.add(prefix);
            }
        }
        if (prefixes.size === 1) {
            return [...prefixes][0];
        }
        if (prefixes.size > 1) {
            return undefined;
        }
        return undefined;
    }
    function finalizePhysicalPinDiagnostics(state) {
        for (const [reason, count] of state.suppressedCountByReason) {
            state.diagnostics?.push({
                severity: 'warning',
                message: `${state.messagePrefixByReason.get(reason) ?? 'CFB physical pin map'} [suppressed] reason=${reason} count=${count}`,
            });
        }
    }
    function buildPhysicalPinNumberCatalogs(document, cacheCatalog, physicalPinDiagnostics) {
        const cache = document.streams.find((stream) => stream.path === 'Root Entry/Cache');
        const stringEntries = cache?.lengthPrefixedStrings ?? [];
        const strings = stringEntries.map((entry) => entry.value);
        const stringOffsets = stringEntries.map((entry) => entry.offset);
        const catalog = new Map();
        const numberingStylesBySection = new Map();
        const numberingStylesByHeaderPartName = new Map();
        const sectionObservations = new Map();
        const profiles = buildPhysicalPinArityProfiles(document, cacheCatalog, physicalPinDiagnostics);
        const acceptedIdentities = new Set();
        const addNumbers = (profile, numbers) => {
            if (numbers.length === 0) {
                return;
            }
            const key = cacheIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName);
            const existing = catalog.get(key) ?? [];
            if (!existing.some((candidate) => arraysEqual(candidate.pinNumbers, numbers) && arraysEqual(candidate.pinIdentity, profile.pinIdentity))) {
                existing.push({
                    pinNumbers: numbers,
                    pinIdentity: profile.pinIdentity,
                    source: PhysicalPadRowSource.CatalogScan,
                });
                catalog.set(key, existing);
            }
        };
        const addAcceptedIdentity = (profile) => {
            acceptedIdentities.add(physicalPadIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName, profile.arity));
        };
        const rows = [];
        for (let index = 0; index < strings.length; index++) {
            const row = cacheCatalogRowAt(strings, index, stringOffsets);
            if (row) {
                rows.push(row);
                continue;
            }
            if (isCacheCatalogHeaderCandidateAt(strings, index) && !hasCacheCatalogFieldDiscriminatorAt(strings, index)) {
                const cellName = stripNormalViewSuffix(strings[index] ?? '');
                emitPhysicalPinDiagnostic(physicalPinDiagnostics, cellName, 'header-unrecognized', `cell="${cellName}" field="${strings[index + 1] ?? ''}" offset=${stringOffsets[index] ?? index}`);
            }
        }
        const recognizedCatalogCells = new Set([
            ...rows.map((row) => row.cellName),
            ...strings.flatMap((value) => (/\.Normal$/i.test(value) ? [stripNormalViewSuffix(value)] : [])),
        ]);
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
            const row = rows[rowIndex];
            const rowEnd = rows
                .slice(rowIndex + 1)
                .find((candidate) => candidate.headerIndex >= (row.bodyStartIndex ?? row.headerIndex + 1))?.headerIndex ??
                strings.length;
            if (row.bodyStartIndex === undefined) {
                emitPhysicalPinDiagnostic(physicalPinDiagnostics, row.cellName, 'header-no-body', `cell="${row.cellName}" offset=${row.headerOffset}`);
                continue;
            }
            const rowProfileResolution = profilesForPhysicalPinRow(profiles, row);
            const rowProfiles = rowProfileResolution.profiles;
            if (rowProfiles.length === 0) {
                emitPhysicalPinDiagnostic(physicalPinDiagnostics, row.cellName, rowProfileResolution.libraryPathAmbiguous ? 'library-path-ambiguous' : 'unresolved-row-key', `cell="${row.cellName}" source="${row.sourceLibName}" offset=${row.headerOffset}`, row.sourceLibName);
                continue;
            }
            const sections = physicalPinSectionsForRow(strings, row, rowEnd, rowProfiles);
            const rowSectionCounts = new Map();
            for (const section of sections) {
                for (const profile of section.profiles) {
                    const identity = cacheIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName);
                    const observation = sectionObservations.get(identity) ?? {
                        cellName: profile.cellName,
                        packageName: profile.packageName,
                        canonicalSourceLibName: profile.canonicalSourceLibName,
                        markers: new Set(),
                        rowSectionCounts: new Set(),
                    };
                    if (section.marker) {
                        observation.markers.add(section.marker);
                    }
                    sectionObservations.set(identity, observation);
                    rowSectionCounts.set(identity, (rowSectionCounts.get(identity) ?? 0) + 1);
                }
            }
            for (const [identity, count] of rowSectionCounts) {
                sectionObservations.get(identity)?.rowSectionCounts.add(count);
            }
            for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
                const section = sections[sectionIndex];
                const sectionEnd = sections[sectionIndex + 1]?.headerIndex ?? rowEnd;
                const firstBodyToken = strings[section.padStartIndex];
                if (firstBodyToken !== undefined && stripNormalViewSuffix(firstBodyToken) === row.cellName) {
                    emitPhysicalPinDiagnostic(physicalPinDiagnostics, row.cellName, 'body-self-name', `cell="${row.cellName}" offset=${row.headerOffset}`);
                    continue;
                }
                for (const profile of section.profiles) {
                    if (!section.marker && physicalPinRootHasProfileFanout(profile, section.profiles)) {
                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'alias-conflict', `cell="${profile.cellName}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
                        continue;
                    }
                    if (physicalPinProfileHasAliasConflict(profile, section.profiles)) {
                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'alias-conflict', `cell="${profile.cellName}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
                        continue;
                    }
                    if (physicalPinProfileHasSameArityConflict(profile, section.profiles)) {
                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'same-library-view-conflict', `cell="${profile.cellName}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
                        continue;
                    }
                    const available = Math.max(0, sectionEnd - section.padStartIndex);
                    if (available < profile.arity) {
                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'row-underflow', `cell="${profile.cellName}" expected=${profile.arity} found=${available} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
                        continue;
                    }
                    const numbers = strings.slice(section.padStartIndex, section.padStartIndex + profile.arity);
                    const vocabularyTokens = numbers.filter((number) => {
                        const token = stripNormalViewSuffix(number);
                        return (!isNumericString(number) &&
                            !/^[A-Z]$/i.test(number) &&
                            terminalPhysicalPadFromPinName(number) === undefined &&
                            token !== row.cellName &&
                            recognizedCatalogCells.has(token));
                    });
                    const vocabularyMatches = vocabularyTokens.length;
                    if (vocabularyMatches >= 1) {
                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'body-vocabulary-suspect', `cell="${profile.cellName}" matches=${vocabularyMatches} tokens="${vocabularyTokens.join(',')}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
                        continue;
                    }
                    addNumbers(profile, numbers);
                    addAcceptedIdentity(profile);
                    const recoveredProfile = recoverExpandedPhysicalPinProfile(profile, strings.slice(section.padStartIndex, sectionEnd), profiles);
                    if (recoveredProfile !== undefined) {
                        addNumbers(recoveredProfile.profile, recoveredProfile.pinNumbers);
                        addAcceptedIdentity(recoveredProfile.profile);
                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, recoveredProfile.profile.cellName, 'row-underflow-recovered', `cell="${recoveredProfile.profile.cellName}" expected=${recoveredProfile.profile.arity} found=${profile.arity} recovered=${recoveredProfile.profile.arity - profile.arity} offset=${row.headerOffset}`, recoveredProfile.profile.canonicalSourceLibName, 'info');
                    }
                    if (section.marker) {
                        const style = alphabeticNumberingForSectionMarker(section.marker);
                        const sectionKey = cacheIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName);
                        if (!numberingStylesBySection.has(sectionKey)) {
                            numberingStylesBySection.set(sectionKey, style);
                        }
                        const headerKey = packageSourceKey(row.cellName, profile.canonicalSourceLibName);
                        if (!numberingStylesByHeaderPartName.has(headerKey)) {
                            numberingStylesByHeaderPartName.set(headerKey, style);
                        }
                    }
                    if (available > profile.arity) {
                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'trailing-junk', `cell="${profile.cellName}" count=${available - profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
                    }
                    if (sectionEnd === strings.length) {
                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'terminal-prefix-unverified', `cell="${profile.cellName}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
                    }
                }
            }
        }
        const directMapsByIdentity = new Map();
        const framedOffsets = new Set();
        for (const source of cache?.cacheSourceLibraries ?? []) {
            for (const section of source.physicalSections ?? []) {
                framedOffsets.add(section.offset);
                const cellName = stripNormalViewSuffix(section.cellName);
                const matchingProfiles = profiles.filter((profile) => profile.cellName === cellName &&
                    profile.canonicalSourceLibName === canonicalSourceLibraryName(source.library.value) &&
                    profile.arity === section.pinNumbers.length &&
                    !profile.hasSameArityConflict);
                if (matchingProfiles.length !== 1) {
                    emitDecisionDiagnostic(physicalPinDiagnostics, 'physical-section-identity-withheld', `cell=${JSON.stringify(cellName)} source=${JSON.stringify(source.library.value)} offset=${section.offset}: framed section has no unique matching electrical interface`, 'CFB physical pin map', 'warning');
                    continue;
                }
                const profile = matchingProfiles[0];
                const key = cacheIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName);
                const observation = sectionObservations.get(key) ?? {
                    cellName: profile.cellName,
                    packageName: profile.packageName,
                    canonicalSourceLibName: profile.canonicalSourceLibName,
                    markers: new Set(),
                    rowSectionCounts: new Set(),
                };
                if (section.selector !== '') {
                    observation.markers.add(section.selector);
                }
                observation.rowSectionCounts.add((source.physicalSections ?? []).filter((candidate) => stripNormalViewSuffix(candidate.cellName) === cellName)
                    .length);
                sectionObservations.set(key, observation);
                const maps = directMapsByIdentity.get(key) ?? [];
                const pinNumbers = section.pinNumbers.map((number, position) => number.length > 0 ? number : profile.pinIdentity[position]);
                if (!maps.some((candidate) => candidate.sectionSelector === section.selector && arraysEqual(candidate.pinNumbers, pinNumbers))) {
                    maps.push({
                        pinNumbers,
                        pinIdentity: profile.pinIdentity,
                        source: PhysicalPadRowSource.FramedRecord,
                        sectionSelector: section.selector,
                    });
                    directMapsByIdentity.set(key, maps);
                }
                addAcceptedIdentity(profile);
            }
        }
        for (const pinMap of cache?.decodedSymbolObjects?.filter(isPinNumberMap) ?? []) {
            if (!framedOffsets.has(pinMap.offset)) {
                emitDecisionDiagnostic(physicalPinDiagnostics, 'physical-section-source-unframed', `cell=${JSON.stringify(pinMap.packageName)} offset=${pinMap.offset}: no owning physical catalogue frame`, 'CFB physical pin map', 'warning');
            }
        }
        for (const [identity, maps] of directMapsByIdentity) {
            const existingMaps = catalog.get(identity) ?? [];
            const existingNumbers = existingMaps.map(physicalPinNumbersForCandidate);
            const hasDisjointSectionSet = existingNumbers.length > 1 && arePinNumberMapsPairwiseDisjoint(existingNumbers);
            catalog.set(identity, hasDisjointSectionSet ? existingMaps : maps);
        }
        return {
            pinNumbers: catalog,
            numberingStyles: { bySection: numberingStylesBySection, byHeaderPartName: numberingStylesByHeaderPartName },
            padPresence: { acceptedIdentities },
            sectionCounts: resolveSectionCounts(sectionObservations, physicalPinDiagnostics),
        };
    }
    function recoverExpandedPhysicalPinProfile(profile, observedNumbers, profiles) {
        const candidates = profiles.filter((candidate) => {
            const missingCount = candidate.arity - profile.arity;
            return (candidate.cellName === profile.cellName &&
                candidate.packageName === profile.packageName &&
                candidate.canonicalSourceLibName !== profile.canonicalSourceLibName &&
                candidate.arity === observedNumbers.length &&
                missingCount >= 1 &&
                missingCount <= 5 &&
                profile.pinIdentity.every((pinName) => candidate.pinIdentity.includes(pinName)));
        });
        const recoveries = candidates.flatMap((candidate) => {
            const recoveredNumbers = candidate.pinIdentity.map(terminalPhysicalPadFromPinName);
            if (recoveredNumbers.some((number) => number === undefined)) {
                return [];
            }
            const pinNumbers = recoveredNumbers.filter((number) => number !== undefined);
            const uniqueNumbers = new Set(pinNumbers.map((number) => number.toUpperCase()));
            const observedIdentity = new Set(observedNumbers.map((number) => number.toUpperCase()));
            const pinIdentity = new Set(candidate.pinIdentity.map((number) => number.toUpperCase()));
            const observedPads = new Set(observedNumbers.map(terminalPhysicalPadFromPinName).filter((number) => number !== undefined));
            const corroboratedByNames = setContentsEqual(observedIdentity, pinIdentity);
            const corroboratedByPads = observedPads.size === observedNumbers.length && setContentsEqual(observedPads, uniqueNumbers);
            return uniqueNumbers.size === candidate.arity && (corroboratedByNames || corroboratedByPads) ?
                [{ profile: candidate, pinNumbers }]
                : [];
        });
        return recoveries.length === 1 ? recoveries[0] : undefined;
    }
    function terminalPhysicalPadFromPinName(pinName) {
        return /(?:^|_)([A-Z]{1,2}[1-9][0-9]?)$/i.exec(pinName)?.[1];
    }
    function setContentsEqual(left, right) {
        return left.size === right.size && Array.from(left).every((value) => right.has(value));
    }
    function physicalPinRootHasProfileFanout(profile, eligibleProfiles) {
        return (eligibleProfiles.filter((candidate) => candidate.arity === profile.arity && arraysEqual(candidate.pinIdentity, profile.pinIdentity)).length > 1);
    }
    function physicalPinProfileHasAliasConflict(profile, eligibleProfiles) {
        return eligibleProfiles.some((candidate) => candidate.cellName === profile.cellName &&
            candidate.arity === profile.arity &&
            candidate.packageName !== profile.packageName);
    }
    function physicalPinProfileHasSameArityConflict(profile, eligibleProfiles) {
        return (profile.hasSameArityConflict ||
            eligibleProfiles.some((candidate) => candidate.arity === profile.arity && !arraysEqual(candidate.pinIdentity, profile.pinIdentity)));
    }
    function physicalPadPresenceAt(catalog, keys, arity, position) {
        const cellName = keys[0];
        if (cellName === undefined) {
            return 'unknown';
        }
        const packageName = keys[1] ?? cellName;
        const sourceLibName = keys[2] ?? '';
        if (!catalog.acceptedIdentities.has(physicalPadIdentityKey(cellName, packageName, sourceLibName, arity))) {
            return 'unknown';
        }
        return position >= 0 && position < arity ? 'present' : 'absent';
    }
    function physicalPadIdentityKey(cellName, packageName, sourceLibName, arity) {
        return `${cacheIdentityKey(cellName, packageName, sourceLibName)}\0${arity}`;
    }
    function buildPhysicalPinArityProfiles(document, cacheCatalog, physicalPinDiagnostics) {
        const identities = new Map();
        for (const group of groupRootCacheObjects(document, physicalPinDiagnostics).filter((group) => group.headerKind === 'view-header' && isXmlMaterializedPackageViewGroup(group))) {
            const cellName = stripNormalViewSuffix(group.name);
            const entry = catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName, physicalPinDiagnostics);
            const packageName = entry?.packageName ?? cellName;
            const sourceLibName = entry?.sourceLibName ?? group.sourceLibName ?? '';
            const canonicalSourceLibName = canonicalSourceLibraryName(sourceLibName);
            const pins = completeCacheSymbolPins(group).map((pin) => pin.name);
            if (pins.length === 0) {
                continue;
            }
            const key = `${cacheIdentityKey(cellName, packageName, canonicalSourceLibName)}\0${pins.length}`;
            const profile = identities.get(key) ?? {
                cellName,
                packageName,
                sourceLibName,
                canonicalSourceLibName,
                arity: pins.length,
                pins: [],
            };
            if (!profile.pins.some((identity) => arraysEqual(identity, pins))) {
                profile.pins.push(pins);
            }
            identities.set(key, profile);
        }
        return [...identities.values()].map((profile) => ({
            cellName: profile.cellName,
            packageName: profile.packageName,
            sourceLibName: profile.sourceLibName,
            canonicalSourceLibName: profile.canonicalSourceLibName,
            arity: profile.arity,
            pinIdentity: profile.pins[0],
            hasSameArityConflict: profile.pins.length > 1,
        }));
    }
    function profilesForPhysicalPinRow(profiles, row) {
        const aliases = profiles.filter((profile) => profile.cellName === row.cellName ||
            profile.packageName === row.cellName ||
            (row.bodyKind === 'multipart' && isSectionOfCatalogPackage(profile.cellName, row.cellName)));
        const rowSource = canonicalSourceLibraryName(row.sourceLibName);
        if (rowSource) {
            return {
                profiles: aliases.filter((profile) => profile.canonicalSourceLibName === rowSource),
                libraryPathAmbiguous: false,
            };
        }
        const libraries = new Set(aliases.map((profile) => profile.canonicalSourceLibName));
        return {
            profiles: libraries.size <= 1 ? aliases : [],
            libraryPathAmbiguous: libraries.size > 1,
        };
    }
    function resolveSectionCounts(observations, diagnostics) {
        const counts = new Map();
        for (const [identity, observation] of observations) {
            const markerCount = observation.markers.size;
            const resolved = Math.max(1, markerCount);
            counts.set(identity, resolved);
            const statedCounts = [...observation.rowSectionCounts].sort((left, right) => left - right);
            if (statedCounts.length > 1 || (markerCount > 0 && !observation.rowSectionCounts.has(markerCount))) {
                emitPhysicalPinDiagnostic(diagnostics, observation.cellName, 'section-count-conflict', `cell="${observation.cellName}" package="${observation.packageName}" rows=${statedCounts.join(',')} markers=${[...observation.markers].join(',')} resolved=${resolved}`, observation.canonicalSourceLibName);
            }
        }
        return counts;
    }
    function physicalPinSectionsForRow(strings, row, rowEnd, rowProfiles) {
        const bodyStartIndex = row.bodyStartIndex;
        if (bodyStartIndex === undefined) {
            return [];
        }
        const sections = [
            {
                headerIndex: row.headerIndex,
                padStartIndex: bodyStartIndex,
                profiles: rowProfiles,
                ...((isMultipartSectionMarker(strings[bodyStartIndex - 2]) &&
                    stripNormalViewSuffix(strings[bodyStartIndex - 1] ?? '') === row.cellName) ?
                    { marker: strings[bodyStartIndex - 2] }
                    : {}),
            },
        ];
        for (let index = bodyStartIndex; index + 1 < rowEnd; index++) {
            const marker = strings[index];
            if (!isMultipartSectionMarker(marker)) {
                continue;
            }
            const sectionName = stripNormalViewSuffix(strings[index + 1]);
            const sectionProfiles = rowProfiles.filter((profile) => profile.cellName === sectionName &&
                (profile.packageName === row.cellName || isSectionOfCatalogPackage(sectionName, row.cellName)));
            if (sectionProfiles.length === 0) {
                continue;
            }
            sections.push({ headerIndex: index, padStartIndex: index + 2, marker, profiles: sectionProfiles });
        }
        if (sections.length > 1) {
            return sections[0].marker ? sections : sections.slice(1);
        }
        return row.bodyKind === 'root' ? sections : [];
    }
    function alphabeticNumberingForSectionMarker(marker) {
        return /^[A-Z]$/.test(marker) ? 1 : 2;
    }
    function sectionIndexMatchesDesignator(designator, index) {
        if (designator === undefined || !Number.isInteger(index) || index < 0) {
            return false;
        }
        if (/^[A-D]$/.test(designator)) {
            return designator.charCodeAt(0) - 'A'.charCodeAt(0) === index;
        }
        const numbered = String(index + 1);
        return designator === numbered || designator === `-${numbered}`;
    }
    function resolveSectionNumberingStyle(catalog, pkgName, sourceLibName) {
        const exact = catalog.bySection.get(cacheIdentityKey(pkgName, pkgName, sourceLibName));
        if (exact !== undefined) {
            return exact;
        }
        const exactHeader = catalog.byHeaderPartName.get(packageSourceKey(pkgName, sourceLibName));
        if (exactHeader !== undefined) {
            return exactHeader;
        }
        const canonicalSource = canonicalSourceLibraryName(sourceLibName);
        const compatibleStyles = [];
        for (const [key, style] of catalog.byHeaderPartName) {
            const separator = key.indexOf('\0');
            const source = separator >= 0 ? key.slice(0, separator) : '';
            const partName = separator >= 0 ? key.slice(separator + 1) : key;
            if ((canonicalSource && source !== canonicalSource) || !isSectionOfCatalogPackage(pkgName, partName)) {
                continue;
            }
            compatibleStyles.push(style);
        }
        const uniqueStyles = Array.from(new Set(compatibleStyles));
        return uniqueStyles.length === 1 ? uniqueStyles[0] : undefined;
    }
    function pinNumberMapsForPackage(catalog, sectionCounts, cellName, packageName, sourceLibName, pins, diagnostics) {
        const normalizedPackageName = stripNormalViewSuffix(packageName);
        const pinCount = pins.length;
        const identity = cacheIdentityKey(cellName, normalizedPackageName, sourceLibName);
        const sectionCount = Math.max(1, sectionCounts.get(identity) ?? 1);
        const undecoded = {
            outcome: pinCount > 0 ? PhysicalPadOutcome.PadsUndecoded : PhysicalPadOutcome.NoPins,
            sectionCount,
        };
        const candidates = catalog.get(identity)?.filter((candidate) => candidate.pinNumbers.length === pinCount) ?? [];
        const pinNames = pins.map((pin) => pin.name);
        const orderedCandidates = candidates.filter((candidate) => arraysEqual(candidate.pinIdentity, pinNames));
        if (sectionCount > 1 &&
            orderedCandidates.length > 0 &&
            orderedCandidates.every((candidate) => candidate.sectionSelector)) {
            const maps = [];
            const covered = new Set();
            for (let index = 0; index < sectionCount; index++) {
                const rows = orderedCandidates.filter((candidate) => sectionIndexMatchesDesignator(candidate.sectionSelector, index));
                if (rows.length === 0 || rows.some((row) => !arraysEqual(row.pinNumbers, rows[0].pinNumbers))) {
                    emitDecisionDiagnostic(diagnostics, 'physical-section-map-withheld', `cell=${JSON.stringify(cellName)} sectionIndex=${index}: framed selectors are missing or disagree`, 'CFB physical pin map', 'warning');
                    return undecoded;
                }
                rows.forEach((row) => covered.add(row));
                maps.push(rows[0].pinNumbers);
            }
            if (covered.size === orderedCandidates.length) {
                return { outcome: PhysicalPadOutcome.Decoded, pinNumberMaps: maps, sectionCount };
            }
            emitDecisionDiagnostic(diagnostics, 'physical-section-map-withheld', `cell=${JSON.stringify(cellName)}: framed selectors are outside the declared section range`, 'CFB physical pin map', 'warning');
            return undecoded;
        }
        if (orderedCandidates.length > 0) {
            const orderedMaps = orderedCandidates.map(physicalPinNumbersForCandidate);
            if (orderedMaps.length > 1 && !arePinNumberMapsPairwiseDisjoint(orderedMaps)) {
                emitPhysicalPinDiagnostic(diagnostics, cellName, 'pad-order-ambiguous', `cell="${cellName}" package="${normalizedPackageName}" arity=${pinCount} candidates=${orderedMaps.length}`, sourceLibName);
                return undecoded;
            }
            return { outcome: PhysicalPadOutcome.Decoded, pinNumberMaps: orderedMaps, sectionCount };
        }
        if (candidates.length > 0) {
            emitPhysicalPinDiagnostic(diagnostics, cellName, 'pad-order-ambiguous', `cell="${cellName}" package="${normalizedPackageName}" arity=${pinCount} candidates=${candidates.length}`, sourceLibName);
        }
        return undecoded;
    }
    function physicalPinNumbersForCandidate(candidate) {
        if (candidate.source === PhysicalPadRowSource.CatalogScan &&
            candidate.pinNumbers.length === 1 &&
            candidate.pinIdentity[0] === '1' &&
            candidate.pinNumbers[0] !== '1') {
            return candidate.pinIdentity;
        }
        return candidate.pinNumbers;
    }
    function buildLibraryInstanceCatalog(document) {
        const libraryStream = document.streams.find((stream) => stream.parsed?.kind === 'library')?.parsed;
        if (libraryStream?.kind !== 'library') {
            return { entries: [] };
        }
        const entries = [];
        for (const record of libraryStream.componentCandidateRecords) {
            const instanceMatch = /^INS(\d+)$/.exec(record.name);
            if (!instanceMatch) {
                continue;
            }
            entries.push({
                record,
                instanceNumber: Number(instanceMatch[1]),
                packageKeys: libraryInstanceCatalogPackageKeys(record),
            });
        }
        entries.sort((left, right) => left.instanceNumber - right.instanceNumber);
        return { entries };
    }
    function libraryInstanceCatalogPackageKeys(record) {
        const packageKeys = [];
        for (const value of record.values) {
            const normalized = normalizeSheetReferenceToken(stripNormalViewSuffix(pathBasename(value)));
            if (normalized.length < 5) {
                continue;
            }
            packageKeys.push({ normalized, compact: normalized.replace(/PORT/g, '') });
        }
        return packageKeys;
    }
    function buildInstanceSourceLibraryCatalog(document, diagnostics) {
        const library = document.streams.find((stream) => stream.path === 'Root Entry/Library')?.parsed;
        if (library?.kind === 'library') {
            for (const record of library.componentCandidateRecords) {
                emitDecisionDiagnostic(diagnostics, 'library-instance-source-unframed', `instance=${JSON.stringify(record.name)} offset=${record.offset}: interned strings do not state an instance-to-library association`, 'CFB source identity', 'warning');
            }
        }
        return new Map();
    }
    function withholdHierarchyAdoption(diagnostics, trust, stream, view, id, arm, branchOffset, occurrenceId, value = OrcadDsnCfbOccurrenceOwnership_1.HierarchyAdoptionValue.Reference) {
        const reason = (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyAdoptionVeto)(trust, stream, view, id, branchOffset, occurrenceId, value);
        if (reason === undefined) {
            return false;
        }
        emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(stream)}, view=${JSON.stringify(view)}, dbId=${id}, arm=${arm}: ${reason}`, 'CFB hierarchy', 'warning');
        return true;
    }
    function withholdUnownedHierarchyAdoption(diagnostics, stream, id, arm) {
        emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(stream)}, dbId=${id}, arm=${arm}: counted owner is unknown`, 'CFB hierarchy', 'warning');
    }
    function buildHierarchyReferenceLookup(document, occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document), diagnostics = createPhysicalPinDiagnosticState([])) {
        const references = new Map();
        for (const key of occurrenceTrust.branchReferenceConflicts) {
            emitDecisionDiagnostic(diagnostics, 'hierarchy-reference-conflict', `${key}: duplicate or disagreeing path-local rows; reference withheld`, 'CFB hierarchy occurrence', 'warning');
        }
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind !== 'hierarchy') {
                continue;
            }
            for (const entry of parsed.entries) {
                const candidate = (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyReferenceCandidate)(entry);
                if (candidate === undefined) {
                    if (entry.parentBranchOffset === undefined &&
                        entry.referenceStringOffset !== undefined &&
                        entry.refInstId !== undefined) {
                        emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(stream.path)}, offset=${entry.offset}, dbId=${entry.refInstId}: framed annotation has no eligible reference`, 'CFB hierarchy', 'warning');
                    }
                    continue;
                }
                if (withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream.path, candidate.viewName, candidate.refInstId, 'reference-entry', candidate.branchOffset, candidate.occurrenceId)) {
                    continue;
                }
                if (candidate.branchOffset === undefined) {
                    withholdUnownedHierarchyAdoption(diagnostics, stream.path, candidate.refInstId, 'reference-entry');
                    continue;
                }
                references.set(JSON.stringify([stream.path, candidate.branchOffset, candidate.refInstId, candidate.occurrenceId]), { ...candidate, streamPath: stream.path });
            }
            for (const string of stream.lengthPrefixedStrings) {
                if (parsed.entries.some((entry) => entry.referenceStringOffset === string.offset)) {
                    continue;
                }
                const refInstId = unsignedHierarchyId(string.precedingInt32Le16);
                if (!isPlausibleHierarchyRefInstId(refInstId) || !isHierarchyOccurrenceReferenceName(string.value)) {
                    continue;
                }
                withholdUnownedHierarchyAdoption(diagnostics, stream.path, refInstId, 'reference-string');
            }
        }
        return references;
    }
    function rootHierarchyPhysicalNetNames(document, viewName, diagnostics) {
        const names = [];
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind !== 'hierarchy' || parsed.viewName.toUpperCase() !== viewName.toUpperCase()) {
                continue;
            }
            const stored = parsed.physicalNetNames ?? [];
            names.push(...stored);
            if (stored.length > 0) {
                diagnostics.diagnostics.push({
                    severity: 'warning',
                    message: `CFB physical net name [wire-provenance-unresolved] view="${parsed.viewName}" records=${stored.length}`,
                    streamPath: stream.path,
                });
            }
        }
        return names;
    }
    function calibrateRecordedPlacedPorts(document) {
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                if (!isPlacedSymbolRole('part-inst')(object)) {
                    continue;
                }
                applyRecordedPortCalibrationToMissingCachePins(object, object.materializedPortInstances ?? []);
            }
        }
    }
    function reportHierarchyRowBelowThreshold(diagnostics, viewName, string, refInstId) {
        const occurrenceId = unsignedHierarchyId(string.precedingInt32Le20) ?? 0;
        if (!isDecodedHierarchyId(refInstId) || refInstId >= OrcadDsnCfbOccurrenceOwnership_1.HIERARCHY_ROW_ID_THRESHOLD) {
            return;
        }
        if (occurrenceId !== 0 && occurrenceId >= OrcadDsnCfbOccurrenceOwnership_1.HIERARCHY_ROW_ID_THRESHOLD) {
            return;
        }
        diagnostics.diagnostics?.push({
            severity: 'info',
            message: `CFB hierarchy occurrence [row-id-below-threshold] view="${viewName}" reference="${string.value}" refInstId=${refInstId} occurrenceId=${occurrenceId}: read as a counted-string neighbour rather than an occurrence row`,
        });
    }
    function buildHierarchyOccurrenceCatalog(document, diagnostics, occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
        const byBranchAndRefInstId = buildHierarchyReferenceLookup(document, occurrenceTrust, diagnostics);
        const references = [...byBranchAndRefInstId.values()];
        const branches = [];
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind !== 'hierarchy') {
                continue;
            }
            for (const branch of parsed.ownershipBranches ?? []) {
                if (branch.viewName === undefined || branch.state !== OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Resolved) {
                    continue;
                }
                const parents = branch.offset === 0 ?
                    [undefined]
                    : parsed.entries.filter((entry) => entry.offset + 8 === branch.offset && entry.parentBranchOffset !== undefined);
                if (parents.length === 0) {
                    emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(stream.path)}, offset=${branch.offset}, view=${JSON.stringify(branch.viewName)}: resolved occurrence branch has no counted parent`, 'CFB hierarchy', 'warning');
                    continue;
                }
                for (const parent of parents) {
                    if (withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream.path, branch.viewName, parent?.dbId, 'occurrence-branch', branch.offset)) {
                        continue;
                    }
                    branches.push({
                        streamPath: stream.path,
                        viewName: branch.viewName,
                        offset: branch.offset,
                        parentOffset: parent?.parentBranchOffset,
                        refInstId: parent?.dbId,
                        occurrenceId: parent?.recordId,
                    });
                }
            }
            for (const string of stream.lengthPrefixedStrings) {
                if (parsed.entries.some((entry) => entry.referenceStringOffset === string.offset)) {
                    continue;
                }
                const refInstId = unsignedHierarchyId(string.precedingInt32Le16);
                if (refInstId === undefined || !isHierarchyOccurrenceReferenceName(string.value)) {
                    continue;
                }
                if (!isPlausibleHierarchyOccurrenceRow(string)) {
                    reportHierarchyRowBelowThreshold(diagnostics, parsed.viewName, string, refInstId);
                    continue;
                }
                withholdUnownedHierarchyAdoption(diagnostics, stream.path, refInstId, 'occurrence-catalogue');
            }
        }
        references.sort((left, right) => left.offset - right.offset);
        const referencesByBranch = new Map();
        for (const reference of references) {
            const key = JSON.stringify([reference.streamPath, reference.branchOffset]);
            const byPlacement = referencesByBranch.get(key) ??
                new Map();
            const rows = byPlacement.get(reference.refInstId);
            if (rows === undefined) {
                byPlacement.set(reference.refInstId, [reference]);
            }
            else {
                rows.push(reference);
            }
            referencesByBranch.set(key, byPlacement);
        }
        const childrenByParentAndPlacement = new Map();
        const rootsByView = new Map();
        for (const branch of branches) {
            const key = JSON.stringify([branch.streamPath, branch.parentOffset, String(branch.refInstId)]);
            const children = childrenByParentAndPlacement.get(key) ?? [];
            children.push(branch);
            childrenByParentAndPlacement.set(key, children);
            if (branch.offset === 0) {
                const view = branch.viewName.toUpperCase();
                const roots = rootsByView.get(view) ?? [];
                roots.push(branch);
                rootsByView.set(view, roots);
            }
        }
        return {
            trust: occurrenceTrust,
            branches,
            references,
            byBranchAndRefInstId,
            referencesByBranch,
            childrenByParentAndPlacement,
            rootsByView,
        };
    }
    function agreeingHierarchyAnnotation(rows, diagnostics, key) {
        const identities = new Set(rows.map((row) => row.occurrenceId));
        if (identities.size > 1) {
            emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `${key}: occurrence identity is ambiguous; agreed reference retained without an identity`, 'CFB hierarchy occurrence', 'warning');
        }
        const { refInstId, reference } = rows[0];
        return { refInstId, reference, occurrenceId: identities.size === 1 ? [...identities][0] : undefined };
    }
    function buildOccurrenceNetSystemIdCatalog(document, diagnostics = createPhysicalPinDiagnosticState([]), trust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
        const viewNames = new Set(document.schematicViews.map((view) => view.viewName.toUpperCase()));
        const catalog = new Map();
        const claims = new Map();
        for (const stream of document.streams) {
            if (stream.parsed?.kind !== 'hierarchy' || !stream.parsed.records) {
                continue;
            }
            let sheetDbId;
            let sheetView;
            for (const record of stream.parsed.records) {
                if (!record.name) {
                    continue;
                }
                if (record.nameOffset !== undefined && record.nameOffset >= 8 && viewNames.has(record.name.toUpperCase())) {
                    sheetDbId = record.dbId;
                    sheetView = record.name;
                    continue;
                }
                if (sheetDbId === undefined) {
                    continue;
                }
                if (withholdHierarchyAdoption(diagnostics, trust, stream.path, sheetView, sheetDbId, 'net-system-id')) {
                    continue;
                }
                let byName = claims.get(sheetDbId);
                if (!byName) {
                    byName = new Map();
                    claims.set(sheetDbId, byName);
                }
                const key = record.name.toUpperCase();
                const values = byName.get(key) ?? new Set();
                values.add(record.recordId);
                byName.set(key, values);
            }
        }
        for (const [id, byName] of claims) {
            const resolved = new Map();
            for (const [name, values] of byName) {
                if (values.size === 1) {
                    for (const value of values) {
                        resolved.set(name, value);
                    }
                }
                else {
                    emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `net-system-id dbId=${id}, net=${JSON.stringify(name)}: conflicting recorded ids`, 'CFB hierarchy', 'warning');
                }
            }
            if (resolved.size > 0) {
                catalog.set(id, resolved);
            }
        }
        return catalog;
    }
    function sheetSymbolCatalogNames(candidate) {
        if (candidate.entry.contentsViewName === UNNAMED_CONTENTS_VIEW) {
            return [candidate.packageName];
        }
        return [candidate.packageName, candidate.entry.contentsViewName];
    }
    function contradictedSheetSymbolNames(candidates, diagnostics) {
        const stated = new Map();
        const contradicted = new Set();
        for (const candidate of candidates) {
            for (const name of sheetSymbolCatalogNames(candidate)) {
                const key = stripNormalViewSuffix(name);
                const known = stated.get(key);
                if (!known) {
                    stated.set(key, candidate);
                    continue;
                }
                if (contradicted.has(key) ||
                    !statesDifferentSheetSymbol({ packageName: key, ...known.entry }, candidate.entry)) {
                    continue;
                }
                contradicted.add(key);
                diagnostics.report('sheet-metadata-conflict', `the cells describing "${key}" state different sheet-symbol metadata, so neither is read as this symbol's`, candidate.streamPath, candidate.offset);
            }
        }
        return contradicted;
    }
    function buildSheetSymbolPackageCatalog(document, sourceFileName, diagnostics) {
        const sheetAliasDiagnostics = new LibrarySheetAliasDiagnosticBudget(diagnostics);
        const catalog = new Map();
        const addEntry = (packageName, entry) => {
            const normalizedPackageName = stripNormalViewSuffix(packageName);
            if (!isPlausibleCatalogPackageName(normalizedPackageName)) {
                return;
            }
            const metadata = { packageName: normalizedPackageName, ...entry };
            const merged = mergeSheetSymbolPackageMetadata(catalog.get(normalizedPackageName), metadata);
            catalog.set(normalizedPackageName, merged);
        };
        const candidates = [];
        for (const stream of document.streams) {
            const packageName = /^Root Entry\/Packages\/([^/]+)$/.exec(stream.path)?.[1];
            if (!packageName) {
                continue;
            }
            for (const record of stream.sheetSymbolMetadataRecords ?? []) {
                const contentsLibName = canonicalChildDsnPath(record.contentsLibName);
                if (!contentsLibName ||
                    !isDsnSourcePath(contentsLibName) ||
                    isSameDsnFileName(contentsLibName, sourceFileName)) {
                    continue;
                }
                const cellName = record.cellViewName ? stripNormalViewSuffix(record.cellViewName) : packageName;
                const entry = sheetSymbolEntryFromRecord(record, contentsLibName, stream.path, cellName, sheetAliasDiagnostics);
                if (!entry) {
                    continue;
                }
                candidates.push({ packageName: cellName, entry, streamPath: stream.path, offset: record.offset });
            }
        }
        for (const stream of document.streams.filter((stream) => stream.path === 'Root Entry/Cache')) {
            for (const record of stream.sheetSymbolMetadataRecords ?? []) {
                const contentsLibName = canonicalChildDsnPath(record.contentsLibName);
                if (!contentsLibName ||
                    !isDsnSourcePath(contentsLibName) ||
                    isSameDsnFileName(contentsLibName, sourceFileName)) {
                    continue;
                }
                if (!record.cellViewName) {
                    sheetAliasDiagnostics.report('sheet-record-uncelled', `the record naming "${contentsLibName}" sits in no cell, so it states no symbol's sheet metadata`, stream.path, record.offset);
                    continue;
                }
                const packageName = stripNormalViewSuffix(record.cellViewName);
                const entry = sheetSymbolEntryFromRecord(record, contentsLibName, stream.path, packageName, sheetAliasDiagnostics);
                if (!entry) {
                    continue;
                }
                candidates.push({ packageName, entry, streamPath: stream.path, offset: record.offset });
            }
        }
        const contradicted = contradictedSheetSymbolNames(candidates, sheetAliasDiagnostics);
        for (const candidate of candidates) {
            if (contradicted.has(stripNormalViewSuffix(candidate.packageName))) {
                continue;
            }
            for (const name of sheetSymbolCatalogNames(candidate)) {
                if (contradicted.has(stripNormalViewSuffix(name))) {
                    continue;
                }
                addEntry(name, candidate.entry);
            }
        }
        for (const stream of document.streams.filter((stream) => stream.path === 'Root Entry/Library')) {
            const rows = orderedStreamTextRows(stream);
            const reportedRecordStarts = new Set();
            for (let index = 0; index < rows.length; index++) {
                const contentsLibName = canonicalChildDsnPath(rows[index].value);
                if (!isDsnSourcePath(contentsLibName) || isSameDsnFileName(contentsLibName, sourceFileName)) {
                    continue;
                }
                const contentsViewName = dsnViewNameFromPath(contentsLibName);
                if (!contentsViewName) {
                    continue;
                }
                const record = libraryRecordBounds(rows, index);
                const reportRecord = !reportedRecordStarts.has(record.start);
                if (record.truncated) {
                    if (reportRecord) {
                        reportedRecordStarts.add(record.start);
                        sheetAliasDiagnostics.report('library-record-truncated-tail', `child design "${contentsViewName}" sits in a block the stream ends without an instance marker, so it states no record and contributes no sheet symbol metadata`, stream.path, rows[record.start]?.offset);
                    }
                    continue;
                }
                const { partValue, unanchored } = librarySheetPartValueInRecord(rows, index, record);
                const entry = { contentsLibName, contentsViewName, ...(partValue ? { partValue } : {}) };
                addEntry(contentsViewName, entry);
                if (partValue) {
                    addEntry(partValue, entry);
                }
                if (unanchored && reportRecord) {
                    sheetAliasDiagnostics.report('sheet-part-value-unanchored', `child design "${contentsViewName}": "${unanchored}" precedes its path in a slot no symbol library opens, so it is read as another column of the record rather than this child's part value`, stream.path, rows[record.start]?.offset);
                }
                const candidates = librarySheetAliasCandidatesInRecord(rows, index, record, contentsViewName);
                for (const alias of candidates.aliases) {
                    addEntry(alias, { contentsLibName, contentsViewName: alias, ...(partValue ? { partValue } : {}) });
                }
                if (!reportRecord) {
                    continue;
                }
                reportedRecordStarts.add(record.start);
                if (candidates.childViewNames.length > 1) {
                    sheetAliasDiagnostics.report('sheet-alias-ambiguous', `Library record names ${candidates.childViewNames.length} child designs (${quoteList(candidates.childViewNames)}); none of its other rows is read as a child view alias`, stream.path, rows[record.start]?.offset);
                    continue;
                }
                if (candidates.rejected.length > 0) {
                    sheetAliasDiagnostics.report('sheet-alias-unsupported', `child design "${contentsViewName}": ${quoteList(candidates.rejected)} share its Library record but name no spelling of it`, stream.path, rows[record.start]?.offset);
                }
            }
        }
        sheetAliasDiagnostics.summarize();
        return catalog;
    }
    const LIBRARY_SHEET_ALIAS_DIAGNOSTIC_LIMIT_PER_REASON = 25;
    const LIBRARY_SHEET_ALIAS_NAMES_PER_DIAGNOSTIC = 8;
    class LibrarySheetAliasDiagnosticBudget {
        constructor(diagnostics) {
            this.diagnostics = diagnostics;
            this.emittedCountByReason = new Map();
            this.suppressedCountByReasonAndStream = new Map();
        }
        report(reason, detail, streamPath, offset) {
            const emitted = this.emittedCountByReason.get(reason) ?? 0;
            this.emittedCountByReason.set(reason, emitted + 1);
            if (emitted >= LIBRARY_SHEET_ALIAS_DIAGNOSTIC_LIMIT_PER_REASON) {
                const key = `${reason} ${streamPath}`;
                this.suppressedCountByReasonAndStream.set(key, (this.suppressedCountByReasonAndStream.get(key) ?? 0) + 1);
                return;
            }
            this.diagnostics?.push({
                severity: 'info',
                message: `CFB DSN sheet symbol [${reason}] ${detail}`,
                streamPath,
                ...(offset === undefined ? {} : { offset }),
            });
        }
        summarize() {
            for (const [key, suppressed] of this.suppressedCountByReasonAndStream) {
                const [reason, streamPath] = key.split(' ');
                this.diagnostics?.push({
                    severity: 'info',
                    message: `CFB DSN sheet symbol [${reason}] ${suppressed} further Library record(s) were not reported`,
                    streamPath,
                });
            }
        }
    }
    function quoteList(values) {
        const listed = values.slice(0, LIBRARY_SHEET_ALIAS_NAMES_PER_DIAGNOSTIC);
        const quoted = listed.map((value) => `"${value}"`).join(', ');
        return values.length > listed.length ? `${quoted} and ${values.length - listed.length} more` : quoted;
    }
    function libraryRecordBounds(rows, index) {
        if (index < 0 || index >= rows.length) {
            return { start: index, end: index };
        }
        if (isLibraryInstanceMarker(rows[index].value)) {
            let runStart = index;
            while (runStart > 0 && isLibraryInstanceMarker(rows[runStart - 1].value)) {
                runStart--;
            }
            let blockStart = runStart;
            while (blockStart > 0 && !isLibraryInstanceMarker(rows[blockStart - 1].value)) {
                blockStart--;
            }
            return { start: blockStart, end: runStart };
        }
        let start = index;
        while (start > 0 && !isLibraryInstanceMarker(rows[start - 1].value)) {
            start--;
        }
        let end = index + 1;
        while (end < rows.length && !isLibraryInstanceMarker(rows[end].value)) {
            end++;
        }
        if (end === rows.length) {
            return { start, end, truncated: true };
        }
        return { start, end };
    }
    function isLibraryInstanceMarker(value) {
        return OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.isLibraryInstanceMarker(value);
    }
    function sheetSymbolEntryFromRecord(record, contentsLibName, streamPath, packageName, diagnostics) {
        const fieldState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState;
        const malformed = [record.view, record.symbolClass, record.partValue].some((field) => field.state === fieldState.malformed);
        if (malformed) {
            diagnostics.report('sheet-metadata-malformed', `the record for child design "${contentsLibName}" does not lay its fields out as counted strings, so it states no sheet-symbol metadata`, streamPath, record.offset);
            return undefined;
        }
        if (record.view.state === fieldState.empty) {
            diagnostics.report('sheet-view-unresolved', `package "${packageName}" states an empty view name for child design "${contentsLibName}", so it names no view of that design`, streamPath, record.offset);
            return { contentsLibName, contentsViewName: UNNAMED_CONTENTS_VIEW };
        }
        const contentsViewName = record.view.value;
        if (contentsViewName === undefined) {
            return undefined;
        }
        const referencePrefix = record.symbolClass.state === fieldState.stated && isSheetReferencePrefix(record.symbolClass.value) ?
            record.symbolClass.value
            : undefined;
        const partValue = record.partValue.state === fieldState.stated ? record.partValue.value : undefined;
        return {
            contentsLibName,
            contentsViewName,
            ...(referencePrefix ? { referencePrefix } : {}),
            ...(partValue ? { partValue } : {}),
        };
    }
    function statesDifferentSheetSymbol(known, incoming) {
        if (known.contentsViewName === UNNAMED_CONTENTS_VIEW || incoming.contentsViewName === UNNAMED_CONTENTS_VIEW) {
            return false;
        }
        return (!isSameDsnFileName(known.contentsLibName, incoming.contentsLibName) ||
            normalizeSheetReferenceToken(known.contentsViewName) !== normalizeSheetReferenceToken(incoming.contentsViewName));
    }
    function librarySheetPartValueInRecord(rows, dsnIndex, record) {
        const previousValue = dsnIndex - 1 >= record.start ? rows[dsnIndex - 1]?.value : undefined;
        if (!previousValue) {
            return {};
        }
        const anchorValue = dsnIndex - 2 >= record.start ? rows[dsnIndex - 2]?.value : undefined;
        if (anchorValue !== undefined && isSourceLibraryPath(anchorValue)) {
            return { partValue: previousValue };
        }
        return isPlausibleLibrarySheetPartValue(previousValue) ? { unanchored: previousValue } : {};
    }
    function childDesignsNamedInRecord(rows, record) {
        const childViewNames = [];
        for (let index = record.start; index < record.end; index++) {
            const rowValue = rows[index].value;
            const childViewName = isDsnSourcePath(rowValue) ? dsnViewNameFromPath(rowValue) : undefined;
            if (childViewName && !childViewNames.some((seen) => isSameChildDesign(seen, childViewName))) {
                childViewNames.push(childViewName);
            }
        }
        return childViewNames;
    }
    function namesSameChildDesign(value, pathViewName) {
        return areSheetReferenceKeysCompatible(normalizeSheetReferenceToken(value), normalizeSheetReferenceToken(pathViewName));
    }
    function isSameChildDesign(left, right) {
        if (isDsnSourcePath(left) && isDsnSourcePath(right)) {
            return resolvedChildDesignPath(left) === resolvedChildDesignPath(right);
        }
        return childDesignName(left) === childDesignName(right);
    }
    function resolvedChildDesignPath(value) {
        return (canonicalChildDsnPath(value) ?? value).toUpperCase();
    }
    function childDesignName(value) {
        const bare = isDsnSourcePath(value) ? pathBasename(value) : value;
        return bare
            .trim()
            .replace(/\.(?:dsn|xml)$/i, '')
            .replace(/\.(?:Normal|IEEE|Convert)$/i, '')
            .replace(/[\s.]+$/, '')
            .replace(/[\s_]+/g, '_')
            .toUpperCase();
    }
    function librarySheetAliasCandidatesInRecord(rows, dsnIndex, record, pathViewName) {
        const childViewNames = childDesignsNamedInRecord(rows, record);
        if (childViewNames.length > 1) {
            return { aliases: [], rejected: [], childViewNames };
        }
        const aliases = [];
        const rejected = [];
        for (let index = record.start; index < record.end; index++) {
            if (index === dsnIndex) {
                continue;
            }
            const value = rows[index].value;
            if (!isPlausibleLibrarySheetViewCandidate(value, pathViewName)) {
                continue;
            }
            if (namesSameChildDesign(value, pathViewName)) {
                aliases.push(value);
            }
            else {
                rejected.push(value);
            }
        }
        return { aliases, rejected, childViewNames };
    }
    function isPlausibleLibrarySheetViewCandidate(value, pathViewName) {
        return isPlausibleLibrarySheetPartValue(value) && !areSheetReferenceKeysCompatible(value, pathViewName);
    }
    function mergeSheetSymbolPackageMetadata(existing, incoming) {
        if (!existing) {
            return incoming;
        }
        const keepExistingView = Boolean(existing.referencePrefix && !incoming.referencePrefix);
        const existingPathMatchesView = childDsnPathMatchesView(existing.contentsLibName, existing.contentsViewName);
        const incomingPathMatchesView = childDsnPathMatchesView(incoming.contentsLibName, incoming.contentsViewName);
        return {
            packageName: existing.packageName,
            contentsLibName: incomingPathMatchesView && !existingPathMatchesView ? incoming.contentsLibName : existing.contentsLibName,
            contentsViewName: keepExistingView ? existing.contentsViewName : incoming.contentsViewName,
            referencePrefix: existing.referencePrefix ?? incoming.referencePrefix,
            partValue: preferredSheetPartValue(existing, incoming),
        };
    }
    function childDsnPathMatchesView(contentsLibName, contentsViewName) {
        const fileName = pathBasename(contentsLibName).replace(/\.dsn$/i, '');
        return areSheetReferenceKeysCompatible(fileName, contentsViewName);
    }
    function preferredSheetPartValue(existing, incoming) {
        if (incoming.partValue && !areSheetReferenceKeysCompatible(incoming.partValue, incoming.contentsViewName)) {
            return incoming.partValue;
        }
        return existing.partValue ?? incoming.partValue;
    }
    function dsnViewNameFromPath(contentsLibName) {
        return contentsLibName
            .split(/[\\/]/)
            .pop()
            ?.replace(/\.dsn$/i, '');
    }
    function isPlausibleLibrarySheetPartValue(value) {
        return value.length > 0;
    }
    function orderedStreamTextRows(stream) {
        const rows = [
            ...stream.lengthPrefixedStrings.map((entry) => ({ offset: entry.offset, value: entry.value })),
            ...(stream.asciiRuns ?? []).map((entry) => ({ offset: entry.offset, value: entry.value })),
        ].sort((left, right) => left.offset - right.offset || left.value.localeCompare(right.value));
        const values = [];
        for (const row of rows) {
            const previous = values[values.length - 1];
            if (previous && previous.value === row.value && row.offset - previous.offset <= 4) {
                continue;
            }
            values.push(row);
        }
        return values;
    }
    function canonicalChildDsnPath(value) {
        if (!value || !isDsnSourcePath(value)) {
            return value;
        }
        return value.replace(/\//g, '\\');
    }
    function isSameDsnFileName(contentsLibName, sourceFileName) {
        if (!sourceFileName) {
            return false;
        }
        return pathBasename(contentsLibName).toUpperCase() === pathBasename(sourceFileName).toUpperCase();
    }
    function pathBasename(value) {
        return value.replace(/\\/g, '/').split('/').pop() ?? value;
    }
    function isPlausibleHierarchySheetReference(value) {
        return Boolean(value && /^[A-Za-z][A-Za-z0-9_.-]*\??$/.test(value) && value !== 'X');
    }
    function buildHierarchySheetReferenceCatalog(document, diagnostics, occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
        const references = [];
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind !== 'hierarchy') {
                continue;
            }
            for (const entry of parsed.entries) {
                const contentsViewName = entry.contentsViewName;
                const claimedId = entry.refInstId ?? entry.dbId;
                if (!contentsViewName ||
                    isGenericSheetViewName(contentsViewName) ||
                    contentsViewName.toUpperCase() === parsed.viewName.toUpperCase()) {
                    continue;
                }
                const isOccurrenceAnnotation = Boolean(!isPlausibleHierarchySheetReference(entry.reference) &&
                    entry.dbId !== undefined &&
                    entry.occurrenceId === undefined &&
                    entry.refInstId === undefined);
                if (withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream.path, contentsViewName, claimedId, 'sheet-reference')) {
                    continue;
                }
                if (!isPlausibleHierarchySheetReference(entry.reference) && !isOccurrenceAnnotation) {
                    continue;
                }
                references.push({
                    contentsViewName,
                    parentViewName: entry.parentViewName,
                    reference: entry.reference ?? '?',
                    offset: entry.offset,
                    ...(entry.dbId !== undefined ? { dbId: entry.dbId } : {}),
                    ...(isOccurrenceAnnotation ? { isOccurrenceAnnotation: true } : {}),
                });
            }
        }
        references.sort((left, right) => left.offset - right.offset);
        const annotationsByDbId = new Map();
        for (const reference of references) {
            if (!reference.isOccurrenceAnnotation || reference.dbId === undefined) {
                continue;
            }
            const entries = annotationsByDbId.get(reference.dbId) ?? [];
            entries.push(reference);
            annotationsByDbId.set(reference.dbId, entries);
        }
        for (const [dbId, entries] of annotationsByDbId) {
            const views = new Set(entries.map((entry) => entry.contentsViewName.toUpperCase()));
            if (views.size > 1) {
                emitBudgetedDiagnostic(diagnostics, String(dbId), 'hierarchy-annotation-ambiguous', `dbId=${dbId} views=${Array.from(views).sort().join(',')}`, 'CFB hierarchy', 'warning');
            }
        }
        return { references, usedIndexes: new Set(), claimCounts: new Map() };
    }
    function buildLibraryStringCatalog(document) {
        return document.streams.find((stream) => stream.path === 'Root Entry/Library')?.lengthPrefixedStrings ?? [];
    }
    function buildCisSchematicInfoCatalog(document) {
        const catalog = new Map();
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind !== 'cis-schematic-info') {
                continue;
            }
            const byRecordId = new Map();
            for (const record of parsed.records) {
                const exportedProperties = record.properties.filter((property) => property.value.length > 0 || isXmlExportedEmptyCisProperty(property.name));
                if (record.recordId <= 0 || exportedProperties.length === 0) {
                    continue;
                }
                byRecordId.set(record.recordId, exportedProperties.map((property) => ({ Name: property.name, Value: property.value })));
            }
            if (byRecordId.size > 0) {
                catalog.set(cisSchematicInfoKey(parsed.viewName, parsed.pageName), byRecordId);
            }
        }
        return catalog;
    }
    function cisSchematicInfoKey(viewName, pageName) {
        return `${viewName}/${pageName}`;
    }
    function isXmlExportedEmptyCisProperty(name) {
        return [
            'CURRENT_RATING',
            'DIELECTRIC',
            'FPLIST',
            'FREQUENCY',
            'ICA_MFR_NAME',
            'ICA_PARTNUM',
            'MATERIAL',
            'NS',
            'Part Number',
            'RATING',
            'ROOM',
            'TOLERANCE',
            'VOLTAGE',
        ].includes(name);
    }
    function libraryEntryForDbId(libraryInstanceCatalog, dbId) {
        const firstAtOrAfter = firstLibraryInstanceCatalogEntryAtOrAfter(libraryInstanceCatalog, dbId);
        return firstAtOrAfter === 0 ? undefined : libraryInstanceCatalog.entries[firstAtOrAfter - 1];
    }
    function libraryEntryForPlacedPackage(libraryInstanceCatalog, dbId, pkgName) {
        return (libraryEntryForCompatiblePackage(libraryInstanceCatalog, dbId, pkgName) ??
            libraryEntryForDbId(libraryInstanceCatalog, dbId));
    }
    function libraryEntryForCompatiblePackage(libraryInstanceCatalog, dbId, pkgName) {
        const normalizedPackageKey = normalizeSheetReferenceToken(stripNormalViewSuffix(pkgName));
        const packageKey = {
            normalized: normalizedPackageKey,
            compact: normalizedPackageKey.replace(/PORT/g, ''),
        };
        if (!packageKey.normalized || packageKey.normalized.length < 5) {
            return undefined;
        }
        const firstInWindow = firstLibraryInstanceCatalogEntryAtOrAfter(libraryInstanceCatalog, dbId - 1000);
        const firstAtOrAfter = firstLibraryInstanceCatalogEntryAtOrAfter(libraryInstanceCatalog, dbId);
        let best;
        for (let index = firstInWindow; index < firstAtOrAfter; index++) {
            const entry = libraryInstanceCatalog.entries[index];
            if (!libraryEntryMatchesPackage(entry, packageKey)) {
                continue;
            }
            best = entry;
        }
        return best;
    }
    function firstLibraryInstanceCatalogEntryAtOrAfter(libraryInstanceCatalog, bound) {
        let lower = 0;
        let upper = libraryInstanceCatalog.entries.length;
        while (lower < upper) {
            const index = Math.floor((lower + upper) / 2);
            const value = libraryInstanceCatalog.entries[index].instanceNumber;
            if (value >= bound) {
                upper = index;
            }
            else {
                lower = index + 1;
            }
        }
        return lower;
    }
    function libraryEntryMatchesPackage(entry, packageKey) {
        return entry.packageKeys.some((valueKey) => {
            if (sheetReferenceKeysMatchDirectly(packageKey.normalized, valueKey.normalized)) {
                return true;
            }
            return compactSheetReferenceKeysMatch(packageKey.compact, valueKey.compact);
        });
    }
    function packageSourceKey(packageName, sourceLibName) {
        return `${canonicalSourceLibraryName(sourceLibName)}\0${stripNormalViewSuffix(packageName)}`;
    }
    function crossSourcePackageKey(packageName) {
        return stripNormalViewSuffix(packageName);
    }
    function canonicalSourceLibraryName(sourceLibName) {
        const normalized = (sourceLibName ?? '').trim().replace(/\\/g, '/');
        if (!normalized) {
            return '';
        }
        const prefix = normalized.startsWith('//') ? '//'
            : normalized.startsWith('/') ? '/'
                : '';
        const segments = [];
        for (const segment of normalized.split('/')) {
            if (!segment || segment === '.') {
                continue;
            }
            if (segment === '..') {
                if (segments.length > 0 && segments[segments.length - 1] !== '..') {
                    segments.pop();
                }
                else if (!prefix) {
                    segments.push(segment);
                }
                continue;
            }
            segments.push(segment);
        }
        return `${prefix}${segments.join('/')}`.toLowerCase();
    }
    function cacheIdentityKey(cellName, packageName, sourceLibName) {
        return `${stripNormalViewSuffix(cellName)}\0${stripNormalViewSuffix(packageName)}\0${canonicalSourceLibraryName(sourceLibName)}`;
    }
    function cacheCellLibraryKey(cellName, sourceLibName) {
        return `${stripNormalViewSuffix(cellName)}\0${canonicalSourceLibraryName(sourceLibName)}`;
    }
    function buildCacheCatalog(document, diagnostics) {
        const cache = document.streams.find((stream) => stream.path === 'Root Entry/Cache');
        const stringEntries = cache?.lengthPrefixedStrings ?? [];
        const strings = stringEntries.map((entry) => entry.value);
        const stringOffsets = stringEntries.map((entry) => entry.offset);
        const catalog = new Map();
        const structurallyRegisteredCells = new Set();
        const framedEntries = collectPackageLibraryEntries(document, diagnostics);
        const framedIdentities = new Set(framedEntries.map((entry) => cacheCellLibraryKey(entry.cellName, entry.sourceLibName)));
        for (let index = 0; index < strings.length; index++) {
            const row = cacheCatalogRowAt(strings, index, stringOffsets);
            if (!row) {
                continue;
            }
            if (row.sourceLibName && !framedIdentities.has(cacheCellLibraryKey(row.cellName, row.sourceLibName))) {
                emitDecisionDiagnostic(diagnostics, 'package-library-unframed', `cell="${row.cellName}" offset=${row.headerOffset}: no framed library; sourced physical mapping withheld`, 'CFB physical pin map', 'warning');
                continue;
            }
            const entry = {
                cellName: row.cellName,
                packageName: row.cellName,
                sourceLibName: row.sourceLibName,
                canonicalSourceLibName: canonicalSourceLibraryName(row.sourceLibName),
                ...(row.referencePrefix ? { referencePrefix: row.referencePrefix } : {}),
                contentsViewName: row.cellName,
                partValue: row.partValue ?? row.cellName,
            };
            addCacheCatalogEntry(catalog, row.cellName, entry);
            structurallyRegisteredCells.add(cacheCellLibraryKey(row.cellName, row.sourceLibName));
            addSectionCatalogEntries(catalog, strings, row.sectionStartIndex, entry, row.provenance === 'split');
        }
        addFramedLibraryCatalogEntries(catalog, framedEntries, structurallyRegisteredCells);
        return catalog;
    }
    function isCacheCatalogHeaderCandidateAt(strings, index) {
        if (index + 3 >= strings.length) {
            return false;
        }
        const cellName = stripNormalViewSuffix(strings[index] ?? '');
        const standardRepeat = stripNormalViewSuffix(strings[index + 2] ?? '') === cellName;
        const alternateSourceRepeat = isAlternateSourceCatalogHeaderAt(strings, index);
        const singleLibraryRepeat = isSingleLibraryCatalogHeaderAt(strings, index);
        const nonLibrarySourceRoot = isNonLibrarySourceRootCatalogHeaderAt(strings, index);
        return (isPlausibleCatalogPackageName(cellName) &&
            (standardRepeat || alternateSourceRepeat || singleLibraryRepeat || nonLibrarySourceRoot));
    }
    function hasCacheCatalogFieldDiscriminatorAt(strings, index) {
        const field = strings[index + 1] ?? '';
        if (isNumericString(field) || (isCacheCatalogMarkerToken(field) && !/^[A-Za-z]$/.test(field))) {
            return true;
        }
        return ((isSourceLibraryPath(field) && isSourceLibraryPath(strings[index + 3])) ||
            isAlternateSourceCatalogHeaderAt(strings, index) ||
            isSingleLibraryCatalogHeaderAt(strings, index) ||
            bareLetterRefdesMultipartBodyStartAt(strings, index) !== undefined ||
            isBareLetterRootCatalogHeaderAt(strings, index) ||
            isNonLibrarySourceRootCatalogHeaderAt(strings, index));
    }
    function isSingleLibraryCatalogHeaderAt(strings, index) {
        const footprint = strings[index + 3] ?? '';
        return (isSourceLibraryPath(strings[index + 1]) &&
            isReferencePrefix(strings[index + 2]) &&
            !isSourceLibraryPath(footprint) &&
            !footprint.startsWith('$') &&
            !isNumericString(footprint) &&
            !isCacheCatalogMarkerToken(footprint) &&
            strings[index + 4] === strings[index]);
    }
    function isAlternateSourceCatalogHeaderAt(strings, index) {
        return (index > 0 &&
            isSourceLibraryPath(strings[index - 1]) &&
            isSourceLibraryPath(strings[index + 1]) &&
            canonicalSourceLibraryName(strings[index - 1]) === canonicalSourceLibraryName(strings[index + 1]) &&
            isReferencePrefix(strings[index + 2]) &&
            strings[index + 3] === strings[index]);
    }
    function bareLetterRefdesMultipartBodyStartAt(strings, index) {
        const cellName = stripNormalViewSuffix(strings[index] ?? '');
        const field = strings[index + 1] ?? '';
        const referencePrefix = strings[index + 3];
        if (!isPlausibleCatalogPackageName(cellName) ||
            stripNormalViewSuffix(strings[index + 2] ?? '') !== cellName ||
            !/^[A-Za-z]$/.test(field) ||
            isSourceLibraryPath(referencePrefix) ||
            !isReferencePrefix(referencePrefix)) {
            return undefined;
        }
        let cursor = index + 4;
        const footprint = strings[cursor];
        if (!isMultipartSectionMarker(footprint) &&
            !isSourceLibraryPath(footprint) &&
            !(footprint ?? '').startsWith('$') &&
            !isNumericString(footprint) &&
            !isCacheCatalogMarkerToken(footprint)) {
            cursor++;
        }
        const sectionName = stripNormalViewSuffix(strings[cursor + 1] ?? '');
        return (isMultipartSectionMarker(strings[cursor]) &&
            sectionName.length > cellName.length &&
            sectionName.startsWith(cellName)) ?
            cursor
            : undefined;
    }
    function isBareLetterRootCatalogHeaderAt(strings, index) {
        const field = strings[index + 1] ?? '';
        const rawCellName = strings[index] ?? '';
        const cursor = index + 4;
        return (/^[A-Za-z]$/.test(field) &&
            isSourceLibraryPath(strings[index + 3]) &&
            strings.slice(cursor + 1, Math.min(cursor + 4, strings.length)).some((value) => value === rawCellName));
    }
    function isNonLibrarySourceRootCatalogHeaderAt(strings, index) {
        const rawCellName = strings[index] ?? '';
        const field = strings[index + 1] ?? '';
        const standardFrame = /^[A-Za-z]$/.test(field) &&
            strings[index + 2] === rawCellName &&
            isReferencePrefix(strings[index + 3]) &&
            (strings[index + 4] === rawCellName || strings[index + 5] === rawCellName);
        const compactFrame = strings[index + 1] === rawCellName && isReferencePrefix(strings[index + 2]) && strings[index + 3] === rawCellName;
        return standardFrame || compactFrame;
    }
    function cacheCatalogRowAt(strings, index, stringOffsets = []) {
        if (!isCacheCatalogHeaderCandidateAt(strings, index) || !hasCacheCatalogFieldDiscriminatorAt(strings, index)) {
            return undefined;
        }
        const rawCellName = strings[index] ?? '';
        const cellName = stripNormalViewSuffix(rawCellName);
        if (isSingleLibraryCatalogHeaderAt(strings, index)) {
            const bodyStartIndex = index + 5;
            return {
                cellName,
                sourceLibName: strings[index + 1] ?? '',
                referencePrefix: strings[index + 2],
                sectionStartIndex: bodyStartIndex,
                bodyStartIndex,
                headerIndex: index,
                headerOffset: stringOffsets[index] ?? index,
                bodyStartOffset: stringOffsets[bodyStartIndex] ?? bodyStartIndex,
                bodyKind: 'root',
                provenance: 'split',
            };
        }
        const field = strings[index + 1] ?? '';
        const counted = isNumericString(field);
        const marker = isCacheCatalogMarkerToken(field) && !/^[A-Za-z]$/.test(field);
        const bareLetterRefdesMultipartBodyStart = bareLetterRefdesMultipartBodyStartAt(strings, index);
        const alternateSourceRepeat = isAlternateSourceCatalogHeaderAt(strings, index);
        const nonLibrarySourceRoot = isNonLibrarySourceRootCatalogHeaderAt(strings, index);
        const sourceLibCandidate = strings[alternateSourceRepeat ? index + 1 : index + 3];
        const sourceLibName = isSourceLibraryPath(sourceLibCandidate) ? sourceLibCandidate : '';
        const split = !counted &&
            !marker &&
            isSourceLibraryPath(field) &&
            isSourceLibraryPath(sourceLibName) &&
            stripNormalViewSuffix(strings[index + 2] ?? '') === cellName;
        const hasRepeatedBodyAnchor = strings
            .slice(index + 4, Math.min(index + 8, strings.length))
            .some((value) => value === rawCellName);
        if (!split &&
            !isSourceLibraryPath(sourceLibName) &&
            !hasRepeatedBodyAnchor &&
            !nonLibrarySourceRoot &&
            bareLetterRefdesMultipartBodyStart === undefined) {
            return undefined;
        }
        let cursor = index + (alternateSourceRepeat ? 2 : 4);
        if (isCacheCatalogMarkerToken(strings[cursor]) && !/^[A-Za-z]$/.test(strings[cursor] ?? '')) {
            cursor++;
        }
        let referencePrefix;
        let partValue;
        let bodyStartIndex;
        let bodyKind;
        if (counted &&
            strings[cursor] === rawCellName &&
            isReferencePrefix(strings[index + 3]) &&
            strings[cursor - 2] === rawCellName) {
            referencePrefix = strings[index + 3];
            bodyStartIndex = cursor + 1;
            cursor = bodyStartIndex;
            bodyKind = 'root';
        }
        else if (alternateSourceRepeat) {
            referencePrefix = isReferencePrefix(strings[cursor]) ? strings[cursor] : undefined;
            if (strings[cursor + 1] === rawCellName) {
                cursor += 2;
                bodyStartIndex = cursor;
                bodyKind = 'root';
            }
        }
        else if (split) {
            referencePrefix = strings[cursor];
            if (strings[cursor + 1] === rawCellName) {
                cursor++;
                while (strings[cursor + 1] === rawCellName) {
                    cursor++;
                }
                cursor++;
                bodyKind = 'root';
            }
            else if (strings[cursor + 2] === rawCellName &&
                isReferencePrefix(strings[cursor]) &&
                !isSourceLibraryPath(strings[cursor + 1]) &&
                !(strings[cursor + 1] ?? '').startsWith('$') &&
                !isNumericString(strings[cursor + 1]) &&
                !isCacheCatalogMarkerToken(strings[cursor + 1])) {
                let anchorIndex = cursor + 2;
                while (strings[anchorIndex + 1] === rawCellName) {
                    anchorIndex++;
                }
                cursor = anchorIndex + 1;
                bodyKind = 'root';
            }
            else {
                cursor++;
                bodyKind = 'multipart';
            }
            bodyStartIndex = cursor;
        }
        else if (bareLetterRefdesMultipartBodyStart !== undefined) {
            referencePrefix = strings[index + 3];
            cursor = bareLetterRefdesMultipartBodyStart;
            bodyStartIndex = cursor;
            bodyKind = 'multipart';
        }
        else if (nonLibrarySourceRoot &&
            strings[index + 1] === rawCellName &&
            isReferencePrefix(strings[index + 2]) &&
            strings[index + 3] === rawCellName) {
            referencePrefix = strings[index + 2];
            cursor = index + 4;
            bodyStartIndex = cursor;
            bodyKind = 'root';
        }
        else {
            const repeatedPackageSearchStart = nonLibrarySourceRoot ? cursor : cursor + 1;
            const repeatedPackageOffset = strings
                .slice(repeatedPackageSearchStart, Math.min(repeatedPackageSearchStart + 3, strings.length))
                .findIndex((value) => value === rawCellName);
            const repeatedPackageIndex = repeatedPackageOffset < 0 ? -1 : repeatedPackageSearchStart - cursor + repeatedPackageOffset;
            if ((counted || marker) &&
                isMultipartSectionMarker(strings[cursor + 1] ?? '') &&
                isSectionOfCatalogPackage(stripNormalViewSuffix(strings[cursor + 2] ?? ''), cellName)) {
                referencePrefix = strings[cursor];
                cursor++;
                bodyStartIndex = cursor;
                bodyKind = 'multipart';
            }
            else if (repeatedPackageIndex > 0) {
                let anchorIndex = cursor + repeatedPackageIndex;
                const referenceCandidate = strings[anchorIndex - 1];
                if (nonLibrarySourceRoot && isReferencePrefix(strings[index + 3])) {
                    referencePrefix = strings[index + 3];
                    partValue = catalogPartValueAfterReferencePrefix(referenceCandidate, cellName);
                }
                else if (anchorIndex - 1 > cursor && isReferencePrefix(strings[cursor])) {
                    referencePrefix = strings[cursor];
                    partValue = catalogPartValueAfterReferencePrefix(referenceCandidate, cellName);
                }
                else if (anchorIndex > cursor && isReferencePrefix(referenceCandidate)) {
                    referencePrefix = referenceCandidate;
                }
                while (strings[anchorIndex + 1] === rawCellName) {
                    anchorIndex++;
                }
                cursor = anchorIndex + 1;
                bodyStartIndex = cursor;
                bodyKind = 'root';
            }
            else if (repeatedPackageIndex === 0 && nonLibrarySourceRoot) {
                referencePrefix = isReferencePrefix(strings[index + 3]) ? strings[index + 3] : undefined;
                while (strings[cursor + 1] === rawCellName) {
                    cursor++;
                }
                cursor++;
                bodyStartIndex = cursor;
                bodyKind = 'root';
            }
            else if ((counted || marker) &&
                isPlausibleCatalogPackageName(stripNormalViewSuffix(strings[cursor + 1] ?? '')) &&
                !isSourceLibraryPath(strings[cursor + 1] ?? '') &&
                isMultipartSectionMarker(strings[cursor + 2] ?? '') &&
                isSectionOfCatalogPackage(stripNormalViewSuffix(strings[cursor + 3] ?? ''), cellName)) {
                referencePrefix = strings[cursor];
                cursor += 2;
                bodyStartIndex = cursor;
                bodyKind = 'multipart';
            }
            else if (isReferencePrefix(strings[cursor])) {
                referencePrefix = strings[cursor];
                cursor++;
            }
        }
        partValue ??= catalogPartValueAfterReferencePrefix(strings[cursor], cellName);
        return {
            cellName,
            sourceLibName,
            ...(referencePrefix ? { referencePrefix } : {}),
            ...(partValue ? { partValue } : {}),
            sectionStartIndex: cursor,
            ...(bodyStartIndex !== undefined ? { bodyStartIndex } : {}),
            headerIndex: index,
            headerOffset: stringOffsets[index] ?? index,
            ...(bodyStartIndex !== undefined ? { bodyStartOffset: stringOffsets[bodyStartIndex] ?? bodyStartIndex } : {}),
            ...(bodyKind ? { bodyKind } : {}),
            provenance: bareLetterRefdesMultipartBodyStart !== undefined ? 'marker'
                : counted ? 'counted'
                    : marker ? 'marker'
                        : 'split',
        };
    }
    function catalogPartValueAfterReferencePrefix(value, packageName) {
        const normalizedValue = stripNormalViewSuffix(value ?? '');
        if (!value ||
            isNumericString(value) ||
            isDeviceDesignatorToken(value) ||
            isSourceLibraryPath(value) ||
            isCacheCatalogMarkerToken(value) ||
            /^(?:REFDES|PART NUMBER|DESCRIPTION)$/i.test(value) ||
            normalizedValue === packageName ||
            isSectionOfCatalogPackage(normalizedValue, packageName)) {
            return undefined;
        }
        return value;
    }
    function addFramedLibraryCatalogEntries(catalog, candidates, structurallyRegisteredCells) {
        for (const candidate of candidates) {
            if (structurallyRegisteredCells.has(cacheCellLibraryKey(candidate.cellName, candidate.sourceLibName))) {
                continue;
            }
            const candidateLibrary = canonicalSourceLibraryName(candidate.sourceLibName);
            const registeredPackageNames = new Set([
                ...Array.from(catalog.values())
                    .flat()
                    .filter((entry) => entry.canonicalSourceLibName === candidateLibrary)
                    .map((entry) => entry.packageName),
                ...candidates
                    .filter((entry) => canonicalSourceLibraryName(entry.sourceLibName) === candidateLibrary)
                    .map((entry) => entry.cellName),
            ]);
            const parentPackageName = parentCatalogPackageName(candidate.cellName, registeredPackageNames);
            const existing = catalogEntriesForGraphicName(catalog, candidate.cellName).find((entry) => entry.canonicalSourceLibName === candidateLibrary);
            if (existing) {
                if (parentPackageName && existing.packageName !== parentPackageName) {
                    addCacheCatalogEntry(catalog, candidate.cellName, {
                        ...existing,
                        packageName: parentPackageName,
                        contentsViewName: candidate.cellName,
                    });
                }
                continue;
            }
            const packageName = parentPackageName ?? candidate.cellName;
            const entry = {
                cellName: candidate.cellName,
                packageName,
                sourceLibName: candidate.sourceLibName,
                canonicalSourceLibName: candidateLibrary,
                ...(candidate.referencePrefix ? { referencePrefix: candidate.referencePrefix } : {}),
                contentsViewName: candidate.cellName,
                partValue: candidate.cellName,
            };
            addCacheCatalogEntry(catalog, candidate.cellName, entry);
        }
    }
    function parentCatalogPackageName(cellName, packageNames) {
        return Array.from(packageNames)
            .filter((packageName) => packageName !== cellName &&
            isSectionOfCatalogPackage(cellName, packageName) &&
            !isAlternatePackageCatalogSection(cellName, packageName))
            .sort((left, right) => right.length - left.length || left.localeCompare(right))[0];
    }
    function addSectionCatalogEntries(catalog, strings, startIndex, entry, preserveSectionPackageIdentity) {
        let pendingDeviceDesignator;
        for (let index = startIndex; index < strings.length; index++) {
            const value = stripNormalViewSuffix(strings[index]);
            if (cacheCatalogRowAt(strings, index)) {
                break;
            }
            if (isDeviceDesignatorToken(value)) {
                pendingDeviceDesignator = value;
                continue;
            }
            if (!isSectionOfCatalogPackage(value, entry.packageName)) {
                continue;
            }
            if (value === entry.packageName) {
                continue;
            }
            if (isAlternatePackageCatalogSection(value, entry.packageName)) {
                addCacheCatalogEntry(catalog, value, {
                    ...entry,
                    cellName: value,
                    packageName: value,
                    contentsViewName: value,
                    partValue: value,
                    ...(pendingDeviceDesignator ? { deviceDesignator: pendingDeviceDesignator } : {}),
                });
                continue;
            }
            addCacheCatalogEntry(catalog, value, {
                ...entry,
                cellName: value,
                sectionParentName: entry.packageName,
                ...(preserveSectionPackageIdentity ?
                    { packageName: value, contentsViewName: value, preserveSectionPackageIdentity: true }
                    : { contentsViewName: value }),
                ...(pendingDeviceDesignator ? { deviceDesignator: pendingDeviceDesignator } : {}),
            });
        }
    }
    function addCacheCatalogEntry(catalog, key, entry) {
        const normalizedKey = stripNormalViewSuffix(key);
        const existingEntries = catalog.get(normalizedKey) ?? [];
        const sameLibrarySection = existingEntries.find((candidate) => candidate.canonicalSourceLibName === entry.canonicalSourceLibName && candidate.sectionParentName);
        if (!entry.sectionParentName && sameLibrarySection && !sameLibrarySection.preserveSectionPackageIdentity) {
            return;
        }
        if (entry.sectionParentName &&
            entry.preserveSectionPackageIdentity &&
            existingEntries.some((candidate) => candidate.canonicalSourceLibName === entry.canonicalSourceLibName && !candidate.sectionParentName)) {
            return;
        }
        const entries = entry.sectionParentName ?
            existingEntries.filter((candidate) => candidate.canonicalSourceLibName !== entry.canonicalSourceLibName)
            : existingEntries.filter((candidate) => candidate.canonicalSourceLibName !== entry.canonicalSourceLibName || !candidate.sectionParentName);
        const identity = cacheIdentityKey(entry.cellName, entry.packageName, entry.canonicalSourceLibName);
        const existingIndex = entries.findIndex((candidate) => cacheIdentityKey(candidate.cellName, candidate.packageName, candidate.canonicalSourceLibName) === identity);
        const existing = existingIndex >= 0 ? entries[existingIndex] : undefined;
        const mergedEntry = (existing?.partValue &&
            existing.partValue !== existing.packageName &&
            (!entry.partValue || entry.partValue === entry.packageName)) ?
            { ...entry, partValue: existing.partValue }
            : entry;
        const updated = existingIndex >= 0 ?
            entries.map((candidate, index) => (index === existingIndex ? mergedEntry : candidate))
            : [...entries, mergedEntry];
        catalog.set(normalizedKey, updated);
        catalog.set(`${normalizedKey}.Normal`, updated);
        catalog.set(`${normalizedKey}.IEEE`, updated);
    }
    function catalogEntriesForGraphicName(catalog, graphicName) {
        for (const key of catalogLookupKeys(graphicName)) {
            const entries = catalog.get(key);
            if (entries) {
                return entries;
            }
        }
        return [];
    }
    function catalogEntryForGraphicName(catalog, graphicName, sourceLibName, diagnostics) {
        const entries = catalogEntriesForGraphicName(catalog, graphicName);
        const canonicalSource = canonicalSourceLibraryName(sourceLibName);
        const matches = entries.filter((entry) => entry.canonicalSourceLibName === canonicalSource);
        if (matches.length === 1) {
            return matches[0];
        }
        if (entries.length > 0) {
            emitDecisionDiagnostic(diagnostics, 'package-library-association-withheld', `cell="${graphicName}" source=${JSON.stringify(sourceLibName)} candidates=${JSON.stringify(entries.map((entry) => entry.sourceLibName))}: no unique exact-source catalogue association`, 'CFB physical pin map', 'warning');
        }
        return undefined;
    }
    function resolvePlacedPartCacheIdentity(object, cacheCatalog, packageViewPinCatalog, physicalPinNumberCatalog, instanceSourceLibraries, markerSourceLibraries, diagnostics) {
        const cellName = stripNormalViewSuffix(object.graphicName);
        const candidates = dedupePlacedPartCacheCandidates(cellName, catalogEntriesForGraphicName(cacheCatalog, object.graphicName));
        const identityFor = (entry, resolution, sourceLibName = entry.sourceLibName) => {
            const cachePins = canonicalSourceLibraryName(sourceLibName) === entry.canonicalSourceLibName ?
                cachePinsForPlacedGraphic(packageViewPinCatalog, object, cellName, entry, diagnostics)
                : undefined;
            return {
                cellName,
                packageName: entry.packageName,
                sourceLibName,
                canonicalSourceLibName: canonicalSourceLibraryName(sourceLibName),
                cacheSourceLibName: entry.sourceLibName,
                catalogEntry: entry,
                hasDeclaredBounds: cachePins !== undefined && packageViewPinCatalog.pinsWithDeclaredBounds.has(cachePins),
                cachePins,
                resolution,
            };
        };
        const selectLibrary = (sourceLibName, resolution) => {
            const canonicalSource = canonicalSourceLibraryName(sourceLibName);
            if (!canonicalSource) {
                return undefined;
            }
            const matches = candidates.filter((entry) => entry.canonicalSourceLibName === canonicalSource);
            return matches.length === 1 ? identityFor(matches[0], resolution) : undefined;
        };
        const markerSource = markerSourceLibraries.get(object);
        const markerCanonical = canonicalSourceLibraryName(markerSource);
        const explicitSource = explicitSourceLibraryForPlacedPart(object);
        const explicitCanonical = canonicalSourceLibraryName(explicitSource);
        const instanceSource = instanceSourceLibraries.get(object.resolvedMetadata?.name ?? '');
        const instanceCanonical = canonicalSourceLibraryName(instanceSource);
        const statedSources = new Set([markerCanonical, explicitCanonical, instanceCanonical].filter((source) => source.length > 0));
        if (statedSources.size > 1) {
            emitPhysicalPinDiagnostic(diagnostics, `${cellName}\0${object.dbId}\0${object.offset}`, 'library-source-conflict', `cell="${cellName}" marker="${markerSource ?? ''}" explicit="${explicitSource ?? ''}" instance="${instanceSource ?? ''}" reference="${object.reference ?? ''}": library withheld`, undefined, 'warning');
            return null;
        }
        if (candidates.length === 0) {
            return undefined;
        }
        const markerMatches = markerCanonical ? candidates.filter((entry) => entry.canonicalSourceLibName === markerCanonical) : [];
        if (markerMatches.length === 1) {
            return identityFor(markerMatches[0], 'placed-library-reference');
        }
        if (markerCanonical &&
            markerSource &&
            candidates.length === 1 &&
            candidates[0].canonicalSourceLibName.length === 0) {
            return identityFor(candidates[0], 'placed-library-reference', markerSource);
        }
        if (!markerCanonical) {
            const explicit = selectLibrary(explicitSource, 'explicit');
            if (explicit) {
                return explicit;
            }
            const associated = selectLibrary(instanceSource, 'instance-library');
            if (associated) {
                return associated;
            }
        }
        emitPhysicalPinDiagnostic(diagnostics, cellName, 'library-source-unresolved', `cell="${cellName}" candidates=${candidates.length} reference="${object.reference ?? ''}"`, undefined, 'warning');
        const consensusPhysicalPinNumbers = physicalPinNumberConsensus(object, cellName, candidates, physicalPinNumberCatalog);
        if (consensusPhysicalPinNumbers) {
            applyPhysicalPinNumberConsensus(object, consensusPhysicalPinNumbers);
            emitPhysicalPinDiagnostic(diagnostics, cellName, 'physical-map-consensus', `cell="${cellName}" candidates=${candidates.length} reference="${object.reference ?? ''}"`, undefined, 'info');
            return undefined;
        }
        emitPhysicalPinDiagnostic(diagnostics, cellName, 'library-marker-ambiguous', `cell="${cellName}" marker=${object.marker} candidates=${candidates.length}`);
        emitPhysicalPinDiagnostic(diagnostics, cellName, 'library-indeterminate', `cell="${cellName}" candidates=${candidates.length} reference="${object.reference ?? ''}"`);
        return undefined;
    }
    function physicalPinNumberConsensus(object, cellName, candidates, catalog) {
        const arity = recordedPlacedPortArity(object);
        if (arity === undefined || candidates.length < 2) {
            return undefined;
        }
        let consensus;
        for (const candidate of candidates) {
            const vectors = new Map();
            for (const map of catalog.get(cacheIdentityKey(cellName, candidate.packageName, candidate.sourceLibName)) ?? []) {
                const vector = physicalPinNumbersForCandidate(map);
                if (vector.length === arity) {
                    vectors.set(JSON.stringify(vector), vector);
                }
            }
            if (vectors.size !== 1) {
                return undefined;
            }
            for (const vector of vectors.values()) {
                if (consensus !== undefined && !arraysEqual(consensus, vector)) {
                    return undefined;
                }
                consensus = vector;
            }
        }
        return consensus;
    }
    function applyPhysicalPinNumberConsensus(object, pinNumbers) {
        for (const port of object.materializedPortInstances ?? []) {
            if (port.position !== undefined && port.position >= 0 && port.position < pinNumbers.length) {
                port.physicalPinNumber = pinNumbers[port.position];
            }
        }
    }
    function isOrdinalPinNumberMap(pinNumbers) {
        return pinNumbers.every((pinNumber, position) => pinNumber === String(position + 1));
    }
    function dedupePlacedPartCacheCandidates(cellName, candidates) {
        const identities = new Map();
        for (const candidate of candidates) {
            const key = cacheIdentityKey(cellName, candidate.packageName, candidate.canonicalSourceLibName);
            if (!identities.has(key)) {
                identities.set(key, candidate);
            }
        }
        return Array.from(identities.values());
    }
    function explicitSourceLibraryForPlacedPart(object) {
        const metadata = object.resolvedMetadata;
        if (metadata?.libName && isSourceLibraryPath(metadata.libName)) {
            return metadata.libName;
        }
        if (metadata?.contentsLibName &&
            /\.olb$/i.test(metadata.contentsLibName) &&
            isSourceLibraryPath(metadata.contentsLibName)) {
            return metadata.contentsLibName;
        }
        return undefined;
    }
    function placedPartGeometryMatchesCachePins(object, cachePins) {
        const ports = object.materializedPortInstances ?? [];
        if (ports.length === 0 || !cachePins?.length) {
            return false;
        }
        return ports.every((port) => {
            const hotptX = port.hotptX ?? port.x;
            const hotptY = port.hotptY ?? port.y;
            const startX = port.startX;
            const startY = port.startY;
            if (hotptX === undefined || hotptY === undefined || startX === undefined || startY === undefined) {
                return false;
            }
            return cachePins.some((pin) => pin.hotptX === hotptX && pin.hotptY === hotptY && pin.startX === startX && pin.startY === startY);
        });
    }
    function buildPlacedPartCacheIdentityCatalog(document, cacheCatalog, packageViewPinCatalog, physicalPinNumberCatalog, instanceSourceLibraries, markerSourceLibraries, diagnostics) {
        const identities = new WeakMap();
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
                    continue;
                }
                const identity = resolvePlacedPartCacheIdentity(object, cacheCatalog, packageViewPinCatalog, physicalPinNumberCatalog, instanceSourceLibraries, markerSourceLibraries, diagnostics);
                if (identity !== undefined) {
                    identities.set(object, identity);
                }
            }
        }
        return identities;
    }
    function buildPlacedViewEvidenceCatalog(document, identities) {
        const placed = new Map();
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
                    continue;
                }
                const identity = identities.get(object);
                if (identity === null) {
                    continue;
                }
                const cellName = identity?.cellName ?? stripNormalViewSuffix(object.graphicName);
                const packageName = identity?.packageName ?? object.resolvedMetadata?.pkgName ?? cellName;
                const sourceLibName = identity?.sourceLibName ?? object.resolvedMetadata?.libName ?? '';
                const key = cacheIdentityKey(cellName, packageName, sourceLibName);
                const objects = placed.get(key) ?? [];
                objects.push(object);
                placed.set(key, objects);
            }
        }
        return placed;
    }
    function pagePointKey(x, y) {
        return `${x}\0${y}`;
    }
    function buildSymbolSourceCatalog(document) {
        const sources = new Map();
        for (const [name, sourceLibNames] of buildSymbolSourceLibraryCatalog(document)) {
            if (sourceLibNames[0]) {
                sources.set(name, sourceLibNames[0]);
            }
        }
        return sources;
    }
    function buildSymbolSourceLibraryCatalog(document) {
        const cache = document.streams.find((stream) => stream.path === 'Root Entry/Cache');
        const strings = cache?.lengthPrefixedStrings.map((entry) => entry.value) ?? [];
        const sources = new Map();
        const addSource = (name, sourceLibName) => {
            if (!name || !isSourceLibraryPath(sourceLibName)) {
                return;
            }
            const values = sources.get(name) ?? [];
            if (!values.includes(sourceLibName)) {
                values.push(sourceLibName);
                sources.set(name, values);
            }
        };
        for (let index = 0; index + 1 < strings.length; index++) {
            const name = stripNormalViewSuffix(strings[index]);
            const compactSource = strings[index + 1];
            if (isSourceLibraryPath(compactSource)) {
                addSource(name, compactSource);
                continue;
            }
            const countedName = stripNormalViewSuffix(strings[index + 2] ?? '');
            const countedSource = strings[index + 3];
            if (isNumericString(strings[index + 1]) && countedName === name && isSourceLibraryPath(countedSource)) {
                addSource(name, countedSource);
            }
        }
        return sources;
    }
    function catalogLookupKeys(graphicName) {
        const stripped = stripNormalViewSuffix(graphicName);
        return [graphicName, stripped, `${stripped}.Normal`, `${stripped}.IEEE`];
    }
    function buildLivePartPackages(document, existingPackageIdentities, placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, diagnostics) {
        const packageObjects = new Map();
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
                    continue;
                }
                const identity = placedPartCacheIdentityCatalog.get(object);
                if (identity === null) {
                    continue;
                }
                const catalogEntry = identity?.catalogEntry;
                const pkgName = identity?.packageName ?? pkgNameFromGraphicName(object.graphicName);
                const markerSource = markerSourceLibraries.get(object);
                const sources = new Set([identity?.sourceLibName, markerSource, catalogEntry?.sourceLibName].filter((source) => source !== undefined && source.length > 0));
                if (sources.size > 1) {
                    emitDecisionDiagnostic(diagnostics, 'live-package-source-conflict', `package="${pkgName}" page="${stream.path}" offset=${object.offset} sources=${JSON.stringify([...sources])}: body withheld`, 'CFB physical pin map', 'warning');
                    continue;
                }
                const [statedSource] = sources;
                const sourceLibName = statedSource ?? '';
                const packageIdentity = packageSourceKey(pkgName, sourceLibName);
                if (!pkgName) {
                    continue;
                }
                if (existingPackageIdentities.has(packageIdentity)) {
                    continue;
                }
                if (sourceLibName.length > 0) {
                    emitPhysicalPinDiagnostic(diagnostics, packageIdentity, 'placed-library-live-package-withheld', `package="${pkgName}" source="${sourceLibName}": no exact source-labelled electrical interface`, undefined, 'warning');
                    continue;
                }
                const group = packageObjects.get(packageIdentity) ?? {
                    packageNames: new Set(),
                    catalogEntries: new Map(),
                    objects: [],
                };
                group.packageNames.add(pkgName);
                if (catalogEntry) {
                    group.catalogEntries.set(JSON.stringify(catalogEntry), catalogEntry);
                }
                group.objects.push(object);
                packageObjects.set(packageIdentity, group);
            }
        }
        const packages = [];
        for (const [packageIdentity, group] of packageObjects) {
            if (group.packageNames.size !== 1 || group.catalogEntries.size > 1) {
                emitDecisionDiagnostic(diagnostics, 'live-package-catalogue-conflict', `packages=${JSON.stringify([...group.packageNames])} catalogues=${group.catalogEntries.size}: body withheld`, 'CFB physical pin map', 'warning');
                continue;
            }
            const [pkgName] = group.packageNames;
            const [catalogEntry] = group.catalogEntries.values();
            existingPackageIdentities.add(packageIdentity);
            packages.push(buildPackageFromPlacedParts(pkgName, '', group.objects, catalogEntry, physicalPadPresenceCatalog, diagnostics));
        }
        return packages;
    }
    function buildPlacedSourcePackages(document, packages, placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, diagnostics) {
        const groups = new Map();
        const resolved = new Map();
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                const identity = object.kind === 'placed-symbol' ? placedPartCacheIdentityCatalog.get(object) : undefined;
                const sourceLibName = object.kind === 'placed-symbol' ? markerSourceLibraries.get(object) : undefined;
                if (object.kind !== 'placed-symbol' ||
                    object.role !== 'part-inst' ||
                    identity === null ||
                    (identity && identity.cacheSourceLibName.length > 0) ||
                    !sourceLibName) {
                    continue;
                }
                const packageName = identity?.packageName ?? pkgNameFromGraphicName(object.graphicName);
                const cellName = stripNormalViewSuffix(object.graphicName);
                const key = packageSourceKey(packageName, sourceLibName);
                const group = groups.get(key) ?? {
                    packageName,
                    sourceLibName,
                    cellNames: new Set(),
                    objects: [],
                };
                group.cellNames.add(cellName);
                group.objects.push(object);
                groups.set(key, group);
            }
        }
        for (const [key, group] of groups) {
            const interfaces = new Map();
            for (const cellName of group.cellNames) {
                const matchesCell = (pkg) => pkg.Name === group.packageName &&
                    pkg.LibPart.some((part) => stripNormalViewSuffix(part.CellName ?? pkg.Name) === cellName);
                const candidates = packages.filter((pkg) => matchesCell(pkg) &&
                    canonicalSourceLibraryName(pkg.SourceLibName) === canonicalSourceLibraryName(group.sourceLibName));
                const normalizedCandidates = candidates
                    .map((candidate) => placedSourcePackageElectricalInterface(candidate, cellName))
                    .filter((candidate) => candidate !== undefined);
                const distinct = new Map(normalizedCandidates.map((candidate) => [JSON.stringify(candidate), candidate]));
                const [electricalInterface] = distinct.size === 1 ? distinct.values() : [];
                if (electricalInterface) {
                    interfaces.set(cellName, electricalInterface);
                    continue;
                }
                emitDecisionDiagnostic(diagnostics, 'placed-library-package-unresolved', `cell="${cellName}" library="${group.sourceLibName}" definitions=${distinct.size}`, 'CFB physical pin map', 'warning');
            }
            if (interfaces.size !== group.cellNames.size) {
                continue;
            }
            const packageKinds = new Set([...interfaces.values()].map((electricalInterface) => JSON.stringify([electricalInterface.alphabeticNumbering, electricalInterface.isHomogeneous])));
            if (packageKinds.size !== 1) {
                emitPhysicalPinDiagnostic(diagnostics, group.packageName, 'placed-library-package-unresolved', `package="${group.packageName}" library="${group.sourceLibName}" interfaces=${packageKinds.size}`, group.sourceLibName, 'warning');
                continue;
            }
            const pkg = buildPackageFromPlacedParts(group.packageName, group.sourceLibName, group.objects, undefined, physicalPadPresenceCatalog, diagnostics);
            for (const libPart of pkg.LibPart) {
                const electricalInterface = interfaces.get(stripNormalViewSuffix(libPart.CellName ?? pkg.Name));
                const view = libPart.NormalView?.[0];
                if (!electricalInterface || !view) {
                    continue;
                }
                pkg.AlphabeticNumbering = electricalInterface.alphabeticNumbering;
                pkg.IsHomogeneous = electricalInterface.isHomogeneous;
                view.BoundingBox = { ...electricalInterface.boundingBox };
                const livePinsByPosition = new Map([...(view.PinScalar ?? []), ...(view.PinBus ?? [])].map((pin) => [pin.Position, pin]));
                const livePinPositions = new Set([
                    ...(view.PinScalar ?? []).map((pin) => pin.Position),
                    ...(view.PinBus ?? []).map((pin) => pin.Position),
                ]);
                const mappedPinPositions = new Set(electricalInterface.physicalParts.flatMap((part) => part.PinNumber?.map((pin) => pin.Position) ?? []));
                const hasMismatchedLivePinSurface = mappedPinPositions.size !== livePinPositions.size ||
                    ![...mappedPinPositions].every((position) => livePinPositions.has(position));
                view.PinScalar = electricalInterface.scalarPins.map((pin) => ({
                    ...pin,
                    Name: hasMismatchedLivePinSurface ? pin.Name : (livePinsByPosition.get(pin.Position)?.Name ?? pin.Name),
                }));
                view.PinBus = electricalInterface.busPins.map((pin) => ({
                    ...pin,
                    Name: hasMismatchedLivePinSurface ? pin.Name : (livePinsByPosition.get(pin.Position)?.Name ?? pin.Name),
                }));
                libPart.PhysicalPart = electricalInterface.physicalParts.map((part) => ({
                    PinNumber: part.PinNumber?.map((pin) => ({ ...pin })),
                }));
            }
            resolved.set(key, pkg);
        }
        return [...resolved.values()];
    }
    function placedSourcePackageElectricalInterface(pkg, cellName) {
        const parts = pkg.LibPart.filter((part) => stripNormalViewSuffix(part.CellName ?? pkg.Name) === cellName);
        const interfaces = [];
        for (const part of parts) {
            const physicalParts = (part.PhysicalPart ?? []).map((physical) => ({
                PinNumber: physical.PinNumber?.map((pin) => ({ Position: pin.Position, Number: pin.Number })),
            }));
            const physicalPositions = new Set(physicalParts.flatMap((physical) => physical.PinNumber?.map((pin) => pin.Position) ?? []));
            for (const view of part.NormalView ?? []) {
                const scalarPins = view.PinScalar ?? [];
                const busPins = view.PinBus ?? [];
                const logicalPositions = new Set([...scalarPins, ...busPins].map((pin) => pin.Position));
                if (logicalPositions.size !== physicalPositions.size ||
                    ![...logicalPositions].every((position) => physicalPositions.has(position))) {
                    return undefined;
                }
                interfaces.push({
                    boundingBox: view.BoundingBox,
                    scalarPins,
                    busPins,
                    physicalParts,
                });
            }
        }
        const distinct = new Map(interfaces.map((candidate) => [JSON.stringify(candidate), candidate]));
        if (distinct.size !== 1) {
            return undefined;
        }
        const [electricalInterface] = distinct.values();
        if (!electricalInterface) {
            return undefined;
        }
        return {
            alphabeticNumbering: pkg.AlphabeticNumbering,
            isHomogeneous: pkg.IsHomogeneous,
            boundingBox: electricalInterface.boundingBox,
            scalarPins: electricalInterface.scalarPins,
            busPins: electricalInterface.busPins,
            physicalParts: electricalInterface.physicalParts,
        };
    }
    function buildPackageFromPlacedParts(pkgName, sourceLibName, objects, catalogEntry, physicalPadPresenceCatalog, diagnostics) {
        const referencePrefix = catalogEntry?.referencePrefix ?? unanimousPlacementReferencePrefix(pkgName, objects, diagnostics);
        const libParts = buildLivePartLibParts(pkgName, objects, catalogEntry, physicalPadPresenceCatalog, referencePrefix);
        const allPins = objects.flatMap((object) => object.materializedPortInstances ?? []);
        return {
            AlphabeticNumbering: 2,
            IsHomogeneous: true,
            Name: pkgName,
            PcbFootprint: objects.find((object) => object.resolvedMetadata?.pcbLib)?.resolvedMetadata?.pcbLib ?? '',
            PcbLib: '',
            RefdesPrefix: referencePrefix,
            SourceLibName: sourceLibName,
            LibPart: libParts.length > 0 ?
                libParts
                : [
                    {
                        CellName: pkgName,
                        NormalView: [
                            buildLivePartNormalView(pkgName, allPins, catalogEntry, physicalPadPresenceCatalog, referencePrefix),
                        ],
                        PhysicalPart: [buildLivePhysicalPart(allPins)],
                    },
                ],
        };
    }
    function buildLivePhysicalPart(pins) {
        return {
            PinNumber: pins.map((pin, index) => ({
                Position: index,
                Number: pin.physicalPinNumber ?? String(pin.pinIndex ?? pin.position ?? index + 1),
            })),
        };
    }
    function buildLivePartLibParts(pkgName, objects, catalogEntry, physicalPadPresenceCatalog, referencePrefix) {
        const groups = new Map();
        for (const object of objects) {
            const cellName = stripNormalViewSuffix(object.graphicName);
            if (!groups.has(cellName)) {
                groups.set(cellName, object);
            }
        }
        return Array.from(groups, ([cellName, object]) => {
            const pins = object.materializedPortInstances ?? [];
            return {
                CellName: cellName,
                NormalView: [
                    buildLivePartNormalView(pkgName, pins, catalogEntry, physicalPadPresenceCatalog, referencePrefix, object),
                ],
                PhysicalPart: [buildLivePhysicalPart(pins)],
            };
        });
    }
    function unanimousPlacementReferencePrefix(pkgName, objects, diagnostics) {
        const prefixes = new Set(objects
            .map((object) => referencePrefixOfDesignator(object.reference ?? ''))
            .filter((prefix) => prefix.length > 0));
        if (prefixes.size === 1) {
            return [...prefixes][0];
        }
        if (prefixes.size > 1) {
            emitPhysicalPinDiagnostic(diagnostics, pkgName, 'package-reference-prefix-ambiguous', `package="${pkgName}" prefixes=${[...prefixes].sort().join(',')}`);
        }
        return '';
    }
    function buildLivePartNormalView(pkgName, pins, catalogEntry, physicalPadPresenceCatalog, referencePrefix, object) {
        const keys = [
            object?.graphicName ?? pkgName,
            catalogEntry?.packageName ?? pkgName,
            catalogEntry?.canonicalSourceLibName ?? '',
        ];
        const convertPin = (port) => materializedPortToSymbolPin(port, physicalPadPresenceAt(physicalPadPresenceCatalog, keys, pins.length, port.position ?? port.pinIndex ?? 0));
        const scalarPins = pins.filter((pin) => !pin.isBus).map(convertPin);
        const busPins = pins.filter((pin) => pin.isBus).map(convertPin);
        return {
            Suffix: NORMAL_VIEW_SUFFIX,
            SymbolColor: color(),
            BoundingBox: boundingBoxFromPins(pins),
            ContentsLibName: '',
            ContentsViewName: catalogEntry?.contentsViewName ?? '',
            ContentsViewType: 5,
            PartValue: catalogEntry?.partValue ?? object?.partValue ?? pkgName,
            Reference: referencePrefix,
            ...(scalarPins.length > 0 ? { PinScalar: scalarPins } : {}),
            ...(busPins.length > 0 ? { PinBus: busPins } : {}),
        };
    }
    function materializedPortToSymbolPin(port, physicalPadPresence) {
        const implicitShield = isImplicitShieldPort(port, physicalPadPresence);
        const shieldPin = isShieldPinName(port.name);
        const type = implicitShield ? 7 : (port.type ?? DEFAULT_PIN_TYPE);
        const powerPin = type === 7;
        return {
            Name: port.name,
            Position: port.position ?? port.pinIndex ?? 0,
            StartX: port.startX ?? port.x ?? 0,
            StartY: port.startY ?? port.y ?? 0,
            HotptX: port.hotptX ?? port.x ?? 0,
            HotptY: port.hotptY ?? port.y ?? 0,
            Type: type,
            Visible: implicitShield || (shieldPin && type === 7 && port.visible !== true) || port.visible === false ? 0 : 1,
            IsLong: powerPin,
            IsClock: false,
            IsDot: false,
            IsLeftPointing: powerPin ? false : (port.hotptX ?? port.x ?? 0) < (port.startX ?? port.x ?? 0),
            IsRightPointing: powerPin ? false : (port.hotptX ?? port.x ?? 0) > (port.startX ?? port.x ?? 0),
            IsNetStyle: false,
            IsGlobal: powerPin,
            IsNoConnect: shieldPin ? false : port.isNoConnect,
            IsNumberVisible: powerPin,
        };
    }
    function boundingBoxFromPins(pins) {
        const points = pins.flatMap((pin) => [
            { x: pin.startX ?? pin.x ?? 0, y: pin.startY ?? pin.y ?? 0 },
            { x: pin.hotptX ?? pin.x ?? 0, y: pin.hotptY ?? pin.y ?? 0 },
        ]);
        if (points.length === 0) {
            return { X1: 0, Y1: 0, X2: 0, Y2: 0 };
        }
        return {
            X1: Math.min(...points.map((point) => point.x)),
            Y1: Math.min(...points.map((point) => point.y)),
            X2: Math.max(...points.map((point) => point.x)),
            Y2: Math.max(...points.map((point) => point.y)),
        };
    }
    function boundingBoxFromSymbolPins(pins) {
        return boundingBoxFromPins(pins.map((pin, position) => ({
            name: pin.name,
            position,
            startX: pin.startX,
            startY: pin.startY,
            hotptX: pin.hotptX,
            hotptY: pin.hotptY,
            isNoConnect: false,
            isBus: false,
            source: 'symbol-pin',
            isExplicit: false,
        })));
    }
    function oneNormalViewPerSuffix(views, packageName, diagnostics) {
        const viewsBySuffix = new Map();
        for (const view of views) {
            viewsBySuffix.set(view.Suffix, [...(viewsBySuffix.get(view.Suffix) ?? []), view]);
        }
        return [...viewsBySuffix.values()].map((sameSuffix) => {
            const [first] = sameSuffix;
            if (sameSuffix.length === 1) {
                return first;
            }
            const kept = sameSuffix.find((view) => (view.PinScalar?.length ?? 0) + (view.PinBus?.length ?? 0) > 0) ?? first;
            const graphicsDonor = hasNormalViewGraphics(kept) ? undefined : sameSuffix.find(hasNormalViewGraphics);
            emitBudgetedDiagnostic(diagnostics, `${packageName}\0${kept.Suffix}\0same-suffix-views`, 'same-suffix-views-merged', `package "${packageName}" splits view "${kept.Suffix}" across ${sameSuffix.length} headers; kept the one with ${(kept.PinScalar?.length ?? 0) + (kept.PinBus?.length ?? 0)} pins`, 'CFB DSN package', 'info');
            if (graphicsDonor === undefined) {
                return kept;
            }
            const { PinScalar, PinBus, ...donorDrawing } = graphicsDonor;
            return { ...donorDrawing, ...kept, BoundingBox: donorDrawing.BoundingBox };
        });
    }
    function hasNormalViewGraphics(view) {
        return !isEmptyNormalView({ ...view, PinScalar: undefined, PinBus: undefined });
    }
    function buildPackage(stream, physicalPinDiagnostics, placedViewEvidence) {
        const groups = groupSymbolObjects(stream).filter(isXmlMaterializedPackageViewGroup);
        const pinMaps = stream.decodedSymbolObjects?.filter(isPinNumberMap) ?? [];
        const expectedArities = new Set(pinMaps.map((pinMap) => pinMap.entries.length));
        const packageName = stream.parsed.packageName;
        const groupViews = groups.map((group) => {
            const cellName = stripNormalViewSuffix(group.name ?? packageName);
            const directPins = group.records.filter(isSymbolPin);
            const pins = directPins.length > 0 ?
                directPins
                : corroboratedSiblingViewPins(group, groups.map((candidate) => ({
                    group: candidate,
                    cellName: stripNormalViewSuffix(candidate.name ?? packageName),
                    packageName,
                    sourceLibName: '',
                    pins: candidate.records.filter(isSymbolPin),
                })), {
                    cellName,
                    packageName,
                    sourceLibName: '',
                    expectedArities,
                    hasInformativePinMap: pinMaps.some((pinMap) => pinMap.entries.some((entry, position) => entry.number !== String(position + 1))),
                    placedObjects: placedViewEvidence.get(cacheIdentityKey(cellName, packageName, '')) ?? [],
                }, physicalPinDiagnostics);
            return buildNormalView(group, pins);
        });
        const normalViews = oneNormalViewPerSuffix(groupViews, packageName, physicalPinDiagnostics);
        const physicalParts = pinMaps.length > 0 ?
            pinMaps.map((pinMap) => buildPhysicalPart(pinMap, normalViews[0]))
            : [physicalPartFromNormalView(normalViews[0])];
        const firstPartValue = stream.decodedSymbolObjects?.find(isPartValue);
        if (firstPartValue?.referencePrefix === undefined) {
            emitBudgetedDiagnostic(physicalPinDiagnostics, `${packageName}\0package-stream`, 'package-reference-prefix-unrecorded', `package "${packageName}" records no reference prefix, so it declares none`, 'CFB DSN package', 'info');
        }
        return {
            AlphabeticNumbering: 2,
            IsHomogeneous: true,
            Name: stream.parsed.packageName,
            PcbFootprint: firstPartValue?.value ?? '',
            PcbLib: '',
            RefdesPrefix: firstPartValue?.referencePrefix ?? '',
            SourceLibName: '',
            LibPart: [
                {
                    CellName: stream.parsed.packageName,
                    ...(normalViews.length > 0 ? { NormalView: normalViews } : {}),
                    PhysicalPart: physicalParts,
                },
            ],
        };
    }
    function physicalPartFromNormalView(normalView) {
        const pins = [...(normalView?.PinScalar ?? []), ...(normalView?.PinBus ?? [])];
        return {
            PinNumber: pins.map((pin) => ({ Position: pin.Position, Number: pin.Name })),
        };
    }
    function buildNormalView(group, pinOverride) {
        const geometry = primitiveGeometry(group.primitives);
        const declaredBox = group.records.find((record) => record.kind === 'view-header')?.declaredBoundingBox;
        const pins = pinOverride ?? group.records.filter(isSymbolPin);
        const scalarPins = orderedSymbolPinEntries(pins.filter((pin) => !isBusName(pin.name)), pins).map(({ pin, position }) => symbolPinScalarFromCfb(pin, position));
        const busPins = orderedSymbolPinEntries(pins.filter((pin) => isBusName(pin.name)), pins).map(({ pin, position }) => symbolPinScalarFromCfb(pin, position));
        const displayProperties = group.records.filter(isSymbolDisplayProperty).map(symbolDisplayPropertyFromCfb);
        const partValue = group.records.find(isPartValue);
        return {
            Suffix: decodedViewSuffix(group.name) ?? NORMAL_VIEW_SUFFIX,
            ...(displayProperties.length > 0 ? { SymbolDisplayProperty: displayProperties } : {}),
            SymbolColor: color(),
            BoundingBox: declaredBox ?? boundingBoxFromGeometry(geometry, { excludeTextWhenPossible: true }),
            ContentsLibName: '',
            ContentsViewName: '',
            ContentsViewType: 5,
            PartValue: partValue?.value ?? '',
            Reference: partValue?.referencePrefix ?? '',
            ...(scalarPins.length > 0 ? { PinScalar: scalarPins } : {}),
            ...(busPins.length > 0 ? { PinBus: busPins } : {}),
            ...geometry,
        };
    }
    function orderedSymbolPinEntries(pins, allPins) {
        const entries = pins.map((pin) => ({ pin, position: allPins.indexOf(pin) }));
        if (entries.length > 1) {
            if (entries.every(({ pin }) => isNumericString(pin.name))) {
                return entries.sort((left, right) => Number(left.pin.name) - Number(right.pin.name));
            }
        }
        return entries;
    }
    function buildPhysicalPart(pinMap, normalView) {
        const pinsByPosition = new Map([...(normalView?.PinScalar ?? []), ...(normalView?.PinBus ?? [])].map((pin) => [pin.Position, pin.Name]));
        return {
            PinNumber: pinMap.entries.map((entry) => ({
                Position: entry.position,
                Number: entry.number === '' ? (pinsByPosition.get(entry.position) ?? String(entry.position + 1)) : entry.number,
            })),
        };
    }
    function buildPortSymbol(stream) {
        const geometry = primitiveGeometry(stream.decodedPrimitives ?? []);
        const pin = firstSymbolPin(stream);
        return {
            Name: stream.parsed.symbolName,
            SourceLibName: '',
            SymbolDisplayProperty: firstSymbolDisplayProperty(stream),
            BoundingBox: portSymbolBoundingBox('', geometry),
            ...(pin ? { PinScalar: symbolPinScalarFromCfb(pin) } : {}),
            ...geometry,
        };
    }
    function buildPortSymbolFromCacheGroup(group, sourceLibName = '', diagnostics) {
        const geometry = primitiveGeometry(portSymbolPrimitives(group.name, group.primitives, diagnostics));
        const pin = group.records.find(isSymbolPin);
        const displayPropertyRecord = group.records.find(isSymbolDisplayProperty);
        const displayProperty = displayPropertyRecord ? symbolDisplayPropertyFromCfb(displayPropertyRecord) : undefined;
        return {
            Name: group.name,
            SourceLibName: sourceLibName,
            ...(displayProperty ? { SymbolDisplayProperty: displayProperty } : {}),
            BoundingBox: portSymbolBoundingBox(sourceLibName, geometry),
            ...(pin ? { PinScalar: symbolPinScalarFromCfb(pin) } : {}),
            ...geometry,
        };
    }
    function portSymbolPrimitives(symbolName, primitives, diagnostics) {
        const kept = primitives.filter((primitive) => primitiveMinimumCoordinate(primitive) >= 0);
        if (kept.length !== primitives.length && diagnostics) {
            emitPhysicalPinDiagnostic(diagnostics, symbolName, 'symbol-primitive-outside-frame', `symbol="${symbolName}" dropped=${primitives.length - kept.length}`);
        }
        return kept;
    }
    function primitiveMinimumCoordinate(primitive) {
        if ('points' in primitive) {
            return Math.min(...primitive.points.flatMap((point) => [point.x, point.y]));
        }
        if ('x1' in primitive) {
            return Math.min(primitive.x1, primitive.y1, primitive.x2, primitive.y2);
        }
        return 0;
    }
    function portSymbolBoundingBox(sourceLibName, geometry) {
        const decoded = boundingBoxFromGeometry(geometry);
        if (decoded.X1 !== decoded.X2 || decoded.Y1 !== decoded.Y2) {
            return decoded;
        }
        if (isCaptureBuiltInLibrary(sourceLibName)) {
            return { X1: 0, X2: 30, Y1: 0, Y2: 20 };
        }
        return decoded;
    }
    const CAPTURE_SYMBOL_LIBRARY_NAME = 'CAPSYM';
    function isCaptureBuiltInLibrary(sourceLibName) {
        return (0, orcadLibraryProvenance_1.isFromCadenceLibrary)(sourceLibName, CAPTURE_SYMBOL_LIBRARY_NAME);
    }
    function offPageSymbolBoundingBox(symbolName, geometry) {
        const boundingBox = boundingBoxFromGeometry(geometry);
        if (/^OFFPAGEDUAL-[LR]$/i.test(symbolName) &&
            boundingBox.X1 === 0 &&
            boundingBox.X2 === 30 &&
            boundingBox.Y1 === 0 &&
            boundingBox.Y2 === 20) {
            return { ...boundingBox, Y2: 21 };
        }
        return boundingBox;
    }
    function buildOffPageSymbol(stream) {
        const geometry = primitiveGeometry(stream.decodedPrimitives ?? []);
        const pins = (stream.decodedSymbolObjects ?? []).filter(isSymbolPin);
        return {
            Name: stream.parsed.symbolName,
            SourceLibName: '',
            SymbolDisplayProperty: firstSymbolDisplayProperty(stream),
            BoundingBox: offPageSymbolBoundingBox(stream.parsed.symbolName, geometry),
            PinScalar: pins.map((pin, index) => offPageSymbolPinScalarFromCfb(pin, index)),
            ...geometry,
        };
    }
    function buildOffPageSymbolFromCacheGroup(group, sourceLibName = '') {
        const geometry = primitiveGeometry(group.primitives);
        const pins = group.records.filter(isSymbolPin);
        const displayProperty = group.records.find(isSymbolDisplayProperty);
        return {
            Name: group.name,
            SourceLibName: sourceLibName,
            ...(displayProperty ? { SymbolDisplayProperty: symbolDisplayPropertyFromCfb(displayProperty) } : {}),
            BoundingBox: offPageSymbolBoundingBox(group.name, geometry),
            PinScalar: pins.map((pin, index) => offPageSymbolPinScalarFromCfb(pin, index)),
            ...geometry,
        };
    }
    function buildGlobalSymbol(stream, liveConnectorPins, diagnostics) {
        const geometry = primitiveGeometry(stream.decodedPrimitives ?? []);
        const pins = (stream.decodedSymbolObjects ?? [])
            .filter(isSymbolPin)
            .map((pin, index) => globalSymbolPinScalarFromCfb(pin, index));
        const corroboratedLivePin = liveConnectorSymbolPin(liveConnectorPins, stream.parsed.symbolName, '');
        return {
            Name: stream.parsed.symbolName,
            SourceLibName: '',
            BoundingBox: boundingBoxFromGeometry(geometry),
            Line: geometry.Line ?? [],
            PinScalar: pins.length > 0 ?
                pins
                : symbolPinsFromCorroboratedLivePin(stream.parsed.symbolName, corroboratedLivePin, diagnostics),
            ...(firstSymbolDisplayProperty(stream) ? { SymbolDisplayProperty: firstSymbolDisplayProperty(stream) } : {}),
            ...geometry,
        };
    }
    function buildGlobalSymbolFromCacheGroup(group, sourceLibName, liveConnectorPins, diagnostics) {
        const geometry = primitiveGeometry(group.primitives);
        const pins = group.records.filter(isSymbolPin).map((pin, index) => globalSymbolPinScalarFromCfb(pin, index));
        const displayProperty = group.records.find(isSymbolDisplayProperty);
        const corroboratedLivePin = liveConnectorSymbolPin(liveConnectorPins, group.name, sourceLibName);
        return {
            Name: group.name,
            SourceLibName: sourceLibName,
            BoundingBox: boundingBoxFromGeometry(geometry),
            Line: geometry.Line ?? [],
            PinScalar: pins.length > 0 ? pins : symbolPinsFromCorroboratedLivePin(group.name, corroboratedLivePin, diagnostics),
            ...(displayProperty ? { SymbolDisplayProperty: symbolDisplayPropertyFromCfb(displayProperty) } : {}),
            ...geometry,
        };
    }
    function symbolPinsFromCorroboratedLivePin(symbolName, pin, diagnostics) {
        if (!pin) {
            if (diagnostics) {
                emitBudgetedDiagnostic(diagnostics, symbolName, 'global-pin-absent', `symbol="${symbolName}"`, 'CFB symbol', 'info');
            }
            return [];
        }
        return [{ ...globalSymbolPinScalarFromCfb(pin), Name: UNNAMED_SYMBOL_PIN }];
    }
    function buildErcSymbol(stream) {
        const geometry = primitiveGeometry(stream.decodedPrimitives ?? []);
        return {
            Name: stream.parsed.symbolName,
            SourceLibName: '',
            BoundingBox: boundingBoxFromGeometry(geometry),
            ...geometry,
        };
    }
    function buildErcSymbolFromCacheGroup(group, sourceLibName = '') {
        const geometry = primitiveGeometry(group.primitives);
        return {
            Name: group.name,
            SourceLibName: sourceLibName,
            BoundingBox: boundingBoxFromGeometry(geometry),
            ...geometry,
        };
    }
    function buildTitleBlockSymbol(document, pageSymbolRoles) {
        const cacheStream = document.streams.find((stream) => stream.parsed?.kind === 'cache');
        if (!cacheStream?.decodedPrimitives?.length) {
            return undefined;
        }
        const titleBlockName = Array.from(pageSymbolRoles).find(([, role]) => role === 'titleblock')?.[0] ?? 'TitleBlock';
        const geometry = primitiveGeometry(cacheStream.decodedPrimitives);
        return {
            Name: titleBlockName,
            SourceLibName: '',
            BoundingBox: boundingBoxFromGeometry(geometry),
            ...geometry,
        };
    }
    function buildPageNets(stream, options, aliasFont, diagnostics) {
        const objects = stream.decodedPageObjects ?? [];
        const wires = objects.filter(isWire);
        const decodedAliases = objects.filter(isAlias);
        const aliases = [
            ...decodedAliases,
            ...synthesizePageAliasesFromStrings(stream.lengthPrefixedStrings, wires, decodedAliases),
        ];
        const placedSymbols = objects.filter((object) => object.kind === 'placed-symbol');
        const explicitJunctions = objects.filter(isJunction);
        const busEntryCandidates = identifyBusEntryCandidates(wires, aliases);
        const componentPinWireIds = findComponentPinAttachedWireIds(wires, placedSymbols);
        const busEntryWireIds = new Set(busEntryCandidates.map((candidate) => candidate.wire.dbId));
        const logicalBusWireIds = identifyLogicalBusWireIds(wires, aliases, busEntryCandidates);
        const netWires = wires.filter((wire) => !busEntryWireIds.has(wire.dbId));
        const metadataNetNames = buildMetadataNetNameLookup(stream.lengthPrefixedStrings, objects);
        const netNameCarriers = buildWireNetNameCarriers(objects);
        const pageBusMemberNames = new Set(aliases
            .filter((alias) => isExplicitBusAliasName(alias.name))
            .flatMap((alias) => collectBusMemberNames(stream, alias.name)));
        const busMemberWireOwnership = buildBusMemberWireOwnership(wires, busEntryCandidates, aliases, metadataNetNames, logicalBusWireIds, componentPinWireIds, pageBusMemberNames, stream, diagnostics);
        const busMemberWireIds = new Set(busMemberWireOwnership.ownerNameByWireId.keys());
        const wireState = new Map();
        for (const wire of netWires) {
            const busMemberOwner = busMemberWireOwnership.ownerNameByWireId.get(wire.dbId);
            const metadataName = metadataNetNames.get(wire.metadataId);
            wireState.set(wire.dbId, {
                wire,
                aliases: [],
                groupNames: busMemberOwner ? [busMemberOwner]
                    : busMemberWireOwnership.conflictingWireIds.has(wire.dbId) ? []
                        : metadataName ? [metadataName]
                            : [],
                junctions: (wire.junctions ?? []).map((junction) => ({ LocX: junction.locX, LocY: junction.locY })),
            });
        }
        const precedingWireByOffset = buildPrecedingWireIndex(objects);
        for (const alias of aliases) {
            const recordedOwners = wires.filter((wire) => wire.dbId === alias.ownerWireDbId);
            if (alias.ownerWireDbId !== undefined && recordedOwners.length !== 1) {
                emitDecisionDiagnostic(diagnostics, 'alias-recorded-owner-unresolved', `alias=${JSON.stringify(alias.name)} offset=${alias.offset} wire=${alias.ownerWireDbId} candidates=${recordedOwners.length}: attachment withheld`, 'CFB alias', 'warning');
                continue;
            }
            const owner = alias.ownerWireDbId === undefined ? findExactAliasOwner(alias, wires) : recordedOwners[0];
            if (owner && busMemberWireOwnership.conflictingWireIds.has(owner.dbId)) {
                continue;
            }
            if (owner && busEntryWireIds.has(owner.dbId)) {
                continue;
            }
            const resolvedOwner = alias.ownerWireDbId === undefined ?
                (owner ?? findNeighbouringAliasOwner(alias, netWires, precedingWireByOffset))
                : owner;
            if (resolvedOwner) {
                wireState.get(resolvedOwner.dbId)?.aliases.push(alias);
            }
            else {
                options.diagnostics?.push({
                    severity: 'warning',
                    message: `Could not attach CFB alias "${alias.name}" to a wire`,
                    streamPath: stream.path,
                    offset: alias.offset,
                });
                emitBudgetedDiagnostic(diagnostics, `${stream.path}\0${alias.offset}`, 'alias-attachment-unbound', `alias="${alias.name}" at (${alias.locX},${alias.locY}) offset=${alias.offset}`, 'CFB alias', 'warning');
            }
        }
        for (const junction of explicitJunctions) {
            const owner = wireState.get(junction.wireDbId);
            if (owner) {
                owner.junctions.push({ LocX: junction.locX, LocY: junction.locY });
            }
        }
        const wireJunctions = new Map(Array.from(wireState, ([wireDbId, state]) => [wireDbId, uniqueJunctions(state.junctions)]));
        const groups = groupConnectedWires(netWires, wireState);
        const netScalars = [];
        const netBuses = [];
        for (const group of groups) {
            const aliasesInGroup = group.flatMap((wire) => wireState.get(wire.dbId)?.aliases ?? []);
            const name = netNameForWireGroup(group, aliasesInGroup, netNameCarriers, metadataNetNames);
            const hasScalarNetName = Boolean(name && !isBusName(name));
            const busWires = hasScalarNetName ? [] : group.filter((wire) => logicalBusWireIds.has(wire.dbId) || isBusName(name));
            const scalarWires = group.filter((wire) => {
                if (busMemberWireIds.has(wire.dbId)) {
                    return false;
                }
                return hasScalarNetName || (!logicalBusWireIds.has(wire.dbId) && !isBusName(name));
            });
            if (busWires.length > 0) {
                const memberNetScalars = buildBusMemberNetScalars(busWires, busEntryCandidates, collectBusMemberNames(stream, name), wires, aliases, metadataNetNames, wireJunctions, aliasFont, busMemberWireOwnership);
                netBuses.push({
                    NetColor: DEFAULT_COLOR_VALUE,
                    NetLineStyle: DEFAULT_NET_LINE_STYLE,
                    NetLineWidth: DEFAULT_NET_LINE_WIDTH,
                    Name: logicalNetBusName(name, memberNetScalars),
                    WireBus: busWires.map((wire) => wireBusFromCfb(wire, wireState.get(wire.dbId)?.aliases ?? [], wireJunctions.get(wire.dbId) ?? [], aliasFont, name)),
                    ...(memberNetScalars.length > 0 ? { NetScalar: memberNetScalars } : {}),
                });
            }
            if (scalarWires.length > 0) {
                netScalars.push({
                    NetColor: DEFAULT_COLOR_VALUE,
                    NetLineStyle: DEFAULT_NET_LINE_STYLE,
                    NetLineWidth: DEFAULT_NET_LINE_WIDTH,
                    Name: '',
                    WireScalars: scalarWires.map((wire) => wireScalarFromCfb(wire, wireState.get(wire.dbId)?.aliases ?? [], uniqueJunctions(wireState.get(wire.dbId)?.junctions ?? []), aliasFont)),
                });
            }
        }
        return {
            NetScalar: removeScalarWiresMaterializedAsBusMembers(netScalars, netBuses),
            NetBus: netBuses.sort(compareNetBusXmlOrder),
        };
    }
    function removeScalarWiresMaterializedAsBusMembers(netScalars, netBuses) {
        const busMemberWireIds = new Set(netBuses.flatMap((bus) => (bus.NetScalar ?? []).flatMap((net) => (net.WireScalars ?? []).map((wire) => wire.DbId))));
        if (busMemberWireIds.size === 0) {
            return [...netScalars];
        }
        return netScalars
            .map((net) => ({
            ...net,
            WireScalars: (net.WireScalars ?? []).filter((wire) => !busMemberWireIds.has(wire.DbId)),
        }))
            .filter((net) => (net.WireScalars ?? []).length > 0);
    }
    function buildBusMemberWireOwnership(wires, busEntryCandidates, aliases, metadataNetNames, logicalBusWireIds, componentPinWireIds, recognizedMemberNames, stream, diagnostics) {
        const memberNames = new Set([
            ...recognizedMemberNames,
            ...busEntryCandidates.map((candidate) => candidate.alias.name),
        ]);
        const explicitClaimsByWireId = new Map();
        const addExplicitClaim = (wireId, name) => {
            if (!name || !memberNames.has(name)) {
                return;
            }
            const claims = explicitClaimsByWireId.get(wireId) ?? new Set();
            claims.add(name);
            explicitClaimsByWireId.set(wireId, claims);
        };
        for (const candidate of busEntryCandidates) {
            addExplicitClaim(candidate.wire.dbId, candidate.alias.name);
        }
        for (const alias of aliases) {
            const owner = findExactAliasOwner(alias, wires);
            if (owner) {
                addExplicitClaim(owner.dbId, alias.name);
            }
        }
        const ownerNameByWireId = new Map();
        const conflictingWireIds = new Set();
        for (const wire of wires) {
            if (logicalBusWireIds.has(wire.dbId) && !componentPinWireIds.has(wire.dbId)) {
                continue;
            }
            const explicitClaims = explicitClaimsByWireId.get(wire.dbId) ?? new Set();
            if (explicitClaims.size > 1) {
                const claims = [...explicitClaims].sort((left, right) => left.localeCompare(right));
                emitBudgetedDiagnostic(diagnostics, `${stream.path}\0${wire.dbId}`, 'bus-member-multi-claim', `stream="${stream.path}" wireDbId=${wire.dbId} members="${claims.join(',')}"`, 'CFB bus member', 'error');
                conflictingWireIds.add(wire.dbId);
                continue;
            }
            const explicitOwner = [...explicitClaims][0];
            const metadataOwner = metadataNetNames.get(wire.metadataId);
            const owner = explicitOwner ?? (metadataOwner && memberNames.has(metadataOwner) ? metadataOwner : undefined);
            if (owner) {
                ownerNameByWireId.set(wire.dbId, owner);
            }
        }
        return { ownerNameByWireId, conflictingWireIds };
    }
    function buildBusMemberNetScalars(busWires, busEntryCandidates, synthesizedMemberNames, wires, aliases, metadataNetNames, wireJunctions, aliasFont, ownership) {
        const wiresByName = new Map();
        const touchedCandidateNames = new Set();
        const synthesizedMemberNameSet = new Set(synthesizedMemberNames);
        const hasSynthesizedMemberRange = synthesizedMemberNameSet.size > 0;
        const candidatesForBus = busEntryCandidates.filter((candidate) => busWires.some((wire) => pointOnWire(candidate.busEntry.BusX, candidate.busEntry.BusY, wire)));
        for (const candidate of candidatesForBus) {
            const name = candidate.alias.name;
            if (name && (!hasSynthesizedMemberRange || synthesizedMemberNameSet.has(name))) {
                touchedCandidateNames.add(name);
            }
        }
        const memberNames = new Set([...synthesizedMemberNames, ...touchedCandidateNames]);
        for (const name of memberNames) {
            const memberWires = [];
            for (const wire of wires) {
                const owner = ownership.ownerNameByWireId.get(wire.dbId);
                if (owner) {
                    if (owner === name) {
                        memberWires.push(wire);
                    }
                    continue;
                }
                if (ownership.conflictingWireIds.has(wire.dbId) || metadataNetNames.get(wire.metadataId) !== name) {
                    continue;
                }
                ownership.ownerNameByWireId.set(wire.dbId, name);
                memberWires.push(wire);
            }
            if (memberWires.length === 0) {
                continue;
            }
            wiresByName.set(name, [
                ...memberWires.map((wire) => {
                    const memberAliases = aliases.filter((alias) => {
                        if (alias.name !== name) {
                            return false;
                        }
                        return findExactAliasOwner(alias, wires)?.dbId === wire.dbId;
                    });
                    const memberCandidates = candidatesForBus.filter((candidate) => candidate.alias.name === name && candidate.wire.dbId === wire.dbId);
                    return wireScalarFromCfb(wire, memberAliases, uniqueJunctions([
                        ...memberCandidates.flatMap((candidate) => busEntryJunctionsForWire(wire, candidate, wireJunctions)),
                        ...busSpineEndpointJunctionsForWire(wire, busWires, wireJunctions),
                        ...busMemberEndpointJunctionsForWire(wire, memberWires, wireJunctions),
                    ]), aliasFont);
                }),
            ]);
        }
        for (const name of synthesizedMemberNames) {
            if (!wiresByName.has(name)) {
                wiresByName.set(name, []);
            }
        }
        return Array.from(memberNames, (name) => {
            const memberWires = wiresByName.get(name) ?? [];
            const uniqueWires = uniqueWireScalarsByDbId(memberWires);
            return {
                NetColor: DEFAULT_COLOR_VALUE,
                NetLineStyle: DEFAULT_NET_LINE_STYLE,
                NetLineWidth: DEFAULT_NET_LINE_WIDTH,
                Name: name,
                WireScalars: uniqueWires.length > 0 ? uniqueWires : undefined,
            };
        }).sort((left, right) => left.Name.localeCompare(right.Name));
    }
    function busEntryJunctionsForWire(wire, candidate, wireJunctions) {
        const junctions = [...(wireJunctions.get(wire.dbId) ?? [])];
        if (pointOnWire(candidate.busEntry.BusX, candidate.busEntry.BusY, wire)) {
            junctions.push({ LocX: candidate.busEntry.BusX, LocY: candidate.busEntry.BusY });
        }
        return uniqueJunctions(junctions);
    }
    function busSpineEndpointJunctionsForWire(wire, busWires, wireJunctions) {
        const junctions = [...(wireJunctions.get(wire.dbId) ?? [])];
        for (const endpoint of [
            { LocX: wire.startX, LocY: wire.startY },
            { LocX: wire.endX, LocY: wire.endY },
        ]) {
            if (busWires.some((busWire) => pointOnWire(endpoint.LocX, endpoint.LocY, busWire))) {
                junctions.push(endpoint);
            }
        }
        return uniqueJunctions(junctions);
    }
    function busMemberEndpointJunctionsForWire(wire, memberWires, wireJunctions) {
        const junctions = [...(wireJunctions.get(wire.dbId) ?? [])];
        for (const endpoint of [
            { LocX: wire.startX, LocY: wire.startY },
            { LocX: wire.endX, LocY: wire.endY },
        ]) {
            if (isBusMemberJunctionOwner(wire, memberWires, endpoint)) {
                junctions.push(endpoint);
            }
        }
        return uniqueJunctions(junctions);
    }
    function isBusMemberJunctionOwner(wire, memberWires, endpoint) {
        const touchingWires = memberWires.filter((memberWire) => pointOnWire(endpoint.LocX, endpoint.LocY, memberWire));
        if (touchingWires.length < 3) {
            return false;
        }
        const owner = touchingWires
            .filter((memberWire) => isWireEndpoint(memberWire, endpoint.LocX, endpoint.LocY))
            .sort((left, right) => Math.max(right.startX, right.endX) - Math.max(left.startX, left.endX))[0];
        return owner?.dbId === wire.dbId;
    }
    function uniqueWireScalarsByDbId(wires) {
        const byDbId = new Map();
        const unique = [];
        for (const wire of wires) {
            const existing = byDbId.get(wire.DbId);
            if (existing) {
                const aliases = [...(existing.Alias ?? []), ...(wire.Alias ?? [])];
                if (aliases.length > 0) {
                    existing.Alias = uniqueAliases(aliases);
                }
                const junctions = [...(existing.Junction ?? []), ...(wire.Junction ?? [])];
                if (junctions.length > 0) {
                    existing.Junction = uniqueJunctions(junctions);
                }
                continue;
            }
            byDbId.set(wire.DbId, wire);
            unique.push(wire);
        }
        return unique;
    }
    function uniqueAliases(aliases) {
        const seen = new Set();
        const unique = [];
        for (const alias of aliases) {
            const key = `${alias.Name}:${alias.LocX}:${alias.LocY}`;
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            unique.push(alias);
        }
        return unique;
    }
    function synthesizePageAliasesFromStrings(strings, wires, existingAliases) {
        const existing = new Set(existingAliases.map((alias) => `${alias.name}:${alias.locX}:${alias.locY}`));
        const synthesized = [];
        for (const string of strings) {
            const locX = string.precedingInt32Le20;
            const locY = string.precedingInt32Le16;
            if (locX === undefined ||
                locY === undefined ||
                !isCoordinateBackedAliasString(string, locX, locY) ||
                !findExactPointOwner(locX, locY, wires)) {
                continue;
            }
            const name = string.value.trim();
            const key = `${name}:${locX}:${locY}`;
            if (existing.has(key)) {
                continue;
            }
            existing.add(key);
            const byteLength = 2 + string.length + (string.hasNullTerminator ? 1 : 0);
            synthesized.push({
                kind: 'alias',
                offset: string.offset,
                byteLength,
                payloadByteLength: byteLength,
                name,
                locX,
                locY,
                color: DEFAULT_COLOR_VALUE,
                rotation: 0,
            });
        }
        return synthesized;
    }
    function isCoordinateBackedAliasString(string, locX, locY) {
        return (Number.isFinite(locX) &&
            Number.isFinite(locY) &&
            locX >= 0 &&
            locY >= 0 &&
            string.followingUInt32Le === PAGE_ALIAS_STRING_MARKER &&
            isPlausibleCoordinateAliasName(string.value));
    }
    function isPlausibleCoordinateAliasName(value) {
        const name = value.trim();
        return name.length > 0 && name.length <= 128 && /^[A-Za-z0-9_+.\-#:[\]/]+$/.test(name) && !/^\d+$/.test(name);
    }
    function collectBusMemberNames(stream, busName) {
        const details = busNameDetails(busName);
        if (!details) {
            return [];
        }
        const values = [
            ...(stream.lengthPrefixedStrings ?? []).map((entry) => entry.value),
            ...(stream.asciiRuns ?? []).map((entry) => entry.value),
        ];
        const names = new Set();
        for (const value of values) {
            if (isBusMemberNameForRange(value, details)) {
                names.add(value);
            }
        }
        return Array.from(names).sort((left, right) => left.localeCompare(right));
    }
    function busNameDetails(busName) {
        const match = /^(.+)\[(\d+)(?:\.\.|:)(\d+)\]$/.exec(busName);
        if (!match) {
            return undefined;
        }
        return { prefix: match[1], start: Number(match[2]), end: Number(match[3]) };
    }
    function isBusMemberNameForRange(value, details) {
        if (value.includes('[')) {
            return false;
        }
        if (value.startsWith(`${details.prefix}.`)) {
            return true;
        }
        const memberIndex = busMemberIndexForPrefix(value, details.prefix);
        if (memberIndex === undefined) {
            return false;
        }
        return memberIndex >= Math.min(details.start, details.end) && memberIndex <= Math.max(details.start, details.end);
    }
    function busMemberIndexForPrefix(value, prefix) {
        const dottedPrefix = `${prefix}.`;
        if (value.startsWith(dottedPrefix)) {
            const match = /^(\d+)/.exec(value.slice(dottedPrefix.length));
            return match ? Number(match[1]) : undefined;
        }
        if ((!/^[A-Za-z_]+$/.test(prefix) && !prefix.endsWith('_')) || !value.startsWith(prefix)) {
            return undefined;
        }
        const match = /^(\d+)/.exec(value.slice(prefix.length));
        return match ? Number(match[1]) : undefined;
    }
    function logicalNetBusName(name, memberNetScalars) {
        const details = busNameDetails(name);
        if (!details || memberNetScalars.length === 0) {
            return name;
        }
        if (memberNetScalars.every((member) => member.Name.startsWith(details.prefix) && !member.Name.startsWith(`${details.prefix}.`))) {
            return '';
        }
        return name;
    }
    function compareNetBusXmlOrder(left, right) {
        const leftDetails = netBusSortDetails(left.Name);
        const rightDetails = netBusSortDetails(right.Name);
        if (leftDetails.base === rightDetails.base && leftDetails.memorySide !== rightDetails.memorySide) {
            return leftDetails.memorySide ? 1 : -1;
        }
        return 0;
    }
    function netBusSortDetails(name) {
        const details = busNameDetails(name);
        const prefix = details?.prefix ?? name;
        return {
            base: prefix.replace(/_M$/i, ''),
            memorySide: /_M$/i.test(prefix),
        };
    }
    function buildPageObjectNetNameLookup(objects, strings = [], connectorSymbolGeometries, diagnostics) {
        const wires = objects.filter(isWire);
        const aliases = objects.filter(isAlias);
        const busEntryWireIds = identifyBusEntryWireIds(wires, aliases);
        const netWires = wires.filter((wire) => !busEntryWireIds.has(wire.dbId));
        const metadataNetNames = buildMetadataNetNameLookup(strings, objects, diagnostics);
        const netNameCarriers = buildWireNetNameCarriers(objects);
        const placedRecordNetNames = buildPlacedRecordNetNameLookup(objects, metadataNetNames);
        const wireState = new Map();
        const exactAliasesByWireId = new Map();
        const names = new Map();
        for (const wire of netWires) {
            wireState.set(wire.dbId, {
                wire,
                aliases: [],
                groupNames: metadataNetNames.get(wire.metadataId) ? [metadataNetNames.get(wire.metadataId)] : [],
                junctions: [],
            });
        }
        const precedingWireByOffset = buildPrecedingWireIndex(objects);
        for (const alias of aliases) {
            const owner = findExactAliasOwner(alias, wires);
            if (owner && busEntryWireIds.has(owner.dbId)) {
                continue;
            }
            if (owner) {
                const exactAliases = exactAliasesByWireId.get(owner.dbId) ?? [];
                exactAliases.push(alias);
                exactAliasesByWireId.set(owner.dbId, exactAliases);
            }
            const resolvedOwner = owner ?? findNeighbouringAliasOwner(alias, netWires, precedingWireByOffset);
            if (resolvedOwner) {
                wireState.get(resolvedOwner.dbId)?.aliases.push(alias);
            }
        }
        const groups = groupConnectedWires(netWires, wireState);
        const groupNameByWireId = new Map();
        for (const group of groups) {
            const aliasesInGroup = group.flatMap((wire) => wireState.get(wire.dbId)?.aliases ?? []);
            const name = netNameForWireGroup(group, aliasesInGroup, netNameCarriers, metadataNetNames);
            if (!name) {
                continue;
            }
            for (const wire of group) {
                groupNameByWireId.set(wire.dbId, name);
            }
        }
        const netIdentityByWireId = wireNetIdentities(groups, new Map([...wireState].map(([wireDbId, state]) => [wireDbId, state.aliases])), netNameCarriers, metadataNetNames);
        for (const object of objects) {
            if (object.kind !== 'placed-symbol' || object.role === 'part-inst' || object.role === 'titleblock') {
                continue;
            }
            const adjacentDrawnPortName = object.role === 'global' ? findAdjacentDrawnPortNetName(object, objects) : undefined;
            if (adjacentDrawnPortName) {
                names.set(object.dbId, adjacentDrawnPortName);
                continue;
            }
            const explicitObjectName = metadataNetNames.get(object.dbId);
            const decodedPlacedName = (object.name &&
                !isGenericPlacedSymbolCarrierName(object, object.name) &&
                !isNumericConnectorRecordName(object, object.name) &&
                plausibleNetNameCandidate(object, object.name, diagnostics)) ?
                object.name
                : undefined;
            const directObjectName = explicitObjectName && !isGenericPlacedSymbolCarrierName(object, explicitObjectName) ?
                explicitObjectName
                : undefined;
            const groupedPlacedRecordName = placedRecordNetNames.get(placedRecordNameKey(object));
            const carrierExplicitObjectName = explicitObjectName && isGenericPlacedSymbolCarrierName(object, explicitObjectName) ? explicitObjectName : (undefined);
            const owner = findPlacedSymbolWireOwner(object, wires, connectorSymbolGeometries, diagnostics, netIdentityByWireId);
            const ownerAliasName = owner ? firstNetName(exactAliasesByWireId.get(owner.dbId) ?? []) : '';
            const connectedGroupName = owner ? groupNameByWireId.get(owner.dbId) : undefined;
            const exactAttachedWireName = owner ?
                (connectedGroupName ??
                    metadataNetNames.get(owner.metadataId) ??
                    (ownerAliasName ? ownerAliasName : undefined))
                : undefined;
            const name = placedSymbolNetName(object, exactAttachedWireName, connectedGroupName, directObjectName, decodedPlacedName, groupedPlacedRecordName, diagnostics);
            if (name) {
                names.set(object.dbId, name);
                continue;
            }
        }
        return names;
    }
    function buildPlacedRecordNetNameLookup(objects, metadataNetNames) {
        const candidates = new Map();
        for (const object of objects) {
            if (object.kind !== 'placed-symbol' || object.role !== 'global') {
                continue;
            }
            const name = metadataNetNames.get(object.dbId);
            if (!name || isGenericPlacedSymbolCarrierName(object, name)) {
                continue;
            }
            const key = placedRecordNameKey(object);
            const names = candidates.get(key) ?? new Set();
            names.add(name);
            candidates.set(key, names);
        }
        return new Map([...candidates].flatMap(([key, names]) => (names.size === 1 ? [[key, names.values().next().value]] : [])));
    }
    function placedRecordNameKey(object) {
        return `${object.symbolName}\0${object.recordType}`;
    }
    function placedSymbolNetName(object, exactAttachedWireName, connectedGroupName, directObjectName, decodedPlacedName, groupedPlacedRecordName, diagnostics) {
        if (directObjectName && plausibleNetNameCandidate(object, directObjectName, diagnostics)) {
            return directObjectName;
        }
        if (decodedPlacedName && plausibleNetNameCandidate(object, decodedPlacedName, diagnostics)) {
            return decodedPlacedName;
        }
        if (exactAttachedWireName && plausibleNetNameCandidate(object, exactAttachedWireName, diagnostics)) {
            return exactAttachedWireName;
        }
        if (groupedPlacedRecordName && plausibleNetNameCandidate(object, groupedPlacedRecordName, diagnostics)) {
            return groupedPlacedRecordName;
        }
        return connectedGroupName && plausibleNetNameCandidate(object, connectedGroupName, diagnostics) ? connectedGroupName
            : undefined;
    }
    function isNumericConnectorRecordName(object, name) {
        return object.role !== 'global' && /^\d+$/.test(name.trim());
    }
    function plausibleNetNameCandidate(object, name, diagnostics) {
        if ((0, portOccurrenceNormalization_1.isPlausibleNetName)(name)) {
            return true;
        }
        if (diagnostics) {
            emitBudgetedDiagnostic(diagnostics, `${object.dbId}\0${name}`, 'implausible-net-name', `dbId=${object.dbId} symbol="${object.symbolName}" name="${name}"`, 'CFB connector', 'warning');
        }
        return false;
    }
    function firstPlausiblePlacedSymbolNetName(object, candidates, diagnostics) {
        for (const candidate of candidates) {
            if (candidate && plausibleNetNameCandidate(object, candidate, diagnostics)) {
                return candidate;
            }
        }
        if (diagnostics) {
            emitBudgetedDiagnostic(diagnostics, `${object.dbId}`, 'connector-name-undecoded', `dbId=${object.dbId} symbol="${object.symbolName}" role=${object.role}`, 'CFB connector', 'warning');
        }
        return '';
    }
    function fillMissingPlacedConnectorNetNames(objects, names, strings, connectorSymbolGeometries, diagnostics) {
        const wires = objects.filter(isWire);
        const metadataNetNames = buildMetadataNetNameLookup(strings, objects, diagnostics);
        const precedingWireByOffset = buildPrecedingWireIndex(objects);
        const aliasesByWireId = new Map();
        for (const alias of objects.filter(isAlias)) {
            const owner = findExactAliasOwner(alias, wires) ?? findNeighbouringAliasOwner(alias, wires, precedingWireByOffset);
            if (owner) {
                aliasesByWireId.set(owner.dbId, [...(aliasesByWireId.get(owner.dbId) ?? []), alias]);
            }
        }
        const netIdentityByWireId = wireNetIdentities(groupConnectedWires(wires, new Map()), aliasesByWireId, buildWireNetNameCarriers(objects), metadataNetNames);
        for (const object of objects) {
            if (object.kind !== 'placed-symbol' ||
                object.role === 'part-inst' ||
                object.role === 'titleblock' ||
                names.has(object.dbId)) {
                continue;
            }
            const recovered = closestResolvedSameSymbolName(object, objects, names, wires, metadataNetNames, connectorSymbolGeometries, diagnostics, netIdentityByWireId);
            if (recovered && (0, portOccurrenceNormalization_1.isPlausibleNetName)(recovered) && recovered !== object.symbolName) {
                names.set(object.dbId, recovered);
                if (diagnostics) {
                    emitBudgetedDiagnostic(diagnostics, String(object.dbId), 'connector-proximity-recovery', `dbId=${object.dbId} symbol="${object.symbolName}" recovered="${recovered}"`, 'CFB connector', 'warning');
                }
            }
        }
    }
    function closestResolvedSameSymbolName(object, objects, names, wires, metadataNetNames, connectorSymbolGeometries, diagnostics, netIdentityByWireId) {
        const maximumPeerDistance = 600;
        let bestSameRecord;
        const sameRecordNames = new Set();
        let best;
        const peerNames = new Set();
        for (const candidate of objects) {
            if (candidate.kind !== 'placed-symbol' ||
                candidate.dbId === object.dbId ||
                candidate.symbolName !== object.symbolName) {
                continue;
            }
            const name = names.get(candidate.dbId);
            if (!name || !(0, portOccurrenceNormalization_1.isPlausibleNetName)(name) || name === candidate.symbolName) {
                continue;
            }
            const distance = Math.abs(candidate.locX - object.locX) + Math.abs(candidate.locY - object.locY);
            if (candidate.recordType === object.recordType) {
                sameRecordNames.add(name);
                if (!bestSameRecord ||
                    distance < bestSameRecord.distance ||
                    (distance === bestSameRecord.distance && name < bestSameRecord.name)) {
                    bestSameRecord = { distance, name };
                }
            }
            if (distance > maximumPeerDistance) {
                continue;
            }
            peerNames.add(name);
            if (!best || distance < best.distance || (distance === best.distance && name < best.name)) {
                best = { distance, name };
            }
        }
        if (bestSameRecord && sameRecordNames.size === 1) {
            if (bestSameRecord.distance > maximumPeerDistance && diagnostics) {
                emitBudgetedDiagnostic(diagnostics, String(object.dbId), 'connector-same-record-peer-distant', `dbId=${object.dbId} symbol="${object.symbolName}" distance=${bestSameRecord.distance}`, 'CFB connector', 'warning');
            }
            return bestSameRecord.name;
        }
        if (!best) {
            return undefined;
        }
        const attachedName = findPlacedSymbolWireOwner(object, wires, connectorSymbolGeometries, diagnostics, netIdentityByWireId);
        const isCorroborated = attachedName && metadataNetNames.get(attachedName.metadataId) === best.name;
        if (peerNames.size === 1 || isCorroborated) {
            return best.name;
        }
        if (diagnostics) {
            emitBudgetedDiagnostic(diagnostics, String(object.dbId), 'connector-name-unresolved', `dbId=${object.dbId} symbol="${object.symbolName}" peerNames="${[...peerNames].sort().join(',')}"`, 'CFB connector', 'warning');
        }
        return undefined;
    }
    function isGenericPlacedSymbolCarrierName(object, name) {
        return name.trim().toUpperCase() === object.symbolName.trim().toUpperCase();
    }
    let NetNameCarrierRank;
    (function (NetNameCarrierRank) {
        NetNameCarrierRank[NetNameCarrierRank["Unattached"] = 0] = "Unattached";
        NetNameCarrierRank[NetNameCarrierRank["AliasLabel"] = 1] = "AliasLabel";
        NetNameCarrierRank[NetNameCarrierRank["GlobalSymbol"] = 2] = "GlobalSymbol";
        NetNameCarrierRank[NetNameCarrierRank["BoundaryConnector"] = 3] = "BoundaryConnector";
    })(NetNameCarrierRank || (NetNameCarrierRank = {}));
    function placedSymbolCarrierRank(role) {
        if (role === 'off-page-connector' || role === 'port') {
            return NetNameCarrierRank.BoundaryConnector;
        }
        return role === 'global' ? NetNameCarrierRank.GlobalSymbol : undefined;
    }
    function buildWireNetNameCarriers(objects) {
        const wires = objects.filter(isWire);
        const carriers = new Map();
        const record = (metadataId, name, rank) => {
            const key = name.trim().toUpperCase();
            if (!key) {
                return;
            }
            const byName = carriers.get(metadataId) ?? new Map();
            byName.set(key, Math.max(byName.get(key) ?? NetNameCarrierRank.Unattached, rank));
            carriers.set(metadataId, byName);
        };
        const precedingWireByOffset = buildPrecedingWireIndex(objects);
        for (const alias of objects.filter(isAlias)) {
            const owner = findExactAliasOwner(alias, wires) ?? findNeighbouringAliasOwner(alias, wires, precedingWireByOffset);
            if (owner) {
                record(owner.metadataId, alias.name, NetNameCarrierRank.AliasLabel);
            }
        }
        for (const object of objects) {
            if (object.kind !== 'placed-symbol') {
                continue;
            }
            const rank = placedSymbolCarrierRank(object.role);
            if (rank === undefined) {
                continue;
            }
            const carried = [object.name?.trim(), object.role === 'global' ? object.symbolName.trim() : undefined].filter((value) => Boolean(value));
            if (carried.length === 0) {
                continue;
            }
            const owner = findPlacedSymbolWireOwner(object, wires);
            for (const value of carried) {
                record(object.dbId, value, rank);
                if (owner) {
                    record(owner.metadataId, value, rank);
                }
            }
        }
        return carriers;
    }
    function netNameForWireGroup(group, aliasesInGroup, carriers, metadataNetNames) {
        const rankOf = (value) => {
            const key = value.trim().toUpperCase();
            let best = NetNameCarrierRank.Unattached;
            for (const wire of group) {
                const rank = carriers.get(wire.metadataId)?.get(key);
                if (rank !== undefined && rank > best) {
                    best = rank;
                }
            }
            return best;
        };
        const candidates = [];
        for (const wire of group) {
            const name = metadataNetNames.get(wire.metadataId);
            if (name && !candidates.includes(name)) {
                candidates.push(name);
            }
        }
        for (const alias of aliasesInGroup) {
            if (alias.name && !candidates.includes(alias.name)) {
                candidates.push(alias.name);
            }
        }
        let best = '';
        let bestRank = NetNameCarrierRank.Unattached;
        for (const candidate of candidates) {
            const rank = rankOf(candidate);
            if (best === '' || rank > bestRank) {
                best = candidate;
                bestRank = rank;
            }
        }
        return best;
    }
    function buildMetadataNetNameLookup(strings, objects, diagnostics) {
        const carriers = buildWireNetNameCarriers(objects);
        const rankOf = (metadataId, value) => carriers.get(metadataId)?.get(value.trim().toUpperCase()) ?? NetNameCarrierRank.Unattached;
        const chosen = new Map();
        for (const entry of strings) {
            const sheetOwner = objects.find((object) => object.kind === 'drawn-inst' &&
                object.recordFrame !== undefined &&
                entry.offset >= object.recordFrame.offset &&
                entry.offset + 2 + entry.length + 1 <= object.recordFrame.endOffset);
            if (sheetOwner !== undefined) {
                if (diagnostics !== undefined) {
                    emitDecisionDiagnostic(diagnostics, 'net-name-owned-sheet-frame', `offset=${entry.offset}: counted string belongs to a sheet frame, not a page net record`, 'CFB net name', 'info');
                }
                continue;
            }
            if (entry.followingUInt32Le === undefined) {
                continue;
            }
            if (!(0, portOccurrenceNormalization_1.isPlausibleNetName)(entry.value)) {
                if (diagnostics) {
                    emitBudgetedDiagnostic(diagnostics, `${entry.followingUInt32Le}\0${entry.value}`, 'implausible-net-name', `metadataId=${entry.followingUInt32Le} name="${entry.value}"`, 'CFB net name', 'warning');
                }
                continue;
            }
            const metadataId = entry.followingUInt32Le;
            const rank = rankOf(metadataId, entry.value);
            const existing = chosen.get(metadataId);
            if (existing === undefined || rank > existing.rank) {
                chosen.set(metadataId, { name: entry.value, rank, tied: [entry.value] });
                continue;
            }
            if (rank === existing.rank && entry.value !== existing.name && !existing.tied.includes(entry.value)) {
                existing.tied.push(entry.value);
            }
        }
        const names = new Map();
        for (const [metadataId, choice] of chosen) {
            names.set(metadataId, choice.name);
            if (diagnostics && choice.tied.length > 1) {
                emitBudgetedDiagnostic(diagnostics, String(metadataId), 'net-name-ambiguous', `metadataId=${metadataId} rank=${NetNameCarrierRank[choice.rank]} candidates=${choice.tied
                    .map((value) => `"${value}"`)
                    .join(', ')} read="${choice.name}"`, 'CFB net name', 'warning');
            }
        }
        return names;
    }
    function findPlacedSymbolWireOwner(object, wires, connectorSymbolGeometries, diagnostics, netIdentityByWireId) {
        const { points, isDecoded } = placedSymbolConnectionPoints(object, connectorSymbolGeometries, diagnostics);
        const exactOwners = points.flatMap((point) => findExactPointOwners(point.x, point.y, wires));
        if (exactOwners.length > 0) {
            return singleNetWireOwner(exactOwners, object, 'connector-probe-nets-competing', netIdentityByWireId, diagnostics);
        }
        let bestDistance = Number.POSITIVE_INFINITY;
        let nearest = [];
        for (const point of points) {
            for (const wire of wires) {
                const distance = distanceToWire(point.x, point.y, wire);
                if (distance > 25 || distance > bestDistance) {
                    continue;
                }
                if (distance < bestDistance) {
                    bestDistance = distance;
                    nearest = [];
                }
                nearest.push(wire);
            }
        }
        if (nearest.length === 0) {
            return undefined;
        }
        return isDecoded ?
            nearest[0]
            : singleNetWireOwner(nearest, object, 'connector-probe-nets-equidistant', netIdentityByWireId, diagnostics);
    }
    function singleNetWireOwner(wires, object, code, netIdentityByWireId, diagnostics) {
        const identities = wires.map((wire) => netIdentityByWireId?.get(wire.dbId) ?? { run: `record:${wire.dbId}` });
        const keys = new Set(identities.map((identity) => identity.name ?? identity.run));
        if (keys.size <= 1) {
            return wires[0];
        }
        const named = identities.some((identity) => identity.name !== undefined);
        if (diagnostics) {
            emitBudgetedDiagnostic(diagnostics, String(object.dbId), named ? code : 'connector-probe-runs-unnamed', `dbId=${object.dbId} symbol="${object.symbolName}" nets=${keys.size}`, 'CFB connector', 'warning');
        }
        return undefined;
    }
    function placedSymbolConnectionPoints(object, catalog, diagnostics) {
        if (isPlacedPortSymbol(object)) {
            const preferRightHotPoint = (object.displayProperties ?? []).some((property) => property.locX < 0);
            if (preferRightHotPoint) {
                return {
                    isDecoded: true,
                    points: [
                        { x: object.locX, y: object.locY + 10 },
                        { x: object.locX + 30, y: object.locY + 10 },
                        { x: object.locX + 10, y: object.locY + 30 },
                        { x: object.locX, y: object.locY },
                        { x: object.locX + 10, y: object.locY + 10 },
                        { x: object.locX + 10, y: object.locY + 20 },
                    ],
                };
            }
            return {
                isDecoded: true,
                points: [
                    { x: object.locX, y: object.locY + 10 },
                    { x: object.locX + 10, y: object.locY + 30 },
                    { x: object.locX + 30, y: object.locY + 10 },
                    { x: object.locX, y: object.locY },
                    { x: object.locX + 10, y: object.locY + 10 },
                    { x: object.locX + 10, y: object.locY + 20 },
                ],
            };
        }
        const geometry = decodedConnectorGeometryForPlacedSymbol(object, catalog);
        if (geometry) {
            return { isDecoded: true, points: transformedConnectorGeometryPoints(object, [geometry]) };
        }
        const decodedCandidates = (catalog?.candidatesBySymbol.get(liveConnectorPinSymbolKey(object.symbolName)) ?? []).filter((candidate) => candidate.hasDecodedBounds === true);
        if (decodedCandidates.length > 0) {
            return { isDecoded: true, points: transformedConnectorGeometryPoints(object, decodedCandidates) };
        }
        if (diagnostics) {
            emitBudgetedDiagnostic(diagnostics, String(object.dbId), 'connector-generic-probe', `dbId=${object.dbId} symbol="${object.symbolName}" loc=(${object.locX},${object.locY})`, 'CFB connector', 'warning');
        }
        return {
            isDecoded: false,
            points: [
                { x: object.locX, y: object.locY },
                { x: object.locX, y: object.locY + 10 },
                { x: object.locX + 10, y: object.locY },
                { x: object.locX + 10, y: object.locY + 10 },
                { x: object.locX + 10, y: object.locY + 20 },
            ],
        };
    }
    function transformedConnectorGeometryPoints(object, geometries) {
        const seen = new Set();
        const orderedGeometries = [...geometries].sort((left, right) => Number(right.hasDecodedBounds === true) - Number(left.hasDecodedBounds === true));
        return orderedGeometries.flatMap((geometry) => geometry.pins.flatMap((pin) => {
            const point = transformedConnectorPinPagePoint(object, pin, geometry.boundingBox);
            const key = pagePointKey(point.x, point.y);
            if (seen.has(key)) {
                return [];
            }
            seen.add(key);
            return [point];
        }));
    }
    function decodedConnectorGeometryForPlacedSymbol(object, catalog) {
        const sourceLibName = object.resolvedMetadata?.libName ?? '';
        const identityGeometry = canonicalSourceLibraryName(sourceLibName) ?
            catalog?.byIdentity.get(liveConnectorPinIdentityKey(object.symbolName, sourceLibName))
            : undefined;
        const symbolKey = liveConnectorPinSymbolKey(object.symbolName);
        const exactGeometry = identityGeometry?.hasDecodedBounds === true ? identityGeometry : undefined;
        const corroboratedSymbolGeometry = catalog?.bySymbol.get(symbolKey);
        const candidateGeometry = exactGeometry ??
            (corroboratedSymbolGeometry?.hasDecodedBounds === true ? corroboratedSymbolGeometry : undefined) ??
            uniquelyTransformedConnectorGeometry(object, (catalog?.candidatesBySymbol.get(symbolKey) ?? []).filter((candidate) => candidate.hasDecodedBounds === true));
        return candidateGeometry?.hasDecodedBounds === true ? candidateGeometry : undefined;
    }
    function uniquelyTransformedConnectorGeometry(object, candidates) {
        const byPageGeometry = new Map();
        for (const candidate of candidates) {
            const key = candidate.pins
                .map((pin) => {
                const point = transformedConnectorPinPagePoint(object, pin, candidate.boundingBox);
                return pagePointKey(point.x, point.y);
            })
                .join('|');
            byPageGeometry.set(key, candidate);
        }
        return byPageGeometry.size === 1 ? byPageGeometry.values().next().value : undefined;
    }
    function transformedConnectorPinPagePoint(object, pin, boundingBox) {
        const centerX = (boundingBox.X1 + boundingBox.X2) / 2;
        const centerY = (boundingBox.Y1 + boundingBox.Y2) / 2;
        let x = pin.hotptX - centerX;
        const y = pin.hotptY - centerY;
        const mirror = object.resolvedMetadata?.mirror ?? ((object.flags & 0x04) !== 0 ? 1 : 0);
        if (mirror === 1) {
            x = -x;
        }
        const rotation = object.resolvedMetadata?.rotation ?? object.flags & 0x03;
        const rotated = rotation === 1 ? { x: y, y: -x }
            : rotation === 2 ? { x: -x, y: -y }
                : rotation === 3 ? { x: -y, y: x }
                    : { x, y };
        const placedCenter = rotation % 2 === 1 ?
            { x: object.locX + centerY, y: object.locY + centerX }
            : { x: object.locX + centerX, y: object.locY + centerY };
        return { x: placedCenter.x + rotated.x, y: placedCenter.y + rotated.y };
    }
    function findAdjacentDrawnPortNetName(object, objects) {
        let best;
        const candidatePoints = [
            { x: object.locX, y: object.locY },
            { x: object.locX + 10, y: object.locY + 10 },
            { x: object.locX + 10, y: object.locY + 20 },
        ];
        for (const drawnInst of objects.filter(isDrawnInst)) {
            for (const port of drawnInst.materializedPortInstances ?? []) {
                if (!port.name || port.isBus || port.isNoConnect) {
                    continue;
                }
                const portX = drawnInst.locX + (port.hotptX ?? port.startX ?? 0);
                const portY = drawnInst.locY + (port.hotptY ?? port.startY ?? 0);
                const distance = Math.min(...candidatePoints.map((point) => Math.abs(point.x - portX) + Math.abs(point.y - portY)));
                if (distance > 25 || (best && distance >= best.distance)) {
                    continue;
                }
                best = { distance, name: port.name };
            }
        }
        return best?.name;
    }
    function buildBusEntries(objects) {
        const entries = [];
        const seen = new Set();
        for (const object of objects) {
            if (object.kind !== 'bus-entry') {
                continue;
            }
            const key = `${object.busX}:${object.busY}:${object.endX}:${object.endY}`;
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            entries.push({ BusX: object.busX, BusY: object.busY, EndX: object.endX, EndY: object.endY });
        }
        return entries;
    }
    function enrichDrawnInstPortsFromPageConnectivity(objects, diagnostics, strings = []) {
        const drawnInsts = objects.filter(isDrawnInst);
        const portInstances = objects.filter(isPortInstance);
        if (drawnInsts.length !== 1 || portInstances.length === 0) {
            return;
        }
        const drawnInst = drawnInsts[0];
        if ((drawnInst.materializedPortInstances?.length ?? 0) >= portInstances.length) {
            return;
        }
        const wireNameLookup = buildWireNetNameLookup(objects, strings);
        const existingPortsByPoint = new Map();
        for (const port of drawnInst.materializedPortInstances ?? []) {
            existingPortsByPoint.set(coordKey(port.hotptX ?? port.startX ?? 0, port.hotptY ?? port.startY ?? 0), port);
        }
        const enrichedPorts = [...(drawnInst.materializedPortInstances ?? [])];
        const boundary = drawnInstBoundaryFromPorts(drawnInst, enrichedPorts);
        const portNameStrings = drawnInstPortNameStrings(drawnInst, strings);
        for (let index = 0; index < portInstances.length; index++) {
            const portInstance = portInstances[index];
            const relX = portInstance.x - drawnInst.locX;
            const relY = portInstance.y - drawnInst.locY;
            const existing = existingPortsByPoint.get(coordKey(relX, relY));
            if (existing) {
                continue;
            }
            const wireName = portInstance.wireDbId !== undefined ? wireNameLookup.get(portInstance.wireDbId) : undefined;
            const rawName = drawnInstPortStringName(portNameStrings, relX, relY, boundary, wireName) ?? wireName ?? portInstance.pinName;
            if (rawName === undefined) {
                emitBudgetedDiagnostic(diagnostics, `${drawnInst.dbId}\0${portInstance.pinIndex ?? index}`, 'sheet-port-name-undecoded', `sheetSymbol=${drawnInst.dbId} pinIndex=${portInstance.pinIndex ?? index} x=${relX} y=${relY}`, 'CFB sheet symbol', 'warning');
                continue;
            }
            const name = rawName;
            enrichedPorts.push({
                name,
                position: portInstance.position ?? index,
                pinIndex: portInstance.pinIndex,
                startX: relX,
                startY: relY,
                hotptX: relX,
                hotptY: relY,
                x: portInstance.x,
                y: portInstance.y,
                ...(portInstance.wireDbId !== undefined ? { wireDbId: portInstance.wireDbId } : {}),
                isNoConnect: portInstance.isNoConnect,
                isBus: isExplicitBusAliasName(rawName) || rawName.includes('['),
                type: portInstance.type ?? inferredDrawnInstPortType(relX, relY, boundary),
                visible: portInstance.visible,
                isPlacedInstPin: false,
                source: 'record',
                isExplicit: true,
            });
            existingPortsByPoint.set(coordKey(relX, relY), enrichedPorts[enrichedPorts.length - 1]);
        }
        addMissingDrawnInstBoundaryPorts(drawnInst, objects.filter(isWire), wireNameLookup, portNameStrings, existingPortsByPoint, enrichedPorts, diagnostics);
        drawnInst.materializedPortInstances = enrichedPorts;
    }
    function buildWireNetNameLookup(objects, strings = []) {
        const wires = objects.filter(isWire);
        const aliases = objects.filter(isAlias);
        const busEntryCandidates = identifyBusEntryCandidates(wires, aliases);
        const busEntryWireIds = new Set(busEntryCandidates.map((candidate) => candidate.wire.dbId));
        const metadataNetNames = buildMetadataNetNameLookup(strings, objects);
        const netNameCarriers = buildWireNetNameCarriers(objects);
        const names = new Map();
        for (const candidate of busEntryCandidates) {
            names.set(candidate.wire.dbId, candidate.alias.name);
        }
        const wireState = new Map();
        for (const wire of wires.filter((wire) => !busEntryWireIds.has(wire.dbId))) {
            wireState.set(wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] });
        }
        for (const alias of aliases) {
            const owner = findExactAliasOwner(alias, wires);
            if (!owner || busEntryWireIds.has(owner.dbId)) {
                continue;
            }
            wireState.get(owner.dbId)?.aliases.push(alias);
            names.set(owner.dbId, alias.name);
        }
        for (const group of groupConnectedWires([...wireState.values()].map((state) => state.wire), wireState)) {
            const metadataName = netNameForWireGroup(group, group.flatMap((wire) => wireState.get(wire.dbId)?.aliases ?? []), netNameCarriers, metadataNetNames);
            if (!metadataName) {
                continue;
            }
            for (const wire of group) {
                names.set(wire.dbId, metadataName);
            }
        }
        return names;
    }
    function addMissingDrawnInstBoundaryPorts(drawnInst, wires, wireNameLookup, portNameStrings, existingPortsByPoint, enrichedPorts, diagnostics) {
        const boundary = drawnInstBoundaryFromPorts(drawnInst, enrichedPorts);
        for (const wire of wires) {
            const rawName = wireNameLookup.get(wire.dbId);
            if (!rawName) {
                continue;
            }
            for (const point of [
                { x: wire.startX, y: wire.startY },
                { x: wire.endX, y: wire.endY },
            ]) {
                const relX = point.x - drawnInst.locX;
                const relY = point.y - drawnInst.locY;
                const key = coordKey(relX, relY);
                if (existingPortsByPoint.has(key) || !isPointOnDrawnInstBoundary(relX, relY, boundary)) {
                    continue;
                }
                const stringName = drawnInstPortStringName(portNameStrings, relX, relY, boundary, rawName);
                const name = stringName ?? rawName;
                const port = {
                    name,
                    position: enrichedPorts.length,
                    startX: relX,
                    startY: relY,
                    hotptX: relX,
                    hotptY: relY,
                    x: point.x,
                    y: point.y,
                    wireDbId: wire.dbId,
                    isNoConnect: false,
                    isBus: isExplicitBusAliasName(stringName ?? rawName) || (stringName ?? rawName).includes('['),
                    type: inferredDrawnInstPortType(relX, relY, boundary),
                    visible: true,
                    isPlacedInstPin: false,
                    source: 'wire-boundary',
                    isExplicit: false,
                };
                emitBudgetedDiagnostic(diagnostics, `${drawnInst.dbId}\0${key}`, 'sheet-port-synthesized-from-wire', `sheetSymbol=${drawnInst.dbId} name="${name}" x=${relX} y=${relY} wire=${wire.dbId}`, 'CFB sheet symbol', 'warning');
                enrichedPorts.push(port);
                existingPortsByPoint.set(key, port);
            }
        }
    }
    function drawnInstPortNameStrings(drawnInst, strings) {
        return strings.filter((entry) => entry.offset > drawnInst.offset &&
            entry.followingUInt32Le !== undefined &&
            entry.followingUInt32Le <= Math.max(drawnInst.relativeDisplayX2, drawnInst.relativeDisplayY2) &&
            isPlausibleDrawnPortName(entry.value));
    }
    function drawnInstPortStringName(portNameStrings, relX, relY, boundary, fallbackName) {
        if (relY === boundary.Y1) {
            const coordinateName = portNameStrings.find((entry) => entry.followingUInt32Le === relX);
            if (coordinateName) {
                return coordinateName.value;
            }
        }
        if (fallbackName) {
            const matchingName = portNameStrings.find((entry) => entry.followingUInt32Le === relX && entry.value === fallbackName);
            if (matchingName) {
                return matchingName.value;
            }
        }
        return undefined;
    }
    function isPlausibleDrawnPortName(value) {
        return /^[A-Za-z0-9_+.\-#[\]/]+$/.test(value);
    }
    function inferredDrawnInstPortType(relX, relY, boundary) {
        if (relY === boundary.Y2) {
            return 1;
        }
        if (relY === boundary.Y1) {
            return 0;
        }
        if (relX === boundary.X1 || relX === boundary.X2) {
            return 1;
        }
        return DEFAULT_PIN_TYPE;
    }
    function enrichDrawnInstReferences(objects, diagnostics, pageName) {
        for (const drawnInst of objects.filter(isDrawnInst)) {
            if (drawnInst.reference || drawnInst.deviceDesignator || drawnInst.name) {
                continue;
            }
            emitPageReferenceDiagnostic(diagnostics, pageName, `drawn-inst-reference-${drawnInst.dbId}`, 'sheet-symbol-reference-unrecorded', `sheet symbol dbId=${drawnInst.dbId} carries no reference, designator or name of its own, so it is left unnamed`);
        }
    }
    function drawnInstBoundaryFromPorts(drawnInst, ports) {
        const portXs = ports.map((port) => port.hotptX ?? port.startX ?? 0);
        const portYs = ports.map((port) => port.hotptY ?? port.startY ?? 0);
        const maxPortY = Math.max(0, ...portYs);
        let y2 = Math.max(0, ...portYs, Math.floor(drawnInst.relativeDisplayY2 / 10) * 10);
        if (y2 - maxPortY <= 10) {
            y2 = maxPortY;
        }
        return {
            X1: Math.min(0, ...portXs),
            X2: Math.max(0, ...portXs, Math.floor(drawnInst.relativeDisplayX2 / 10) * 10),
            Y1: Math.min(0, ...portYs),
            Y2: y2,
        };
    }
    function isPointOnDrawnInstBoundary(relX, relY, boundary) {
        return (relX >= boundary.X1 &&
            relX <= boundary.X2 &&
            relY >= boundary.Y1 &&
            relY <= boundary.Y2 &&
            (relX === boundary.X1 || relX === boundary.X2 || relY === boundary.Y1 || relY === boundary.Y2));
    }
    function identifyBusEntryWireIds(wires, aliases) {
        return new Set(identifyBusEntryCandidates(wires, aliases).map((candidate) => candidate.wire.dbId));
    }
    function identifyBusEntryCandidates(wires, aliases) {
        const busSpineWireIds = findBusSpineWireIds(wires);
        const busSpineGroupKeys = buildBusSpineGroupKeyLookup(wires, busSpineWireIds);
        const explicitBusAliasNames = aliases.map((alias) => alias.name).filter(isExplicitBusAliasName);
        const candidateSets = [];
        for (const alias of aliases) {
            const owner = findExactAliasOwner(alias, wires);
            if (!owner || isBusWire(owner) || !isBusEntryMemberName(alias.name, explicitBusAliasNames)) {
                continue;
            }
            const candidates = findBusEntryPointCandidatesForWire(owner, wires, busSpineWireIds, busSpineGroupKeys);
            if (candidates.length > 0) {
                candidateSets.push({ wire: owner, alias, candidates });
            }
        }
        const preferredDirections = new Map();
        for (const candidateSet of candidateSets) {
            const candidates = preferredBusEntryCandidates(candidateSet.candidates);
            const selected = candidates[0];
            if (selected && !preferredDirections.has(selected.groupSideKey)) {
                preferredDirections.set(selected.groupSideKey, selected.direction);
            }
        }
        return candidateSets.map((candidateSet) => {
            const candidates = preferredBusEntryCandidates(candidateSet.candidates);
            const selected = candidates.find((candidate) => preferredDirections.get(candidate.groupSideKey) === candidate.direction) ??
                candidates[0];
            return { wire: candidateSet.wire, alias: candidateSet.alias, busEntry: selected.busEntry };
        });
    }
    function findComponentPinAttachedWireIds(wires, placedSymbols) {
        const ids = new Set();
        for (const wire of wires) {
            if (wireHasPlacedSymbolPinAttachment(wire, placedSymbols)) {
                ids.add(wire.dbId);
            }
        }
        return ids;
    }
    function wireHasPlacedSymbolPinAttachment(wire, placedSymbols) {
        for (const placedSymbol of placedSymbols) {
            for (const port of placedSymbol.materializedPortInstances ?? []) {
                const x = placedSymbol.locX + (port.hotptX ?? port.startX ?? 0);
                const y = placedSymbol.locY + (port.hotptY ?? port.startY ?? 0);
                if (pointOnWire(x, y, wire)) {
                    return true;
                }
            }
        }
        return false;
    }
    function findBusEntryPointCandidatesForWire(wire, wires, busSpineWireIds, busSpineGroupKeys) {
        const candidates = [];
        for (const endpoint of [
            { x: wire.startX, y: wire.startY },
            { x: wire.endX, y: wire.endY },
        ]) {
            for (const busPoint of busEntryBusPointCandidates(endpoint.x, endpoint.y)) {
                const busWire = wires.find((candidate) => candidate.dbId !== wire.dbId &&
                    busSpineWireIds.has(candidate.dbId) &&
                    pointOnWire(busPoint.x, busPoint.y, candidate));
                if (busWire) {
                    const entry = normalizeBusEntry({ BusX: busPoint.x, BusY: busPoint.y, EndX: endpoint.x, EndY: endpoint.y }, busWire);
                    candidates.push({
                        busEntry: entry,
                        busWire,
                        groupSideKey: busEntryGroupSideKey(busSpineGroupKeys, busWire, entry),
                        direction: Math.sign(entry.BusY - entry.EndY),
                        isEndpoint: isWireEndpoint(busWire, busPoint.x, busPoint.y),
                    });
                }
            }
        }
        return candidates;
    }
    function preferredBusEntryCandidates(candidates) {
        const endpointCandidates = candidates.filter((candidate) => candidate.isEndpoint);
        return endpointCandidates.length > 0 ? endpointCandidates : [...candidates];
    }
    function normalizeBusEntry(entry, _busWire) {
        return entry;
    }
    function buildBusSpineGroupKeyLookup(wires, busSpineWireIds) {
        const busSpineWires = wires.filter((wire) => busSpineWireIds.has(wire.dbId));
        const groups = groupConnectedWires(busSpineWires, new Map(busSpineWires.map((wire) => [wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] }])));
        const keys = new Map();
        for (const group of groups) {
            const key = group
                .map((wire) => wire.dbId)
                .sort((left, right) => left - right)
                .join(',');
            for (const wire of group) {
                keys.set(wire.dbId, key);
            }
        }
        return keys;
    }
    function busEntryGroupSideKey(busSpineGroupKeys, busWire, entry) {
        return `${busSpineGroupKeys.get(busWire.dbId) ?? busWire.dbId}:${Math.sign(entry.BusX - entry.EndX)}`;
    }
    function findBusSpineWireIds(wires) {
        const nonAliasWires = wires.filter((wire) => wire.wireType !== 11);
        const groups = groupConnectedWires(nonAliasWires, new Map(nonAliasWires.map((wire) => [wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] }])));
        const ids = new Set();
        for (const group of groups) {
            if (group.some(isBusWire)) {
                for (const wire of group) {
                    ids.add(wire.dbId);
                }
            }
        }
        return ids;
    }
    function identifyLogicalBusWireIds(wires, aliases, busEntryCandidates) {
        const ids = findBusSpineWireIdsFromBusEntries(wires, busEntryCandidates);
        const nonAliasWires = wires.filter((wire) => wire.wireType !== 11);
        const nonAliasGroups = groupConnectedWires(nonAliasWires, new Map(nonAliasWires.map((wire) => [wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] }])));
        for (const alias of aliases) {
            if (!isExplicitBusAliasName(alias.name)) {
                continue;
            }
            const owner = findExactAliasOwner(alias, wires);
            if (!owner) {
                continue;
            }
            ids.add(owner.dbId);
            for (const group of nonAliasGroups) {
                if (group.some((wire) => wiresTouch(owner, wire))) {
                    for (const wire of group) {
                        ids.add(wire.dbId);
                    }
                }
            }
        }
        return ids;
    }
    function findBusSpineWireIdsFromBusEntries(wires, busEntryCandidates) {
        const nonAliasWires = wires.filter((wire) => wire.wireType !== 11);
        const groups = groupConnectedWires(nonAliasWires, new Map(nonAliasWires.map((wire) => [wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] }])));
        const ids = new Set();
        for (const group of groups) {
            if (busEntryCandidates.some((candidate) => group.some((wire) => pointOnWire(candidate.busEntry.BusX, candidate.busEntry.BusY, wire)))) {
                for (const wire of group) {
                    ids.add(wire.dbId);
                }
            }
        }
        return ids;
    }
    function isExplicitBusAliasName(name) {
        return /^[^\s=\[\]]+\[[^\s=\[\]]+\]$/.test(name.trim());
    }
    function recordedPlacementReference(object, fallbacks) {
        const recorded = object.referenceTail?.reference ??
            object.resolvedMetadata?.reference ??
            fallbacks.find((fallback) => fallback !== undefined);
        const reference = recorded ?? '?';
        return { reference, unannotated: reference.endsWith('?'), recorded: recorded !== undefined };
    }
    function buildMultipartSectionSuffixLookup(document, placedPartCacheIdentityCatalog, sectionCounts, diagnostics, occurrenceTrust = document.hierarchyOccurrenceTrust ??
        (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
        const suffixes = new WeakMap();
        const referenceResolutions = new WeakMap();
        const referenceRows = new Map();
        const occurrenceSelectors = new Map();
        const library = document.streams.find((stream) => stream.parsed?.kind === 'library')?.parsed;
        const libraryStrings = library?.kind === 'library' ? library.stringCatalog : undefined;
        for (const { streamPath, entry } of occurrenceTrust.rejectedEntries) {
            if (entry.ownershipState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved &&
                entry.sectionSelectorStringIndex === undefined) {
                emitDecisionDiagnostic(diagnostics, 'occurrence-selector-withheld', `stream=${JSON.stringify(streamPath)}, offset=${entry.offset}, refInstId=${entry.refInstId ?? entry.dbId}: incomplete occurrence annotation rejects branch trust`, 'CFB hierarchy occurrence', 'warning');
            }
            emitDecisionDiagnostic(diagnostics, 'occurrence-ownership-withheld', `stream=${JSON.stringify(streamPath)}, offset=${entry.offset}, refInstId=${entry.refInstId ?? entry.dbId}: branch trust is withheld; no occurrence annotation supplied`, 'CFB hierarchy occurrence', 'warning');
        }
        for (const stream of document.streams) {
            if (stream.parsed?.kind !== 'hierarchy') {
                continue;
            }
            for (const entry of stream.parsed.entries) {
                if (entry.parentBranchOffset === undefined &&
                    entry.referenceStringOffset !== undefined &&
                    entry.owningViewName !== undefined) {
                    const key = JSON.stringify([entry.owningViewName.toUpperCase(), entry.refInstId ?? entry.dbId]);
                    const rows = referenceRows.get(key) ?? [];
                    rows.push({ entry, streamPath: stream.path });
                    referenceRows.set(key, rows);
                }
                if (!occurrenceTrust.trustedEntries.has(entry)) {
                    continue;
                }
                const id = entry.refInstId ?? entry.dbId;
                if (id === undefined || entry.owningViewName === undefined || entry.sectionSelectorStringIndex === undefined) {
                    continue;
                }
                const pageKey = JSON.stringify([entry.owningViewName.toUpperCase(), id]);
                const selectors = occurrenceSelectors.get(pageKey) ?? [];
                selectors.push({
                    selector: libraryStrings?.[entry.sectionSelectorStringIndex],
                    index: entry.sectionSelectorStringIndex,
                    reference: entry.name,
                });
                occurrenceSelectors.set(pageKey, selectors);
            }
        }
        const streamsByView = new Map();
        for (const [stream, views] of occurrenceTrust.viewsByStream) {
            for (const view of views) {
                const streams = streamsByView.get(view) ?? [];
                streams.push(stream);
                streamsByView.set(view, streams);
            }
        }
        const placements = [];
        for (const stream of document.streams) {
            const parsed = stream.parsed;
            if (parsed?.kind !== 'page') {
                continue;
            }
            const objects = stream.decodedPageObjects ?? [];
            for (const object of objects) {
                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
                    continue;
                }
                placements.push({
                    object,
                    strings: stream.lengthPrefixedStrings,
                    viewName: parsed.viewName,
                });
            }
        }
        for (const placement of placements) {
            if (placement.object.referenceState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld) {
                suffixes.set(placement.object, { state: MultipartSectionState.Withheld });
                emitDecisionDiagnostic(diagnostics, 'placement-reference-connectivity-withheld', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: recorded reference was rejected; withholding pin connections`, 'CFB physical section', 'warning');
                continue;
            }
            const pageKey = JSON.stringify([placement.viewName.toUpperCase(), placement.object.dbId]);
            const declaredSections = declaredSectionCount(placement.object, placedPartCacheIdentityCatalog, sectionCounts);
            const { unannotated } = recordedPlacementReference(placement.object, [placement.object.reference]);
            const streams = streamsByView.get(placement.viewName.toUpperCase()) ?? [];
            const scopeVetoed = streams.some((stream) => withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream, placement.viewName, placement.object.dbId, 'placement-annotation'));
            const vetoed = scopeVetoed ||
                streams.some((stream) => withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream, placement.viewName, placement.object.dbId, 'section-selector', undefined, undefined, OrcadDsnCfbOccurrenceOwnership_1.HierarchyAdoptionValue.Section));
            if (unannotated) {
                const rows = referenceRows.get(pageKey) ?? [];
                const pathVetoed = !scopeVetoed &&
                    rows.some(({ entry, streamPath }) => withholdHierarchyAdoption(diagnostics, occurrenceTrust, streamPath, placement.viewName, placement.object.dbId, 'page-instance-unannotated', entry.ownershipBranchOffset));
                if (scopeVetoed || pathVetoed || rows.some(({ entry }) => !occurrenceTrust.trustedEntries.has(entry))) {
                    referenceResolutions.set(placement.object, { state: MultipartSectionState.ReferenceWithheld });
                    emitDecisionDiagnostic(diagnostics, 'occurrence-reference-unresolved', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: occurrence trust or same-path conflict withholds the page annotation`, 'CFB reference', 'warning');
                }
                else if (rows.every(({ entry }) => entry.name === undefined || entry.name === '')) {
                    referenceResolutions.set(placement.object, { state: MultipartSectionState.NoOccurrenceAnnotation });
                    emitDecisionDiagnostic(diagnostics, 'occurrence-reference-unavailable', `dbId=${placement.object.dbId}: no tagged occurrence annotation; no reference or section override supplied`, 'CFB reference', 'info');
                }
                else {
                    const reference = rows[0] === undefined ? undefined : (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyReferenceCandidate)(rows[0].entry)?.reference;
                    const agrees = reference !== undefined &&
                        rows.every(({ entry }) => (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyReferenceCandidate)(entry)?.reference === reference);
                    emitDecisionDiagnostic(diagnostics, agrees ? 'page-instance-unannotated' : 'page-instance-reference-varies', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: ${agrees ? 'page reference remains unannotated; agreeing references belong only to their occurrences' : 'independent occurrence references do not agree; page reference remains unresolved'}; paths=${JSON.stringify(rows.map(({ entry, streamPath }) => ({ path: [streamPath, entry.ownershipBranchOffset, entry.occurrenceId], reference: entry.name })))}`, 'CFB reference', 'info');
                }
            }
            if (vetoed) {
                suffixes.set(placement.object, declaredSections === undefined || declaredSections < 2 ?
                    { state: MultipartSectionState.NoOccurrenceAnnotation }
                    : { state: MultipartSectionState.Withheld });
                emitDecisionDiagnostic(diagnostics, declaredSections === undefined || declaredSections < 2 ?
                    'occurrence-reference-unresolved'
                    : 'occurrence-section-unresolved', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: occurrence ownership is unresolved; withholding occurrence-derived placement evidence`, declaredSections === undefined || declaredSections < 2 ? 'CFB reference' : 'CFB physical section', 'warning');
                continue;
            }
            const selectors = occurrenceSelectors.get(pageKey) ?? [];
            if (selectors.length === 0 && declaredSections !== undefined && declaredSections > 1) {
                suffixes.set(placement.object, { state: MultipartSectionState.Withheld });
                emitDecisionDiagnostic(diagnostics, 'occurrence-section-unresolved', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: no complete branch ownership and nonempty occurrence set; withholding multi-section pin connections`, 'CFB physical section', 'warning');
                continue;
            }
            if (declaredSections === undefined || declaredSections < 2) {
                continue;
            }
            const identity = placedPartCacheIdentityCatalog.get(placement.object);
            const nonempty = selectors.filter((candidate) => candidate.selector !== '');
            const recorded = placement.object.resolvedMetadata?.deviceDesignator;
            const occurrence = nonempty[0] ?? selectors[0];
            const occurrenceOverridesPage = unannotated && nonempty.length > 0;
            if (unannotated) {
                emitDecisionDiagnostic(diagnostics, 'page-instance-unannotated', `dbId=${placement.object.dbId}: page reference remains unannotated; evaluating recorded occurrence section; occurrence references=${JSON.stringify(selectors.map((candidate) => candidate.reference))}`, 'CFB physical section', 'info');
            }
            const index = multipartSectionIndexFromMarker(placement.object, placement.strings);
            const sections = document.streams.flatMap((stream) => (stream.cacheSourceLibraries ?? []).flatMap((source) => (identity !== undefined &&
                identity !== null &&
                source.physicalPackageName === identity.packageName &&
                canonicalSourceLibraryName(source.library.value) === canonicalSourceLibraryName(identity.cacheSourceLibName)) ?
                (source.physicalSections ?? []).filter((section) => stripNormalViewSuffix(section.cellName) === identity.cellName)
                : []));
            const markerSections = sections.filter((section) => index !== undefined && sectionIndexMatchesDesignator(section.selector, index));
            const markerDesignators = new Set(markerSections.map((section) => section.selector));
            const markerDesignator = markerDesignators.size === 1 ? markerSections[0]?.selector : undefined;
            const selector = occurrenceOverridesPage ? occurrence?.selector
                : recorded?.length ? recorded
                    : markerDesignator;
            const selectedSections = sections.filter((section) => section.selector === selector);
            const agrees = nonempty.every((candidate) => candidate.selector !== undefined && candidate.selector === selector) &&
                (occurrenceOverridesPage ||
                    index === undefined ||
                    (index < declaredSections && (selector === markerDesignator || sections.length === 0))) &&
                (selectedSections.length === 0 ||
                    selectedSections.every((section) => arraysEqual(section.pinNumbers, selectedSections[0].pinNumbers)));
            const accepted = (selector !== undefined || (index !== undefined && sections.length === 0 && nonempty.length === 0)) &&
                (recorded === undefined || recorded === '' || selectedSections.length > 0) &&
                (!occurrenceOverridesPage || selectedSections.length > 0) &&
                agrees;
            suffixes.set(placement.object, accepted ?
                {
                    state: MultipartSectionState.Resolved,
                    designator: selector ?? `-${(index ?? 0) + 1}`,
                }
                : { state: MultipartSectionState.Withheld });
            emitDecisionDiagnostic(diagnostics, accepted ? 'occurrence-section-selected' : 'occurrence-section-unresolved', `dbId=${placement.object.dbId} recorded=${JSON.stringify(recorded)} marker=${index} selectors=${JSON.stringify(selectors)}: ${accepted ? 'using recorded section evidence with agreeing corroboration' : 'withholding pin connections without agreeing recorded section and source/view evidence'}`, 'CFB physical section', accepted ? 'info' : 'warning');
        }
        for (const { object } of placements) {
            const reference = referenceResolutions.get(object);
            if (reference === undefined) {
                continue;
            }
            const section = suffixes.get(object);
            if (section === undefined || section.state === MultipartSectionState.NoOccurrenceAnnotation) {
                suffixes.set(object, reference);
            }
        }
        return suffixes;
    }
    function multipartSectionIndexFromMarker(object, strings) {
        const frame = object.recordFrame;
        if (!frame) {
            return undefined;
        }
        const markers = strings.filter((string) => string.offset >= object.offset &&
            string.offset >= frame.offset &&
            string.offset + 2 + string.length + 1 + 2 === frame.endOffset &&
            string.hasNullTerminator &&
            stripNormalViewSuffix(string.value) === stripNormalViewSuffix(object.graphicName) &&
            string.followingUInt32Le !== undefined);
        const indices = markers.map((marker) => (marker.followingUInt32Le ?? 0) & 0xffff);
        return indices.length > 0 && indices.every((index) => index === indices[0]) ? indices[0] : undefined;
    }
    function declaredSectionCount(object, placedPartCacheIdentityCatalog, sectionCounts) {
        const identity = placedPartCacheIdentityCatalog.get(object);
        if (!identity) {
            return undefined;
        }
        return sectionCounts.get(cacheIdentityKey(identity.cellName, identity.packageName, identity.cacheSourceLibName));
    }
    function buildPageReferenceLookup(strings, objects, cacheCatalog, placedPartCacheIdentityCatalog, diagnostics, pageName) {
        const partsByDbId = new Map(objects.filter(isPlacedSymbolRole('part-inst')).map((object) => [object.dbId, object]));
        const netNames = new Set(objects.filter(isAlias).map((alias) => alias.name.toUpperCase()));
        const wireMetadataIds = new Set(objects.filter(isWire).map((wire) => wire.metadataId));
        for (const [metadataId, name] of buildMetadataNetNameLookup(strings, objects)) {
            if (wireMetadataIds.has(metadataId)) {
                netNames.add(name.toUpperCase());
            }
        }
        const pageStringsByDbId = new Map();
        for (let index = 0; index < strings.length; index++) {
            const string = strings[index];
            if (!isPotentialPageReferenceName(string.value)) {
                continue;
            }
            const dbId = precedingPageGraphicDbId(strings, index);
            const object = dbId === undefined ? undefined : partsByDbId.get(dbId);
            if (dbId === undefined || object === undefined) {
                continue;
            }
            const values = pageStringsByDbId.get(dbId) ?? new Set();
            values.add(string.value);
            pageStringsByDbId.set(dbId, values);
        }
        const uniqueCandidates = new Map();
        for (const [dbId, values] of pageStringsByDbId) {
            const object = partsByDbId.get(dbId);
            if (object === undefined) {
                continue;
            }
            const recorded = recordedPlacementReference(object, [object.reference]);
            if (recorded.recorded) {
                emitDecisionDiagnostic(diagnostics, 'reference-recovery-withheld', `page=${JSON.stringify(pageName)} dbId=${dbId} source=page-string candidates=${JSON.stringify([...values])} recorded=${JSON.stringify(recorded.reference)}: recorded page evidence takes precedence`, 'CFB page reference', 'info');
                continue;
            }
            const candidates = new Map([...values]
                .filter((value) => isValidPageReferenceForPlacedPart(value, object, cacheCatalog, placedPartCacheIdentityCatalog, netNames))
                .map((value) => [value.toUpperCase(), value]));
            if (candidates.size === 0) {
                continue;
            }
            const prefixMatches = object ?
                Array.from(candidates.values()).filter((candidate) => pageReferenceMatchesCatalogPrefix(candidate, object, cacheCatalog, placedPartCacheIdentityCatalog))
                : [];
            const candidate = candidates.size === 1 ? candidates.values().next().value
                : prefixMatches.length === 1 ? prefixMatches[0]
                    : undefined;
            if (candidate === undefined) {
                emitPageReferenceDiagnostic(diagnostics, pageName, String(dbId), 'page-reference-conflict', `page="${pageName}" dbId=${dbId} candidates=${Array.from(candidates.values()).join(',')}`);
                continue;
            }
            uniqueCandidates.set(dbId, candidate);
        }
        const ownersByReference = new Map();
        for (const object of partsByDbId.values()) {
            const { reference, unannotated } = recordedPlacementReference(object, [
                object.reference,
                uniqueCandidates.get(object.dbId),
            ]);
            if (reference.length === 0 || unannotated) {
                continue;
            }
            const owners = ownersByReference.get(reference.toUpperCase()) ?? [];
            owners.push(object);
            ownersByReference.set(reference.toUpperCase(), owners);
        }
        const references = new Map();
        for (const [dbId, reference] of uniqueCandidates) {
            const owners = ownersByReference.get(reference.toUpperCase()) ?? [];
            if (owners.length > 1 && !areMultipartReferencePeers(owners, placedPartCacheIdentityCatalog)) {
                emitPageReferenceDiagnostic(diagnostics, pageName, reference.toUpperCase(), 'duplicate-page-reference', `page="${pageName}" dbId=${dbId} reference="${reference}" dbIds=${owners.map((owner) => owner.dbId).join(',')}`);
                continue;
            }
            references.set(dbId, reference);
            emitPageReferenceDiagnostic(diagnostics, pageName, String(dbId), 'reference-recovered', `page="${pageName}" dbId=${dbId} source=page-string reference="${reference}"`, 'info');
        }
        return references;
    }
    function isValidPageReferenceForPlacedPart(candidate, object, cacheCatalog, placedPartCacheIdentityCatalog, netNames) {
        const normalizedCandidate = candidate.toUpperCase();
        if (netNames.has(normalizedCandidate) || isPageReferencePlaceholder(candidate)) {
            return false;
        }
        const cacheIdentity = placedPartCacheIdentityCatalog.get(object);
        const entries = cacheIdentity ? [cacheIdentity.catalogEntry] : catalogEntriesForGraphicName(cacheCatalog, object.graphicName);
        const forbiddenValues = new Set([
            object.symbolName,
            object.graphicName,
            stripNormalViewSuffix(object.symbolName),
            stripNormalViewSuffix(object.graphicName),
            object.partValue,
            object.resolvedMetadata?.name,
            object.resolvedMetadata?.partValue,
            object.resolvedMetadata?.pkgName,
            ...entries.flatMap((entry) => [entry.cellName, entry.packageName, entry.partValue]),
            ...(object.resolvedMetadata?.userProperties ?? []).map((property) => property.value),
            ...(object.userProperties ?? []).map((property) => property.value),
        ]
            .filter((value) => value !== undefined && value.length > 0)
            .map((value) => stripNormalViewSuffix(value).toUpperCase()));
        if (forbiddenValues.has(stripNormalViewSuffix(candidate).toUpperCase())) {
            return false;
        }
        return true;
    }
    function pageReferenceMatchesCatalogPrefix(candidate, object, cacheCatalog, placedPartCacheIdentityCatalog) {
        const cacheIdentity = placedPartCacheIdentityCatalog.get(object);
        const entries = cacheIdentity ? [cacheIdentity.catalogEntry] : catalogEntriesForGraphicName(cacheCatalog, object.graphicName);
        const prefixes = new Set(entries
            .map((entry) => entry.referencePrefix)
            .filter((prefix) => prefix !== undefined && prefix.length > 0)
            .map((prefix) => prefix.toUpperCase()));
        if (prefixes.size !== 1) {
            return false;
        }
        const prefix = prefixes.values().next().value;
        return (prefix !== undefined && candidate.toUpperCase().startsWith(prefix) && /^\d/.test(candidate.slice(prefix.length)));
    }
    function areMultipartReferencePeers(objects, placedPartCacheIdentityCatalog) {
        const identities = objects.map((object) => placedPartCacheIdentityCatalog.get(object));
        if (identities.some((identity) => identity === undefined)) {
            return false;
        }
        const resolvedIdentities = identities.filter((identity) => identity !== undefined);
        const packageKeys = new Set(resolvedIdentities.map((identity) => packageSourceKey(identity.catalogEntry.sectionParentName ?? identity.packageName, identity.sourceLibName)));
        const catalogBackedPeers = packageKeys.size === 1 &&
            resolvedIdentities.every((identity) => identity.catalogEntry.deviceDesignator !== undefined ||
                identity.catalogEntry.sectionParentName !== undefined ||
                identity.catalogEntry.preserveSectionPackageIdentity === true);
        return catalogBackedPeers || arePhysicalMultipartPackagePeers(objects, resolvedIdentities);
    }
    function arePhysicalMultipartPackagePeers(objects, identities) {
        const sourceLibraries = new Set(identities.map((identity) => identity.canonicalSourceLibName));
        const packageNames = identities.map((identity) => identity.packageName);
        const packageNameSet = new Set(packageNames.map((name) => name.toUpperCase()));
        const sectionStemCandidates = packageNames.map((name) => /^(.+)[A-Z]$/i.exec(name)?.[1]?.toUpperCase());
        const sectionStems = new Set(sectionStemCandidates.filter((name) => name !== undefined));
        const partNumbers = new Set(objects
            .map((object) => physicalPartNumberForPlacedObject(object)?.toUpperCase())
            .filter((partNumber) => partNumber !== undefined));
        return (sourceLibraries.size === 1 &&
            packageNameSet.size === objects.length &&
            sectionStemCandidates.every((stem) => stem !== undefined) &&
            sectionStems.size === 1 &&
            partNumbers.size === 1 &&
            objects.every((object) => physicalPartNumberForPlacedObject(object) !== undefined));
    }
    function physicalPartNumberForPlacedObject(object) {
        return [...(object.resolvedMetadata?.userProperties ?? []), ...(object.userProperties ?? [])].find((property) => /^(?:Part Number|Part_Number|APN)$/i.test(property.name))?.value;
    }
    function emitPageReferenceDiagnostic(diagnostics, pageName, dedupeKey, reason, detail, severity = 'warning') {
        if (reason === 'page-reference-conflict' || reason === 'duplicate-page-reference') {
            emitDecisionDiagnostic(diagnostics, reason, detail, 'CFB page reference', severity);
            return;
        }
        emitBudgetedDiagnostic(diagnostics, `${pageName}\0${dedupeKey}`, reason, detail, 'CFB page reference', severity);
    }
    function applySheetReferenceDisplayOverrides(partInsts, strings, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences) {
        const labels = sheetReferenceDisplayLabels(strings);
        if (labels.length === 0) {
            return;
        }
        const claimedPartInsts = new Set();
        for (const label of labels) {
            const match = nearestSheetPartInstForDisplayLabel(label, partInsts, partInstanceOccurrenceReferences, claimedPartInsts);
            if (!match) {
                continue;
            }
            const existingReference = partInstanceOccurrenceReferences.get(match);
            if (!existingReference) {
                continue;
            }
            const hierarchyReference = hierarchySheetReferenceForDisplayLabel(hierarchySheetReferenceCatalog, label.reference, existingReference) ??
                existingReference;
            partInstanceOccurrenceReferences.set(match, {
                ...hierarchyReference,
                reference: label.reference,
                dbId: match.DbId,
            });
            claimedPartInsts.add(match);
        }
    }
    function sheetReferenceDisplayLabels(strings) {
        const labels = [];
        for (const string of strings) {
            const reference = sheetReferenceFromRefinLabel(string.value);
            const x = string.precedingInt32Le20;
            const y = string.precedingInt32Le16;
            if (!reference || x === undefined || y === undefined || !isPlausiblePageLabelCoordinate(x, y)) {
                continue;
            }
            labels.push({ reference, x, y, offset: string.offset });
        }
        return labels;
    }
    function hierarchySheetReferenceForDisplayLabel(catalog, reference, existingReference) {
        const referenceKey = normalizeSheetReferenceToken(reference);
        const existingContentsKey = normalizeSheetReferenceToken(existingReference.contentsViewName);
        return catalog.references.find((candidate) => {
            const candidateReferenceKey = normalizeSheetReferenceToken(candidate.reference);
            const candidateContentsKey = normalizeSheetReferenceToken(candidate.contentsViewName);
            return (candidateReferenceKey === referenceKey &&
                (!existingContentsKey ||
                    candidateContentsKey === existingContentsKey ||
                    areSheetReferenceKeysCompatible(candidateContentsKey, existingContentsKey)));
        });
    }
    function sheetReferenceFromRefinLabel(value) {
        const match = /^([A-Za-z][A-Za-z0-9_.-]*\d)_REFIN$/i.exec(value);
        return match?.[1];
    }
    function isPlausiblePageLabelCoordinate(x, y) {
        return x >= -100_000 && x <= 100_000 && y >= -100_000 && y <= 100_000;
    }
    function nearestSheetPartInstForDisplayLabel(label, partInsts, partInstanceOccurrenceReferences, claimedPartInsts) {
        let best;
        for (const partInst of partInsts) {
            if (claimedPartInsts.has(partInst)) {
                continue;
            }
            const existingReference = partInstanceOccurrenceReferences.get(partInst);
            if (!existingReference ||
                !areSheetReferenceKeysCompatible(normalizeSheetReferenceToken(label.reference).replace(/\d+$/, ''), normalizeSheetReferenceToken(existingReference.reference).replace(/\d+$/, ''))) {
                continue;
            }
            const distance = sheetReferenceDisplayLabelDistance(label, partInst);
            if (distance > 160) {
                continue;
            }
            if (!best || distance < best.distance) {
                best = { partInst, distance };
            }
        }
        return best?.partInst;
    }
    function sheetReferenceDisplayLabelDistance(label, partInst) {
        const anchorX = partInst.LocX - 55;
        const anchorY = partInst.LocY + 40;
        return Math.abs(label.x - anchorX) + Math.abs(label.y - anchorY);
    }
    function isPotentialPageReferenceName(name) {
        return /^[A-Za-z][A-Za-z0-9_.-]*\d[A-Za-z0-9_.-]*$/.test(name);
    }
    function isPageReferencePlaceholder(name) {
        return name.endsWith('?') || /^(?:\?|UNKNOWN|UNANNOTATED|NONE|N\/A)$/i.test(name);
    }
    function precedingPageGraphicDbId(strings, index) {
        for (let candidateIndex = index - 1; candidateIndex >= Math.max(0, index - 4); candidateIndex--) {
            const candidate = strings[candidateIndex];
            if (!/\.(?:Normal|IEEE|Convert)$/i.test(candidate.value)) {
                continue;
            }
            const dbId = candidate.followingUInt32Le;
            if (dbId !== undefined && dbId > 0 && dbId <= 1_000_000_000) {
                return dbId;
            }
        }
        return undefined;
    }
    function isBusEntryMemberName(name, explicitBusAliasNames = []) {
        if (isExplicitBusAliasName(name)) {
            return false;
        }
        if (name.includes('.')) {
            return true;
        }
        if (!/\d(?:#)?$/i.test(name)) {
            return false;
        }
        return (explicitBusAliasNames.some((busAliasName) => isExplicitBusAliasMemberName(name, busAliasName)) ||
            isShortImplicitBusMemberName(name));
    }
    function isExplicitBusAliasMemberName(memberName, busAliasName) {
        const prefix = busAliasName.replace(/\[[^\]]+\]/g, '');
        const ordinal = /(\d+)(?:#)?$/i.exec(memberName)?.[1];
        return Boolean(ordinal &&
            prefix &&
            (memberName === `${prefix}${ordinal}` || (!prefix.endsWith('_') && memberName === `${prefix}_${ordinal}`)));
    }
    function isShortImplicitBusMemberName(name) {
        return name.split('_').length <= 3;
    }
    function busEntryBusPointCandidates(x, y) {
        return [
            { x: x - 10, y: y - 10 },
            { x: x + 10, y: y - 10 },
            { x: x - 10, y: y + 10 },
            { x: x + 10, y: y + 10 },
        ];
    }
    let PartPropertyPairBlockState;
    (function (PartPropertyPairBlockState) {
        PartPropertyPairBlockState["Absent"] = "absent";
        PartPropertyPairBlockState["Unresolved"] = "unresolved";
        PartPropertyPairBlockState["Resolved"] = "resolved";
    })(PartPropertyPairBlockState || (PartPropertyPairBlockState = {}));
    function partPropertyPairBlockState(object) {
        if (object.propertyPairs === undefined) {
            return PartPropertyPairBlockState.Absent;
        }
        return (object.resolvedPropertyPairCount ?? 0) > 0 ?
            PartPropertyPairBlockState.Resolved
            : PartPropertyPairBlockState.Unresolved;
    }
    function emitPartPropertySourceDiagnostic(diagnostics, dbId, state, countsBySource) {
        const supplied = Array.from(countsBySource)
            .filter(([, count]) => count > 0)
            .map(([source, count]) => `${source}=${count}`);
        emitBudgetedDiagnostic(diagnostics, `part-properties\0${dbId}`, state === PartPropertyPairBlockState.Absent ?
            'part-properties-without-pair-block'
            : 'part-properties-pair-block-unresolved', `dbId=${dbId} sources=${supplied.length > 0 ? supplied.join(',') : 'none'}`, 'CFB part properties', 'warning');
    }
    function reportNonReferencePlaceholder(properties, dbId, diagnostics) {
        const placeholders = (properties ?? []).filter((property) => property.displayedValue !== undefined && /^[A-Za-z][A-Za-z0-9_.-]*\?$/.test(property.displayedValue));
        const referenceRecord = placeholders.find((property) => property.propertyName === 'Part Reference');
        if (referenceRecord) {
            return;
        }
        const shown = placeholders[0];
        if (!shown) {
            return;
        }
        emitBudgetedDiagnostic(diagnostics, `part-reference\0${dbId}`, 'reference-placeholder-on-non-reference-record', `dbId=${dbId} record=${shown.propertyName ?? `0x${shown.propertyCode.toString(16)}`} value="${shown.displayedValue ?? ''}"`, 'CFB part reference', 'warning');
    }
    function buildPartInst(object, libraryInstanceCatalog, libraryStringCatalog, cisPageProperties, placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, multipartSectionSuffixes, pageReferenceLookup, variantPropertyCatalog, physicalPinDiagnostics, hasRepeatedCatalogInstanceName, viewName) {
        const metadata = object.resolvedMetadata;
        const section = multipartSectionSuffixes.get(object);
        const cacheIdentity = placedPartCacheIdentityCatalog.get(object);
        const catalogEntry = cacheIdentity?.catalogEntry;
        const displayProperties = (object.displayProperties ?? []).map(partDisplayPropertyFromCfb);
        const pkgName = cacheIdentity?.packageName ?? metadata?.pkgName ?? object.graphicName.replace(/\.(?:Normal|IEEE|Convert)$/i, '');
        const libraryEntry = libraryEntryForPlacedPackage(libraryInstanceCatalog, object.dbId, pkgName);
        const nearestLibraryEntry = libraryEntryForDbId(libraryInstanceCatalog, object.dbId);
        const exactRowProperties = userPropertiesFromCfb((object.userProperties ?? []).filter((property) => property.source === 'library-catalog'));
        const variantProperties = variantUserPropertiesForPart(variantPropertyCatalog, exactRowProperties, object, pkgName);
        const cisProperties = cisPageProperties?.get(object.dbId) ?? [];
        const displayDerivedProperties = userPropertiesFromCfb(partUserPropertiesFromCfbObject(object));
        const userProperties = finalizePartUserProperties(mergeUserProperties(exactRowProperties, cisProperties, variantProperties, displayDerivedProperties)).filter((property) => object.valueState !== OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld || property.Name !== 'Value');
        const pairBlockState = partPropertyPairBlockState(object);
        if (pairBlockState !== PartPropertyPairBlockState.Resolved) {
            emitPartPropertySourceDiagnostic(physicalPinDiagnostics, object.dbId, pairBlockState, new Map([
                ['cis-schematic-info', cisProperties.length],
                ['variant-update', variantProperties.length],
                [
                    'display-property',
                    partUserPropertiesFromCfbObject(object).filter((property) => property.source === 'display-property').length,
                ],
                ['display-catalog-value', exactRowProperties.length],
            ]));
        }
        const dbIdSheetReference = hierarchySheetReferenceForDbId(hierarchySheetReferenceCatalog, object.dbId, viewName);
        const exactSheetPackage = findSheetSymbolPackageMetadata(sheetSymbolPackageCatalog, pkgName);
        const strictSheetPackage = exactSheetSymbolPackageMetadata(sheetSymbolPackageCatalog, pkgName);
        const displaySheetPackage = sheetPackageFromDisplayCatalog(object, libraryStringCatalog, exactSheetPackage, physicalPinDiagnostics);
        const exactSheetPackageWithDisplayValue = exactSheetPackage && displaySheetPackage ?
            mergeSheetSymbolPackageMetadata(exactSheetPackage, displaySheetPackage)
            : exactSheetPackage;
        const candidateSheetPackage = (shouldPreferDisplaySheetPackage(displaySheetPackage, exactSheetPackage) ? displaySheetPackage : undefined) ??
            exactSheetPackageWithDisplayValue ??
            findSheetSymbolPackageMetadata(sheetSymbolPackageCatalog, dbIdSheetReference?.contentsViewName) ??
            findSheetSymbolPackageMetadata(sheetSymbolPackageCatalog, object.partValue) ??
            displaySheetPackage;
        const hasHierarchyOccurrenceAnnotation = hierarchySheetReferenceCatalog.references.some((reference) => reference.isOccurrenceAnnotation === true &&
            (reference.parentViewName === undefined || reference.parentViewName.toUpperCase() === viewName.toUpperCase()) &&
            reference.dbId === object.dbId &&
            !hierarchySheetReferenceCatalog.usedIndexes.has(hierarchySheetReferenceCatalog.references.indexOf(reference)));
        const sheetReference = consumeHierarchySheetReference(hierarchySheetReferenceCatalog, candidateSheetPackage, pkgName, object.dbId, object, physicalPinDiagnostics, Boolean(strictSheetPackage ||
            (object.materializedPortInstances?.length ?? 0) >= 5 ||
            (dbIdSheetReference &&
                candidateSheetPackage &&
                areSheetReferenceKeysCompatible(dbIdSheetReference.contentsViewName, candidateSheetPackage.contentsViewName))), viewName);
        const isHierarchyCarrier = Boolean(sheetReference || hasHierarchyOccurrenceAnnotation);
        const sheetPackage = isHierarchyCarrier ? candidateSheetPackage : undefined;
        const ports = cacheIdentity === null ?
            { scalar: [], bus: [] }
            : portInstsFromMaterializedPorts(materializedPartPorts(object, cacheIdentity?.cachePins, isHierarchyCarrier), object.dbId, physicalPadPresenceCatalog, [object.graphicName, pkgName, canonicalSourceLibraryName(cacheIdentity?.cacheSourceLibName)], physicalPinDiagnostics, isHierarchyCarrier || cacheIdentity?.hasDeclaredBounds === true);
        const libName = cacheIdentity === null ? '' : ((cacheIdentity?.sourceLibName ?? markerSourceLibraries.get(object) ?? metadata?.libName ?? ''));
        const objectPartValue = usablePlacedPartValue(object.partValue, cacheIdentity?.cachePins) ? object.partValue : undefined;
        const libraryPartValue = (usablePlacedPartValue(userProperties.find((property) => property.Name === 'Value')?.Value, cacheIdentity?.cachePins)) ?
            userProperties.find((property) => property.Name === 'Value')?.Value
            : undefined;
        const catalogPartValue = usablePlacedPartValue(catalogEntry?.partValue, cacheIdentity?.cachePins) ? catalogEntry?.partValue : undefined;
        const libraryPcbFootprint = userProperties.find((property) => property.Name === 'PCB Footprint')?.Value;
        const pageReference = pageReferenceLookup.get(object.dbId);
        reportNonReferencePlaceholder(object.displayProperties, object.dbId, physicalPinDiagnostics);
        const genericSheetReferencePrefix = sheetPackage && isGenericSheetViewName(sheetPackage.contentsViewName) ? sheetPackage.contentsViewName : undefined;
        const objectReference = pageReference ??
            (sheetPackage && (isSheetMetadataValue(object.reference, sheetPackage) || genericSheetReferencePrefix) ?
                undefined
                : object.reference);
        const { reference: recordedReference, unannotated } = recordedPlacementReference(object, [
            objectReference,
            sheetPackage?.referencePrefix ? `${sheetPackage.referencePrefix}?` : undefined,
        ]);
        const sheetContentsViewName = dbIdSheetReference?.contentsViewName ??
            (sheetPackage?.contentsViewName && !isGenericSheetViewName(sheetPackage.contentsViewName) ?
                sheetPackage.contentsViewName
                : (sheetReference?.contentsViewName ?? sheetPackage?.contentsViewName));
        const catalogSectionInstance = isCatalogSectionInstance(object, pkgName, catalogEntry);
        const graphicSectionDesignator = deviceDesignatorFromGraphicSection(object.graphicName, pkgName);
        const partValue = object.valueState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld ? ''
            : sheetPackage ?
                (metadata?.partValue ??
                    sheetPackage.partValue ??
                    objectPartValue ??
                    libraryPartValue ??
                    catalogPartValue ??
                    pkgName)
                : catalogSectionInstance ? catalogSectionPartValue(libraryPartValue, pkgName)
                    : (metadata?.partValue ?? objectPartValue ?? libraryPartValue ?? catalogPartValue ?? pkgName);
        const partInst = {
            Color: object.color,
            DbId: object.dbId,
            DeviceDesignator: metadata?.deviceDesignator ??
                (section?.state === MultipartSectionState.Withheld ? '' : section?.designator) ??
                catalogEntry?.deviceDesignator ??
                graphicSectionDesignator ??
                '',
            LibName: libName,
            LocX: object.locX,
            LocY: object.locY,
            Mirror: metadata?.mirror ?? 0,
            Name: (hasRepeatedCatalogInstanceName &&
                nearestLibraryEntry &&
                object.dbId - nearestLibraryEntry.instanceNumber <= 32) ?
                nearestLibraryEntry.record.name
                : (libraryEntry?.record.name ?? metadata?.name ?? `INS${object.dbId}`),
            PkgName: pkgName,
            PowerPinsVisible: metadata?.powerPinsVisible ?? false,
            Rotation: metadata?.rotation ?? 0,
            GraphicName: object.graphicName,
            ...(displayProperties.length > 0 ? { DisplayProperty: displayProperties } : {}),
            ...(userProperties.length > 0 ? { UserProperty: userProperties } : {}),
            ContentsLibName: nonEmpty(metadata?.contentsLibName) ?? sheetPackage?.contentsLibName ?? '',
            ContentsViewName: nonEmpty(metadata?.contentsViewName) ?? sheetContentsViewName ?? catalogEntry?.contentsViewName ?? '',
            ContentsViewType: isHierarchyCarrier ? 1 : (metadata?.contentsViewType ?? 5),
            IsPrimitiveProp: isHierarchyCarrier ? 0 : (metadata?.isPrimitiveProp ?? 2),
            PartValue: partValue,
            Reference: (object.referenceState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld ||
                (unannotated && section?.state === MultipartSectionState.ReferenceWithheld)) ?
                ''
                : recordedReference,
            ...(ports.scalar.length > 0 && section?.state !== MultipartSectionState.Withheld ?
                { PortInstScalar: ports.scalar }
                : {}),
            ...(ports.bus.length > 0 && section?.state !== MultipartSectionState.Withheld ? { PortInstBus: ports.bus } : {}),
            ...(metadata?.pcbLib || libraryPcbFootprint ? { PCBLib: metadata?.pcbLib ?? libraryPcbFootprint } : {}),
        };
        if (sheetReference?.reference) {
            partInstanceOccurrenceReferences.set(partInst, sheetReference);
        }
        return partInst;
    }
    function isCatalogSectionInstance(object, pkgName, catalogEntry) {
        const graphicPackageName = stripNormalViewSuffix(object.graphicName);
        return Boolean((catalogEntry || graphicPackageName !== pkgName) &&
            graphicPackageName !== pkgName &&
            isSectionOfCatalogPackage(graphicPackageName, pkgName));
    }
    function catalogSectionPartValue(libraryPartValue, pkgName) {
        if (libraryPartValue &&
            (libraryPartValue === pkgName ||
                (/[a-z]/.test(libraryPartValue) && !/[A-Z]\d+_/.test(libraryPartValue)) ||
                (/^[A-Z]+\d[A-Z0-9]*$/.test(libraryPartValue) && !libraryPartValue.includes('_')) ||
                (/^(?=.*[A-Z])(?=.*\d)[A-Z0-9]{5,}$/.test(libraryPartValue) && !libraryPartValue.includes('_')))) {
            return libraryPartValue;
        }
        return pkgName;
    }
    function deviceDesignatorFromGraphicSection(graphicName, pkgName) {
        const graphicPackageName = stripNormalViewSuffix(graphicName);
        if (!graphicPackageName.startsWith(pkgName)) {
            return undefined;
        }
        const suffix = graphicPackageName.slice(pkgName.length).replace(/^[-_]/, '');
        return suffix && isDeviceDesignatorToken(suffix) ? suffix : undefined;
    }
    function variantUserPropertiesForPart(catalog, displayCatalogProperties, object, pkgName) {
        const byName = new Map(displayCatalogProperties.map((property) => [property.Name, property.Value]));
        const partNumber = byName.get('Part_Number');
        const value = byName.get('Value') ?? object.partValue;
        if (!partNumber || !value) {
            return [];
        }
        const properties = catalog.get(variantPropertyKey(partNumber, value)) ?? [];
        const sourcePackage = properties.find((property) => property.Name === 'Source Package')?.Value;
        return !sourcePackage || sourcePackage === pkgName ? properties : [];
    }
    function materializedPartPorts(object, cachePins, isSheetSymbol = false) {
        const ports = applyCachePinNamesToMaterializedPorts(object.materializedPortInstances ?? [], cachePins);
        applyRecordedPortCalibrationToMissingCachePins(object, ports);
        return orderMaterializedPartPortsForXml(object, ports, isSheetSymbol);
    }
    function isPlacedPinAnchor(port) {
        return port.x !== undefined && port.y !== undefined && port.hotptX !== undefined && port.hotptY !== undefined;
    }
    function applyRecordedPortCalibrationToMissingCachePins(object, ports) {
        const anchors = ports.filter(isPlacedPinAnchor);
        if (anchors.length === 0) {
            return;
        }
        for (const port of ports) {
            if (port.x !== undefined ||
                port.y !== undefined ||
                port.source !== 'symbol-pin' ||
                port.isExplicit !== false ||
                port.hotptX === undefined ||
                port.hotptY === undefined) {
                continue;
            }
            const predictedPoints = new Map();
            for (const anchor of anchors) {
                const delta = transformPlacedPinDelta(object, port.hotptX - anchor.hotptX, port.hotptY - anchor.hotptY);
                const point = { x: anchor.x + delta.x, y: anchor.y + delta.y };
                predictedPoints.set(pagePointKey(point.x, point.y), point);
            }
            if (predictedPoints.size === 1) {
                const point = predictedPoints.values().next().value;
                if (point) {
                    port.x = point.x;
                    port.y = point.y;
                }
            }
        }
    }
    function transformPlacedPinDelta(object, x, y) {
        const mirror = object.resolvedMetadata?.mirror ?? ((object.flags & 0x04) !== 0 ? 1 : 0);
        const mirroredX = mirror === 1 ? -x : x;
        const rotation = object.resolvedMetadata?.rotation ?? object.flags & 0x03;
        return (rotation === 1 ? { x: y, y: -mirroredX }
            : rotation === 2 ? { x: -mirroredX, y: -y }
                : rotation === 3 ? { x: -y, y: mirroredX }
                    : { x: mirroredX, y });
    }
    function orderMaterializedPartPortsForXml(object, ports, isSheetSymbol) {
        if (isSheetSymbol ||
            object.resolvedMetadata?.contentsViewType === 1 ||
            isDsnSourcePath(object.resolvedMetadata?.contentsLibName ?? '')) {
            return [...ports].sort((left, right) => compareXmlTextOrder(left.name, right.name) || (left.position ?? 0) - (right.position ?? 0));
        }
        return ports;
    }
    function applyCachePinNamesToMaterializedPorts(ports, cachePins) {
        if (!cachePins?.length) {
            return [...ports];
        }
        const usedCachePositions = new Set();
        const mapped = ports.map((port, index) => {
            const cachePin = matchingCachePinForMaterializedPort(port, cachePins, index, usedCachePositions);
            return cachePin ?
                {
                    ...port,
                    position: cachePins.indexOf(cachePin),
                    name: cachePin.name,
                    type: cachePin.pinType ?? port.type,
                    hotptX: port.hotptX ?? cachePin.hotptX,
                    hotptY: port.hotptY ?? cachePin.hotptY,
                    startX: port.startX ?? cachePin.startX,
                    startY: port.startY ?? cachePin.startY,
                    isNoConnect: port.isNoConnect,
                }
                : { ...port };
        });
        const usedPositions = new Set(mapped.map((port, index) => port.position ?? index));
        for (let position = 0; position < cachePins.length; position++) {
            if (usedPositions.has(position)) {
                continue;
            }
            mapped.push(materializedPortFromCachePin(cachePins[position], position));
        }
        return mapped.sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
    }
    function materializedPortFromCachePin(pin, position) {
        return {
            name: pin.name,
            position,
            pinIndex: position + 1,
            startX: pin.startX,
            startY: pin.startY,
            hotptX: pin.hotptX,
            hotptY: pin.hotptY,
            isNoConnect: false,
            isBus: false,
            type: pin.pinType ?? DEFAULT_PIN_TYPE,
            source: 'symbol-pin',
            isExplicit: false,
        };
    }
    function matchingCachePinForMaterializedPort(port, cachePins, index, usedCachePositions) {
        if (port.source === 'record' &&
            port.isExplicit === true &&
            port.position !== undefined &&
            port.pinIndex === port.position + 1) {
            if (usedCachePositions.has(port.position)) {
                return undefined;
            }
            const recorded = cachePins[port.position];
            if (recorded) {
                usedCachePositions.add(port.position);
            }
            return recorded;
        }
        const hotptX = port.hotptX ?? port.x ?? 0;
        const hotptY = port.hotptY ?? port.y ?? 0;
        const startX = port.startX ?? hotptX;
        const startY = port.startY ?? hotptY;
        const exact = cachePins.find((pin, position) => !usedCachePositions.has(position) &&
            pin.hotptX === hotptX &&
            pin.hotptY === hotptY &&
            pin.startX === startX &&
            pin.startY === startY);
        if (exact) {
            usedCachePositions.add(cachePins.indexOf(exact));
            return exact;
        }
        const fallbackPosition = port.position ?? index;
        if (usedCachePositions.has(fallbackPosition)) {
            return undefined;
        }
        const fallback = cachePins[fallbackPosition];
        if (fallback) {
            usedCachePositions.add(fallbackPosition);
        }
        return fallback;
    }
    function buildDrawnInst(object, libraryStringCatalog, diagnostics, portDbIds) {
        const ports = drawnPortInstsFromMaterializedPorts(object.materializedPortInstances ?? [], portDbIds);
        const displayProperties = (object.displayProperties ?? []).map(drawnInstDisplayPropertyFromCfb);
        const bounds = drawnInstBoundsFromPorts(object);
        return {
            Color: object.color,
            DbId: object.dbId,
            DeviceDesignator: object.deviceDesignator ?? object.reference ?? '',
            LibName: '',
            LocX: object.locX,
            LocY: object.locY,
            Mirror: object.mirror,
            Name: drawnInstName(object, diagnostics),
            Rotation: object.rotation,
            X1: bounds.X1,
            X2: bounds.X2,
            Y1: bounds.Y1,
            Y2: bounds.Y2,
            ...(displayProperties.length > 0 ? { DisplayProperty: displayProperties } : {}),
            ContentsLibName: drawnInstContentsLibName(object, libraryStringCatalog),
            ContentsViewName: object.contentsViewName ?? '',
            ContentsViewType: object.contentsViewType ?? 1,
            IsPrimitiveProp: object.isPrimitiveProp ?? 2,
            PartValue: object.partValue ?? object.contentsViewName ?? object.name ?? '',
            Reference: object.reference ?? object.deviceDesignator ?? '',
            ...(ports.scalar.length > 0 ? { PortInstScalar: ports.scalar } : {}),
            ...(ports.bus.length > 0 ? { PortInstBus: ports.bus } : {}),
        };
    }
    function drawnInstContentsLibName(object, libraryStringCatalog) {
        const decoded = object.contentsLibName;
        if (decoded !== undefined && decoded.length > 0) {
            return decoded;
        }
        return externalSchematicPathForView(libraryStringCatalog, object.contentsViewName) ?? '';
    }
    function drawnInstName(object, diagnostics) {
        const name = object.name ?? object.deviceDesignator;
        if (name === undefined) {
            emitBudgetedDiagnostic(diagnostics, `${object.dbId}`, 'sheet-symbol-name-undecoded', `dbId=${object.dbId} contentsViewName="${object.contentsViewName ?? ''}"`, 'CFB sheet symbol', 'warning');
            return '';
        }
        if (!name.includes(' ') || name !== name.toUpperCase() || /\d/.test(name)) {
            return name;
        }
        return name.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
    }
    function externalSchematicPathForView(libraryStringCatalog, contentsViewName) {
        if (!contentsViewName) {
            return undefined;
        }
        const expected = `${contentsViewName}.DSN`;
        const candidates = libraryStringCatalog
            .map((entry) => entry.value)
            .filter((value) => value.toUpperCase().endsWith(expected.toUpperCase()))
            .sort((left, right) => externalSchematicPathScore(right, contentsViewName) - externalSchematicPathScore(left, contentsViewName));
        return candidates[0];
    }
    function externalSchematicPathScore(value, contentsViewName) {
        let score = 0;
        if (value.startsWith('.\\')) {
            score += 100;
        }
        if (value.includes(`\\${contentsViewName}\\`)) {
            score += 20;
        }
        if (value === `.\\${contentsViewName}\\${contentsViewName}.DSN`) {
            score += 10;
        }
        score -= value.length / 100;
        return score;
    }
    function isSheetMetadataValue(value, sheetPackage) {
        const key = normalizeSheetReferenceToken(value);
        return Boolean(key &&
            (areSheetReferenceKeysCompatible(key, normalizeSheetReferenceToken(sheetPackage.contentsViewName)) ||
                areSheetReferenceKeysCompatible(key, normalizeSheetReferenceToken(sheetPackage.partValue))));
    }
    function sheetPackageFromDisplayCatalog(object, libraryStrings, exactSheetPackage, diagnostics) {
        const indexes = [
            object.referenceTail?.valueStringIndex,
            ...(object.displayProperties ?? []).map((property) => property.libraryStringIndex),
        ].filter((index) => index !== undefined && index > 0 && index < libraryStrings.length);
        for (const index of indexes) {
            const metadata = sheetPackageFromLibraryStringIndex(libraryStrings, index, object, exactSheetPackage, diagnostics);
            if (metadata) {
                return metadata;
            }
        }
        return undefined;
    }
    function sheetPackageFromLibraryStringIndex(libraryStrings, index, object, exactSheetPackage, diagnostics) {
        const indexedValue = libraryStrings[index]?.value;
        const indexRecord = libraryRecordBounds(libraryStrings, index);
        if (indexRecord.truncated) {
            return undefined;
        }
        const packageName = stripNormalViewSuffix(object.graphicName);
        const owned = ownedLibraryDsnPath(libraryStrings, index, indexRecord, packageName, exactSheetPackage, diagnostics);
        if (!owned) {
            return undefined;
        }
        const { dsnIndex, contentsLibName, pathViewName, ownedViewName } = owned;
        const pathRecord = libraryRecordBounds(libraryStrings, dsnIndex);
        const { partValue } = librarySheetPartValueInRecord(libraryStrings, dsnIndex, pathRecord);
        if (exactSheetPackage?.contentsLibName &&
            isDsnSourcePath(exactSheetPackage.contentsLibName) &&
            childDsnPathMatchesView(exactSheetPackage.contentsLibName, exactSheetPackage.contentsViewName) &&
            !isSameDsnFileName(exactSheetPackage.contentsLibName, contentsLibName)) {
            return undefined;
        }
        const exactSheetViewName = exactSheetPackage?.contentsViewName && !isGenericSheetViewName(exactSheetPackage.contentsViewName) ?
            exactSheetPackage.contentsViewName
            : undefined;
        const contentsViewName = ownedViewName ?? exactSheetViewName ?? exactSheetPackage?.contentsViewName ?? pathViewName;
        const indexedSheetViewName = indexedValue && isDisplaySheetViewAliasForPath(indexedValue, pathViewName) ? indexedValue : undefined;
        return {
            packageName,
            contentsLibName,
            contentsViewName: indexedSheetViewName ?? contentsViewName,
            ...(exactSheetPackage?.referencePrefix ? { referencePrefix: exactSheetPackage.referencePrefix } : {}),
            ...(partValue ? { partValue } : {}),
        };
    }
    function ownedLibraryDsnPath(libraryStrings, index, indexRecord, packageName, exactSheetPackage, diagnostics) {
        const indexedValue = libraryStrings[index]?.value;
        const owners = [];
        const windowStart = Math.max(0, index - LIBRARY_DISPLAY_PATH_WINDOW);
        const windowEnd = Math.min(libraryStrings.length, index + LIBRARY_DISPLAY_PATH_WINDOW + 1);
        for (let candidate = windowStart; candidate < windowEnd; candidate++) {
            const rawPath = libraryStrings[candidate]?.value;
            if (!isDsnSourcePath(rawPath)) {
                continue;
            }
            const contentsLibName = preferredProjectModuleDsnPath(libraryStrings, rawPath);
            const pathViewName = contentsLibName ? dsnViewNameFromPath(contentsLibName) : undefined;
            if (!contentsLibName || !pathViewName) {
                continue;
            }
            const namedByExactPath = exactSheetPackage?.contentsLibName !== undefined &&
                isDsnSourcePath(exactSheetPackage.contentsLibName) &&
                isSameDsnFileName(exactSheetPackage.contentsLibName, contentsLibName);
            const namedByIndexedValue = Boolean(indexedValue && isDisplaySheetViewAliasForPath(indexedValue, pathViewName));
            const namedByPackageName = isSameChildDesign(packageName, pathViewName);
            const namedByExactView = exactSheetPackage?.contentsViewName !== undefined &&
                isSameChildDesign(exactSheetPackage.contentsViewName, pathViewName);
            const namedByPackage = namedByPackageName ? true : namedByExactView;
            const recordViewName = libraryStrings
                .slice(indexRecord.start, indexRecord.end)
                .map((row) => row.value)
                .find((value) => value !== rawPath && isDisplaySheetViewAliasForPath(value, pathViewName));
            const rank = namedByExactPath ? 0
                : namedByIndexedValue ? 1
                    : namedByPackage ? 2
                        : recordViewName ? 3
                            : undefined;
            if (rank === undefined) {
                continue;
            }
            const ownedViewName = namedByIndexedValue ? indexedValue : recordViewName;
            owners.push({
                dsnIndex: candidate,
                contentsLibName,
                pathViewName,
                ...(ownedViewName ? { ownedViewName } : {}),
                rank,
                distance: Math.abs(candidate - index),
            });
        }
        if (owners.length === 0) {
            return undefined;
        }
        owners.sort((left, right) => left.rank - right.rank || left.distance - right.distance);
        const best = owners[0];
        const rival = owners.find((owner) => owner.rank === best.rank &&
            owner.distance === best.distance &&
            !isSameChildDesign(owner.contentsLibName, best.contentsLibName));
        if (!rival) {
            return best;
        }
        emitBudgetedDiagnostic(diagnostics, `${packageName}\0${index}`, 'sheet-owner-ambiguous', `the display row names "${best.pathViewName}" and "${rival.pathViewName}" on the same evidence and at the same distance, so it names neither`, 'Sheet symbol owner ambiguous', 'warning');
        return undefined;
    }
    const LIBRARY_DISPLAY_PATH_WINDOW = 8;
    function preferredProjectModuleDsnPath(libraryStrings, rawPath, diagnostics) {
        if (!rawPath) {
            return undefined;
        }
        const basename = pathBasename(rawPath).toLowerCase();
        const projectLocalPaths = new Map();
        for (const entry of libraryStrings) {
            const path = entry.value.replace(/\//g, '\\');
            if (!/^\.\\modules\\/i.test(path) || pathBasename(path).toLowerCase() !== basename) {
                continue;
            }
            const key = collapsedWindowsPath(path).toLowerCase();
            if (!projectLocalPaths.has(key)) {
                projectLocalPaths.set(key, path);
            }
        }
        const [projectLocalPath] = projectLocalPaths.values();
        if (projectLocalPaths.size === 1 && projectLocalPath !== undefined) {
            return canonicalChildDsnPath(projectLocalPath);
        }
        if (diagnostics) {
            emitBudgetedDiagnostic(diagnostics, basename, projectLocalPaths.size === 0 ? 'child-path-unresolved' : 'child-path-ambiguous', `recorded="${rawPath}" registered=${projectLocalPaths.size}`, 'CFB hierarchy', 'warning');
        }
        return canonicalChildDsnPath(rawPath);
    }
    function collapsedWindowsPath(path) {
        const resolved = [];
        for (const segment of path.split('\\')) {
            if (segment === '.') {
                continue;
            }
            if (segment === '..') {
                const previous = resolved[resolved.length - 1];
                if (previous === undefined || previous === '..') {
                    resolved.push(segment);
                    continue;
                }
                resolved.pop();
                continue;
            }
            const trimmed = segment.replace(/[. ]+$/, '');
            if (trimmed === '' && segment !== '') {
                continue;
            }
            resolved.push(trimmed);
        }
        return resolved.join('\\');
    }
    function shouldPreferDisplaySheetPackage(displaySheetPackage, exactSheetPackage) {
        if (!displaySheetPackage || !exactSheetPackage) {
            return Boolean(displaySheetPackage);
        }
        if (isGenericSheetViewName(exactSheetPackage.contentsViewName)) {
            return true;
        }
        if (displaySheetPackage.contentsViewName === exactSheetPackage.contentsViewName) {
            return false;
        }
        if (areSheetReferenceKeysCompatible(normalizeSheetReferenceToken(displaySheetPackage.contentsViewName), normalizeSheetReferenceToken(exactSheetPackage.contentsViewName))) {
            return false;
        }
        return /\d/.test(displaySheetPackage.contentsViewName) && /\d/.test(exactSheetPackage.contentsViewName);
    }
    const UNNAMED_CONTENTS_VIEW = '';
    function isGenericSheetViewName(value) {
        return (value === UNNAMED_CONTENTS_VIEW ||
            (value !== undefined && value.toUpperCase() === OrcadDsnCfbMaterializer_1.PENDING_CONTENTS_VIEW_NAME.toUpperCase()));
    }
    function isDisplaySheetViewAliasForPath(value, pathViewName) {
        return areSheetReferenceKeysCompatible(normalizeSheetReferenceToken(value), normalizeSheetReferenceToken(pathViewName));
    }
    function hierarchySheetReferenceForDbId(catalog, objectDbId, viewName) {
        if (objectDbId === undefined) {
            return undefined;
        }
        return catalog.references.find((reference, index) => !catalog.usedIndexes.has(index) &&
            reference.dbId === objectDbId &&
            (reference.parentViewName === undefined || reference.parentViewName.toUpperCase() === viewName.toUpperCase()));
    }
    function consumeHierarchySheetReference(catalog, sheetPackage, pkgName, objectDbId, object, diagnostics, allowPackageFallback = true, viewName) {
        if (objectDbId !== undefined) {
            const annotationIndexes = [];
            for (let index = 0; index < catalog.references.length; index++) {
                if (catalog.usedIndexes.has(index)) {
                    continue;
                }
                const reference = catalog.references[index];
                if (reference.dbId !== objectDbId ||
                    (reference.parentViewName !== undefined && reference.parentViewName.toUpperCase() !== viewName?.toUpperCase())) {
                    continue;
                }
                if (!reference.isOccurrenceAnnotation &&
                    sheetPackage &&
                    (allowPackageFallback ||
                        areSheetReferenceKeysCompatible(reference.contentsViewName, sheetPackage.contentsViewName))) {
                    const claimKey = hierarchySheetClaimKey(sheetPackage, pkgName);
                    return claimHierarchySheetReference(catalog, index, claimKey);
                }
                if (reference.isOccurrenceAnnotation) {
                    annotationIndexes.push(index);
                }
            }
            if (annotationIndexes.length > 0) {
                const views = new Set(annotationIndexes.map((index) => catalog.references[index].contentsViewName.toUpperCase()));
                const annotation = catalog.references[annotationIndexes[0]];
                const namedPorts = new Set((object?.materializedPortInstances ?? [])
                    .map((port) => port.name.trim().toUpperCase())
                    .filter((name) => name.length > 0));
                const hasExplicitTarget = Boolean(sheetPackage &&
                    isDsnSourcePath(sheetPackage.contentsLibName) &&
                    areSheetReferenceKeysCompatible(sheetPackage.contentsViewName, annotation.contentsViewName));
                if (views.size === 1 && hasExplicitTarget && namedPorts.size > 0 && sheetPackage) {
                    return claimHierarchySheetReference(catalog, annotationIndexes[0], hierarchySheetClaimKey(sheetPackage, pkgName));
                }
                if (diagnostics) {
                    emitBudgetedDiagnostic(diagnostics, String(objectDbId), views.size > 1 ? 'hierarchy-annotation-ambiguous' : 'hierarchy-annotation-unresolved', `dbId=${objectDbId} package="${pkgName}" views=${Array.from(views).sort().join(',')} target=${hasExplicitTarget ? 'present' : 'missing'} ports=${namedPorts.size}`, 'CFB hierarchy', 'warning');
                }
                return undefined;
            }
        }
        if (!sheetPackage) {
            return undefined;
        }
        if (!allowPackageFallback) {
            return undefined;
        }
        const claimKey = hierarchySheetClaimKey(sheetPackage, pkgName);
        const claimOrdinal = (catalog.claimCounts.get(claimKey) ?? 0) + 1;
        if (isGenericSheetViewName(sheetPackage.contentsViewName)) {
            const moduleKeys = [pkgName, sheetPackage.packageName, sheetPackage.partValue]
                .map((name) => normalizedSheetModuleInstanceKey(name))
                .filter((key) => key !== '');
            const matchingIndexes = [];
            for (let index = 0; index < catalog.references.length; index++) {
                if (catalog.usedIndexes.has(index)) {
                    continue;
                }
                const reference = catalog.references[index];
                if (reference.isOccurrenceAnnotation ||
                    (reference.parentViewName !== undefined && reference.parentViewName.toUpperCase() !== viewName?.toUpperCase())) {
                    continue;
                }
                const referenceKey = normalizedSheetModuleInstanceKey(reference.reference);
                if (moduleKeys.some((key) => areSheetReferenceKeysCompatible(key, referenceKey))) {
                    matchingIndexes.push(index);
                }
            }
            if (matchingIndexes.length === 0 && diagnostics) {
                emitPhysicalPinDiagnostic(diagnostics, pkgName, 'sheet-reference-module-unregistered', `package="${pkgName}" modules="${moduleKeys.join(',')}"`);
            }
            const selectedIndex = selectHierarchySheetReferenceIndex(catalog, matchingIndexes, sheetPackage, pkgName, claimOrdinal);
            if (selectedIndex !== undefined) {
                return claimHierarchySheetReference(catalog, selectedIndex, claimKey);
            }
        }
        const packageKey = normalizeSheetReferenceToken(sheetPackage.contentsViewName);
        const matchingIndexes = [];
        for (let index = 0; index < catalog.references.length; index++) {
            if (catalog.usedIndexes.has(index)) {
                continue;
            }
            const reference = catalog.references[index];
            if (reference.isOccurrenceAnnotation ||
                (reference.parentViewName !== undefined && reference.parentViewName.toUpperCase() !== viewName?.toUpperCase())) {
                continue;
            }
            const referenceKey = normalizeSheetReferenceToken(reference.contentsViewName);
            if (areSheetReferenceKeysCompatible(referenceKey, packageKey)) {
                matchingIndexes.push(index);
            }
        }
        const selectedIndex = selectHierarchySheetReferenceIndex(catalog, matchingIndexes, sheetPackage, pkgName, claimOrdinal);
        return selectedIndex !== undefined ? claimHierarchySheetReference(catalog, selectedIndex, claimKey) : undefined;
    }
    function claimHierarchySheetReference(catalog, index, claimKey) {
        catalog.usedIndexes.add(index);
        catalog.claimCounts.set(claimKey, (catalog.claimCounts.get(claimKey) ?? 0) + 1);
        return catalog.references[index];
    }
    function selectHierarchySheetReferenceIndex(catalog, indexes, sheetPackage, pkgName, claimOrdinal) {
        if (indexes.length === 0) {
            return undefined;
        }
        return (indexes.find((index) => hierarchyReferenceOrdinal(catalog.references[index].reference) === claimOrdinal &&
            hierarchyReferenceMatchesSheetPackage(catalog.references[index], sheetPackage, pkgName)) ??
            indexes.find((index) => hierarchyReferenceOrdinal(catalog.references[index].reference) === claimOrdinal) ??
            indexes.find((index) => hierarchyReferenceMatchesSheetPackage(catalog.references[index], sheetPackage, pkgName)) ??
            indexes[0]);
    }
    function hierarchySheetClaimKey(sheetPackage, pkgName) {
        const candidates = [
            pathBasename(sheetPackage.contentsLibName),
            sheetPackage.packageName,
            pkgName,
            sheetPackage.contentsViewName,
        ].map(normalizeSheetReferenceToken);
        return candidates.find((candidate) => candidate.length > 0) ?? '';
    }
    function hierarchyReferenceOrdinal(reference) {
        const match = /(\d+)$/.exec(reference);
        return match ? Number.parseInt(match[1], 10) : undefined;
    }
    function hierarchyReferenceMatchesSheetPackage(reference, sheetPackage, pkgName) {
        const referenceKey = normalizeSheetReferenceToken(reference.reference).replace(/\d+$/, '');
        if (!referenceKey) {
            return false;
        }
        const packageKeys = [
            pathBasename(sheetPackage.contentsLibName),
            sheetPackage.packageName,
            pkgName,
            sheetPackage.contentsViewName,
        ]
            .map((value) => normalizeSheetReferenceToken(value).replace(/\d+$/, ''))
            .filter(Boolean);
        return packageKeys.some((packageKey) => packageKey.includes(referenceKey) ||
            referenceKey.includes(packageKey) ||
            areSheetReferenceKeysCompatible(packageKey, referenceKey));
    }
    function findSheetSymbolPackageMetadata(catalog, pkgName) {
        if (!pkgName) {
            return undefined;
        }
        const exact = exactSheetSymbolPackageMetadata(catalog, pkgName);
        if (exact) {
            return exact;
        }
        const pkgKey = normalizeSheetReferenceToken(pkgName);
        return Array.from(catalog.values()).find((candidate) => {
            if (candidate.contentsViewName === UNNAMED_CONTENTS_VIEW) {
                return false;
            }
            const packageKey = normalizeSheetReferenceToken(candidate.packageName);
            const viewKey = normalizeSheetReferenceToken(candidate.contentsViewName);
            if (areSheetReferenceKeysCompatible(pkgKey, packageKey)) {
                return true;
            }
            return areSheetReferenceKeysCompatible(pkgKey, viewKey);
        });
    }
    function exactSheetSymbolPackageMetadata(catalog, pkgName) {
        if (!pkgName) {
            return undefined;
        }
        return catalog.get(pkgName) ?? catalog.get(stripNormalViewSuffix(pkgName));
    }
    function sheetReferenceKeysMatchDirectly(left, right) {
        if (!left || !right) {
            return false;
        }
        if (left === right) {
            return true;
        }
        return left.length >= 5 && right.length >= 5 && (left.startsWith(right) || right.startsWith(left));
    }
    function compactSheetReferenceKeysMatch(compactLeft, compactRight) {
        if (compactLeft === compactRight) {
            return true;
        }
        return compactLeft.startsWith(compactRight) || compactRight.startsWith(compactLeft);
    }
    function areSheetReferenceKeysCompatible(left, right) {
        if (sheetReferenceKeysMatchDirectly(left, right)) {
            return true;
        }
        if (!left || !right) {
            return false;
        }
        return compactSheetReferenceKeysMatch(left.replace(/PORT/g, ''), right.replace(/PORT/g, ''));
    }
    function normalizedSheetModuleInstanceKey(value) {
        return normalizeSheetReferenceToken(value).replace(/^X\d+/, '').replace(/\d+$/, '');
    }
    function normalizeSheetReferenceToken(value) {
        return (value ?? '')
            .trim()
            .replace(/\.(?:dsn|xml)$/i, '')
            .replace(/\.(?:Normal|IEEE|Convert)$/i, '')
            .replace(/[^A-Za-z0-9]+/g, '')
            .toUpperCase();
    }
    function nonEmpty(value) {
        if (!value || value === OrcadDsnCfbMaterializer_1.PENDING_CONTENTS_VIEW_NAME) {
            return undefined;
        }
        return value;
    }
    function buildTitleBlock(object, pageCount) {
        const displayProperties = (object.displayProperties ?? []).map(titleBlockDisplayPropertyFromCfb);
        const userProperties = userPropertiesFromCfb(object.userProperties).sort((left, right) => left.Name < right.Name ? -1
            : left.Name > right.Name ? 1
                : 0);
        return {
            Color: object.color,
            DbId: object.dbId,
            LibName: object.resolvedMetadata?.libName ?? '',
            LocX: object.locX,
            LocY: object.locY,
            Name: object.symbolName,
            Rotation: object.resolvedMetadata?.rotation ?? 0,
            SymbolName: object.symbolName,
            KeepInLowerRightCorner: true,
            PageCount: pageCount,
            PageNumber: 0,
            ...(displayProperties.length > 0 ? { DisplayProperty: displayProperties } : {}),
            ...(userProperties.length > 0 ? { UserProperty: userProperties } : {}),
        };
    }
    function buildGlobal(object, pageObjectNetNames = new Map(), diagnostics) {
        const displayProperties = (object.displayProperties ?? []).map((property) => globalDisplayPropertyFromCfb(property, diagnostics));
        const attachedNetName = pageObjectNetNames.get(object.dbId);
        const name = object.symbolName === '0' ?
            object.symbolName
            : firstPlausiblePlacedSymbolNetName(object, [attachedNetName, object.symbolName], diagnostics);
        const normalizedDisplayProperties = addSyntheticIrefDisplayProperty(displayProperties, object, name, literalIrefValue(object), diagnostics);
        const userProperties = userPropertiesFromCfb(object.userProperties ?? []);
        if (object.symbolName === '0' &&
            attachedNetName &&
            plausibleNetNameCandidate(object, attachedNetName, diagnostics)) {
            userProperties.push({ Name: 'NODENAME', Value: attachedNetName });
        }
        return {
            Color: object.color,
            DbId: object.dbId,
            LibName: object.resolvedMetadata?.libName ?? '',
            LocX: object.locX,
            LocY: object.locY,
            Mirror: object.resolvedMetadata?.mirror ?? 0,
            Name: name,
            Rotation: object.resolvedMetadata?.rotation ?? 0,
            SymbolName: object.symbolName,
            ...(normalizedDisplayProperties[0] ? { DisplayProperty: normalizedDisplayProperties[0] } : {}),
            ...(userProperties.length ? { UserProperty: userProperties } : {}),
        };
    }
    function drawnInstBoundsFromPorts(object) {
        const ports = object.materializedPortInstances ?? [];
        if (ports.length === 0) {
            return {
                X1: object.relativeDisplayX1,
                X2: object.relativeDisplayX2,
                Y1: object.relativeDisplayY1,
                Y2: object.relativeDisplayY2,
            };
        }
        const maxPortY = Math.max(0, ...ports.map((port) => port.hotptY ?? port.startY ?? 0));
        let snappedY2 = Math.floor(object.relativeDisplayY2 / 10) * 10;
        if (snappedY2 - maxPortY <= 10) {
            snappedY2 = maxPortY;
        }
        else if (maxPortY <= snappedY2 - 50) {
            snappedY2 -= 10;
        }
        return {
            X1: Math.min(0, ...ports.map((port) => port.hotptX ?? port.startX ?? 0)),
            X2: Math.max(0, ...ports.map((port) => port.hotptX ?? port.startX ?? 0), Math.floor(object.relativeDisplayX2 / 10) * 10),
            Y1: Math.min(0, ...ports.map((port) => port.hotptY ?? port.startY ?? 0)),
            Y2: Math.max(maxPortY, snappedY2),
        };
    }
    function buildDrawnInstPortDbIdLookup(objects, diagnostics) {
        const ids = new WeakMap();
        for (const owner of objects.filter(isDrawnInst)) {
            const frame = owner.recordFrame;
            const connections = objects
                .filter(isPortInstance)
                .filter((pin) => frame !== undefined && pin.offset >= frame.offset && pin.offset < frame.endOffset);
            const ports = owner.materializedPortInstances ?? [];
            const candidates = ports.map((port) => connections.filter((pin) => owner.rotation === 0 &&
                owner.mirror === 0 &&
                port.hotptX !== undefined &&
                port.hotptY !== undefined &&
                pin.x === owner.locX + port.hotptX &&
                pin.y === owner.locY + port.hotptY));
            ids.set(owner, candidates.map((matches, index) => {
                const pin = matches.length === 1 ? matches[0] : undefined;
                if (pin &&
                    candidates.filter((other) => other.some((candidate) => candidate.pinIndex === pin.pinIndex)).length === 1) {
                    return owner.dbId * 2 ** 16 + pin.pinIndex;
                }
                emitDecisionDiagnostic(diagnostics, 'drawn-port-identity-withheld', `owner=${owner.dbId} name=${JSON.stringify(ports[index].name)}: no unique framed local pin identity`, 'CFB drawing port', 'warning');
                return undefined;
            }));
        }
        return ids;
    }
    function buildOffPageConnector(object, pageObjectNetNames = new Map(), objects = [], diagnostics) {
        const displayProperties = connectorDisplayPropertiesFromCfb(object.displayProperties, object.dbId, diagnostics);
        const name = firstPlausiblePlacedSymbolNetName(object, [
            pageObjectNetNames.get(object.dbId),
            object.reference,
            object.partValue,
            object.resolvedMetadata?.reference,
            object.resolvedMetadata?.partValue,
        ], diagnostics);
        const namedDisplayProperties = addSyntheticNameDisplayProperty(displayProperties, object, name, diagnostics);
        const normalizedDisplayProperties = addSyntheticIrefDisplayProperty(namedDisplayProperties, object, name, literalIrefValue(object), diagnostics);
        const userProperties = userPropertiesFromCfb(object.userProperties);
        return {
            Color: object.color,
            DbId: object.dbId,
            LibName: object.resolvedMetadata?.libName ?? '',
            LocX: object.locX,
            LocY: object.locY,
            Mirror: object.resolvedMetadata?.mirror ?? 0,
            Name: name,
            Rotation: object.resolvedMetadata?.rotation ??
                (/^OFFPAGELEFT-R$/i.test(object.symbolName) ? inferOffPageConnectorRotation(object, objects) : 0),
            SymbolName: object.symbolName,
            ...(normalizedDisplayProperties.length > 0 ? { OffPageConnectorDisplayProp: normalizedDisplayProperties } : {}),
            ...(userProperties.length ? { OffPageConnectorUserProp: userProperties } : {}),
        };
    }
    function inferOffPageConnectorRotation(object, objects) {
        if (!/^OFFPAGELEFT-R$/i.test(object.symbolName)) {
            return 0;
        }
        const wires = objects.filter(isWire);
        if (findExactPointOwner(object.locX, object.locY + 10, wires)) {
            return 2;
        }
        return 0;
    }
    function buildPort(object, pageObjectNetNames = new Map(), diagnostics) {
        const displayProperties = connectorDisplayPropertiesFromCfb(object.displayProperties, object.dbId, diagnostics);
        const name = firstPlausiblePlacedSymbolNetName(object, [
            pageObjectNetNames.get(object.dbId),
            object.reference,
            object.partValue,
            object.resolvedMetadata?.reference,
            object.resolvedMetadata?.partValue,
        ], diagnostics);
        const namedDisplayProperties = addSyntheticNameDisplayProperty(displayProperties, object, name, diagnostics);
        const enrichedDisplayProperties = addSyntheticIrefDisplayProperty(namedDisplayProperties, object, name, literalIrefValue(object), diagnostics);
        const userProperties = userPropertiesFromCfb(object.userProperties);
        const rotation = object.resolvedMetadata?.rotation ?? 0;
        const portType = portTypeFromSymbolName(object.symbolName);
        return {
            Color: object.color,
            DbId: object.dbId,
            LibName: object.resolvedMetadata?.libName ?? '',
            LocX: object.locX,
            LocY: object.locY,
            Mirror: object.resolvedMetadata?.mirror ?? 0,
            Name: name,
            Rotation: rotation,
            SymbolName: object.symbolName,
            ...(portType === undefined ? {} : { Type: portType }),
            ...(enrichedDisplayProperties.length > 0 ? { PortDisplayProp: enrichedDisplayProperties } : {}),
            ...(userProperties.length ? { PortUserProp: userProperties } : {}),
        };
    }
    function portTypeFromSymbolName(symbolName) {
        if (/^PORTNO-/i.test(symbolName)) {
            return 7;
        }
        return undefined;
    }
    function compareNamedPageObjects(left, right) {
        const byName = compareOrcadName(left.Name, right.Name);
        return byName || left.DbId - right.DbId;
    }
    function compareOrcadName(left, right) {
        return (left < right ? -1
            : left > right ? 1
                : 0);
    }
    function comparePageObjectDbId(left, right) {
        return left.dbId - right.dbId;
    }
    function buildOccurrences(schematics, rootSchematicName, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, occurrenceNetSystemIds, schematicViewNames, diagnostics) {
        const rootSchematic = schematics.find((schematic) => schematic.Name === rootSchematicName) ?? schematics[0];
        const rootOccurrence = {
            RefInstId: '-1',
            TitleBlockOccurrence: rootSchematic ? buildTitleBlockOccurrencesForSchematic(rootSchematic) : [],
            InstOccurrence: rootSchematic ?
                buildChildOccurrencesForSchematic(rootSchematic, schematics, new Set(), partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, schematicViewNames)
                : [],
        };
        const rootView = rootSchematic === undefined ? undefined : schematicViewNames.get(rootSchematic);
        const roots = rootView === undefined ? [] : (hierarchyOccurrenceCatalog.rootsByView.get(rootView.toUpperCase()) ?? []);
        if (roots.length === 1) {
            applyHierarchyOccurrencePath(rootOccurrence, roots[0], hierarchyOccurrenceCatalog, diagnostics);
        }
        else if (roots.length > 1) {
            emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', 'occurrence root has multiple counted paths', 'CFB hierarchy occurrence', 'warning');
        }
        attachOccurrenceNetSystemIds(rootOccurrence, occurrenceNetSystemIds);
        return { InstOccurrence: [rootOccurrence] };
    }
    function applyHierarchyOccurrencePath(occurrence, branch, catalog, diagnostics) {
        if (branch.occurrenceId !== undefined) {
            occurrence.UserProperty = [
                ...(occurrence.UserProperty ?? []).filter((property) => property.Name !== 'ID'),
                { Name: 'ID', Value: String(branch.occurrenceId) },
            ];
        }
        const children = occurrence.InstOccurrence ?? [];
        const byPlacement = catalog.referencesByBranch.get(JSON.stringify([branch.streamPath, branch.offset]));
        for (const [id, rows] of byPlacement ?? []) {
            if (withholdHierarchyAdoption(diagnostics, catalog.trust, branch.streamPath, branch.viewName, id, 'occurrence-path', branch.offset)) {
                continue;
            }
            const reference = agreeingHierarchyAnnotation(rows, diagnostics, JSON.stringify([branch.streamPath, branch.offset, id]));
            const matches = children.filter((child) => child.RefInstId === String(reference.refInstId));
            if (matches.length > 1) {
                emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(branch.streamPath)}, branch=${branch.offset}, dbId=${reference.refInstId}: occurrence placement is not unique`, 'CFB hierarchy occurrence', 'warning');
                continue;
            }
            if (matches.length === 0) {
                children.push(instOccurrenceFromHierarchyReference(reference));
            }
            else {
                const properties = instOccurrenceFromHierarchyReference(reference).UserProperty ?? [];
                matches[0].UserProperty = [
                    ...(matches[0].UserProperty ?? []).filter((property) => !properties.some((replacement) => replacement.Name === property.Name)),
                    ...properties,
                ];
            }
        }
        occurrence.InstOccurrence = children;
        for (const child of children) {
            const paths = catalog.childrenByParentAndPlacement.get(JSON.stringify([branch.streamPath, branch.offset, child.RefInstId])) ??
                [];
            if (paths.length === 1) {
                applyHierarchyOccurrencePath(child, paths[0], catalog, diagnostics);
            }
            else if (paths.length > 1) {
                emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(branch.streamPath)}, branch=${branch.offset}, dbId=${child.RefInstId}: occurrence child has multiple counted paths`, 'CFB hierarchy occurrence', 'warning');
            }
        }
    }
    function attachOccurrenceNetSystemIds(occurrence, catalog) {
        const dbId = Number.parseInt(occurrence.RefInstId, 10);
        const byName = Number.isNaN(dbId) ? undefined : catalog.get(dbId);
        if (byName && byName.size > 0) {
            occurrence.UserProperty = [
                ...(occurrence.UserProperty ?? []),
                ...Array.from(byName, ([name, id]) => ({
                    Name: `${LogicalSchema_1.OrCadDSN.OCCURRENCE_NET_SYSTEM_ID_PROP_PREFIX}${name}`,
                    Value: String(id),
                })),
            ];
        }
        for (const child of occurrence.InstOccurrence ?? []) {
            attachOccurrenceNetSystemIds(child, catalog);
        }
    }
    function buildChildOccurrencesForSchematic(schematic, allSchematics, visited, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, schematicViewNames) {
        const occurrences = [];
        const viewName = schematicViewNames.get(schematic);
        if (viewName === undefined) {
            throw new Error('Converted schematic has no decoded folder identity');
        }
        for (const page of schematic.Page) {
            for (const drawnInst of page.DrawnInst ?? []) {
                if (!drawnInst.ContentsViewName && !drawnInst.ContentsLibName) {
                    continue;
                }
                const target = targetSchematicForSheetReference(drawnInst, allSchematics);
                const key = `drawn:${drawnInst.DbId}:${target?.Name ?? ''}`;
                const nextVisited = new Set(visited);
                nextVisited.add(key);
                const children = target && !visited.has(key) ?
                    buildChildOccurrencesForSchematic(target, allSchematics, nextVisited, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, schematicViewNames)
                    : [];
                occurrences.push(instOccurrenceFromDrawnInst(drawnInst, target, children, page));
            }
            for (const partInst of page.PartInst ?? []) {
                const target = partInst.ContentsViewType === 1 ? targetSchematicForSheetReference(partInst, allSchematics) : undefined;
                const key = `part:${partInst.DbId}:${target?.Name ?? ''}`;
                const nextVisited = new Set(visited);
                nextVisited.add(key);
                const children = target && !visited.has(key) ?
                    buildChildOccurrencesForSchematic(target, allSchematics, nextVisited, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, schematicViewNames)
                    : [];
                const occurrenceReference = partInstanceOccurrenceReferences.get(partInst);
                occurrences.push(instOccurrenceFromPartInst(partInst, target, children, page, occurrenceReference));
            }
        }
        return occurrences;
    }
    function instOccurrenceFromHierarchyReference(reference) {
        return {
            RefInstId: String(reference.refInstId),
            UserProperty: [
                ...(reference.occurrenceId !== undefined ? [{ Name: 'ID', Value: String(reference.occurrenceId) }] : []),
                { Name: 'Part Reference', Value: reference.reference },
                { Name: 'Reference', Value: reference.reference },
            ],
            InstOccurrence: [],
        };
    }
    function targetSchematicForSheetReference(instance, allSchematics) {
        const exact = allSchematics.find((candidate) => candidate.Name === instance.ContentsViewName);
        if (exact) {
            return exact;
        }
        const nameTokens = [instance.Name, instance.PartValue, instance.ContentsViewName].filter(Boolean).join(' ');
        if (/pwm/i.test(nameTokens)) {
            const pwmTargets = allSchematics.filter((candidate) => /pwm/i.test(candidate.Name));
            if (pwmTargets.length === 1) {
                return pwmTargets[0];
            }
        }
        return undefined;
    }
    function storedPageNumber(titleBlocks) {
        const numbers = new Set();
        for (const titleBlock of titleBlocks ?? []) {
            for (const property of titleBlock.UserProperty ?? []) {
                if (property.Name !== 'Page Number') {
                    continue;
                }
                if (!/^\+?\d+$/.test(property.Value.trim())) {
                    return undefined;
                }
                const parsed = Number.parseInt(property.Value, 10);
                if (parsed <= 0) {
                    return undefined;
                }
                numbers.add(parsed);
            }
        }
        const [only] = numbers;
        return numbers.size === 1 && only !== undefined ? only : undefined;
    }
    function buildTitleBlockOccurrencesForSchematic(schematic) {
        const occurrences = [];
        for (const page of schematic.Page) {
            const pageNumber = storedPageNumber(page.TitleBlock);
            occurrences.push({
                UserProperty: [
                    { Name: 'Schematic Path', Value: `/${schematic.Name}/${page.Name}` },
                    ...(pageNumber === undefined ?
                        []
                        : [
                            { Name: 'Page Number', Value: String(pageNumber) },
                            { Name: 'Schematic Page Number', Value: String(pageNumber) },
                        ]),
                ],
            });
        }
        return occurrences;
    }
    function instOccurrenceFromDrawnInst(drawnInst, target, children, page) {
        const occurrenceReference = drawnInst.Reference === '' ? drawnInst.DeviceDesignator : drawnInst.Reference;
        return {
            RefInstId: String(drawnInst.DbId),
            UserProperty: [
                { Name: 'Reference', Value: occurrenceReference },
                { Name: 'Part Reference', Value: occurrenceReference },
                { Name: 'Value', Value: drawnInst.PartValue },
            ],
            PortOccurrence: portOccurrencesFromPorts(drawnInst.PortInstScalar, drawnInst.PortInstBus, page),
            TitleBlockOccurrence: target ? buildTitleBlockOccurrencesForSchematic(target) : [],
            InstOccurrence: children,
        };
    }
    function instOccurrenceFromPartInst(partInst, target, children, page, occurrenceReference) {
        const reference = occurrenceReference?.reference ?? partInst.Reference;
        return {
            RefInstId: String(partInst.DbId),
            UserProperty: [
                { Name: 'Reference', Value: reference },
                { Name: 'Part Reference', Value: reference },
                { Name: 'Value', Value: partInst.PartValue },
            ],
            PortOccurrence: portOccurrencesFromPorts(partInst.PortInstScalar, partInst.PortInstBus, page),
            TitleBlockOccurrence: target ? buildTitleBlockOccurrencesForSchematic(target) : [],
            InstOccurrence: children,
        };
    }
    function portOccurrencesFromPorts(scalarPorts, busPorts, page) {
        const occurrences = [];
        for (const port of scalarPorts ?? []) {
            occurrences.push(portOccurrenceForPort(port));
        }
        for (const port of busPorts ?? []) {
            const baseOccurrence = portOccurrenceForPort(port);
            occurrences.push(baseOccurrence);
            for (const memberName of busMemberNamesForPort(page, port.Name)) {
                occurrences.push({
                    KeyType: 'PortName',
                    RefBusMemberName: memberName,
                    RefKey: baseOccurrence.RefKey,
                    RefPortName: memberName,
                });
            }
        }
        const sorted = occurrences.sort(comparePortOccurrencesByXmlRefKey);
        return (0, portOccurrenceNormalization_1.normalizePortOccurrences)(sorted).portOccurrences;
    }
    function portOccurrenceForPort(port) {
        return {
            KeyType: 'PinPosition',
            RefBusMemberName: '',
            RefKey: 'Position' in port ? port.Position : port.Name,
            RefPortName: port.Name,
        };
    }
    function comparePortOccurrencesByXmlRefKey(left, right) {
        const leftKey = numericOccurrenceRefKey(left.RefKey);
        const rightKey = numericOccurrenceRefKey(right.RefKey);
        if (leftKey !== rightKey) {
            return leftKey - rightKey;
        }
        return compareXmlTextOrder(String(left.RefPortName), String(right.RefPortName));
    }
    function numericOccurrenceRefKey(refKey) {
        const parsed = typeof refKey === 'number' ? refKey : Number(refKey);
        return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    }
    function busMemberNamesForPort(page, portName) {
        const catalog = pageBusMemberCatalog(page);
        const catalogNames = catalog.get(portName);
        if (catalogNames?.length) {
            return [...catalogNames];
        }
        const range = busNameDetails(portName);
        if (!range || /\d$/.test(range.prefix)) {
            return [];
        }
        const step = range.start <= range.end ? 1 : -1;
        const names = [];
        for (let index = range.start; step > 0 ? index <= range.end : index >= range.end; index += step) {
            names.push(`${range.prefix}${index}`);
        }
        return names;
    }
    function pageBusMemberCatalog(page) {
        const catalog = new Map();
        for (const netBus of page.NetBus ?? []) {
            const memberNames = (netBus.NetScalar ?? []).map((netScalar) => netScalar.Name).filter(Boolean);
            if (memberNames.length === 0) {
                continue;
            }
            const names = new Set();
            if (netBus.Name) {
                names.add(netBus.Name);
            }
            for (const wire of netBus.WireBus ?? []) {
                if (wire.Name) {
                    names.add(wire.Name);
                }
                for (const alias of wire.Alias ?? []) {
                    if (alias.Name) {
                        names.add(alias.Name);
                    }
                }
            }
            for (const name of names) {
                catalog.set(name, memberNames);
            }
        }
        return catalog;
    }
    function primitiveGeometry(primitives) {
        const geometry = {};
        for (const primitive of primitives) {
            switch (primitive.kind) {
                case 'line':
                    pushValue(geometry, 'Line', lineFromPrimitive(primitive));
                    break;
                case 'rect':
                    pushValue(geometry, 'Rectangle', rectangleFromPrimitive(primitive));
                    break;
                case 'ellipse':
                    pushValue(geometry, 'Ellipse', ellipseFromPrimitive(primitive));
                    break;
                case 'polygon':
                    pushValue(geometry, 'Polygon', polygonFromPrimitive(primitive));
                    break;
                case 'polyline':
                    pushValue(geometry, 'Polyline', polylineFromPrimitive(primitive));
                    break;
                case 'arc':
                    pushValue(geometry, 'Arc', arcFromPrimitive(primitive));
                    break;
                case 'comment-text':
                    pushValue(geometry, 'CommentText', commentTextFromPrimitive(primitive, { useExactTextBoxHeight: true }));
                    break;
                case 'bitmap':
                    pushValue(geometry, 'Bitmap', bitmapFromPrimitive(primitive));
                    break;
            }
        }
        return geometry;
    }
    function buildPagePrimitiveGraphics(primitives, textOptions = {}) {
        const page = {};
        for (const primitive of primitives) {
            switch (primitive.kind) {
                case 'line':
                    pushValue(page, 'GraphicLineInst', {
                        Color: DEFAULT_COLOR_VALUE,
                        DbId: dbIdFromOffset(primitive.offset, 10),
                        EndX: primitive.x2,
                        EndY: primitive.y2,
                        LineStyle: primitive.lineStyle,
                        LineWidth: primitive.lineWidth,
                        LocX: primitive.x1,
                        LocY: primitive.y1,
                        Name: `I${dbIdFromOffset(primitive.offset, 10)}`,
                        Rotation: 0,
                        StartX: primitive.x1,
                        StartY: primitive.y1,
                    });
                    break;
                case 'rect':
                    pushValue(page, 'GraphicBoxInst', {
                        Color: DEFAULT_COLOR_VALUE,
                        DbId: dbIdFromOffset(primitive.offset, 20),
                        FillStyle: primitive.fillStyle,
                        HatchStyle: primitive.hatchStyle,
                        LineStyle: primitive.lineStyle,
                        LineWidth: primitive.lineWidth,
                        LocX: primitive.x1,
                        LocY: primitive.y1,
                        Name: `I${dbIdFromOffset(primitive.offset, 20)}`,
                        Rotation: 0,
                        X1: primitive.x1,
                        X2: primitive.x2,
                        Y1: primitive.y1,
                        Y2: primitive.y2,
                    });
                    break;
                case 'ellipse':
                    pushValue(page, 'GraphicEllipseInst', {
                        Color: DEFAULT_COLOR_VALUE,
                        DbId: dbIdFromOffset(primitive.offset, 30),
                        FillStyle: primitive.fillStyle,
                        HatchStyle: primitive.hatchStyle,
                        LineStyle: primitive.lineStyle,
                        LineWidth: primitive.lineWidth,
                        LocX: primitive.x1,
                        LocY: primitive.y1,
                        Name: `I${dbIdFromOffset(primitive.offset, 30)}`,
                        Rotation: 0,
                        X1: primitive.x1,
                        X2: primitive.x2,
                        Y1: primitive.y1,
                        Y2: primitive.y2,
                    });
                    break;
                case 'polygon':
                    pushValue(page, 'GraphicPolygonInst', {
                        Color: DEFAULT_COLOR_VALUE,
                        DbId: dbIdFromOffset(primitive.offset, 40),
                        FillStyle: primitive.fillStyle,
                        HatchStyle: primitive.hatchStyle,
                        LineStyle: primitive.lineStyle,
                        LineWidth: primitive.lineWidth,
                        LocX: primitive.points[0]?.x ?? 0,
                        LocY: primitive.points[0]?.y ?? 0,
                        Name: `I${dbIdFromOffset(primitive.offset, 40)}`,
                        Rotation: 0,
                        PolygonPoint: primitive.points.map((point) => ({ X: point.x, Y: point.y })),
                    });
                    break;
                case 'comment-text':
                    pushValue(page, 'GraphicCommentTextInst', commentTextFromPrimitive(primitive, textOptions));
                    break;
                case 'bitmap':
                    pushValue(page, 'GraphicBitMapInst', {
                        DbId: primitive.dbId ?? dbIdFromOffset(primitive.offset, 50),
                        LocX: primitive.locX,
                        LocY: primitive.locY,
                        Value: bitmapValueFromPrimitive(primitive),
                        X1: primitive.locX,
                        X2: primitive.x2,
                        Y1: primitive.locY,
                        Y2: primitive.y2,
                    });
                    break;
                case 'arc':
                    break;
            }
        }
        return page;
    }
    function groupSymbolObjects(stream) {
        const groups = [];
        let current = {
            name: stream.parsed?.kind === 'package' ? stream.parsed.packageName
                : stream.parsed?.kind === 'symbol' ? stream.parsed.symbolName
                    : undefined,
            records: [],
            primitives: stream.decodedPrimitives ?? [],
        };
        for (const record of stream.decodedSymbolObjects ?? []) {
            if (record.kind === 'view-header') {
                if (current.records.length > 0) {
                    groups.push(current);
                }
                current = { name: record.name, records: [record], primitives: stream.decodedPrimitives ?? [] };
                continue;
            }
            current.records.push(record);
        }
        if (current.records.length > 0 || current.primitives.length > 0) {
            groups.push(current);
        }
        return groups.length > 0 ? groups : [{ records: [], primitives: stream.decodedPrimitives ?? [] }];
    }
    function pageSizeInfoFromCfb(pageSizeInfo) {
        return {
            IsMetric: pageSizeInfo?.isMetric ?? false,
            PinToPin: pageSizeInfo?.pinToPin ?? 100,
            Height: pageSizeInfo?.height ?? 11700,
            Width: pageSizeInfo?.width ?? 16500,
        };
    }
    function pageDateTime(page, diagnostics) {
        return dateTimeFromEpoch(page.pageDateTime?.modifyDateEpoch ?? page.pageDateTime?.createDateEpoch ?? 0, diagnostics);
    }
    function dateTimeFromEpoch(epoch = 0, diagnostics) {
        if (!isRepresentableEpoch(epoch)) {
            if (epoch !== 0 && diagnostics) {
                emitBudgetedDiagnostic(diagnostics, 'date-time', 'page-date-out-of-range', `epoch=${epoch}`, 'CFB page', 'warning');
            }
            return {
                CreateDate: '',
                CreateDateEpoch: 0,
                CreateTime: '',
                ModifyDate: '',
                ModifyDateEpoch: 0,
                ModifyTime: '',
            };
        }
        const stamp = new Date(epoch * 1000).toISOString();
        return {
            CreateDate: stamp.slice(0, 10),
            CreateDateEpoch: epoch,
            CreateTime: stamp.slice(11, 19),
            ModifyDate: stamp.slice(0, 10),
            ModifyDateEpoch: epoch,
            ModifyTime: stamp.slice(11, 19),
        };
    }
    function defaultValues(defaultFont = font()) {
        return {
            DefaultFont: [defaultFont],
            DefaultPageRec: {
                ANSIGridRefs: 1,
                BorderDisplayed: 1,
                BorderPrinted: 1,
                GridRefDisplayed: 1,
                GridRefPrinted: 1,
                HorizontalLabelCount: 2,
                HorizontalLabelIsAscending: 0,
                HorizontalLabelIsChar: 0,
                HorizontalLabelWidth: 10,
                IsMetric: 0,
                PinToPin: 100,
                TitleBlockDisplayed: 1,
                TitleBlockPrinted: 1,
                VerticalLabelCount: 2,
                VerticalLabelIsAscending: 0,
                VerticalLabelIsChar: 1,
                VerticalLabelWidth: 6,
            },
            DefaultPlacedInstIsPrimitive: 2,
            DefaultDrawnInstIsPrimitive: 2,
            DefaultPartFieldMappings: [],
        };
    }
    function isRecord(value) {
        return typeof value === 'object' && value !== null && !Array.isArray(value);
    }
    function pageCommentTextOptionsForDocument(document) {
        const dsnStream = document.streams.find((stream) => stream.parsed?.kind === 'dsn-stream');
        const json = dsnStream?.parsed?.kind === 'dsn-stream' && isRecord(dsnStream.parsed.json) ? dsnStream.parsed.json : undefined;
        return {
            defaultFontName: font().Name,
            ...(json?.InstalledVersionBase !== undefined ? { useExactTextBoxHeight: true } : {}),
        };
    }
    function font() {
        return {
            Charset: 0,
            Escapement: 0,
            Height: 8,
            Italic: 0,
            Name: '',
            Orientation: 0,
            Weight: 400,
            Width: 3,
        };
    }
    function arialFont(height) {
        return {
            Charset: 0,
            Escapement: 0,
            Height: height,
            Italic: 0,
            Name: 'Arial',
            Orientation: 0,
            Weight: 400,
            Width: 0,
        };
    }
    function color(value = DEFAULT_COLOR_VALUE) {
        return { Value: value };
    }
    function displayType(value = DEFAULT_DISPLAY_TYPE_VALUE) {
        return { ValueIfValueExist: 0, Value: value };
    }
    function lineFromPrimitive(primitive) {
        return {
            LineStyle: primitive.lineStyle,
            LineWidth: primitive.lineWidth,
            X1: primitive.x1,
            X2: primitive.x2,
            Y1: primitive.y1,
            Y2: primitive.y2,
        };
    }
    function rectangleFromPrimitive(primitive) {
        return {
            FillStyle: primitive.fillStyle,
            HatchStyle: primitive.hatchStyle,
            LineStyle: primitive.lineStyle,
            LineWidth: primitive.lineWidth,
            X1: primitive.x1,
            X2: primitive.x2,
            Y1: primitive.y1,
            Y2: primitive.y2,
        };
    }
    function ellipseFromPrimitive(primitive) {
        return {
            FillStyle: primitive.fillStyle,
            HatchStyle: primitive.hatchStyle,
            LineStyle: primitive.lineStyle,
            LineWidth: primitive.lineWidth,
            X1: primitive.x1,
            X2: primitive.x2,
            Y1: primitive.y1,
            Y2: primitive.y2,
        };
    }
    function polygonFromPrimitive(primitive) {
        return {
            FillStyle: primitive.fillStyle,
            HatchStyle: primitive.hatchStyle,
            LineStyle: primitive.lineStyle,
            LineWidth: primitive.lineWidth,
            PolygonPoint: primitive.points.map((point) => ({ X: point.x, Y: point.y })),
        };
    }
    function polylineFromPrimitive(primitive) {
        return {
            LineStyle: primitive.lineStyle,
            LineWidth: primitive.lineWidth,
            PolylinePoint: primitive.points.map((point) => ({ X: point.x, Y: point.y })),
        };
    }
    function arcFromPrimitive(primitive) {
        return {
            EndX: primitive.endX,
            EndY: primitive.endY,
            LineStyle: primitive.lineStyle,
            LineWidth: primitive.lineWidth,
            StartX: primitive.startX,
            StartY: primitive.startY,
            X1: primitive.x1,
            X2: primitive.x2,
            Y1: primitive.y1,
            Y2: primitive.y2,
        };
    }
    function commentTextFromPrimitive(primitive, options = {}) {
        return {
            DbId: dbIdFromOffset(primitive.offset, 60),
            LocX: primitive.locX,
            LocY: primitive.locY,
            Name: primitive.name,
            TextJustification: primitive.textFlags,
            X1: primitive.x1,
            X2: primitive.x2,
            Y1: primitive.y1,
            Y2: primitive.y2,
            Font: fontFromTextBounds(primitive, options),
            Color: DEFAULT_COLOR_VALUE,
        };
    }
    function fontFromTextBounds(primitive, options = {}) {
        const lineCount = commentTextLineCount(primitive.name);
        const height = Math.abs(primitive.y2 - primitive.y1);
        const effectiveHeight = options.useExactTextBoxHeight ? height : Math.max(1, height - 1);
        return {
            ...font(),
            Height: -Math.round(effectiveHeight / lineCount),
            Name: options.defaultFontName ?? font().Name,
        };
    }
    function commentTextLineCount(name) {
        const lines = name.split(/\r\n|\r|\n/);
        if (lines.length > 1 && lines[lines.length - 1] === '') {
            lines.pop();
        }
        return Math.max(1, lines.length);
    }
    function bitmapFromPrimitive(primitive) {
        return {
            LocX: primitive.locX,
            LocY: primitive.locY,
            Value: bitmapValueFromPrimitive(primitive),
            X1: primitive.locX,
            X2: primitive.x2,
            Y1: primitive.locY,
            Y2: primitive.y2,
        };
    }
    function bitmapValueFromPrimitive(primitive) {
        const payload = primitive.dibPayload;
        if (!payload?.length) {
            return '';
        }
        if (payload[0] === 0x42 && payload[1] === 0x4d) {
            return (0, base64_1.uint8ArrayToBase64)(payload);
        }
        return (0, base64_1.uint8ArrayToBase64)(prependBmpHeader(payload));
    }
    function prependBmpHeader(dib) {
        const dibHeaderSize = readUInt32LE(dib, 0);
        const fileHeaderSize = 14;
        const fileSize = fileHeaderSize + dib.length;
        const bmp = new Uint8Array(fileSize);
        bmp[0] = 0x42;
        bmp[1] = 0x4d;
        writeUInt32LE(bmp, 2, fileSize);
        writeUInt32LE(bmp, 10, fileHeaderSize + dibHeaderSize);
        bmp.set(dib, fileHeaderSize);
        return bmp;
    }
    function boundingBoxFromGeometry(geometry, options = {}) {
        const points = [];
        for (const line of geometry.Line ?? []) {
            points.push({ X: line.X1, Y: line.Y1 }, { X: line.X2, Y: line.Y2 });
        }
        for (const rect of geometry.Rectangle ?? []) {
            points.push({ X: rect.X1, Y: rect.Y1 }, { X: rect.X2, Y: rect.Y2 });
        }
        for (const ellipse of geometry.Ellipse ?? []) {
            points.push({ X: ellipse.X1, Y: ellipse.Y1 }, { X: ellipse.X2, Y: ellipse.Y2 });
        }
        for (const polygon of geometry.Polygon ?? []) {
            points.push(...polygon.PolygonPoint);
        }
        for (const polyline of geometry.Polyline ?? []) {
            points.push(...polyline.PolylinePoint);
        }
        for (const arc of geometry.Arc ?? []) {
            points.push(...arcExtentPoints(arc));
        }
        if (!options.excludeTextWhenPossible || points.length === 0) {
            for (const text of geometry.CommentText ?? []) {
                points.push({ X: text.X1, Y: text.Y1 }, { X: text.X2, Y: text.Y2 });
            }
        }
        for (const bitmap of geometry.Bitmap ?? []) {
            points.push({ X: bitmap.X1, Y: bitmap.Y1 }, { X: bitmap.X2, Y: bitmap.Y2 });
        }
        if (points.length === 0) {
            return { X1: 0, X2: 0, Y1: 0, Y2: 0 };
        }
        return snapBoundingBox({
            X1: Math.min(...points.map((point) => point.X)),
            X2: Math.max(...points.map((point) => point.X)),
            Y1: Math.min(...points.map((point) => point.Y)),
            Y2: Math.max(...points.map((point) => point.Y)),
        });
    }
    function arcExtentPoints(arc) {
        const centerX = (arc.X1 + arc.X2) / 2;
        const centerY = (arc.Y1 + arc.Y2) / 2;
        const radiusX = (arc.X2 - arc.X1) / 2;
        const radiusY = (arc.Y2 - arc.Y1) / 2;
        const endpoints = [
            { X: arc.StartX, Y: arc.StartY },
            { X: arc.EndX, Y: arc.EndY },
        ];
        if (radiusX <= 0 || radiusY <= 0) {
            return [
                { X: arc.X1, Y: arc.Y1 },
                { X: arc.X2, Y: arc.Y2 },
            ];
        }
        const angleOf = (x, y) => {
            const angle = Math.atan2((y - centerY) / radiusY, (x - centerX) / radiusX);
            return angle < 0 ? angle + 2 * Math.PI : angle;
        };
        const startAngle = angleOf(arc.StartX, arc.StartY);
        const clockwiseSweep = (startAngle - angleOf(arc.EndX, arc.EndY) + 2 * Math.PI) % (2 * Math.PI);
        const sweep = arc.StartX === arc.EndX && arc.StartY === arc.EndY ? 2 * Math.PI
            : clockwiseSweep === 0 ? 2 * Math.PI
                : clockwiseSweep;
        const extremes = [
            { angle: 0, point: { X: centerX + radiusX, Y: centerY } },
            { angle: Math.PI / 2, point: { X: centerX, Y: centerY + radiusY } },
            { angle: Math.PI, point: { X: centerX - radiusX, Y: centerY } },
            { angle: (3 * Math.PI) / 2, point: { X: centerX, Y: centerY - radiusY } },
        ];
        return [
            ...endpoints,
            ...extremes
                .filter(({ angle }) => (startAngle - angle + 2 * Math.PI) % (2 * Math.PI) <= sweep)
                .map(({ point }) => point),
        ];
    }
    function snapBoundingBox(box) {
        return {
            X1: Math.floor(box.X1 / 10) * 10,
            X2: Math.ceil(box.X2 / 10) * 10,
            Y1: Math.floor(box.Y1 / 10) * 10,
            Y2: Math.ceil(box.Y2 / 10) * 10,
        };
    }
    function emitDerivedSymbolBoundingBoxDiagnostics(cache, options) {
        if (!options.diagnostics) {
            return;
        }
        for (const symbol of [...(cache.PortSymbol ?? []), ...(cache.OffPageSymbol ?? []), ...(cache.GlobalSymbol ?? [])]) {
            options.diagnostics.push({
                severity: 'info',
                message: `CFB symbol [symbol-bbox-derived] name="${symbol.Name}" sourceLibName="${symbol.SourceLibName}"`,
            });
        }
    }
    function symbolPinScalarFromCfb(pin, position = 0) {
        const type = pin.pinType;
        const flags = pin.displayFlags;
        const isSet = (flag) => flags !== undefined && (flags & flag) !== 0;
        const powerPin = type === POWER_PIN_TYPE;
        return {
            HotptX: pin.hotptX,
            HotptY: pin.hotptY,
            Name: pin.name,
            Position: position,
            StartX: pin.startX,
            StartY: pin.startY,
            ...(type === undefined ? {} : { Type: type }),
            ...(flags === undefined ?
                {}
                : {
                    Visible: isSet(PinFlag.IsVisible) ? 1 : 0,
                    IsLong: isSet(PinFlag.IsLong),
                    IsLeftPointing: isSet(PinFlag.IsLeftPointing),
                    IsRightPointing: isSet(PinFlag.IsRightPointing),
                }),
            IsClock: isSet(PinFlag.IsClock),
            IsDot: isSet(PinFlag.IsDot),
            IsNetStyle: false,
            IsNoConnect: false,
            IsGlobal: powerPin || isSet(PinFlag.IsHidden),
        };
    }
    function offPageSymbolPinScalarFromCfb(pin, position = 0) {
        const scalar = symbolPinScalarFromCfb(pin, position);
        scalar.IsLong = true;
        scalar.Visible = 0;
        scalar.IsGlobal = true;
        return scalar;
    }
    function globalSymbolPinScalarFromCfb(pin, position = 0) {
        const scalar = symbolPinScalarFromCfb(pin, position);
        scalar.Type = 7;
        scalar.IsLong = true;
        scalar.IsLeftPointing = false;
        scalar.IsRightPointing = false;
        scalar.Visible = 0;
        scalar.IsGlobal = true;
        return scalar;
    }
    function portInstsFromMaterializedPorts(ports, dbIdSeed, physicalPadPresenceCatalog, physicalPadKeys, diagnostics, hasDeclaredBounds) {
        const scalar = [];
        const bus = [];
        for (let index = 0; index < ports.length; index++) {
            const port = ports[index];
            const position = port.position ?? index;
            const requiresRecordedPosition = !hasDeclaredBounds || port.hotptX === undefined || port.hotptY === undefined;
            if (requiresRecordedPosition && (port.x === undefined || port.y === undefined)) {
                emitDecisionDiagnostic(diagnostics, 'placed-port-position-unresolved', `dbId=${dbIdSeed} position=${position} name="${port.name}": no recorded page coordinate or complete decoded pin transform (declaredBounds=${hasDeclaredBounds}); connectivity withheld`, 'CFB placed port', 'warning');
            }
            const physicalPadPresence = physicalPadPresenceAt(physicalPadPresenceCatalog, physicalPadKeys, ports.length, position);
            const implicitShield = isImplicitShieldPort(port, physicalPadPresence);
            const shieldPin = isShieldPinName(port.name);
            const converted = {
                RequiresRecordedPosition: requiresRecordedPosition,
                IsPlacedInstPin: port.isPlacedInstPin ?? true,
                DbId: dbIdSeed * 1000 + index,
                HotptX: port.hotptX ?? 0,
                HotptY: port.hotptY ?? 0,
                Name: port.name,
                Position: position,
                StartX: port.startX ?? port.hotptX ?? 0,
                StartY: port.startY ?? port.hotptY ?? 0,
                Type: implicitShield ? 7 : (port.type ?? DEFAULT_PIN_TYPE),
                Visible: (implicitShield ||
                    (shieldPin &&
                        port.type === 7 &&
                        port.visible !== true &&
                        !(physicalPadPresence === 'present' && port.isExplicit === true && hasResolvableWireGeometry(port)))) ?
                    false
                    : (port.visible ?? true),
                IsNoConnect: shieldPin ? false : (port.isNoConnect ?? false),
                ...(port.x !== undefined && port.y !== undefined ? { RecordedX: port.x, RecordedY: port.y } : {}),
                ...(port.wireDbId !== undefined ? { RecordedWireDbId: port.wireDbId } : {}),
            };
            (port.isBus ? bus : scalar).push(converted);
        }
        return { scalar, bus };
    }
    function hasResolvableWireGeometry(port) {
        return port.wireDbId !== undefined && port.x !== undefined && port.y !== undefined;
    }
    function isShieldPinName(name) {
        return /^SHIELD\d*$/i.test(name);
    }
    function isImplicitShieldPort(port, physicalPadPresence) {
        if (!isShieldPinName(port.name) || physicalPadPresence === 'present') {
            return false;
        }
        if (physicalPadPresence === 'absent') {
            return true;
        }
        if (port.visible !== undefined) {
            return !port.visible;
        }
        if (port.isExplicit !== undefined) {
            return !port.isExplicit;
        }
        return false;
    }
    function drawnPortInstsFromMaterializedPorts(ports, portDbIds) {
        const scalar = [];
        const bus = [];
        for (let index = 0; index < ports.length; index++) {
            const port = ports[index];
            const dbId = portDbIds?.[index];
            const converted = {
                DbId: dbId,
                HotptX: port.hotptX ?? 0,
                HotptY: port.hotptY ?? 0,
                Name: port.name,
                StartX: port.startX ?? 0,
                StartY: port.startY ?? 0,
                Type: port.type ?? DEFAULT_PIN_TYPE,
                Visible: port.visible ?? true,
                IsLong: true,
                IsClock: false,
                IsDot: false,
                IsLeftPointing: true,
                IsRightPointing: true,
                IsNetStyle: port.source === 'drawn-port',
                IsGlobal: false,
                IsNoConnect: port.isNoConnect,
            };
            (port.isBus ? bus : scalar).push(converted);
        }
        return { scalar, bus };
    }
    function partDisplayPropertyFromCfb(property) {
        return {
            DbId: dbIdFromOffset(property.offset, 70),
            LocX: property.locX,
            LocY: property.locY,
            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
            Rotation: displayPropertyRotation(property),
            TextJustification: 0,
            Font: font(),
            Color: color(property.color),
            DisplayType: displayType(displayPropertyDisplayType(property)),
        };
    }
    function drawnInstDisplayPropertyFromCfb(property) {
        return {
            DbId: dbIdFromOffset(property.offset, 80),
            LocX: property.locX,
            LocY: property.locY,
            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
            Rotation: displayPropertyRotation(property),
            TextJustification: 0,
            Font: drawnInstDisplayPropertyFont(property),
            Color: color(property.color),
            DisplayType: displayType(displayPropertyDisplayType(property)),
        };
    }
    function globalDisplayPropertyFromCfb(property, diagnostics) {
        return {
            DbId: dbIdFromOffset(property.offset, 90),
            LocX: property.locX,
            LocY: property.locY,
            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
            Rotation: displayPropertyRotation(property),
            TextJustification: 0,
            Font: connectorDisplayPropertyFont(property, diagnostics),
            Color: color(property.color),
            DisplayType: displayType(connectorDisplayPropertyDisplayType(property)),
        };
    }
    function connectorDisplayPropertiesFromCfb(properties, dbId, diagnostics) {
        const records = properties ?? [];
        const named = records.filter((property) => displayPropertyLogicalName(property) !== undefined);
        if (named.length > 0 && !named.some((property) => displayPropertyLogicalName(property) === 'Name')) {
            emitBudgetedDiagnostic(diagnostics, `connector-display-name\0${dbId}`, 'connector-text-not-filed-under-name', `dbId=${dbId} records=${named.map((property) => displayPropertyLogicalName(property)).join(',')}`, 'CFB connector', 'warning');
        }
        return records.map((property) => connectorDisplayPropertyFromCfb(property, diagnostics));
    }
    function connectorDisplayPropertyFromCfb(property, diagnostics) {
        return {
            DbId: dbIdFromOffset(property.offset, 90),
            LocX: property.locX,
            LocY: property.locY,
            Name: displayPropertyLogicalName(property) ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
            Rotation: displayPropertyRotation(property),
            TextJustification: 0,
            Font: connectorDisplayPropertyFont(property, diagnostics),
            Color: color(property.color),
            DisplayType: displayType(connectorDisplayPropertyDisplayType(property)),
        };
    }
    function titleBlockDisplayPropertyFromCfb(property) {
        return {
            DbId: dbIdFromOffset(property.offset, 100),
            LocX: property.locX,
            LocY: property.locY,
            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
            Rotation: property.rotation,
            TextJustification: 0,
            Font: font(),
            Color: color(property.color),
            DisplayType: displayType(property.displayType),
        };
    }
    function symbolDisplayPropertyFromCfb(property) {
        return {
            LocX: property.locX,
            LocY: property.locY,
            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
            Rotation: 0,
            TextJustification: 0,
            Font: font(),
            Color: color(property.color),
            DisplayType: displayType(property.displayType),
        };
    }
    function addSyntheticIrefDisplayProperty(displayProperties, object, name, irefValue, diagnostics) {
        if (!irefValue || displayProperties.some((property) => property.Name === 'IREF')) {
            return displayProperties;
        }
        const displayProperty = syntheticIrefDisplayProperty(object, name, irefValue, diagnostics);
        return displayProperty ? [...displayProperties, displayProperty] : displayProperties;
    }
    function addSyntheticNameDisplayProperty(displayProperties, object, name, diagnostics) {
        if (displayProperties.some((property) => property.Name === 'Name')) {
            return displayProperties;
        }
        const displayProperty = syntheticNameDisplayProperty(object, name, diagnostics);
        return displayProperty ? [...displayProperties, displayProperty] : displayProperties;
    }
    function syntheticNameDisplayProperty(object, name, diagnostics) {
        const locX = syntheticNameDisplayLocX(object, name, diagnostics);
        if (locX === undefined) {
            return undefined;
        }
        return {
            DbId: dbIdFromOffset(object.offset, 89),
            LocX: locX,
            LocY: 6,
            Name: 'Name',
            Rotation: object.resolvedMetadata?.rotation ?? 0,
            TextJustification: 0,
            Font: font(),
            Color: color(object.color),
            DisplayType: displayType(),
        };
    }
    function syntheticNameDisplayLocX(object, name, diagnostics) {
        if (/-L$/i.test(object.symbolName)) {
            return 32;
        }
        const labelFont = font();
        const measurement = OrcadCaptureFontMetrics_1.OrcadCaptureFontMetrics.measure(name, labelFont);
        if (measurement.advance === undefined) {
            emitTextAdvanceDiagnostic(diagnostics, 'name-display-advance-unknown', labelFont, measurement.miss);
            return undefined;
        }
        return -(measurement.advance + 1);
    }
    function syntheticIrefDisplayProperty(object, name, irefValue, diagnostics) {
        const nameProperty = (object.displayProperties ?? []).find((property) => displayPropertyLogicalName(property) === 'Name');
        if (!nameProperty) {
            emitBudgetedDiagnostic(diagnostics, object.symbolName, 'iref-display-anchor-missing', `symbol "${object.symbolName}" carries no Name display record to place the cross-reference label against`, 'CFB text metric', 'warning');
            return undefined;
        }
        const labelFont = connectorDisplayPropertyFont(nameProperty, diagnostics);
        if (!carriesSampledDisplayTextStyle(nameProperty)) {
            emitUndecodedTextStyleDiagnostic(diagnostics, 'iref-display-advance-unknown', nameProperty.textStyleId);
            return undefined;
        }
        const irefLabelLeads = nameProperty.locX < 0;
        const measurement = OrcadCaptureFontMetrics_1.OrcadCaptureFontMetrics.measure(irefLabelLeads ? irefValue : name, labelFont);
        if (measurement.advance === undefined) {
            emitTextAdvanceDiagnostic(diagnostics, 'iref-display-advance-unknown', labelFont, measurement.miss);
            return undefined;
        }
        return {
            DbId: dbIdFromOffset(nameProperty.offset, 91),
            LocX: irefLabelLeads ? nameProperty.locX - measurement.advance : nameProperty.locX + measurement.advance,
            LocY: nameProperty.locY,
            Name: 'IREF',
            Rotation: displayPropertyRotation(nameProperty),
            TextJustification: 0,
            Font: labelFont,
            Color: color(nameProperty.color),
            DisplayType: displayType(),
        };
    }
    function emitTextAdvanceDiagnostic(state, reason, labelFont, miss) {
        if (!miss) {
            return;
        }
        const fontDescription = OrcadCaptureFontMetrics_1.OrcadCaptureFontMetrics.describeFont(labelFont);
        const glyphDetail = miss.glyph === undefined ? '' : ` glyph "${miss.glyph}"`;
        emitBudgetedDiagnostic(state, `${fontDescription}\0${miss.glyph ?? ''}`, reason, `${fontDescription}: ${miss.reason}${glyphDetail}`, 'CFB text metric', 'warning');
    }
    function displayPropertyLogicalName(property) {
        return property.propertyName;
    }
    function drawnInstDisplayPropertyFont(property) {
        return property.displayType === 46 || (property.displayType & 0x4000) !== 0 ? arialFont(-15) : font();
    }
    const SAMPLED_DISPLAY_TEXT_STYLE_ID = 1;
    function carriesSampledDisplayTextStyle(property) {
        return property.textStyleId === SAMPLED_DISPLAY_TEXT_STYLE_ID;
    }
    function emitUndecodedTextStyleDiagnostic(state, reason, textStyleId) {
        const described = textStyleId === undefined ? 'absent' : String(textStyleId);
        emitBudgetedDiagnostic(state, `text-style-${described}`, reason, `display text style ${described} carries no decoded width, weight or slant, so no measured row applies`, 'CFB text metric', 'warning');
    }
    function connectorDisplayPropertyFont(property, diagnostics) {
        if (property.displayType === 36) {
            return arialFont(-6);
        }
        if (property.displayType === 29) {
            return arialFont(-7);
        }
        if (usesSmallConnectorTextStyle(property.propertyCode)) {
            emitAssumedTextStyleDiagnostic(diagnostics, property.propertyCode);
            return connectorSmallFont();
        }
        return font();
    }
    function usesSmallConnectorTextStyle(propertyCode) {
        return propertyCode === 0x87;
    }
    function emitAssumedTextStyleDiagnostic(state, propertyCode) {
        const code = `0x${propertyCode.toString(16)}`;
        emitBudgetedDiagnostic(state, code, 'connector-text-style-assumed', `display code ${code} is read as the small port-label text style; the record carries no field that proves it`, 'CFB text metric', 'warning');
    }
    function connectorSmallFont() {
        return {
            Charset: 0,
            Escapement: 0,
            Height: -9,
            Italic: 0,
            Name: '',
            Orientation: 0,
            Weight: 400,
            Width: 4,
        };
    }
    function displayPropertyRotation(property) {
        if ((property.displayType & 0x4000) !== 0) {
            return 1;
        }
        if (property.displayType === 29 || property.displayType === 46) {
            return 0;
        }
        return property.rotation;
    }
    function displayPropertyDisplayType(property) {
        if ((property.displayType & 0x4000) !== 0) {
            return DEFAULT_DISPLAY_TYPE_VALUE;
        }
        if (property.displayType === 29 || property.displayType === 46) {
            return DEFAULT_DISPLAY_TYPE_VALUE;
        }
        return property.displayType;
    }
    function connectorDisplayPropertyDisplayType(property) {
        if (property.textStyleId === 1 && property.displayType === 0) {
            return DEFAULT_DISPLAY_TYPE_VALUE;
        }
        return displayPropertyDisplayType(property);
    }
    function partUserPropertiesFromCfbObject(object) {
        return (object.userProperties ?? []).filter((property) => property.source !== 'display-property' || !['Part Reference', 'Value'].includes(property.name));
    }
    function userPropertiesFromCfb(properties) {
        return (properties ?? []).map((property) => ({ Name: property.name, Value: property.value }));
    }
    function literalIrefValue(object) {
        return (object.userProperties ?? []).find((property) => property.name === 'IREF')?.value;
    }
    function usablePlacedPartValue(value, cachePins) {
        if (value === undefined || value.length === 0) {
            return false;
        }
        return !cachePins?.some((pin) => pin.name.toUpperCase() === value.toUpperCase());
    }
    function normalizeCatalogValue(value) {
        return /^\d+R$/i.test(value) ? value.slice(0, -1) : value;
    }
    function mergeUserProperties(...groups) {
        const merged = new Map();
        for (const property of groups.flat()) {
            if (property.Name === 'Name') {
                continue;
            }
            if (!merged.has(property.Name)) {
                merged.set(property.Name, property);
            }
        }
        return Array.from(merged.values());
    }
    function finalizePartUserProperties(properties) {
        const byName = new Map(properties.map((property) => [property.Name, property.Value]));
        return Array.from(byName, ([Name, Value]) => ({ Name, Value }));
    }
    function wireScalarFromCfb(wire, aliases, junctions, aliasFont = font()) {
        return {
            ...(wire.userProperties?.length ? { UserProperty: userPropertiesFromCfb(wire.userProperties) } : {}),
            DbId: wire.dbId,
            EndX: wire.endX,
            EndY: wire.endY,
            Name: '',
            StartX: wire.startX,
            StartY: wire.startY,
            Color: color(wire.color),
            LineStyle: wire.lineStyle ?? DEFAULT_LINE_STYLE,
            LineWidth: wire.lineWidth ?? DEFAULT_LINE_WIDTH,
            BoundingBox: boundingBoxFromWire(wire),
            ...(aliases.length > 0 ? { Alias: aliases.map((alias) => aliasFromCfb(alias, aliasFont)) } : {}),
            ...(junctions.length > 0 ? { Junction: junctions } : {}),
        };
    }
    function wireBusFromCfb(wire, aliases, junctions, aliasFont = font(), _netGroupName) {
        return {
            DbId: wire.dbId,
            EndX: wire.endX,
            EndY: wire.endY,
            Name: '',
            StartX: wire.startX,
            StartY: wire.startY,
            Color: color(wire.color),
            LineStyle: wire.lineStyle ?? DEFAULT_LINE_STYLE,
            LineWidth: wire.lineWidth ?? DEFAULT_NET_LINE_WIDTH,
            BoundingBox: boundingBoxFromWire(wire),
            ...(aliases.length > 0 ? { Alias: aliases.map((alias) => aliasFromCfb(alias, aliasFont)) } : {}),
            ...(junctions.length > 0 ? { Junction: junctions } : {}),
        };
    }
    function aliasFromCfb(alias, aliasFont) {
        return {
            DbId: dbIdFromOffset(alias.offset, 110),
            Color: alias.color,
            LocX: alias.locX,
            LocY: alias.locY,
            Name: alias.name,
            Rotation: alias.rotation,
            Font: { ...aliasFont },
        };
    }
    function boundingBoxFromWire(wire) {
        return {
            X1: Math.min(wire.startX, wire.endX),
            X2: Math.max(wire.startX, wire.endX),
            Y1: Math.min(wire.startY, wire.endY),
            Y2: Math.max(wire.startY, wire.endY),
        };
    }
    function wireNetIdentities(groups, aliasesByWireId, carriers, metadataNetNames) {
        const identities = new Map();
        for (let index = 0; index < groups.length; index++) {
            const group = groups[index];
            const aliasesInGroup = group.flatMap((wire) => aliasesByWireId.get(wire.dbId) ?? []);
            const groupName = netNameForWireGroup(group, aliasesInGroup, carriers, metadataNetNames);
            for (const wire of group) {
                const ownName = metadataNetNames.get(wire.metadataId);
                const name = ownName ?? (groupName === '' ? undefined : groupName);
                identities.set(wire.dbId, { run: `run:${index}`, ...(name === undefined ? {} : { name }) });
            }
        }
        return identities;
    }
    function groupConnectedWires(wires, wireState) {
        const parent = new Map();
        for (const wire of wires) {
            parent.set(wire.dbId, wire.dbId);
        }
        const find = (id) => {
            const current = parent.get(id) ?? id;
            if (current === id) {
                return id;
            }
            const root = find(current);
            parent.set(id, root);
            return root;
        };
        const union = (a, b) => {
            const rootA = find(a);
            const rootB = find(b);
            if (rootA !== rootB) {
                parent.set(rootB, rootA);
            }
        };
        const endpointMap = new Map();
        for (const wire of wires) {
            for (const key of [coordKey(wire.startX, wire.startY), coordKey(wire.endX, wire.endY)]) {
                const ids = endpointMap.get(key) ?? [];
                ids.push(wire.dbId);
                endpointMap.set(key, ids);
            }
        }
        for (const ids of endpointMap.values()) {
            for (let i = 1; i < ids.length; i++) {
                union(ids[0], ids[i]);
            }
        }
        for (let i = 0; i < wires.length; i++) {
            for (let j = i + 1; j < wires.length; j++) {
                const a = wires[i];
                const b = wires[j];
                if (pointOnWire(a.startX, a.startY, b) ||
                    pointOnWire(a.endX, a.endY, b) ||
                    pointOnWire(b.startX, b.startY, a) ||
                    pointOnWire(b.endX, b.endY, a)) {
                    union(a.dbId, b.dbId);
                }
            }
        }
        const aliasMap = new Map();
        for (const wire of wires) {
            for (const alias of wireState.get(wire.dbId)?.aliases ?? []) {
                if (!alias.name) {
                    continue;
                }
                const ids = aliasMap.get(alias.name) ?? [];
                ids.push(wire.dbId);
                aliasMap.set(alias.name, ids);
            }
            for (const groupName of wireState.get(wire.dbId)?.groupNames ?? []) {
                if (!groupName) {
                    continue;
                }
                const ids = aliasMap.get(netGroupKey(groupName)) ?? [];
                ids.push(wire.dbId);
                aliasMap.set(netGroupKey(groupName), ids);
            }
        }
        for (const ids of aliasMap.values()) {
            for (let i = 1; i < ids.length; i++) {
                union(ids[0], ids[i]);
            }
        }
        const groups = new Map();
        for (const wire of wires) {
            const root = find(wire.dbId);
            const group = groups.get(root) ?? [];
            group.push(wire);
            groups.set(root, group);
        }
        return Array.from(groups.values());
    }
    function wiresTouch(a, b) {
        return (pointOnWire(a.startX, a.startY, b) ||
            pointOnWire(a.endX, a.endY, b) ||
            pointOnWire(b.startX, b.startY, a) ||
            pointOnWire(b.endX, b.endY, a));
    }
    function findNeighbouringAliasOwner(alias, wires, precedingWireByOffset) {
        const precedingWireDbId = precedingWireByOffset.get(alias.offset);
        if (precedingWireDbId === undefined) {
            return undefined;
        }
        const ranked = wires
            .map((wire) => ({ wire, distance: distanceToWire(alias.locX, alias.locY, wire) }))
            .sort((left, right) => left.distance - right.distance);
        const nearest = ranked[0];
        if (!nearest ||
            ranked.some((candidate) => candidate.wire !== nearest.wire && candidate.distance === nearest.distance)) {
            return undefined;
        }
        return nearest.wire.dbId === precedingWireDbId ? nearest.wire : undefined;
    }
    function buildPrecedingWireIndex(objects) {
        const precedingWireByOffset = new Map();
        let precedingWireDbId;
        for (const object of [...objects].sort((left, right) => left.offset - right.offset)) {
            if (precedingWireDbId !== undefined) {
                precedingWireByOffset.set(object.offset, precedingWireDbId);
            }
            if (isWire(object)) {
                precedingWireDbId = object.dbId;
            }
        }
        return precedingWireByOffset;
    }
    function findExactAliasOwner(alias, wires) {
        const owners = wires.filter((wire) => pointOnWire(alias.locX, alias.locY, wire));
        if (owners.length <= 1) {
            return owners[0];
        }
        return owners
            .map((wire) => ({
            wire,
            isPrecedingRecord: wire.offset <= alias.offset,
            offsetDistance: Math.abs(alias.offset - wire.offset),
            isEndpoint: isWireEndpoint(wire, alias.locX, alias.locY),
        }))
            .sort((left, right) => {
            if (left.isPrecedingRecord !== right.isPrecedingRecord) {
                return left.isPrecedingRecord ? -1 : 1;
            }
            if (left.offsetDistance !== right.offsetDistance) {
                return left.offsetDistance - right.offsetDistance;
            }
            if (left.isEndpoint !== right.isEndpoint) {
                return left.isEndpoint ? 1 : -1;
            }
            return left.wire.dbId - right.wire.dbId;
        })[0]?.wire;
    }
    function findExactPointOwner(x, y, wires) {
        return wires.find((wire) => pointOnWire(x, y, wire));
    }
    function findExactPointOwners(x, y, wires) {
        return wires.filter((wire) => pointOnWire(x, y, wire));
    }
    function pointOnWire(x, y, wire) {
        const cross = (x - wire.startX) * (wire.endY - wire.startY) - (y - wire.startY) * (wire.endX - wire.startX);
        if (cross !== 0) {
            return false;
        }
        return (x >= Math.min(wire.startX, wire.endX) &&
            x <= Math.max(wire.startX, wire.endX) &&
            y >= Math.min(wire.startY, wire.endY) &&
            y <= Math.max(wire.startY, wire.endY));
    }
    function isWireEndpoint(wire, x, y) {
        return (wire.startX === x && wire.startY === y) || (wire.endX === x && wire.endY === y);
    }
    function distanceToWire(x, y, wire) {
        const dx = wire.endX - wire.startX;
        const dy = wire.endY - wire.startY;
        if (dx === 0 && dy === 0) {
            return squaredDistance(x, y, wire.startX, wire.startY);
        }
        const t = Math.max(0, Math.min(1, ((x - wire.startX) * dx + (y - wire.startY) * dy) / (dx * dx + dy * dy)));
        return squaredDistance(x, y, wire.startX + t * dx, wire.startY + t * dy);
    }
    function squaredDistance(x1, y1, x2, y2) {
        return (x1 - x2) ** 2 + (y1 - y2) ** 2;
    }
    function uniqueJunctions(junctions) {
        const seen = new Set();
        return junctions.filter((junction) => {
            const key = coordKey(junction.LocX, junction.LocY);
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }
    function coordKey(x, y) {
        return `${x},${y}`;
    }
    function firstNetName(aliases) {
        return aliases.find((alias) => alias.name)?.name ?? '';
    }
    function netGroupKey(name) {
        return name.trim().toUpperCase();
    }
    function maxDbId(pages) {
        let max = 0;
        for (const page of pages) {
            const ids = [
                ...(page.PartInst ?? []).map((item) => item.DbId),
                ...(page.DrawnInst ?? []).map((item) => item.DbId),
                ...(page.Global ?? []).map((item) => item.DbId),
                ...(page.OffPageConnector ?? []).map((item) => item.DbId),
                ...(page.TitleBlock ?? []).map((item) => item.DbId),
                ...(page.Port ?? []).map((item) => item.DbId),
                ...(page.NetScalar ?? []).flatMap((net) => net.WireScalars?.map((wire) => wire.DbId) ?? []),
                ...(page.NetBus ?? []).flatMap((net) => net.WireBus?.map((wire) => wire.DbId) ?? []),
            ];
            for (const id of ids) {
                max = Math.max(max, id);
            }
        }
        return max;
    }
    function maxPageModifyEpoch(pages) {
        return Math.max(0, ...pages
            .map((page) => page.parsed.pageDateTime?.modifyDateEpoch ?? 0)
            .filter((epoch) => isRepresentableEpoch(epoch)));
    }
    function isRepresentableEpoch(epoch) {
        if (epoch <= 0) {
            return false;
        }
        const milliseconds = epoch * 1000;
        return Number.isFinite(new Date(milliseconds).getTime()) && milliseconds <= LAST_FOUR_DIGIT_YEAR_MS;
    }
    function dbIdFromOffset(offset, namespace) {
        return namespace * 1_000_000 + offset;
    }
    function pushValue(target, key, value) {
        const arr = target[key] ?? [];
        arr.push(value);
        target[key] = arr;
    }
    function arraysEqual(left, right) {
        return left.length === right.length && left.every((value, index) => value === right[index]);
    }
    function readUInt32LE(bytes, offset) {
        return ((bytes[offset] ?? 0) |
            ((bytes[offset + 1] ?? 0) << 8) |
            ((bytes[offset + 2] ?? 0) << 16) |
            ((bytes[offset + 3] ?? 0) << 24));
    }
    function writeUInt32LE(bytes, offset, value) {
        bytes[offset] = value & 0xff;
        bytes[offset + 1] = (value >> 8) & 0xff;
        bytes[offset + 2] = (value >> 16) & 0xff;
        bytes[offset + 3] = (value >> 24) & 0xff;
    }
    function findDesignName(document) {
        const dsnStream = document.streams.find((stream) => stream.parsed?.kind === 'dsn-stream')?.parsed;
        if (dsnStream?.kind !== 'dsn-stream' || typeof dsnStream.json !== 'object' || dsnStream.json === null) {
            return undefined;
        }
        const json = dsnStream.json;
        return (typeof json.name === 'string' ? json.name
            : typeof json.Name === 'string' ? json.Name
                : undefined);
    }
    function collectPageSymbolRoles(document) {
        const roles = new Map();
        for (const stream of document.streams) {
            for (const object of stream.decodedPageObjects ?? []) {
                if (object.kind === 'placed-symbol') {
                    roles.set(object.symbolName, object.role);
                }
            }
        }
        return roles;
    }
    function isPlacedSymbolRole(role) {
        return (object) => object.kind === 'placed-symbol' && object.role === role;
    }
    function isLogicalPagePort(object) {
        return object.kind === 'placed-symbol' && isPlacedPortSymbol(object);
    }
    function isDrawnInst(object) {
        return object.kind === 'drawn-inst';
    }
    function isWire(object) {
        return object.kind === 'wire';
    }
    function isBusWire(wire) {
        return wire.wireType === 21;
    }
    function isAlias(object) {
        return object.kind === 'alias';
    }
    function isJunction(object) {
        return object.kind === 'junction';
    }
    function isPortInstance(object) {
        return object.kind === 'port-instance';
    }
    function isSymbolPin(record) {
        return record.kind === 'symbol-pin';
    }
    function isCacheObjectGroupHeader(record) {
        return record.kind === 'view-header' || (record.kind === 'named-record' && record.name !== 'Dynamic Left');
    }
    function isSymbolObjectRecord(record) {
        return (record.kind === 'view-header' ||
            record.kind === 'symbol-pin' ||
            record.kind === 'display-property' ||
            record.kind === 'part-value' ||
            record.kind === 'pin-number-map' ||
            record.kind === 'named-record');
    }
    function isSymbolDisplayProperty(record) {
        return record.kind === 'display-property';
    }
    function isPartValue(record) {
        return record.kind === 'part-value';
    }
    function isPinNumberMap(record) {
        return record.kind === 'pin-number-map';
    }
    function firstSymbolPin(stream) {
        return stream.decodedSymbolObjects?.find(isSymbolPin);
    }
    function firstSymbolDisplayProperty(stream) {
        const property = stream.decodedSymbolObjects?.find(isSymbolDisplayProperty);
        return property ? symbolDisplayPropertyFromCfb(property) : undefined;
    }
    function isPortSymbolName(name, pageSymbolRoles) {
        return pageSymbolRoles.get(name) === 'port' || isPortConventionSymbolName(name);
    }
    function isPortConventionSymbolName(name) {
        return /^PORT/i.test(name);
    }
    function isPlacedPortSymbol(object) {
        return object.role === 'port' || (object.role === 'unknown' && isPortConventionSymbolName(object.symbolName));
    }
    function isOffPageSymbolName(name) {
        return /^OFF(?:PAGE|_)/i.test(name);
    }
    function isErcSymbolName(name) {
        return /^ERC/i.test(name);
    }
    function isBusName(name) {
        return Boolean(name && /\[[^\]]+\]/.test(name));
    }
    function referencePrefixOfDesignator(reference) {
        return /^([A-Za-z]+)\d+[A-Za-z0-9_-]*$/.exec(reference)?.[1] ?? '';
    }
    function leadingLettersOfPackageName(packageName) {
        return /^[A-Za-z]+/.exec(packageName)?.[0] ?? '';
    }
    function isHierarchyOccurrenceReferenceName(name) {
        return (name.length <= 64 &&
            (/^[A-Za-z][A-Za-z0-9_.-]*\d[A-Za-z0-9_.-]*$/.test(name) || /^[A-Za-z]+\?$/.test(name) || /^\d+$/.test(name)));
    }
    function isPlausibleHierarchyRefInstId(value) {
        return isDecodedHierarchyId(value) && value >= OrcadDsnCfbOccurrenceOwnership_1.HIERARCHY_ROW_ID_THRESHOLD;
    }
    const isDecodedHierarchyId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.isDecodedObjectId;
    function unsignedHierarchyId(value) {
        return value === undefined ? undefined : value >>> 0;
    }
    function isPlausibleHierarchyOccurrenceRow(string) {
        const refInstId = unsignedHierarchyId(string.precedingInt32Le16);
        if (refInstId === undefined) {
            return false;
        }
        if (isPlausibleHierarchyRefInstId(refInstId)) {
            return true;
        }
        return (refInstId > 0 &&
            refInstId <= 100_000 &&
            isPlausibleHierarchyOccurrenceId(unsignedHierarchyId(string.precedingInt32Le20)) &&
            string.followingUInt32Le === 0);
    }
    function isPlausibleHierarchyOccurrenceId(value) {
        return isDecodedHierarchyId(value) && value >= OrcadDsnCfbOccurrenceOwnership_1.HIERARCHY_ROW_ID_THRESHOLD;
    }
    function pkgNameFromGraphicName(graphicName) {
        return graphicName.replace(/\.(?:Normal|IEEE|Convert)$/i, '');
    }
    function stripNormalViewSuffix(value) {
        return value.replace(/\.(?:Normal|IEEE|Convert)$/i, '');
    }
    const NORMAL_VIEW_SUFFIX = '.Normal';
    function decodedViewSuffix(name) {
        return /\.(?:Normal|IEEE|Convert)$/i.exec(name ?? '')?.[0];
    }
    function isNumericString(value) {
        return Boolean(value && /^[0-9]+$/.test(value));
    }
    function isCacheCatalogMarkerToken(value) {
        return Boolean(value && ((value.length <= 2 && /^[^A-Za-z0-9]+$/.test(value)) || /^[A-Za-z]$/.test(value)));
    }
    function isMultipartSectionMarker(value) {
        return Boolean(value && (/^-\d+$/.test(value) || /^[A-Z]$/.test(value)));
    }
    function isSourceLibraryPath(value) {
        return Boolean(value && /\.(?:OLB|DSN)$/i.test(value));
    }
    function isDsnSourcePath(value) {
        return Boolean(value && /\.DSN$/i.test(value));
    }
    function isPlausibleCatalogPackageName(value) {
        if (value === 'R') {
            return true;
        }
        return ((value.length > 1 || value === 'R') &&
            /[A-Za-z0-9]/.test(value) &&
            !isSourceLibraryPath(value) &&
            !isNumericString(value) &&
            !value.startsWith('$') &&
            !/^(?:A|B|C|D|E|F|G|H|J|K|L|M|N|P|R|U|X|Y|Z|\+|-|OUT|V\+|V-|GND|VCC|Cadence|Title|Sheet|Rev|Size|Date:|of)$/i.test(value));
    }
    function isReferencePrefix(value) {
        return Boolean(value && /^[A-Z]{1,4}$/.test(value));
    }
    function isSheetReferencePrefix(value) {
        return Boolean(value && /^[A-Za-z][A-Za-z0-9_]{0,31}$/.test(value) && !isSourceLibraryPath(value));
    }
    function isDeviceDesignatorToken(value) {
        return /^[A-Z]{1,2}$/.test(value) || /^-\d+$/.test(value);
    }
    function isSectionOfCatalogPackage(candidate, packageName) {
        if (candidate === packageName) {
            return true;
        }
        if (candidate.startsWith(`${packageName}-`) || candidate.startsWith(`${packageName}_`)) {
            return true;
        }
        if (candidate.startsWith(packageName) && /^[A-Z]$/.test(candidate.slice(packageName.length))) {
            return true;
        }
        return false;
    }
    function isAlternatePackageCatalogSection(candidate, packageName) {
        return candidate !== packageName && candidate.startsWith(`${packageName}_`) && /_M\d+M\d+$/i.test(candidate);
    }
})(OrcadDsnCfbLogicalSchemaConverter || (exports.OrcadDsnCfbLogicalSchemaConverter = OrcadDsnCfbLogicalSchemaConverter = {}));
function convertOrCadDsnCfbToLogicalSchema(document, options = {}) {
    return OrcadDsnCfbLogicalSchemaConverter.convertDocument(document, options);
}
function parseOrCadDsnCfbLogicalSchema(fileContents, options = {}) {
    return OrcadDsnCfbLogicalSchemaConverter.parseLogicalSchema(fileContents, options);
}
//# sourceMappingURL=OrcadDsnCfbLogicalSchemaConverter.js.map